import { createI18n } from 'vue-i18n';

const messages = {
  en: {
    home: "Home",
    products: "Our Modules",
    subscriptions: "Subscriptions",
    features: "Core Features",
    coming_soon: "Innovations",
    contact_us: "Contact Us",
    hero_title: "The Offline-First POS with Accounting-Grade Integrity",
    hero_subtitle: "Built for businesses that demand accuracy. Features immutable invoices, append-only payment ledger, and conflict-safe multi-device sync.",
    book_demo: "Explore the Platform",
    hero_badge_new: "Finio 2.0: Now with Enhanced Audit Safety",

    // --- Value Pillars ---
    pillar_offline_title: "True Offline-First Architecture",
    pillar_offline_desc: "Not just 'intermittent' support. Finio stores 100% of data locally and syncs conflict-free when online. Zero downtime.",
    pillar_integrity_title: "Immutable Accounting Core",
    pillar_integrity_desc: "Invoices cannot be altered after creation. Payments are append-only. Your financial integrity is mathematically guaranteed.",
    pillar_conflict_title: "Atomic Transactions",
    pillar_conflict_desc: "Crash-safe operations ensure that if a device fails mid-transaction, your data remains consistent and uncorrupted.",
    pillar_modular_title: "Closed Accounting Periods",
    pillar_modular_desc: "Strict End-of-Day locking prevents retroactive tampering of historical financial periods.",

    // --- Why Finio Section ---
    why_finio_title: "Why Finio Is Not a Typical POS",
    why_finio_subtitle: "Most POS systems are just calculators. Finio is an accounting engine.",

    why_finio_card_1_title: "Calculators vs. Ledgers",
    why_finio_card_1_desc: "Generic POS systems treat sales as temporary numbers. Finio treats every sale as a permanent, immutable financial record.",

    why_finio_card_2_title: "Cloud-Dependent vs. Offline-First",
    why_finio_card_2_desc: "Others stop working when the internet drops. Finio operates locally with server-grade integrity, syncing only when stable.",

    why_finio_card_3_title: "Feature-Bloat vs. Accuracy",
    why_finio_card_3_desc: "We don't chase features that compromise reliability. Every module is built to ensure your numbers match reality.",

    // --- Solutions Section ---
    solutions_title: "Tailored Solutions for Every Industry",
    solution_restaurant_title: "Restaurants & Cafes",
    solution_restaurant_desc: "Manage orders, kitchen, delivery, and reservations from a single platform.",
    solution_retail_title: "Retail Stores",
    solution_retail_desc: "Streamline inventory, sales, and customer loyalty with ease.",
    solution_service_title: "Service Businesses",
    solution_service_desc: "Booking, invoicing, and CRM solutions for service providers and hotels.",

    // --- Modules Section ---
    our_modules_title: "A Production-Ready, Modular Ecosystem",

    // 1. Sales & Operations
    mod_sales_title: "Sales & Operations",
    mod_pos_title: "POS System",
    mod_pos_desc: "High-speed offline-first point of sale with barcode scanning, multi-cart management, and conflict-free sync.",
    mod_kds_title: "Kitchen Display System",
    mod_kds_desc: "Direct link between front-of-house and kitchen. Real-time station routing, timers, and order status tracking.",
    mod_delivery_title: "Delivery & Orders",
    mod_delivery_desc: "Manage delivery platforms, drivers, and phone orders efficiently from a single dashboard.",

    // 2. Accounting & Finance
    mod_finance_title: "Accounting & Finance",
    mod_invoice_title: "Invoicing Engine",
    mod_invoice_desc: "Generate immutable, tax-compliant invoices. Track payments, deposits, and customer credit history.",
    mod_cash_title: "Cash Management",
    mod_cash_desc: "Secure opening float, blind closures, and precise cash reconciliation to prevent theft and errors.",
    mod_expense_title: "Expenses & Capital",
    mod_expense_desc: "Track petty cash expenses, supplier payments, and operational costs directly from the register.",

    // 3. Management & Back-Office
    mod_mgmt_title: "Management & Back-Office",
    mod_inventory_title: "Inventory Control",
    mod_inventory_desc: "Real-time stock tracking with movement logs, low-stock alerts, and supplier purchase orders.",
    mod_crm_title: "Customers & Suppliers",
    mod_crm_desc: "Detailed CRM profiles with purchase history, loyalty points, and supplier ledgers.",
    mod_admin_title: "Accountant Web Panel",
    mod_admin_desc: "Remote access for accountants to audit sales, export journals, and manage configuration.",

    // 4. Hospitality & Advanced
    mod_hosp_title: "Hospitality & Advanced",
    mod_floor_title: "Floor Management",
    mod_floor_desc: "Visual table map with occupancy status, reservation management, and waiter assignment.",
    mod_pms_title: "Hotel & PMS Module",
    mod_pms_desc: "Complete hotel management: room booking, housekeeping, and guest check-in/out integration.",
    mod_api_title: "Open API Ecosystem",
    mod_api_desc: "Connect third-party apps, self-order kiosks, and web ordering platforms via our secure API.",

    // --- Legacy Keys (Restored for Compatibility) ---
    pos_module_title: "Core POS Engine",
    pos_module_description: "Offline-native transactional core. Handles split payments, void auditing, and session management without internet dependency.",
    kds_module_title: "Production System",
    kds_module_description: "Zero-latency routing. Orders are fire-and-forget from POS, guaranteed delivery to prep stations with visual timers.",
    inventory_module_title: "Perpetual Inventory",
    inventory_module_description: "FIFO cost tracking with recipe deduction. Supports complex nested compounds and commissary transfer workflows.",
    pms_module_title: "Property Management",
    pms_module_description: "Folio management integrated with POS. Room charges are atomic transactions validated against guest status.",
    printing_module_title: "Print Routing Server",
    printing_module_description: "Rule-based print routing. Supports failover re-routing, legacy ESC/POS hardware, and custom template injection.",
    web_server_module_title: "Central Command",
    web_server_module_description: "Real-time aggregation of multi-site data. Push menu updates, pull audit logs, and manage user permissions globally.",

    // --- Services Section ---
    professional_services_title: "Professional Services",
    custom_software_title: "Custom Software Development",
    custom_software_description: "We offer custom software solutions tailored to your unique business needs, from web to mobile applications.",
    hardware_software_title: "Hardware & Software Solutions",
    hardware_software_description: "We provide seamless integration of hardware and software to run your systems efficiently, including support and installation.",
    consulting_title: "Consulting & Integrated Solutions",
    consulting_description: "Our team offers comprehensive consulting to help you choose and implement the best technology solutions for your project.",

    // --- Core Features Section ---
    // --- Core Features Section ---
    core_features_title: "System Architecture & Capabilities",

    // 1. Accounting-Grade Protection
    feat_protect_title: "Immutable Financial Core",
    feat_protect_desc: "Double-entry ledger recording with append-only verification. Historical data is mathematically locked against tampering.",

    // 2. Offline-First Sync Engine
    feat_sync_engine_title: "Conflict-Free Replication",
    feat_sync_engine_desc: "Local-first database architecture (Hive). Operations commit locally instantly and replicate to cloud with eventual consistency.",

    // 3. Trusted Time & Audit Safety
    feat_audit_title: "Server-Authoritative Time",
    feat_audit_desc: "Transactions are timestamped by server authority to prevent local clock manipulation and closing-time fraud.",

    // 4. Professional Closures
    feat_closure_title: "Professional Day Closures",
    feat_closure_desc: "Manage opening floats, perform blind Z-closures, and reconcile cash with automated variance tracking.",

    // 5. Roles & Permissions
    feat_roles_title: "Role-Based Access Control (RBAC)",
    feat_roles_desc: "Define exact access levels for cashiers, managers, accountants, and kitchen staff with granular permission gates.",

    // 6. Integrations & APIs
    feat_integrations_title: "Integrations & APIs",
    feat_integrations_desc: "Connect with ERPs, WooCommerce, and payment gateways via our extensive developer API.",

    // --- Everything Included (features beyond the essentials) ---
    all_features_title: "Everything Included",
    all_features_subtitle: "Powerful capabilities already built into Finio — beyond the essentials.",
    all_group_revenue: "Revenue & Growth",
    all_revenue_giftcards_title: "Gift Cards",
    all_revenue_giftcards_desc: "Issue, reload and void gift cards, with end-of-day cash tracking and a live liability panel.",
    all_revenue_loyalty_title: "Loyalty Program",
    all_revenue_loyalty_desc: "Bronze, Silver and Gold tiers with points earning, redemption and expiry.",
    all_revenue_coupons_title: "Coupons & Promotions",
    all_revenue_coupons_desc: "Usage limits, scheduled campaigns and ROI statistics per coupon.",
    all_revenue_loans_title: "Customer Credit & Loans",
    all_revenue_loans_desc: "B2B customer loans and credit limits with balance tracking.",
    all_group_ops: "Operations & Loss Prevention",
    all_ops_shifts_title: "Staff Shifts & Scheduling",
    all_ops_shifts_desc: "Weekly recurring shifts, conflict detection and push reminders.",
    all_ops_loss_title: "Loss Prevention Suite",
    all_ops_loss_desc: "Waste log, stocktake / cycle count and refund + replace — stop losing money.",
    all_ops_fefo_title: "FEFO & Batch Tracking",
    all_ops_fefo_desc: "Expiration and batch tracking with first-expired-first-out costing.",
    all_ops_transfers_title: "Multi-Location Transfers",
    all_ops_transfers_desc: "Move stock between locations and warehouses with full traceability.",
    all_ops_barcode_title: "Barcode Label Printing",
    all_ops_barcode_desc: "Generate and print barcode labels and price tags.",
    all_ops_purchasing_title: "Purchasing & Suppliers",
    all_ops_purchasing_desc: "Purchase orders and supplier ledgers for complete back-office control.",
    all_group_ai: "AI & Guest Experience",
    all_ai_translation_title: "AI Product Translation",
    all_ai_translation_desc: "Translate product names and descriptions automatically.",
    all_ai_enrichment_title: "ChatGPT Enrichment",
    all_ai_enrichment_desc: "Enrich catalogue data with descriptions, categories and attributes.",
    all_ai_perplexity_title: "Perplexity Research",
    all_ai_perplexity_desc: "Pull live product and market insights straight into Finio.",
    all_ai_waiter_title: "Call Waiter",
    all_ai_waiter_desc: "Guests call staff in real time with a red-alert floor view.",
    all_ai_feedback_title: "Customer Feedback + QR",
    all_ai_feedback_desc: "Collect reviews through a web form and QR code.",
    all_ai_kiosk_title: "Kiosk / Self-Order",
    all_ai_kiosk_desc: "Self-service kiosk and self-ordering mode for busy venues.",
    all_ai_map_title: "Restaurant Map",
    all_ai_map_desc: "Drag-and-drop floor plans with multi-floor and multi-location support.",
    all_ai_glovo_title: "Glovo Marketplace",
    all_ai_glovo_desc: "Ingest Glovo delivery orders directly into your POS.",
    all_group_finance: "Back-Office & Finance",
    all_fin_fiscal_title: "Fiscal Compliance",
    all_fin_fiscal_desc: "fiskaly SIGN ES and fiskaltrust (AT/DE/FR) for certified fiscalization.",
    all_fin_dunning_title: "Dunning Reminders",
    all_fin_dunning_desc: "Automated payment reminders for overdue customer balances.",
    all_fin_payroll_title: "Payroll & Salaries",
    all_fin_payroll_desc: "Manage salaries and generate payroll PDFs from the accountant panel.",
    all_fin_docs_title: "Document Chain",
    all_fin_docs_desc: "Refund + replace, document quotas and the BC → BL → FA → AV chain.",
    all_group_platform: "Platform & Reliability",
    all_plat_lan_title: "LAN Offline Redundancy",
    all_plat_lan_desc: "Multi-device offline operation over your local network, with automatic sync.",
    all_plat_desktop_title: "Desktop Apps",
    all_plat_desktop_desc: "Native apps for Windows, macOS and Linux, plus Web, iOS and Android.",

    // --- Comparison: real PRO-only gates ---
    sub_cat_pro_only: "PRO only",
    sub_cmp_refund_replace: "Refund + Replace",
    sub_cmp_quota: "Document quota management",
    sub_cmp_doc_chain: "Document chain (BC → BL → FA → AV)",
    sub_cmp_whatsapp_invoice: "WhatsApp invoices",
    sub_cmp_max_registers: "Maximum cash registers",
    sub_cmp_reconciliation: "Cash reconciliation",
    sub_cmp_customer_pdf: "Customer PDF statements",
    sub_cmp_stock_reports: "Stock reports",
    sub_cmp_delivery_orders: "Delivery orders",
    sub_cmp_store_orders: "Web / store orders",
    sub_cmp_health_date: "Health date-range reports",
    sub_cmp_liquid_capital: "Liquid capital PDF",

    // Legacy keys (kept if referenced elsewhere, but mostly replaced above)
    feature_auth_title: "Multi-Tenant Authentication",
    feature_auth_desc: "Secure, role-based authentication designed for a multi-tenant architecture, ensuring data isolation and security.",
    feature_offline_title: "Zero-Latency Local Commit",
    feature_offline_desc: "Keep your operations running smoothly, even without an internet connection. Data syncs automatically once you're back online.",
    feature_sync_title: "Background Delta Sync",
    feature_sync_desc: "Leverages Firebase and a powerful sync service to ensure data is always up-to-date across all devices and modules.",
    feature_remote_title: "Accountant & Admin Web Panel",
    feature_remote_desc: "Secure remote access for accountants and admins to audit sales, journals, and configuration.",
    feature_reporting_title: "Reconciled Financial Statements",
    feature_reporting_desc: "Gain critical insights with comprehensive analytics on sales, product performance, and customer behavior.",
    feature_security_title: "Role-Based Access Control (RBAC)",
    feature_security_desc: "From permission gates in the UI to automatic data backups and encryption, your data is always protected.",
    feature_pms_rooms: "Room & Occupancy Management",
    feature_pms_booking: "Guest Booking & CRM",
    feature_print_network: "Network Printer Discovery",
    feature_print_custom: "Customizable Receipt Templates",

    // --- Innovations Section ---
    innovations_title: "Future-Ready Solutions",
    innovations_intro: "Be among the first to experience the future of business automation. Join our early adopter program and shape the next generation of smart business solutions.",
    smart_store_title: "Finio Smart Store",
    smart_store_desc: "Enable a checkout-free experience with AI-powered cameras, self-buy devices, and automated theft detection.",
    smart_store_feature_1_title: "AI-Powered Cameras",
    smart_store_feature_1_desc: "Advanced computer vision tracks customer movements and product selections in real-time.",
    smart_store_feature_2_title: "Self-Checkout Devices",
    smart_store_feature_2_desc: "Customers scan and pay for items independently, reducing wait times and staff requirements.",
    smart_store_feature_3_title: "Automated Theft Detection",
    smart_store_feature_3_desc: "Machine learning algorithms identify suspicious behavior and alert security in real-time.",
    smart_store_feature_4_title: "Smart Inventory Tracking",
    smart_store_feature_4_desc: "Automatically track stock levels as items are picked, ensuring accurate inventory counts.",
    smart_airbnb_title: "Finio Smart Airbnb",
    smart_airbnb_desc: "Automate your property management with AI booking confirmations, smart lock entry, and dynamic access codes.",
    smart_airbnb_feature_1_title: "AI Booking Assistant",
    smart_airbnb_feature_1_desc: "Automated responses to inquiries, instant booking confirmations, and intelligent pricing suggestions.",
    smart_airbnb_feature_2_title: "Smart Lock Integration",
    smart_airbnb_feature_2_desc: "Generate unique access codes for each guest, automatically activated and deactivated based on bookings.",
    smart_airbnb_feature_3_title: "Dynamic Access Management",
    smart_airbnb_feature_3_desc: "Codes change automatically after checkout, ensuring maximum security for your property.",
    smart_airbnb_feature_4_title: "Guest Communication Hub",
    smart_airbnb_feature_4_desc: "Centralized messaging, check-in instructions, and automated follow-ups for reviews.",
    early_adopter_title: "Be an Early Adopter",
    early_adopter_intro: "Join our exclusive early adopter program and get special pricing, priority support, and the opportunity to influence product development.",
    early_adopter_benefit_1: "Special launch pricing",
    early_adopter_benefit_2: "Priority implementation and support",
    early_adopter_benefit_3: "Direct input on features and roadmap",
    early_adopter_benefit_4: "Exclusive access to beta features",
    early_adopter_form_title: "Express Your Interest",
    early_adopter_form_intro: "Fill out the form below to be among the first to experience these innovative solutions.",
    solution_interested: "Solution of Interest",
    select_solution: "Select a solution",
    business_type: "Type of Business",
    current_challenges: "Current Challenges",
    early_adopter_submit: "Join Early Adopter Program",

    // Other keys from previous version...
    footer_finio_description: "The most advanced POS system to manage and grow your business efficiently and professionally.",
    quick_links: "Quick Links",
    support: "Support",
    help_center: "Help Center",
    terms_conditions: "Terms & Conditions",
    privacy_policy: "Privacy Policy",
    contact_us_footer: "Contact Us",
    contact_phone: "+212 664 241 482",
    contact_phone_alt: "+212 808 646 672",
    contact_phone_eu: "+30 694 569 2586",
    contact_email: "email",
    contact_address: "Av. Aoussou Ou Basselam, Tétouan, Morocco",
    copyright: "© 2025 Finio Solutions. All rights reserved.",
    company_name: "Finio Solutions",
    company_ice: "ICE: 003689594000059",
    company_siege: "Based: Tetouan, Morocco",
    company_address_full: "Av. Aoussou Ou Basselam, Tétouan, Morocco",
    contact_page_title: "Contact Us",
    contact_page_intro: "We are here to answer all your inquiries and provide the necessary support. Do not hesitate to contact us.",
    send_message: "Send us a Message",
    full_name: "Full Name",
    email_address: "Email Address",
    phone_number: "Phone Number",
    subject: "Subject",
    your_message: "Your Message",
    send_button: "Send Message",
    contact_info_title: "Contact Information",

    // --- Navigation Links ---
    nav_documentation: "Documentation",
    nav_faq: "FAQ",
    nav_security: "Security & Data Protection",
    nav_changelog: "Changelog",
    nav_about_us: "About Us",
    nav_compliance: "Compliance & Audit",

    // Footer Columns

    footer_col_product: "Product",
    footer_col_resources: "Resources",
    footer_col_terms: "Terms",
    footer_col_connect: "Connect",

    // --- About Us Page ---
    // --- About Us Page ---
    about_hero_title: "Engineered for Financial Truth",
    about_hero_subtitle: "We don't just build POS software. We build audit-grade financial engines that prevent fraud, eliminate error, and guarantee data consistency.",
    about_mission_title: "Our Mandate",
    about_mission_text: "To eliminate financial ambiguity in retail and hospitality. We provide the mathematical certainty that every cent earned is accounted for, tracked, and secured.",
    about_vision_title: "Our Engineering Standard",
    about_vision_text: "We reject the industry standard of 'good enough'. We envision a future where business software is as reliable as a bank vault and as precise as a Swiss watch.",
    about_values_title: "Operational Ethics",
    about_values_text: "Accuracy over speed. Integrity over features. We prioritize the safety of your data above all marketing claims.",
    about_story_title: "The Zero-Tolerance Origin",
    about_story_paragraph_1: "Finio began when we audited a restaurant chain using a popular cloud POS. We found ghost orders, deleted invoices, and floating inventory that vanished without a trace. The system allowed it. The database permitted it.",
    about_story_paragraph_2: "We built Finio to close those loopholes. We designed an immutable ledger where deletion is impossible—only reversal. We built a sync engine that survives network crashes without corrupting a single byte.",
    about_story_paragraph_3: "Today, Finio is the standard for operators who demand truth. We don't just help you sell; we help you prove what you sold. From single stores to enterprise chains, we provide the backbone for audit-ready operations.",
    about_why_choose_title: "Why Operators Trust Finio",
    about_reason_1_title: "Immutable Ledger",
    about_reason_1_desc: "Invoices and payments are append-only. No one—not even you—can silently delete a transaction record.",
    about_reason_2_title: "Military-Grade Encryption",
    about_reason_2_desc: "Data is encrypted at rest and in transit. Your business intelligence remains rigorously private.",
    about_reason_3_title: "Offline Sovereignty",
    about_reason_3_desc: "We don't rent you access to your data. It lives on your device, fully functional without any external server dependency.",
    about_reason_4_title: "Audit-Ready Reports",
    about_reason_4_desc: "Our reports aren't just pretty charts. They are mathematically reconciled financial statements ready for your accountant.",
    about_reason_5_title: "Conflict-Free Sync",
    about_reason_5_desc: "Our proprietary sync engine handles multi-device collisions with deterministic logic, ensuring zero data loss.",
    about_reason_6_title: "Ecosystem Integrity",
    about_reason_6_desc: "Every module—KDS, Inventory, Web Panel—feeds into the same single source of truth. No data silos.",
    about_stats_title: "System Reliability Metrics",
    about_stat_1: "Data Conflicts",
    about_stat_2: "Uptime",
    about_stat_3: "Audit Score",
    about_stat_4: "Support SLA",
    about_team_title: "The Engineering Team",
    about_team_intro: "We are not marketers. We are systems architects, security specialists, and financial software engineers dedicated to the science of business management.",
    about_team_member_1_name: "Systems Architecture",
    about_team_member_1_role: "Core Infrastructure",
    about_team_member_1_bio: "Designing the immutable ledger and offline-first database topology that powers every Finio instance.",
    about_team_member_2_name: "Security & Compliance",
    about_team_member_2_role: "Data Integrity",
    about_team_member_2_bio: "Ensuring every transaction meets strict audit standards and is protected against tampering.",
    about_team_member_3_name: "Customer Operations",
    about_team_member_3_role: "Deployment Success",
    about_team_member_3_bio: "Helping enterprises migrate their complex data structures to Finio's rigorous environment.",
    about_team_member_4_name: "Product Design",
    about_team_member_4_role: "UX & Workflow",
    about_team_member_4_bio: "Translating complex accounting logic into fast, intuitive workflows for frontline staff.",
    about_core_values_title: "Core Protocols",
    about_value_1_title: "Truth First",
    about_value_1_desc: "We never fudge numbers. If the math doesn't balance, the system halts. Ambiguity is a bug.",
    about_value_2_title: "Local First",
    about_value_2_desc: "The device in your hand is the source of truth. The cloud is just a backup.",
    about_value_3_title: "Zero Data Loss",
    about_value_3_desc: "We are paranoid about data safety. We assume networks fail, power cuts happen, and hardware breaks.",
    about_value_4_title: "Transparent Logic",
    about_value_4_desc: "We don't hide calculations. Every total can be traced back to its atomic transactions.",
    about_value_5_title: "Partnership",
    about_value_5_desc: "We build tools for serious operators. If you care about accuracy, we are your partner.",
    about_growth_title: "The Future of Integrity",
    about_growth_text_1: "As we scale, our commitment to the core rigidness of accounting remains unchanged. We are expanding into AI and automation, but never at the cost of accuracy.",
    about_growth_text_2: "Our roadmap includes 'Smart Store' and 'Smart Property' modules, but unlike others, ours are built on the same immutable foundation. AI suggestions will be audit-logged.",
    about_growth_text_3: "Finio is building the operating system for the next generation of transparent, efficient, and audit-safe businesses in North Africa and beyond.",
    about_cta_title: "Ready for Serious Business?",
    about_cta_subtitle: "Join the operators who have stopped worrying about data discrepancies and started focusing on growth.",
    explore_products: "View Technical Modules",

    // --- Checkout Page ---
    checkout_title: "Complete Your Subscription",
    checkout_intro: "Fill in your details below to complete your subscription. We'll contact you within 24 hours to confirm your plan.",
    complete_payment: "Complete Payment",
    subscription_success: "Success! Your subscription has been activated.",
    fill_personal_info: "Please fill in your Personal Information first.",
    email_required_card: "Email address is required for Card payments.",
    personal_info_title: "Personal Information",
    company_name_optional: "Company Name (Optional)",
    city: "City",
    country: "Country",
    payment_method: "Payment Method",
    select_payment: "Select payment method",
    payment_card: "By Card",
    payment_cash: "By Cash",
    payment_cheque: "By Cheque",
    payment_bank_cheque: "By Bank Cheque",
    subscription_details_title: "Subscription Details",
    selected_plan: "Selected Plan",
    subscription_duration: "Subscription Duration",
    select_duration: "Select duration",
    duration_3months: "3 Months",
    duration_6months: "6 Months (1 Month Free)",
    duration_12months: "12 Months (2.5 Months Free)",
    total_amount: "Total Amount",
    complete_subscription: "Complete Subscription",
    redirecting_to_payment: "Redirecting to secure payment...",
    sub_recurring_note: "You'll be charged {amount} MAD for the selected period now, then renew automatically at {yearly} MAD/year until you cancel.",
    payment_success_title: "Payment Successful",
    payment_success_desc: "Thank you! Your payment has been received and your subscription is being activated.",
    payment_failed_title: "Payment Failed",
    payment_failed_desc: "Your payment could not be completed and no charge was made. You can try again or contact our team.",
    order_reference: "Order reference",
    amount_paid: "Amount paid",
    try_again: "Try Again",

    // --- Hardware Page (Morocco only) ---
    nav_hardware: "Hardware",
    hw_title: "POS Hardware Packs",
    hw_subtitle: "Complete point-of-sale setups — assembled, tested and delivered anywhere in Morocco.",
    hw_morocco_badge: "Available in Morocco only",
    hw_delivery_badge: "Delivery from 50 MAD",
    hw_packs_title: "Choose Your Pack",
    hw_includes: "What's included",
    hw_hardware_price: "Hardware price",
    hw_with_scanner: "With 2D scanner",
    hw_get_quote: "Get this pack",
    hw_pricelist_title: "Components We Offer",
    hw_pricelist_intro: "All the components we can supply for your setup.",
    hw_item_col: "Item",
    hw_price_col: "Price",
    hw_software_title: "Finio software is billed separately",
    hw_software_desc: "Hardware prices are one-time and Finio software is billed separately. When you add a yearly Finio subscription to your hardware order, you get an extra 30% off the first year only — for example, the Starter plan (normally 1900 MAD/year) becomes 1330 MAD for the first year, then renews at 1900 MAD/year.",
    hw_cta_title: "Need help choosing?",
    hw_cta_desc: "Tell us about your business and we'll recommend the right pack.",
    hw_pack_starter_name: "Starter Pack",
    hw_pack_starter_desc: "The essential setup for small shops and cafés.",
    hw_pack_android_name: "Android Pack",
    hw_pack_android_desc: "Touch-screen POS running on Android — compact and flexible.",
    hw_pack_pro_name: "Professional Pack",
    hw_pack_pro_desc: "The all-in-one terminal setup for high-volume businesses.",
    hw_item_touch: "Touch Screen 17\"",
    hw_item_printer: "Thermal Printer 80mm",
    hw_item_scanner: "2D Scanner",
    hw_item_drawer: "Cash Drawer (Tiroir)",
    hw_item_tablet: "Tablet G-Tab T12",
    hw_item_support: "High-End Stand",
    hw_item_pc: "PC i5",
    hw_item_aio: "All-In-One Terminal",
    hw_item_tvbox: "AndroidBox",
    // Hardware order / configuration page
    hw_order_title: "Configure Your Pack",
    hw_order_subtitle: "Choose your options and payment method, then place your order.",
    hw_order_back: "Back to packs",
    hw_order_selected_pack: "Selected pack",
    hw_scanner_title: "2D Scanner",
    hw_scanner_without: "Without scanner",
    hw_scanner_with: "With 2D scanner",
    hw_subscription_title: "Finio Software",
    hw_sub_hardware_only: "Hardware only",
    hw_sub_add: "Add Finio subscription",
    hw_sub_plan: "Software plan",
    hw_sub_first_year: "First year (30% off)",
    hw_sub_renews: "Renews at",
    hw_sub_per_year: "/year",
    hw_payment_title: "Payment method",
    hw_pay_card: "By card",
    hw_pay_card_desc: "Secure online payment via Chari Pay",
    hw_pay_cod: "Cash on delivery",
    hw_pay_transfer: "Bank transfer",
    hw_pay_transfer_note: "We'll send you the bank details right after you place the order.",
    hw_details_title: "Your details",
    hw_field_name: "Full name",
    hw_field_phone: "Phone",
    hw_field_email: "Email",
    hw_field_company: "Company (optional)",
    hw_field_city: "City",
    hw_field_address: "Address",
    hw_summary_title: "Order summary",
    hw_sum_pack: "Hardware pack",
    hw_sum_scanner: "2D scanner",
    hw_sum_subscription: "Finio software (1st year)",
    hw_sum_delivery: "Delivery",
    hw_sum_total: "Total",
    hw_place_order: "Place order",
    hw_processing: "Processing...",
    hw_order_success_title: "Order received!",
    hw_order_success_desc: "Thank you. Our team will contact you shortly to confirm delivery and payment.",
    hw_new_order: "Place another order",
    hw_required_missing: "Please fill in the required fields.",
    months: "months",
    month_free: "month free",
    months_free: "months free",
    subscriptions_page_title: "Transparent Plans for Your Business",
    subscriptions_intro: "Choose the plan that fits your needs with no hidden fees.",
    pricing_basic_title: "Starter",
    pricing_basic_price: "200",
    pricing_pro_title: "Business",
    pricing_pro_price: "350",
    pricing_custom_title: "Custom",
    pricing_custom_contact: "Contact Us",
    pricing_currency: "MAD/month",
    pricing_basic_yearly: "Yearly 1900 MAD/year",
    pricing_basic_yearly_savings: "2.5 months free",
    pricing_pro_yearly: "Yearly 3325 MAD/year",
    pricing_pro_yearly_savings: "2.5 months free",
    pricing_most_popular: "Most Popular",
    pricing_basic_feature_1: "Full-featured application",
    pricing_basic_feature_2: "Inventory and customer management",
    pricing_basic_feature_3: "Smart analytics",
    pricing_basic_feature_4: "24/7 technical support",
    pricing_pro_feature_1: "All Basic features",
    pricing_pro_feature_2: "Web dashboard for accountant",
    pricing_pro_feature_3: "Document management (Quotes, Invoices)",
    pricing_pro_feature_4: "Advanced reports",
    pricing_pro_feature_5: "Priority technical support",
    pricing_custom_feature_1: "Restaurant System (KDS)",
    pricing_custom_feature_2: "Hotel System (PMS)",
    pricing_custom_feature_3: "Full customization",
    pricing_custom_feature_4: "Integration with your existing systems",
    pricing_custom_feature_5: "A price tailored to your needs",
    pricing_get_started: "Get Started",
    pricing_choose_plan: "Choose Business Plan",
    pricing_request_quote: "Request a Quote",
    contact_plans_title: "For plan details and pricing",
    contact_plans_text: "Please contact us directly to get a customized quote that meets your business requirements.",
    contact_us_plans: "Contact Us",
    learn_more: "Learn More",

    // --- Custom Plan Content ---
    sub_custom_title: "Finio Custom",
    sub_custom_tagline: "Tailored enterprise solutions for complex environments",
    sub_custom_pricing_contact: "Contact us for a quote",
    sub_custom_everything_pro: "EVERYTHING IN BUSINESS, PLUS:",

    sub_custom_f1_title: "Full Hotel System (Enterprise PMS)",
    sub_custom_f1_desc: "Rooms, occupancy, services, multi-department and multi-property management.",
    sub_custom_f2_title: "Enterprise ERP Integration",
    sub_custom_f2_desc: "Connect Finio with your existing ERP systems for accounting, inventory, HR, and reporting.",
    sub_custom_f3_title: "Smart Store Solutions",
    sub_custom_f3_desc: "Advanced retail automation, connected devices, and custom operational workflows.",
    sub_custom_f4_title: "Smart House & Smart Property Integration",
    sub_custom_f4_desc: "Integration with IoT systems, access control, automation, and smart infrastructure.",
    sub_custom_f5_title: "Full System Customization",
    sub_custom_f5_desc: "Custom modules, workflows, and business logic adapted to your exact needs.",
    sub_custom_f6_title: "Advanced API & System Connectivity",
    sub_custom_f6_desc: "Secure integrations with third-party platforms and internal enterprise systems.",
    sub_custom_f7_title: "Multi-location & Enterprise Scalability",
    sub_custom_f7_desc: "Designed for large companies, chains, and complex organizational structures.",
    sub_custom_f8_title: "Dedicated Project Analysis & Deployment",
    sub_custom_f8_desc: "Technical study, configuration, and deployment aligned with your business requirements.",
    sub_custom_f9_title: "Custom Pricing & Long-Term Support",
    sub_custom_f9_desc: "A pricing model tailored to scope, integrations, and support level.",

    sub_custom_contact_text: "Get in touch for a personalized proposal.",

    // --- Subscription Page Enhanced Content ---
    sub_safety_header: "Safety and accounting integrity are included by default.",
    sub_safety_desc: "Finio is built with strong accounting and data protection at its core. Both plans share the same secure foundation — the difference is how much control, visibility, and scalability you need.",
    sub_safety_note: "We do not sell safety as an upgrade.",

    sub_basic_title: "Finio Starter",
    sub_basic_tagline: "Everything you need to sell, manage, and close your day with confidence.",
    sub_basic_desc: "Finio Starter includes the full POS experience with built-in protections that ensure your data is always correct and reliable.",
    sub_included_features: "Included Features",
    sub_basic_f1: "Full Point of Sale (retail, fast food, restaurant)",
    sub_basic_f2: "Product, customer, and supplier management",
    sub_basic_f3: "Barcode scanning and multiple price levels",
    sub_basic_f4: "Discounts, coupons, and saved carts",
    sub_basic_f5: "Cash register opening & end-of-day (Z / clôture)",
    sub_basic_f6: "Invoice creation, printing, and tracking",
    sub_basic_f7: "Secure refunds and customer balance handling",
    sub_basic_f8: "Offline-first operation with automatic sync",
    sub_basic_f9: "Immutable invoices and tamper-resistant accounting",
    sub_basic_f10: "Multi-device safe synchronization",
    sub_best_for: "Best For",
    sub_basic_best_for: "Small and medium businesses that manage daily operations directly from the app and want a reliable, compliant POS without complexity.",

    sub_pro_title: "Finio Business",
    sub_pro_tagline: "Designed for growth, structure, and professional accounting workflows.",
    sub_pro_desc: "Finio Business builds on everything in Starter and adds advanced modules for administration, accounting, and automation.",
    sub_everything_basic_plus: "Everything in Starter, plus:",
    sub_pro_f1: "Accountant & Administration Web Panel",
    sub_pro_f1_detail: "Manage employee salaries, expenses, invoices (B2C & B2B), and accounting statistics from a dedicated web interface.",
    sub_pro_f2: "Advanced financial reports and analytics",
    sub_pro_f3: "Kitchen Display System (KDS) with stations and timers",
    sub_pro_f4: "Hotel / PMS modules (rooms, occupancy, services)",
    sub_pro_f5: "Web ordering (waiter tablets & customer self-ordering)",
    sub_pro_f6: "WhatsApp and automated notifications",
    sub_pro_f7: "Product & integration APIs",
    sub_pro_f8: "WooCommerce synchronization",
    sub_pro_f9: "Tools for scaling and multi-workflow operations",
    sub_pro_f10: "Advanced Printing System (Label & Receipt)",
    sub_pro_best_for: "Growing businesses, restaurants with kitchens, hotels, multi-device environments, and companies working with accountants or external systems.",

    sub_safety_compliance_title: "A note on safety & compliance",
    sub_safety_compliance_intro: "We do not sell safety as an upgrade. Both BASIC and PRO include:",
    sub_compliance_1: "Immutable invoices",
    sub_compliance_2: "Append-only payment ledger",
    sub_compliance_3: "Crash-safe payments",
    sub_compliance_4: "Period-locked accounting",
    sub_compliance_5: "Proper cash, bank, and customer ledger handling",
    sub_compliance_6: "Multi-country accounting-ready foundations",
    sub_safety_difference: "The difference between plans is capability, not risk.",

    sub_compare_title: "Feature Comparison",
    sub_compare_feature: "Feature",
    sub_cat_core_pos: "Core POS Features",
    sub_cmp_pos: "Full Point of Sale System",
    sub_cmp_products: "Product, customer & supplier management",
    sub_cmp_barcode: "Barcode scanning & price levels",
    sub_cmp_discounts: "Discounts, coupons & saved carts",
    sub_cmp_eod: "End-of-day (Z / clôture)",
    sub_cmp_invoices: "Invoice creation & tracking",
    sub_cmp_refunds: "Secure refunds & customer balance",
    sub_cmp_offline: "Offline-first with auto sync",
    sub_cat_safety: "Safety & Compliance",
    sub_cmp_immutable: "Immutable invoices",
    sub_cmp_ledger: "Append-only payment ledger",
    sub_cmp_crash_safe: "Crash-safe payments",
    sub_cmp_period_lock: "Period-locked accounting",
    sub_cat_advanced: "Advanced Features (PRO Only)",
    sub_cmp_web_panel: "Accountant & Admin Web Panel",
    sub_cmp_advanced_reports: "Advanced financial reports",
    sub_cmp_kds: "Kitchen Display System (KDS)",
    sub_cmp_hotel: "Hotel / PMS modules",
    sub_cmp_web_ordering: "Web ordering (tablets & self-order)",
    sub_cmp_whatsapp: "WhatsApp notifications",
    sub_cmp_api: "Product & integration APIs",
    sub_cmp_woocommerce: "WooCommerce synchronization",
    sub_cmp_print: "Advanced Printing System",
    sub_cmp_remote: "Remote Access (iOS & Android)",
    sub_cmp_auth: "Multi-tenant Auth & Permissions",

    sub_which_plan_title: "Which plan should I choose?",
    sub_choose_basic: "Choose Starter if you want a complete, secure POS and manage everything from the app.",
    sub_choose_pro: "Choose Business if you need accounting administration, advanced modules, automation, or integrations.",
    sub_choose_custom: "A fully customized Finio solution designed around your existing systems, operations, and enterprise needs. Finio Custom adapts to your business — not the other way around.",

    // --- 404 Page ---
    page_not_found_title: "Page Not Found",
    page_not_found_desc: "The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.",
    back_to_home: "Back to Home",

    // --- Help Center ---
    help_welcome: "Welcome to Finio Help Center",
    help_intro: "Find answers to frequently asked questions and get the support you need to make the most of Finio PRO.",
    help_faq_title: "Frequently Asked Questions",
    help_faq_1_q: "What is Finio PRO?",
    help_faq_1_a: "Finio PRO is a complete restaurant and hospitality management platform that includes POS, KDS, inventory management, web server, PMS, and advanced printing systems.",
    help_faq_2_q: "Does Finio PRO work offline?",
    help_faq_2_a: "Yes! Finio PRO features an offline-first architecture that keeps your operations running smoothly even without internet. Data automatically syncs when you're back online.",
    help_faq_3_q: "What hardware do I need?",
    help_faq_3_a: "Finio PRO works with standard POS hardware including receipt printers, barcode scanners, and cash drawers. We also provide hardware consultation and installation services.",
    help_faq_4_q: "How secure is my data?",
    help_faq_4_a: "Your data is protected with enterprise-grade security including encryption, automatic backups, role-based permissions, and multi-tenant data isolation.",
    help_faq_5_q: "Can I access the Accountant Web Panel remotely?",
    help_faq_5_a: "Absolutely! Monitor and manage your business from anywhere in the world using our dedicated iOS and Android mobile applications.",
    help_contact_support: "Need More Help?",
    help_contact_text: "Our support team is here to assist you. Contact us through:",
    help_search_placeholder: "Search for answers...",
    help_docs_desc: "Detailed guides on how to use every feature of Finio.",
    help_faq_desc: "Quick answers to common questions.",
    help_security_desc: "How we keep your data safe and compliant.",
    help_changelog_desc: "Latest updates and improvements.",
    contact_email_plain: "email",
    contact_phone_plain: "+212808646672",

    // --- Terms & Conditions ---
    terms_last_updated: "Last Updated",
    terms_date: "November 2025",
    terms_acceptance_title: "Acceptance of Terms",
    terms_acceptance_text: "By accessing and using Finio PRO services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.",
    terms_services_title: "Description of Services",
    terms_services_text: "Finio Solutions provides a comprehensive business management platform including point of sale, inventory management, customer relationship management, and related services. We reserve the right to modify, suspend, or discontinue any part of our services at any time.",
    terms_account_title: "User Accounts and Responsibilities",
    terms_account_text: "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.",
    terms_offline_title: "Offline Mode & Data Responsibility",
    terms_offline_text: "Finio PRO's offline mode stores data locally on your device. You are responsible for ensuring your device is synced with the cloud regularly. Finio Solutions is not liable for data loss caused by device failure or loss prior to synchronization.",
    terms_payment_title: "Payment and Subscription",
    terms_payment_text: "Subscription fees are billed in advance on a recurring basis. All fees are non-refundable except as required by law. We reserve the right to change our pricing with 30 days notice to active subscribers.",
    terms_hardware_title: "Hardware & Printer Compatibility",
    terms_hardware_text: "Finio works with standard ESC/POS printers and tablets. We provide a list of recommended hardware but do not guarantee compatibility with all third-party devices unless explicitly certified by us.",
    terms_intellectual_title: "Intellectual Property",
    terms_intellectual_text: "All content, features, and functionality of Finio PRO are owned by Finio Solutions and are protected by international copyright, trademark, and other intellectual property laws.",
    terms_limitation_title: "Limitation of Liability",
    terms_limitation_text: "Finio Solutions shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.",
    terms_termination_title: "Termination",
    terms_termination_text: "We reserve the right to terminate or suspend your account and access to our services at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.",
    terms_governing_title: "Governing Law",
    terms_governing_text: "These Terms shall be governed by and construed in accordance with the laws of Morocco, without regard to its conflict of law provisions.",
    terms_changes_title: "Changes to Terms",
    terms_changes_text: "We reserve the right to modify these terms at any time. We will notify users of any material changes via email or through our service. Continued use of the service after such modifications constitutes acceptance of the updated terms.",
    terms_contact_title: "Contact Information",
    terms_contact_text: "For any questions about these Terms and Conditions, please contact us at:",

    // --- Privacy Policy ---
    privacy_last_updated: "Last Updated",
    privacy_date: "November 2025",
    privacy_intro_title: "Introduction",
    privacy_intro_text: "Finio Solutions ('we', 'our', or 'us') respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use Finio PRO services.",
    privacy_collection_title: "Information We Collect",
    privacy_collection_text: "We collect several types of information to provide and improve our services:",
    privacy_collection_item1: "Personal identification information (name, email address, phone number)",
    privacy_collection_item2: "Business information (company name, address, tax identification)",
    privacy_collection_item3: "Usage data (how you interact with our services, features used)",
    privacy_collection_item4: "Technical data (IP address, browser type, device information)",
    privacy_pos_data_item: "Transactional data (sales history, inventory levels, customer purchases) generated through the POS system",
    privacy_pos_title: "POS & Transactional Data",
    privacy_pos_text: "We collect and process transactional data to provide you with sales reports, inventory tracking, and business analytics. This data belongs to you. We do not use your sales data for marketing purposes or share it with third parties.",
    privacy_offline_storage_title: "Offline Local Storage",
    privacy_offline_storage_text: "When using Finio PRO offline, data is stored securely on your local device using an encrypted local database. This data effectively remains under your physical control until the device reconnects to the internet and syncs with our secure cloud servers.",
    privacy_use_title: "How We Use Your Information",
    privacy_use_text: "We use the collected information for the following purposes:",
    privacy_use_item1: "To provide, maintain, and improve our services",
    privacy_use_item2: "To process transactions and send related information",
    privacy_use_item3: "To send administrative information, updates, and security alerts",
    privacy_use_item4: "To respond to your requests, questions, and provide customer support",
    privacy_sharing_title: "Information Sharing and Disclosure",
    privacy_sharing_text: "We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances: with your consent, to comply with legal obligations, to protect our rights and safety, or with service providers who assist in our operations under strict confidentiality agreements.",
    privacy_security_title: "Data Security",
    privacy_security_text: "We implement industry-standard security measures to protect your personal information, including encryption, secure servers, regular security audits, and access controls. However, no method of transmission over the internet is 100% secure.",
    privacy_retention_title: "Data Retention",
    privacy_retention_text: "We retain your personal information only for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required by law.",
    privacy_rights_title: "Your Privacy Rights",
    privacy_rights_text: "You have the right to:",
    privacy_rights_item1: "Access and receive a copy of your personal data",
    privacy_rights_item2: "Request correction of inaccurate or incomplete data",
    privacy_rights_item3: "Request deletion of your personal data",
    privacy_rights_item4: "Object to or restrict certain processing of your data",
    privacy_cookies_title: "Cookies and Tracking Technologies",
    privacy_cookies_text: "We use cookies and similar tracking technologies to track activity on our service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.",
    privacy_children_title: "Children's Privacy",
    privacy_children_text: "Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.",
    privacy_changes_title: "Changes to This Privacy Policy",
    privacy_changes_text: "We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the 'Last Updated' date.",
    privacy_contact_title: "Contact Us",
    "privacy_contact_text": "If you have any questions about this Privacy Policy, please contact us at:",

    // --- Documentation ---
    // Docs Navigation
    "docs_nav_title": "Documentation",
    "docs_resources_title": "Resources",
    "docs_nav_getting_started": "Getting Started",
    "docs_nav_pos_usage": "POS Usage Manual",
    "docs_nav_kds_workflow": "KDS Workflow",
    "docs_nav_pms_overview": "PMS & Hotel",
    "docs_nav_offline_sync": "Offline & Sync",
    "docs_nav_roles_permissions": "Roles & Permissions",
    "docs_nav_finance_closures": "Invoices & Closures",
    "docs_nav_hardware_printers": "Printers & Hardware",
    "docs_nav_integrations": "Integrations",
    "docs_nav_compliance": "Compliance & Fiscal",
    "docs_nav_troubleshooting": "Troubleshooting",

    // Getting Started
    "docs_gs_title": "Getting Started",
    "docs_gs_lead": "Welcome to Finio, an advanced offline-first POS & Business Management System designed for retail, restaurants, cafés, and hospitality businesses.",
    "docs_gs_intro": "Finio combines Point of Sale, Kitchen Display (KDS), Inventory, Accounting, and Hotel PMS into one unified platform, with strong data integrity, auditability, and multi-device synchronization.",
    "docs_gs_access_title": "1. Accessing Finio",
    "docs_gs_step_1": "<strong>Install Finio</strong> on your device (Android / Desktop / Tablet).",
    "docs_gs_step_2": "<strong>Open the app.</strong>",
    "docs_gs_step_3": "<strong>Log in</strong> using your tenant account.",
    "docs_gs_step_4": "<strong>Select your store / branch</strong> if applicable.",
    "docs_gs_offline_note": "<em>Finio works offline by default. Internet is only required for cloud sync and multi-device sharing.</em>",
    "docs_gs_config_title": "2. Store Configuration",
    "docs_gs_config_intro": "Before starting sales, configure your store settings:",
    "docs_gs_company_title": "Company Details",
    "docs_gs_company_go_to": "Go to: <strong>Settings → Company</strong>",
    "docs_gs_company_configure": "Configure:",
    "docs_gs_company_list_1": "Business name",
    "docs_gs_company_list_2": "Logo",
    "docs_gs_company_list_3": "Address",
    "docs_gs_company_list_4": "Tax ID (ICE / VAT)",
    "docs_gs_company_list_5": "Currency",
    "docs_gs_company_list_6": "Country",
    "docs_gs_company_note": "These details appear on Invoices, Receipts, and Accounting exports.",
    "docs_gs_taxes_title": "Taxes",
    "docs_gs_taxes_go_to": "Go to: <strong>Settings → Taxes</strong>",
    "docs_gs_taxes_list_1": "Create one or more tax rates",
    "docs_gs_taxes_list_2": "Assign taxes to products",
    "docs_gs_taxes_list_3": "Supports: Tax-inclusive pricing, Tax-exclusive pricing, Multiple tax classes",
    "docs_gs_taxes_note": "<strong>Note:</strong> Taxes are frozen at invoice creation to ensure accounting integrity.",
    "docs_gs_hardware_title": "Printers & Hardware",
    "docs_gs_hardware_go_to": "Go to: <strong>Settings → Hardware</strong>",
    "docs_gs_hardware_list_1": "Discover network printers",
    "docs_gs_hardware_list_2": "Assign printers to: Receipts, Kitchen tickets, Bar / stations",
    "docs_gs_hardware_list_3": "Configure print templates per station",
    "docs_gs_hardware_note": "<em>Printer templates are versioned and safely recoverable.</em>",
    "docs_gs_adding_title": "3. Adding Products",
    "docs_gs_adding_go_to": "Go to: <strong>Inventory → Products</strong>",
    "docs_gs_adding_intro": "You can add products in three ways:",
    "docs_gs_manual_title": "Manual Entry",
    "docs_gs_manual_list_1": "Name, Category, Price (multi-price supported)",
    "docs_gs_manual_list_2": "Tax, Stock tracking",
    "docs_gs_manual_list_3": "Image, Modifiers & variants",
    "docs_gs_csv_title": "CSV Import (Pro Plan)",
    "docs_gs_csv_list_1": "Bulk import products",
    "docs_gs_csv_list_2": "Categories auto-created",
    "docs_gs_csv_list_3": "Supports variants & prices",
    "docs_gs_types_title": "Advanced Product Types",
    "docs_gs_types_list_1": "Simple products",
    "docs_gs_types_list_2": "Variant products (size, color, etc.)",
    "docs_gs_types_list_3": "Composite products (recipes / bundles)",
    "docs_gs_types_list_4": "Stock-only products (not sellable)",

    // POS Usage
    "docs_pos_title": "Your First Sale (POS)",
    "docs_pos_lead": "Processing sales in Finio is fast and secure. Follow these steps to complete your first transaction.",
    "docs_pos_steps_title": "Steps to Sell",
    "docs_pos_step_1": "<strong>Open POS</strong> from the main menu.",
    "docs_pos_step_2": "<strong>Select products</strong> from the grid or scan a barcode.",
    "docs_pos_step_3": "<strong>Choose customer</strong> (optional) from the top bar.",
    "docs_pos_step_4": "Tap <strong>Pay</strong>.",
    "docs_pos_step_5": "<strong>Select payment method:</strong>",
    "docs_pos_method_1": "Cash",
    "docs_pos_method_2": "Card",
    "docs_pos_method_3": "Bank",
    "docs_pos_method_4": "Customer balance",
    "docs_pos_method_5": "Cheque",
    "docs_pos_method_6": "Mixed payments",
    "docs_pos_step_6": "<strong>Confirm payment</strong>.",
    "docs_pos_step_7": "<strong>Receipt prints automatically</strong> (if configured).",
    "docs_pos_note": "<strong>Note:</strong> Every sale creates an immutable invoice for accounting safety.",
    "docs_pos_features_title": "Key Features",
    "docs_pos_feat_multi": "<strong>Multi-payment support:</strong> Split bills easily between cash and card.",
    "docs_pos_feat_hold": "<strong>Hold & Retrieve:</strong> Save orders for later if a customer isn't ready.",
    "docs_pos_feat_offline": "<strong>Offline Mode:</strong> Continue selling even without internet; data syncs automatically when online.",



    // Hardware
    "docs_hardware_title": "Printers & Printing",
    "docs_hardware_lead": "Configure receipts, kitchen tickets, and printer routing for seamless operations.",
    "docs_hardware_features_title": "Printing Features",
    "docs_hardware_feat_receipts": "<strong>Receipts:</strong> Automatic customer receipts upon payment.",
    "docs_hardware_feat_kitchen": "<strong>Kitchen Tickets:</strong> Send orders directly to the kitchen or bar.",
    "docs_hardware_feat_routing": "<strong>Station Routing:</strong> Route specific items (e.g., drinks vs. food) to different printers.",
    "docs_hardware_feat_templates": "<strong>Templates:</strong> Customize print layouts with the template editor.",
    "docs_hardware_feat_queue": "<strong>Queue Monitoring:</strong> Track print jobs and status.",
    "docs_hardware_supported_title": "Supported Hardware",
    "docs_hardware_bt": "<strong>Bluetooth Printers:</strong> For mobile and tablet-based setups.",
    "docs_hardware_network": "<strong>Network (LAN) Printers:</strong> Standard ethernet connection for reliability.",
    "docs_hardware_recovery_note": "<strong>Feature:</strong> Auto-recovery ensures print jobs aren't lost if a printer goes offline temporarily.",

    // Integrations
    "docs_int_title": "Integrations",
    "docs_int_lead": "Connect Finio with third-party tools to extend your ecosystem.",
    "docs_int_web_title": "Web Integrations",
    "docs_int_api": "<strong>Product API:</strong> Sync products and inventory with external systems.",
    "docs_int_kds": "<strong>KDS Web:</strong> Browser-based Kitchen Display System access.",
    "docs_int_pms": "<strong>PMS Web:</strong> Property Management System interface.",
    "docs_int_feedback": "<strong>Feedback Web:</strong> Customer feedback collection.",
    "docs_int_waiter": "<strong>Waiter / Self-Order:</strong> Mobile ordering for staff or customers.",
    "docs_int_arch_title": "Service Architecture",
    "docs_int_arch_intro": "All web services are designed for security and performance:",
    "docs_int_local": "<strong>Local Execution:</strong> Run services locally for low latency.",
    "docs_int_auth": "<strong>Authenticated:</strong> Secure access via tenant credentials.",
    "docs_int_rate": "<strong>Rate-limited:</strong> ID protection against abuse.",
    "docs_int_limited": "<strong>Tenant-isolated:</strong> Data separation for multi-tenant environments.",

    // Compliance
    "docs_comp_title": "Accounting & Compliance",
    "docs_comp_lead": "Core Accounting Model: How Finio protects your financial data.",
    "docs_comp_model_title": "Core Accounting Model",
    "docs_comp_immutable_title": "Immutable Invoices",
    "docs_comp_immutable_desc": "Once an invoice is finalized, it cannot be edited or deleted. It exists permanently in the ledger.",
    "docs_comp_append_title": "Append-Only Ledger",
    "docs_comp_append_desc": "Payments and balance updates are additive. We never overwrite a record; we only add new compensating entries.",
    "docs_comp_safety_title": "Transaction Safety",
    "docs_comp_safety_desc": "All operations are atomic. If a crash occurs, the system recovers to a consistent state without data loss.",

    // Security
    "docs_sec_title": "Security & Data Protection",
    "docs_sec_lead": "Finio employs an \"Audit-First\" security model designed for financial compliance and data integrity.",
    "docs_sec_enc_title": "Data Encryption",
    "docs_sec_enc_intro": "We treat your business data as sensitive financial assets.",
    "docs_sec_table_state": "State",
    "docs_sec_table_method": "Method",
    "docs_sec_table_detail": "Detail",
    "docs_sec_rest_cloud": "<strong>At Rest (Cloud)</strong>",
    "docs_sec_rest_cloud_desc": "Google Cloud Firestore encrypted storage.",
    "docs_sec_rest_device": "<strong>At Rest (Device)</strong>",
    "docs_sec_rest_device_desc": "Local database files are encrypted to prevent extraction from stolen tablets.",
    "docs_sec_transit": "<strong>In Transit</strong>",
    "docs_sec_transit_desc": "All sync traffic flows over secured HTTPS channels.",
    "docs_sec_rbac_title": "Role-Based Access Control (RBAC)",
    "docs_sec_rbac_intro": "Permissions are strictly enforced at the UI and API level.",
    "docs_sec_role_owner": "<strong>Owner:</strong> Full access to all settings, accounting, and deletions.",
    "docs_sec_role_manager": "<strong>Manager:</strong> Can void items and run EOD reports, but cannot delete history.",
    "docs_sec_role_cashier": "<strong>Cashier:</strong> Can create sales and take payments. Cannot void sent orders without approval.",
    "docs_sec_role_waiter": "<strong>Waiter:</strong> Restricted to order creation only. No payment or drawer access.",
    "docs_sec_audit_title": "Immutable Audit Logs",
    "docs_sec_audit_intro": "Every critical action triggers a permanent entry in the <code>audit_log</code> collection. This log is visible in the Accountant Web Panel.",
    "docs_sec_audit_events": "<strong>Logged Events:</strong> Voids, Refunds, Discounts (>10%), Drawer Openings, Price Changes.",
    "docs_sec_audit_meta": "<strong>Metadata:</strong> User ID, Exact Timestamp, Device ID, GPS Location (optional).",
    "docs_sec_alert_box": "<strong>Alert System:</strong> Suspicious patterns (e.g., 5 voids in 1 minute) trigger automatic \"Red Flag\" notifications to the business owner via WhatsApp.",

    // Troubleshooting
    "docs_ts_title": "Troubleshooting",
    "docs_ts_lead": "Find quick solutions to common operational issues.",
    "docs_ts_issues_title": "Common Issues",
    "docs_ts_printer_title": "Printer not printing",
    "docs_ts_printer_1": "<strong>Check network:</strong> Ensure printer is connected to the same Wi-Fi/LAN.",
    "docs_ts_printer_2": "<strong>Restart service:</strong> Restart the Finio Printer Service or the printer itself.",
    "docs_ts_printer_3": "<strong>Re-assign template:</strong> Go to Settings → Hardware and verify the printer assignment.",
    "docs_ts_sync_title": "Sync not working",
    "docs_ts_sync_1": "<strong>Check internet:</strong> Verify your device has an active connection.",
    "docs_ts_sync_2": "<strong>Automatic Sync:</strong> Sync resumes automatically once online; no manual action needed.",
    "docs_ts_sync_3": "<strong>Data Safety:</strong> Your data is safe locally and will upload when possible.",
    "docs_ts_balance_title": "Wrong cash balance",
    "docs_ts_balance_1": "<strong>Sessions:</strong> Check if the correct register session is open.",
    "docs_ts_balance_2": "<strong>Refunds:</strong> Verify if any refunds were processed after the session started.",
    "docs_ts_balance_3": "<strong>EOD:</strong> Review End-of-Day closure reports for discrepancies.",
    "docs_ts_help_box": "<strong>Need more help?</strong> Contact support at <a href=\"mailto:support\u0040finio.pro\">support\u0040finio.pro</a>.",

    // Compliance Extended
    "docs_comp_intro_title": "How Accounting Works in Finio",
    "docs_comp_intro_p1": "Finio is not a \"simple POS\". It is an accounting-safe transaction system designed to protect financial data against manipulation, errors, crashes, and multi-device conflicts.",
    "docs_comp_intro_p2": "This section explains how Finio accounting works, why some actions are restricted, and how this protects your business during audits.",
    "docs_comp_invoice_title": "What Is an Invoice?",
    "docs_comp_invoice_p1": "An Invoice in Finio is a legal accounting document, not a draft.",
    "docs_comp_invoice_frozen": "When an invoice is created, the following are frozen permanently:",
    "docs_comp_invoice_list_1": "Product prices",
    "docs_comp_invoice_list_2": "Taxes",
    "docs_comp_invoice_list_3": "Discounts",
    "docs_comp_invoice_list_4": "Totals",
    "docs_comp_immutability_title": "Invoice Immutability (Critical Rule)",
    "docs_comp_immutability_intro": "Once created:",
    "docs_comp_immutability_list_1": "You cannot edit totals",
    "docs_comp_immutability_list_2": "You cannot change tax",
    "docs_comp_immutability_list_3": "You cannot modify items",
    "docs_comp_immutability_why": "<strong>Why?</strong> Because editing invoices after creation breaks audit trails, invalidates tax reports, and creates fraud risk.",
    "docs_comp_immutability_compliance": "This is intentional and required for compliance.",
    "docs_comp_payments_title": "Payments: Append-Only Ledger",
    "docs_comp_payments_intro": "Finio does not modify payments. Instead, it uses an append-only payment ledger.",
    "docs_comp_payments_how_title": "How Payments Work",
    "docs_comp_payments_list_1": "Each payment is added as a new record",
    "docs_comp_payments_list_2": "Payments are never overwritten",
    "docs_comp_payments_list_3": "Refunds are new ledger entries, not edits",
    "docs_comp_payments_guarantee": "This guarantees full payment history, tamper resistance, and safe multi-device sync.",
    "docs_comp_example_title": "Example",
    "docs_comp_table_action": "Action",
    "docs_comp_table_result": "What Finio Does",
    "docs_comp_table_row_1_a": "Customer pays cash",
    "docs_comp_table_row_1_b": "Adds a payment entry",
    "docs_comp_table_row_2_a": "Cheque validated later",
    "docs_comp_table_row_2_b": "Adds a status-change entry",
    "docs_comp_table_row_3_a": "Refund issued",
    "docs_comp_table_row_3_b": "Adds a refund entry",
    "docs_comp_table_row_4_a": "Sync conflict",
    "docs_comp_table_row_4_b": "Merges entries safely",
    "docs_comp_refunds_title": "Refunds (No Invoice Editing)",
    "docs_comp_refunds_intro": "Refunds in Finio:",
    "docs_comp_refunds_list_1": "Do NOT edit the invoice",
    "docs_comp_refunds_list_2": "Do NOT remove payments",
    "docs_comp_refunds_list_3": "Create a negative ledger entry",
    "docs_comp_refunds_why": "<strong>Why This Matters:</strong> The original sale remains visible, the refund is clearly tracked, and auditors can reconstruct the full history.",
    "docs_comp_refunds_box": "<strong>Routing:</strong> Cash refunds reduce the cash drawer. Card refunds reduce the bank balance. Store credit increases the customer balance.",
    "docs_comp_eod_title": "Closed Periods & Audit Protection",
    "docs_comp_eod_intro": "Once an End-of-Day (EOD) is completed:",
    "docs_comp_eod_list_1": "The accounting period is locked",
    "docs_comp_eod_list_2": "Past transactions cannot be modified",
    "docs_comp_eod_list_3": "Refunds after EOD are recorded as new events",
    "docs_comp_eod_prevent": "This prevents backdated manipulation, hidden corrections, and audit inconsistencies.",
    "docs_comp_trust_title": "Why Accountants Trust Finio",
    "docs_comp_trust_intro": "Finio enforces enterprise accounting controls, not POS limitations:",
    "docs_comp_trust_list_1": "Immutable invoices",
    "docs_comp_trust_list_2": "Append-only payments",
    "docs_comp_trust_list_3": "Period locking",
    "docs_comp_trust_list_4": "Register-bound cash",
    "docs_comp_trust_list_5": "Server-time protection",
    "docs_comp_reg_title": "Cash Registers & Reconciliation",
    "docs_comp_reg_what_title": "What Is a Cash Register in Finio?",
    "docs_comp_reg_what_desc": "A Cash Register represents a physical cash drawer. Every cash movement must be linked to a register, linked to an open session, and tracked per period. This ensures cash accountability.",
    "docs_comp_float_title": "Opening Float",
    "docs_comp_float_step_1": "Open the register",
    "docs_comp_float_step_2": "Enter opening cash amount",
    "docs_comp_float_desc": "This becomes the baseline for reconciliation.",
    "docs_comp_z_title": "End-of-Day (Z Closure)",
    "docs_comp_z_intro": "At the end of the day or shift:",
    "docs_comp_z_step_1": "Perform End-of-Day closure",
    "docs_comp_z_step_2": "Finio calculates: Expected cash, Actual cash, Difference, Margin",
    "docs_comp_z_step_3": "Period is closed and locked",
    "docs_comp_z_after": "After EOD:",
    "docs_comp_z_list_1": "You cannot change past cash sales",
    "docs_comp_z_list_2": "You cannot backdate payments",
    "docs_comp_z_list_3": "New activity starts a new period",
    "docs_comp_reg_box": "<strong>Why Cash Is Strictly Bound:</strong> Without register binding, cash goes untracked and theft cannot be detected. Finio blocks cash payments without an open register and session context.",
    "docs_comp_offline_title": "Offline & Multi-Device Behavior",
    "docs_comp_offline_first_title": "Offline-First by Design",
    "docs_comp_offline_first_desc": "Finio works without internet. All actions save locally first, sync safely later, and never block sales.",
    "docs_comp_drops_title": "What Happens If Internet Drops?",
    "docs_comp_drops_desc": "Nothing breaks. Sales continue, payments are recorded, and sync resumes automatically. No data loss.",
    "docs_comp_multi_title": "Multi-Device Safety (Very Important)",
    "docs_comp_multi_scenario": "<strong>Scenario:</strong> Two devices, same invoice.",
    "docs_comp_multi_list_1": "Device A adds payment",
    "docs_comp_multi_list_2": "Device B adds another payment",
    "docs_comp_multi_list_3": "Both sync later",
    "docs_comp_multi_merge": "Finio merges payments, it does not overwrite. This is possible because payments are append-only and invoices are versioned.",
    "docs_comp_time_title": "Device Time Protection",
    "docs_comp_time_desc": "Finio uses server-aligned time to detect clock manipulation, prevent backdating, and protect accounting periods.",
    "docs_comp_faq_title": "FAQ (Accounting & Trust)",
    "docs_comp_faq_1_q": "Can I edit an invoice after payment?",
    "docs_comp_faq_1_a": "No. Invoices are immutable to protect accounting integrity and audits.",
    "docs_comp_faq_2_q": "Can I delete a payment?",
    "docs_comp_faq_2_a": "No. Payments are ledger entries and cannot be removed.",
    "docs_comp_faq_3_q": "What happens if I refund after End-of-Day?",
    "docs_comp_faq_3_a": "A new refund record is created in the next period. Past periods remain locked.",
    "docs_comp_faq_4_q": "Can two cashiers use the same register?",
    "docs_comp_faq_4_a": "Yes, but not at the same time. Each register has controlled sessions.",
    "docs_comp_faq_5_q": "What if device time is wrong?",
    "docs_comp_faq_5_a": "Finio detects and prevents time manipulation.",
    "docs_comp_faq_6_q": "Is Finio safe for tax audits?",
    "docs_comp_faq_6_a": "Yes. It enforces accounting rules required by auditors and fiscal authorities.",
    "docs_comp_faq_7_q": "Can accountants access data remotely?",
    "docs_comp_faq_7_a": "Yes. Through the Accountant Web Panel, not the POS.",
    "docs_comp_panel_title": "Accountant Web Panel",
    "docs_comp_panel_desc": "A separate web platform for accountants and managers. It is NOT a POS.",
    "docs_comp_panel_what": "What Accountants Can Do",
    "docs_comp_panel_list_1": "View invoices",
    "docs_comp_panel_list_2": "Review payments & refunds",
    "docs_comp_panel_list_3": "Track expenses",
    "docs_comp_panel_list_4": "Manage payroll & salaries",
    "docs_comp_panel_list_5": "Analyze margins & cash flow",
    "docs_comp_panel_list_6": "Export data",
    "docs_comp_panel_list_7": "Monitor multiple branches",
    "docs_comp_panel_remote": "All remotely, without accessing POS devices.",
    "docs_comp_panel_box": "<strong>Why This Matters:</strong> Separation of duties (no cashier access to accounting tools) and professional audit workflows. This is a major enterprise feature.",
    "docs_comp_int_title": "Integrations & Developer Resources",
    "docs_comp_api_title": "Product API",
    "docs_comp_api_desc": "For ERP systems, inventory scanners, external apps, and warehouse systems.",
    "docs_comp_api_sec": "<strong>Security Model:</strong> Device pairing, Token-based auth, Tenant isolation, Rate limiting. No cross-tenant access is possible.",
    "docs_comp_woo_title": "WooCommerce Integration",
    "docs_comp_woo_list_1": "Sync products from Finio → WooCommerce",
    "docs_comp_woo_list_2": "Send orders from WooCommerce → Finio",
    "docs_comp_woo_limits": "<strong>Important Limitations:</strong> One-way product sync, Orders require approval, Stock sync depends on configuration.",
    "docs_comp_local_title": "Local Web Servers (Advanced)",
    "docs_comp_local_desc": "Finio includes embedded web servers for Waiter tablets, Self-ordering kiosks, PMS web access, and Feedback forms. All are authenticated, LAN-based, and rate-limited.",
    "docs_comp_sec_title": "Security Model",
    "docs_comp_sec_list_1": "Tenant isolation",
    "docs_comp_sec_list_2": "Role-based permissions",
    "docs_comp_sec_list_3": "Server-time enforcement",
    "docs_comp_sec_list_4": "Offline encryption",
    "docs_comp_sec_list_5": "No shared data between businesses",

    // Offline Sync (English)
    "docs_offline_title": "Offline Architecture & Sync",
    "docs_offline_lead": "Finio's distributed system ensures 100% uptime through a local-first Hive database architecture.",
    "docs_offline_hive_title": "Local-First Database (Hive)",
    "docs_offline_hive_desc": "Finio does not rely on a constant cloud connection. Instead, it runs a full NoSQL database (Hive) directly on your device hardware.",
    "docs_offline_hive_list_1": "<strong>Read/Write Speed:</strong> Instant zero-latency operations (< 16ms).",
    "docs_offline_hive_list_2": "<strong>Data Sovereignity:</strong> Your full product catalog, customer list, and transaction history reside on your device.",
    "docs_offline_hive_list_3": "<strong>Encryption:</strong> Local data is encrypted at rest using AES-256 standards.",
    "docs_offline_why_box": "<strong>Why this matters:</strong> Most web POS systems stop working when WiFi drops. Finio continues to process sales, print receipts, and open cash drawers because the logic is local.",
    "docs_offline_queue_title": "The Delta Sync Queue",
    "docs_offline_queue_desc": "When you perform an action (e.g., Close a Bill), Finio writes it to a persistent \"Sync Queue\".",
    "docs_offline_queue_step_1": "<strong>Action Commit:</strong> The transaction is saved to the local ledger instantly.",
    "docs_offline_queue_step_2": "<strong>Queue Entry:</strong> A JSON payload of the mutation is added to the <code>sync_queue</code> table.",
    "docs_offline_queue_step_3": "<strong>Background Worker:</strong> A background process attempts to flush the queue to the cloud.",
    "docs_offline_queue_step_4": "<strong>Retry Logic:</strong> If the network is down, the worker sleeps and retries with exponential backoff.",
    "docs_offline_conflict_title": "Conflict Resolution Strategy",
    "docs_offline_conflict_desc": "In a multi-device environment, Finio uses a <strong>Deterministic Merge Strategy</strong> to handle collisions.",
    "docs_offline_append_title": "Append-Only Ledger",
    "docs_offline_append_desc": "We never overwrite payment records. If Device A receives $50 and Device B receives $20 for the same invoice:",
    "docs_offline_append_list_1": "Device A commits Payment ID #101 ($50).",
    "docs_offline_append_list_2": "Device B commits Payment ID #102 ($20).",
    "docs_offline_append_list_3": "Cloud Server accepts both.",
    "docs_offline_append_list_4": "Invoice Balance is updated to reflect $70 paid.",
    "docs_offline_time_title": "Server-Authoritative Time",
    "docs_offline_time_desc": "To prevent fraud (e.g., changing the device clock to backdate sales), all sync events are stamped with <code>server_timestamp</code> upon arrival. Financial reports use this server time for truth-telling.",

    // KDS Workflow (English)
    "docs_kds_title": "KDS (Kitchen Display System)",
    "docs_kds_lead": "Optimize your kitchen operations with real-time order tracking.",
    "docs_kds_overview_title": "Overview",
    "docs_kds_overview_desc": "Go to: <strong>KDS</strong> from the main menu.",
    "docs_kds_list_1": "<strong>Real-time Orders:</strong> Orders appear instantly as they are placed.",
    "docs_kds_list_2": "<strong>Station Grouping:</strong> Orders are routed to specific stations (Kitchen, Bar, Grill).",
    "docs_kds_list_3": "<strong>Timers:</strong> Track preparation time for each item.",
    "docs_kds_list_4": "<strong>Multi-screen:</strong> Support for multiple display screens across different stations.",
    "docs_kds_flow_title": "Status Flow",
    "docs_kds_flow_desc": "Orders move through the following stages:",
    "docs_kds_flow_step_1": "<strong>New:</strong> Order received.",
    "docs_kds_flow_step_2": "<strong>In preparation:</strong> Staff acknowledges the order.",
    "docs_kds_flow_step_3": "<strong>Ready:</strong> Item is cooked and ready to serve.",
    "docs_kds_flow_step_4": "<strong>Served:</strong> Order completed.",
    "docs_kds_note_box": "<strong>Note:</strong> KDS works locally over LAN, ensuring functionality even without internet.",

    // PMS Overview (English)
    "docs_pms_title": "PMS & Hotel Module",
    "docs_pms_lead": "Manage rooms, bookings, and guests efficiently with our integrated Property Management System.",
    "docs_pms_key_title": "Key Features",
    "docs_pms_key_desc": "Go to: <strong>PMS</strong> from the main menu.",
    "docs_pms_list_1": "<strong>Room Management:</strong> Visual overview of room status and availability.",
    "docs_pms_list_2": "<strong>Check-in / Check-out:</strong> Streamlined guest arrival and departure flows.",
    "docs_pms_list_3": "<strong>Occupancy Tracking:</strong> Monitor occupancy rates in real-time.",
    "docs_pms_list_4": "<strong>Integrated Billing:</strong> Room charges are automatically linked to guest invoices.",
    "docs_pms_list_5": "<strong>Housekeeping:</strong> Manage cleaning schedules and room maintenance.",
    "docs_pms_designed_title": "Designed for",
    "docs_pms_designed_1": "Hotels",
    "docs_pms_designed_2": "Riads",
    "docs_pms_designed_3": "Guesthouses",
    "docs_pms_designed_4": "Short-stay rentals",

    // Roles & Permissions (English)
    "docs_roles_title": "Roles & Permissions",
    "docs_roles_lead": "Control access and ensure security with granular role management.",
    "docs_roles_manage_title": "Managing Roles",
    "docs_roles_manage_desc": "Go to: <strong>Settings → Roles & Permissions</strong>",
    "docs_roles_list_1": "<strong>Create Custom Roles:</strong> Define unique roles tailored to your business needs (e.g., Cashier, Manager, Accountant).",
    "docs_roles_list_2": "<strong>Granular Permissions:</strong> Assign specific rights to each role.",
    "docs_roles_scopes_title": "Permission Scopes",
    "docs_roles_scopes_desc": "Permissions can be applied across various modules:",
    "docs_roles_scopes_1": "<strong>POS:</strong> Selling access, voiding items, applying discounts.",
    "docs_roles_scopes_2": "<strong>Inventory:</strong> Adding products, stock adjustments.",
    "docs_roles_scopes_3": "<strong>Accounting:</strong> Viewing reports, closing shifts.",
    "docs_roles_scopes_4": "<strong>Settings:</strong> Changing system configurations.",

    // Finance Closures (English)
    "docs_finance_title": "Invoices & Closures",
    "docs_finance_lead": "Track your finances, manage invoices, and secure your cash flow.",
    "docs_faq_title": "Frequently Asked Questions",
    "docs_faq_subtitle": "Answers to common questions about Finio's POS, KDS, and business management platform.",
    "docs_faq_cat_pos": "POS & Sales",
    "docs_faq_q_offline_sell": "Can I sell products without an internet connection?",
    "docs_faq_a_offline_sell": "Yes. Finio is an <strong>offline-first</strong> system. All sales, Z-closures, and table movements are saved locally on your device. The system automatically syncs everything to the cloud once the connection is restored.",
    "docs_faq_q_prices": "Does the POS support multiple price levels?",
    "docs_faq_a_prices": "Yes, you can configure multiple price levels (e.g., Happy Hour, VIP, Staff) and switch between them instantly during a sale.",
    "docs_faq_q_split": "Can I split bills or merge tables?",
    "docs_faq_a_split": "Absolutely. You can split payments by amount or by item, and easily merge tables or transfer items between them.",
    "docs_faq_cat_sync": "Offline Mode & Sync",
    "docs_faq_q_break": "What happens if my tablet breaks while offline?",
    "docs_faq_a_break": "Finio is designed to protect your data even in offline environments. When multiple devices are connected on the same local network, they automatically share and synchronize data with each other offline. This means your data is stored on more than one device locally. If one device becomes damaged before syncing, the data remains available on the other devices on the network. Once any device reconnects to the internet, all data is automatically synchronized to the cloud.",
    "docs_faq_q_status": "How do I know if I am online or offline?",
    "docs_faq_a_status": "A status indicator in the top-right corner of the POS screen shows your connection status: Green for Online, and Gray/Red for Offline.",
    "docs_faq_cat_billing": "Subscriptions & Billing",
    "docs_faq_q_plan": "Can I change my plan later?",
    "docs_faq_a_plan": "Yes, you can upgrade from Starter to Business at any time. The remaining balance of your current subscription will be pro-rated.",
    "docs_faq_q_cancel": "Is there a cancellation fee?",
    "docs_faq_a_cancel": "No. You can cancel your subscription at any time. Your access will remain active until the end of your billing period.",
    "docs_faq_cat_hardware": "Hardware",
    "docs_faq_q_printers": "What receipt printers are compatible?",
    "docs_faq_a_printers": "Finio supports most ESC/POS compatible network (Ethernet/WiFi) printers, including major brands like Epson, Star Micronics, and Xprinter.",
    "docs_faq_q_tablet": "Can I use my existing tablet?",
    "docs_faq_a_tablet": "Yes, Finio works on any modern iOS (iPad) or Android tablet. We recommend devices with at least 3GB of RAM.",
    "docs_faq_cat_security": "Security & Data",
    "docs_faq_q_access": "Who has access to my sales data?",
    "docs_faq_a_access": "Only you and authorized users. Finio staff cannot access your operational data without explicit permission for support.",
    "docs_faq_q_legal": "Are my invoices legally compliant?",
    "docs_faq_a_legal": "Yes. Finio generates immutable, sequentially numbered invoices that comply with tax regulations. Once finalized, an invoice cannot be altered or deleted.",
    "docs_finance_invoices_title": "Invoices",
    "docs_finance_invoices_go_to": "Go to: <strong>Invoices</strong>",
    "docs_finance_invoices_history": "<strong>View History:</strong> Access all past invoices.",
    "docs_finance_invoices_filtering": "<strong>Filtering:</strong> Filter by date, status, register, or user.",
    "docs_finance_invoices_actions": "<strong>Actions:</strong> Print or export invoices PDF. Public invoice sharing is also supported.",
    "docs_finance_important_rules": "Important Rules:",
    "docs_finance_rule_1": "Invoice totals are immutable once created.",
    "docs_finance_rule_2": "Payments are append-only (cannot be deleted, only offset).",
    "docs_finance_rule_3": "Refunds never rewrite history; they create new counter-transactions.",
    "docs_finance_registers_title": "Cash Registers",
    "docs_finance_registers_sessions": "<strong>Sessions:</strong> Each cash register operates in sessions.",
    "docs_finance_registers_tracking": "<strong>Tracking:</strong> Cash payments must be linked to an open register session.",
    "docs_finance_registers_security": "<strong>Security:</strong> Prevents untracked cash movements and enhances accountability.",
    "docs_finance_closures_title": "Opening Float & End-of-Day (Z Closure)",
    "docs_finance_closures_start": "<strong>Start of Shift:</strong> Set the opening float amount.",
    "docs_finance_closures_end": "<strong>End of Shift:</strong> Perform the End-of-Day closure.",
    "docs_finance_closures_reconciliation": "<strong>Reconciliation:</strong> The system calculates Expected vs. Actual cash and highlights any differences.",
    "docs_finance_closures_locking": "<strong>Locking:</strong> Closed periods are locked and cannot be modified.",

    // --- Changelog ---
    "nav_changelog_title": "Finio Changelog",
    "changelog_version": "Version 1.0 → Version 2.0",
    "changelog_overview_title": "Overview",
    "changelog_overview_p1": "Finio Version 1.0 was a pilot release, delivered to a limited number of selected clients across different business sectors (restaurants, retail, hospitality, and service-based operations).",
    "changelog_overview_p2": "The goal of v1.0 was not scale, but learning.",
    "changelog_overview_p3": "Through direct, hands-on collaboration with real businesses, we observed how POS systems behave under real operational pressure: offline usage, staff mistakes, refunds, inventory loss, cash discrepancies, and multi-device usage.",
    "changelog_overview_p4": "Version 2.0 is the result of that learning.",
    "changelog_overview_p5": "It is not an iteration — it is a structural upgrade, designed to:",
    "changelog_overview_list_1": "Minimize operational risk",
    "changelog_overview_list_2": "Prevent silent data manipulation",
    "changelog_overview_list_3": "Reduce theft and accounting errors",
    "changelog_overview_list_4": "Identify weak points in business operations",
    "changelog_overview_list_5": "Prepare businesses to scale on a solid foundation",

    "changelog_v1_title": "Version 1.0 — Pilot Phase (Foundational Learning)",
    "changelog_v1_purpose_title": "Purpose",
    "changelog_v1_purpose_1": "Validate offline-first usage",
    "changelog_v1_purpose_2": "Observe real staff behavior",
    "changelog_v1_purpose_3": "Understand failure points in existing POS workflows",
    "changelog_v1_purpose_4": "Identify where businesses lose money without realizing it",

    "changelog_v1_char_title": "Characteristics",
    "changelog_v1_char_1": "Core POS functionality",
    "changelog_v1_char_2": "Basic offline support",
    "changelog_v1_char_3": "Inventory & sales tracking",
    "changelog_v1_char_4": "Manual reconciliation workflows",
    "changelog_v1_char_5": "Limited protection against data mutation",
    "changelog_v1_char_6": "Reactive reporting (what happened, not why)",

    "changelog_findings_title": "Key Findings from Pilot Clients",
    "changelog_findings_intro": "During real-world usage, we identified recurring problems across industries:",
    "changelog_findings_1": "Invoices being modified after payment",
    "changelog_findings_2": "Payments overwritten or lost during sync",
    "changelog_findings_3": "Refund-heavy products masking inventory or quality issues",
    "changelog_findings_4": "Cash discrepancies without clear cause",
    "changelog_findings_5": "Multi-device conflicts causing data inconsistency",
    "changelog_findings_6": "Businesses scaling before fixing internal weaknesses",
    "changelog_findings_outro": "These insights directly shaped Version 2.0.",

    "changelog_v2_title": "Version 2.0 — Production & Risk-Control Release",
    "changelog_v2_desc": "Version 2.0 was rebuilt with control, integrity, and prevention as first-class principles.",

    "changelog_acc_title": "Accounting & Data Integrity (Major Upgrade)",
    "changelog_acc_inv_title": "Immutable Invoices",
    "changelog_acc_inv_1": "Once an invoice is issued, financial totals cannot be altered.",
    "changelog_acc_inv_2": "Prevents silent edits, manipulation, or post-payment changes.",
    "changelog_acc_inv_3": "Ensures audit-safe accounting records.",

    "changelog_acc_pay_title": "Append-Only Payment Ledger",
    "changelog_acc_pay_1": "Payments are never overwritten or deleted.",
    "changelog_acc_pay_2": "Every payment, refund, or status change is recorded as a new ledger entry.",
    "changelog_acc_pay_3": "Full traceability of all financial actions.",

    "changelog_acc_atomic_title": "Atomic Transactions",
    "changelog_acc_atomic_1": "Payments are processed in crash-safe, atomic operations.",
    "changelog_acc_atomic_2": "No partial states if the device shuts down mid-transaction.",
    "changelog_acc_atomic_3": "Eliminates ghost payments and mismatched balances.",

    "changelog_acc_time_title": "Server-Time Enforcement",
    "changelog_acc_time_1": "Prevents device clock manipulation.",
    "changelog_acc_time_2": "All financial events follow a trusted, monotonic timeline.",

    "changelog_off_title": "Offline & Multi-Device Reliability",
    "changelog_off_design_title": "Offline-First by Design",
    "changelog_off_design_1": "Full operation without internet.",
    "changelog_off_design_2": "No feature degradation when offline.",

    "changelog_off_local_title": "Offline Local Network Synchronization",
    "changelog_off_local_1": "Devices on the same local network share data offline.",
    "changelog_off_local_2": "Creates local redundancy across devices.",
    "changelog_off_local_3": "Data survives even if one device is damaged.",

    "changelog_off_sync_title": "Conflict-Safe Multi-Device Sync",
    "changelog_off_sync_1": "Payments and invoices are merged safely across devices.",
    "changelog_off_sync_2": "No lost transactions when multiple terminals are used.",
    "changelog_off_sync_3": "Server authority resolves conflicts without overwriting history.",

    "changelog_off_cloud_title": "Automatic Cloud Recovery",
    "changelog_off_cloud_1": "As soon as any device reconnects to the internet, all data syncs automatically to the cloud.",

    "changelog_inv_title": "Inventory, Refunds & Loss Prevention",
    "changelog_inv_refund_title": "Refund Intelligence",
    "changelog_inv_refund_1": "Refunds are treated as financial reversals, not generic expenses.",
    "changelog_inv_refund_2": "Clear linkage between refunds, products, and payment methods.",

    "changelog_inv_risk_title": "Product Risk Detection",
    "changelog_inv_risk_1": "Identification of products with: High refund rates, frequent complaints, or stock discrepancies.",
    "changelog_inv_risk_2": "Helps detect: Bad suppliers, quality issues, or fraud/misuse.",

    "changelog_inv_stock_title": "Stock Integrity Improvements",
    "changelog_inv_stock_1": "Better tracking of damaged, returned, and non-sellable items.",
    "changelog_inv_stock_2": "Reduced inventory drift over time.",

    "changelog_health_title": "Business Health & Decision Support (New)",
    "changelog_health_ind_title": "Business Health Indicators",
    "changelog_health_ind_1": "Early warning signals for: excessive refunds, shrinking margins, cash imbalance, stock leakage.",
    "changelog_health_ind_2": "Designed to fix problems before scaling, not after failure.",

    "changelog_health_ops_title": "Operational Visibility",
    "changelog_health_ops_1": "Clear separation between: sales performance, operational issues, accounting inconsistencies.",

    "changelog_cash_title": "Cash Control & Reconciliation",
    "changelog_cash_bind_title": "Cash Register Binding",
    "changelog_cash_bind_1": "Every cash movement must be linked to an active register session.",
    "changelog_cash_bind_2": "Prevents untraceable cash operations.",

    "changelog_cash_eod_title": "Closed Accounting Periods (EOD Locking)",
    "changelog_cash_eod_1": "Once a day is closed, historical data is protected.",
    "changelog_cash_eod_2": "Prevents retroactive manipulation.",
    "changelog_cash_eod_3": "Ensures clean daily reconciliation.",

    "changelog_plat_title": "Platform & Architecture Improvements",
    "changelog_plat_mod_title": "Modular System Maturity",
    "changelog_plat_mod_1": "POS, KDS, Inventory, PMS, Delivery, Accounting Panel fully aligned.",
    "changelog_plat_mod_2": "Clear role separation (Cashier, Manager, Accountant, Admin).",

    "changelog_plat_web_title": "Improved Web Panels",
    "changelog_plat_web_1": "Accountant & management access for: Expenses, Salaries, B2B invoices, Financial statistics, Multi-branch oversight.",

    "changelog_reps_title": "What Version 2.0 Represents",
    "changelog_reps_intro": "Finio v2.0 is built for businesses that want to:",
    "changelog_reps_list_1": "Grow without losing control",
    "changelog_reps_list_2": "Prevent theft and silent errors",
    "changelog_reps_list_3": "Detect bad products early",
    "changelog_reps_list_4": "Trust their numbers",
    "changelog_reps_list_5": "Scale on a healthy operational foundation",
    "changelog_reps_outro": "It is no longer just a POS — it is a business integrity platform.",

    "changelog_future_title": "Looking Forward",
    "changelog_future_1": "Version 2.0 establishes the non-negotiable foundation.",
    "changelog_future_2": "Future versions will build on top of this integrity, not around it.",

  },
  ar: {
    "home": "الرئيسية",
    "products": "وحداتنا",
    "subscriptions": "الاشتراكات",
    "features": "الميزات الأساسية",
    "coming_soon": "الابتكارات",
    "contact_us": "اتصل بنا",
    "hero_title": "نظام نقاط البيع (POS) الذي يعمل دون اتصال مع نزاهة محاسبية معتمدة",
    "hero_subtitle": "صُمم للشركات التي تتطلب الدقة. يتميز بفواتير غير قابلة للتغيير، وسجل مدفوعات للإضافة فقط، ومزامنة آمنة من التعارض بين أجهزة متعددة.",
    "book_demo": "اكتشف المنصة",
    "hero_badge_new": "Finio 2.0: الآن مع أمان تدقيق مُحسّن",

    "// --- Value Pillars ---": "",
    "pillar_offline_title": "بنية حقيقية للعمل دون اتصال (Offline-First)",
    "pillar_offline_desc": "ليس مجرد دعم 'متقطع'. Finio يخزن 100% من البيانات محليًا ويقوم بالمزامنة دون تعارض عند الاتصال. صفر توقف عن العمل.",
    "pillar_integrity_title": "جوهر محاسبي غير قابل للتغيير",
    "pillar_integrity_desc": "لا يمكن تعديل الفواتير بعد إنشائها. المدفوعات تتم بنظام الإضافة فقط. نزاهتك المالية مضمونة رياضيًا.",
    "pillar_conflict_title": "عمليات ذرية (Atomic Transactions)",
    "pillar_conflict_desc": "عمليات آمنة ضد الأعطال تضمن أنه في حالة تعطل الجهاز أثناء المعاملة، تظل بياناتك متسقة وغير تالفة.",
    "pillar_modular_title": "فترات محاسبية مغلقة",
    "pillar_modular_desc": "إغلاق صارم لنهاية اليوم يمنع التلاعب الرجعي بالفترات المالية التاريخية.",

    "// --- Why Finio Section ---": "",
    "why_finio_title": "لماذا لا يعد Finio نظام نقاط بيع تقليدي",
    "why_finio_subtitle": "معظم أنظمة نقاط البيع مجرد آلات حاسبة. Finio هو محرك محاسبي.",

    "why_finio_card_1_title": "الآلات الحاسبة مقابل دفاتر الحسابات",
    "why_finio_card_1_desc": "تعامل أنظمة POS العامة المبيعات كأرقام مؤقتة. يعامل Finio كل عملية بيع كسجل مالي دائم وغير قابل للتغيير.",

    "why_finio_card_2_title": "الاعتماد على السحابة مقابل الأولوية للمحلي",
    "why_finio_card_2_desc": "الأنظمة الأخرى تتوقف عند انقطاع الإنترنت. Finio يعمل محليًا بنزاهة الخوادم، ويقوم بالمزامنة فقط عند استقرار الاتصال.",

    "why_finio_card_3_title": "تضخم الميزات مقابل الدقة",
    "why_finio_card_3_desc": "نحن لا نلاحق الميزات التي تضر بالموثوقية. تم بناء كل وحدة لضمان تطابق أرقامك مع الواقع.",

    "// --- Special Offer Section ---": "",
    "home_offer_badge": "عرض محدود للمشتركين الأوائل",
    "home_offer_title": "كن من بين الأوائل واحصل على 3 أشهر مجانًا",
    "home_offer_desc": "انضم إلى أول 100 مشترك واحصل على 3 أشهر مجانًا على أي خطة.",
    "home_offer_btn": "احصل على العرض",

    "// --- Solutions Section ---": "",
    "solutions_title": "حلول مصممة لكل صناعة",
    "solution_restaurant_title": "المطاعم والمقاهي",
    "solution_restaurant_desc": "أدِر الطلبات، المطبخ، التوصيل، والحجوزات من منصة واحدة.",
    "solution_retail_title": "متاجر التجزئة",
    "solution_retail_desc": "قم بتبسيط المخزون، المبيعات، وولاء العملاء بكل سهولة.",
    "solution_service_title": "الشركات الخدمية",
    "solution_service_desc": "حلول الحجز، الفواتير، وإدارة علاقات العملاء لمقدمي الخدمات والفنادق.",

    "// --- Modules Section ---": "",
    "our_modules_title": "نظام بيئي معياري جاهز للإنتاج",

    "// 1. Sales & Operations": "",
    "mod_sales_title": "المبيعات والعمليات",
    "mod_pos_title": "نظام نقاط البيع (POS)",
    "mod_pos_desc": "نقطة بيع عالية السرعة تعمل دون اتصال بالإنترنت مع مسح الباركود، إدارة عربات متعددة، ومزامنة خالية من التعارض.",
    "mod_kds_title": "نظام عرض المطبخ (KDS)",
    "mod_kds_desc": "رابط مباشر بين الصالة والمطبخ. توجيه المحطات في الوقت الفعلي، مؤقتات، وتتبع حالة الطلب.",
    "mod_delivery_title": "التوصيل والطلبات",
    "mod_delivery_desc": "إدارة منصات التوصيل، السائقين، وطلبات الهاتف بكفاءة من لوحة تحكم واحدة.",

    "// 2. Accounting & Finance": "",
    "mod_finance_title": "المحاسبة والمالية",
    "mod_invoice_title": "محرك الفوترة",
    "mod_invoice_desc": "إنشاء فواتير غير قابلة للتغيير ومتوافقة ضريبيًا. تتبع المدفوعات، الودائع، وسجل ائتمان العملاء.",
    "mod_cash_title": "إدارة النقد",
    "mod_cash_desc": "تأمين المبلغ الافتتاحي، إغلاقات عمياء، وتسوية نقدية دقيقة لمنع السرقة والأخطاء.",
    "mod_expense_title": "المصروفات ورأس المال",
    "mod_expense_desc": "تتبع المصروفات النثرية، مدفوعات الموردين، والتكاليف التشغيلية مباشرة من الصندوق.",

    "// 3. Management & Back-Office": "",
    "mod_mgmt_title": "الإدارة والمكتب الخلفي",
    "mod_inventory_title": "مراقبة المخزون",
    "mod_inventory_desc": "تتبع المخزون في الوقت الفعلي مع سجلات الحركة، تنبيهات انخفاض المخزون، وأوامر الشراء من الموردين.",
    "mod_crm_title": "العملاء والموردين",
    "mod_crm_desc": "ملفات تعريف CRM مفصلة مع سجل الشراء، نقاط الولاء، ودفاتر الموردين.",
    "mod_admin_title": "لوحة ويب المحاسب",
    "mod_admin_desc": "وصول عن بُعد للمحاسبين لتدقيق المبيعات، تصدير اليوميات، وإدارة التكوين.",

    "// 4. Hospitality & Advanced": "",
    "mod_hosp_title": "الضيافة والميزات المتقدمة",
    "mod_floor_title": "إدارة الطاولات والصالة",
    "mod_floor_desc": "خريطة مرئية للطاولات مع حالة الإشغال، إدارة الحجوزات، وتعيين النوادل.",
    "mod_pms_title": "وحدة الفنادق (PMS)",
    "mod_pms_desc": "إدارة كاملة للفندق: حجز الغرف، التدبير المنزلي، وتكامل تسجيل دخول/خروج الضيوف.",
    "mod_api_title": "نظام API المفتوح",
    "mod_api_desc": "ربط تطبيقات الطرف الثالث، أكشاك الخدمة الذاتية، ومنصات الطلب عبر الويب عبر واجهة برمجة التطبيقات الآمنة الخاصة بنا.",

    "// --- Legacy Keys (Restored for Compatibility) ---": "",
    "pos_module_title": "محرك POS الأساسي",
    "pos_module_description": "جوهر المعاملات المحلي. يعالج المدفوعات المقسمة، تدقيق الإلغاء، وإدارة الجلسات دون الاعتماد على الإنترنت.",
    "kds_module_title": "نظام الإنتاج",
    "kds_module_description": "توجيه بدون تأخير (Zero-latency). الطلبات تُرسل وتُنفذ من نقطة البيع، مع ضمان الوصول إلى محطات التحضير بمؤقتات مرئية.",
    "inventory_module_title": "المخزون الدائم",
    "inventory_module_description": "تتبع التكلفة بنظام الوارد أولاً يصرف أولاً (FIFO) مع خصم الوصفات. يدعم المركبات المتداخلة المعقدة وسير عمل تحويلات المستودع المركزي.",
    "pms_module_title": "إدارة الممتلكات",
    "pms_module_description": "إدارة الفواتير المدمجة مع نقطة البيع. رسوم الغرف هي معاملات ذرية يتم التحقق منها مقابل حالة الضيف.",
    "printing_module_title": "خادم توجيه الطباعة",
    "printing_module_description": "توجيه طباعة قائم على القواعد. يدعم إعادة التوجيه عند الفشل، وأجهزة ESC/POS القديمة، وحقن القوالب المخصصة.",
    "web_server_module_title": "القيادة المركزية",
    "web_server_module_description": "تجميع البيانات في الوقت الفعلي للمواقع المتعددة. تحديث القوائم، سحب سجلات التدقيق، وإدارة أذونات المستخدمين عالميًا.",

    "// --- Services Section ---": "",
    "professional_services_title": "الخدمات الاحترافية",
    "custom_software_title": "تطوير البرمجيات المخصصة",
    "custom_software_description": "نقدم حلول برمجية مخصصة تناسب احتياجات عملك الفريدة، من الويب إلى تطبيقات الهاتف المحمول.",
    "hardware_software_title": "حلول الأجهزة والبرمجيات",
    "hardware_software_description": "نوفر تكاملاً سلسًا بين الأجهزة والبرمجيات لتشغيل أنظمتك بكفاءة، بما في ذلك الدعم والتركيب.",
    "consulting_title": "الاستشارات والحلول المتكاملة",
    "consulting_description": "يقدم فريقنا استشارات شاملة لمساعدتك في اختيار وتنفيذ أفضل الحلول التقنية لمشروعك.",

    "// --- Core Features Section ---": "",
    "core_features_title": "بنية النظام والقدرات",

    "// 1. Accounting-Grade Protection": "",
    "feat_protect_title": "جوهر مالي غير قابل للتغيير",
    "feat_protect_desc": "تسجيل دفتر القيد المزدوج مع التحقق من الإضافة فقط. البيانات التاريخية مقفلة رياضيًا ضد التلاعب.",

    "// 2. Offline-First Sync Engine": "",
    "feat_sync_engine_title": "نسخ متماثل خالي من التعارض",
    "feat_sync_engine_desc": "بنية قاعدة بيانات تعطي الأولوية للمحلي (Hive). العمليات تُحفظ محليًا فورًا وتُنسخ إلى السحابة مع اتساق نهائي.",

    "// 3. Trusted Time & Audit Safety": "",
    "feat_audit_title": "توقيت موثوق من الخادم",
    "feat_audit_desc": "يتم ختم المعاملات زمنيًا بواسطة سلطة الخادم لمنع التلاعب بالتوقيت المحلي والاحتيال في وقت الإغلاق.",

    "// 4. Professional Closures": "",
    "feat_closure_title": "إغلاقات يومية احترافية",
    "feat_closure_desc": "إدارة المبالغ الافتتاحية، إجراء إغلاقات يومية (Z) عمياء، وتسوية النقد مع تتبع التباين الآلي.",

    "// 5. Roles & Permissions": "",
    "feat_roles_title": "التحكم في الوصول القائم على الأدوار (RBAC)",
    "feat_roles_desc": "حدد مستويات الوصول الدقيقة للصرافين، المديرين، المحاسبين، وموظفي المطبخ مع بوابات إذن دقيقة.",

    "// 6. Integrations & APIs": "",
    "feat_integrations_title": "التكاملات وواجهات برمجة التطبيقات",
    "feat_integrations_desc": "تواصل مع أنظمة ERP، وWooCommerce، وبوابات الدفع عبر واجهة برمجة تطبيقات المطورين الشاملة لدينا.",

    // --- كل شيء مشمول ---
    "all_features_title": "كل شيء مشمول",
    "all_features_subtitle": "قدرات قوية مدمجة بالفعل في Finio — تتجاوز الأساسيات.",
    "all_group_revenue": "الإيرادات والنمو",
    "all_revenue_giftcards_title": "بطاقات الهدايا",
    "all_revenue_giftcards_desc": "أصدر واشحن وألغِ بطاقات الهدايا، مع تتبع نقدي نهاية اليوم ولوحة التزامات مباشرة.",
    "all_revenue_loyalty_title": "برنامج الولاء",
    "all_revenue_loyalty_desc": "مستويات برونزية وفضية وذهبية مع كسب النقاط واستبدالها وانتهاء صلاحيتها.",
    "all_revenue_coupons_title": "الكوبونات والعروض",
    "all_revenue_coupons_desc": "حدود الاستخدام والحملات المجدولة وإحصاءات العائد لكل كوبون.",
    "all_revenue_loans_title": "ائتمان وقروض العملاء",
    "all_revenue_loans_desc": "قروض وحدود ائتمان للشركات (B2B) مع تتبع الأرصدة.",
    "all_group_ops": "العمليات ومنع الخسائر",
    "all_ops_shifts_title": "ورديات وجدولة الموظفين",
    "all_ops_shifts_desc": "ورديات أسبوعية متكررة، وكشف التعارض، وتنبيهات فورية.",
    "all_ops_loss_title": "حزمة منع الخسائر",
    "all_ops_loss_desc": "سجل الهدر، والجرد/العد الدوري، والاسترجاع + الاستبدال — أوقف خسارة الأموال.",
    "all_ops_fefo_title": "تتبع FEFO والدفعات",
    "all_ops_fefo_desc": "تتبع الصلاحية والدفعات مع احتساب التكلفة (الأول انتهاءً أول خروجًا).",
    "all_ops_transfers_title": "التحويل بين الفروع",
    "all_ops_transfers_desc": "انقل المخزون بين الفروع والمستودعات مع تتبع كامل.",
    "all_ops_barcode_title": "طباعة ملصقات الباركود",
    "all_ops_barcode_desc": "أنشئ واطبع ملصقات الباركود وبطاقات الأسعار.",
    "all_ops_purchasing_title": "المشتريات والموردون",
    "all_ops_purchasing_desc": "أوامر الشراء ودفاتر الموردين للتحكم الكامل في المكاتب الخلفية.",
    "all_group_ai": "الذكاء الاصطناعي وتجربة الضيوف",
    "all_ai_translation_title": "ترجمة المنتجات بالذكاء الاصطناعي",
    "all_ai_translation_desc": "ترجم أسماء المنتجات وأوصافها تلقائيًا.",
    "all_ai_enrichment_title": "إثراء المحتوى بـ ChatGPT",
    "all_ai_enrichment_desc": "أثرِ بيانات الكتالوج بالأوصاف والفئات والسمات.",
    "all_ai_perplexity_title": "بحث Perplexity",
    "all_ai_perplexity_desc": "اجلب رؤى المنتجات والسوق الحية مباشرة إلى Finio.",
    "all_ai_waiter_title": "نداء النادل",
    "all_ai_waiter_desc": "ينادي الضيوف الموظفين فورًا مع عرض أرضية بتنبيه أحمر.",
    "all_ai_feedback_title": "تقييم العملاء + QR",
    "all_ai_feedback_desc": "اجمع التقييمات عبر نموذج ويب ورمز QR.",
    "all_ai_kiosk_title": "كشك / الطلب الذاتي",
    "all_ai_kiosk_desc": "كشك خدمة ذاتية ووضع طلب ذاتي للأماكن المزدحمة.",
    "all_ai_map_title": "خريطة المطعم",
    "all_ai_map_desc": "مخططات سحب وإفلات مع دعم متعدد الطوابق والفروع.",
    "all_ai_glovo_title": "سوق Glovo",
    "all_ai_glovo_desc": "استقبل طلبات توصيل Glovo مباشرة في نظامك.",
    "all_group_finance": "المكتب الخلفي والمالية",
    "all_fin_fiscal_title": "الامتثال الضريبي",
    "all_fin_fiscal_desc": "fiskaly SIGN ES وfiskaltrust (AT/DE/FR) لفوترة ضريبية معتمدة.",
    "all_fin_dunning_title": "تنبيهات التحصيل",
    "all_fin_dunning_desc": "تذكيرات دفع آلية للأرصدة المتأخرة على العملاء.",
    "all_fin_payroll_title": "الرواتب والأجور",
    "all_fin_payroll_desc": "أدر الرواتب وأنشئ ملفات PDF للرواتب من لوحة المحاسب.",
    "all_fin_docs_title": "سلسلة المستندات",
    "all_fin_docs_desc": "الاسترجاع + الاستبدال، وحصص المستندات، وسلسلة BC → BL → FA → AV.",
    "all_group_platform": "المنصة والموثوقية",
    "all_plat_lan_title": "التكرار المحلي دون إنترنت",
    "all_plat_lan_desc": "تشغيل متعدد الأجهزة دون إنترنت عبر شبكتك المحلية مع مزامنة تلقائية.",
    "all_plat_desktop_title": "تطبيقات سطح المكتب",
    "all_plat_desktop_desc": "تطبيقات أصلية لويندوز وماك ولينكس، إضافة إلى الويب وiOS وأندرويد.",

    // --- المقارنة: ميزات PRO الحقيقية ---
    "sub_cat_pro_only": "احترافي فقط",
    "sub_cmp_refund_replace": "الاسترجاع + الاستبدال",
    "sub_cmp_quota": "إدارة حصة المستندات",
    "sub_cmp_doc_chain": "سلسلة المستندات (BC → BL → FA → AV)",
    "sub_cmp_whatsapp_invoice": "فواتير واتساب",
    "sub_cmp_max_registers": "الحد الأقصى لصناديق النقد",
    "sub_cmp_reconciliation": "تسوية الصندوق",
    "sub_cmp_customer_pdf": "كشوف العملاء PDF",
    "sub_cmp_stock_reports": "تقارير المخزون",
    "sub_cmp_delivery_orders": "طلبات التوصيل",
    "sub_cmp_store_orders": "طلبات الويب / المتجر",
    "sub_cmp_health_date": "تقارير حسب الفترة (الصحة)",
    "sub_cmp_liquid_capital": "PDF رأس المال السائل",

    "// Legacy keys": "",
    "feature_auth_title": "مصادقة متعددة المستأجرين",
    "feature_auth_desc": "مصادقة آمنة قائمة على الأدوار مصممة لبنية متعددة المستأجرين، مما يضمن عزل البيانات والأمان.",
    "feature_offline_title": "حفظ محلي بدون تأخير",
    "feature_offline_desc": "حافظ على سير عملياتك بسلاسة، حتى بدون اتصال بالإنترنت. تتم مزامنة البيانات تلقائيًا بمجرد عودتك للاتصال.",
    "feature_sync_title": "مزامنة الفروقات في الخلفية",
    "feature_sync_desc": "يستفيد من Firebase وخدمة مزامنة قوية لضمان تحديث البيانات دائمًا عبر جميع الأجهزة والوحدات.",
    "feature_remote_title": "لوحة ويب المحاسب والمدير",
    "feature_remote_desc": "وصول آمن عن بُعد للمحاسبين والمسؤولين لتدقيق المبيعات واليوميات والتكوين.",
    "feature_reporting_title": "بيانات مالية مُسواة",
    "feature_reporting_desc": "اكتسب رؤى نقدية مع تحليلات شاملة للمبيعات وأداء المنتجات وسلوك العملاء.",
    "feature_security_title": "التحكم في الوصول القائم على الأدوار (RBAC)",
    "feature_security_desc": "من بوابات الأذونات في واجهة المستخدم إلى النسخ الاحتياطي التلقائي للبيانات والتشفير، بياناتك محمية دائمًا.",
    "feature_pms_rooms": "إدارة الغرف والإشغال",
    "feature_pms_booking": "حجز الضيوف و CRM",
    "feature_print_network": "اكتشاف طابعات الشبكة",
    "feature_print_custom": "قوالب إيصالات قابلة للتخصيص",

    "// --- Innovations Section ---": "",
    "innovations_title": "حلول جاهزة للمستقبل",
    "innovations_intro": "كن من بين الأوائل الذين يختبرون مستقبل أتمتة الأعمال. انضم إلى برنامج المتبنين الأوائل وساهم في تشكيل الجيل القادم من حلول الأعمال الذكية.",
    "smart_store_title": "متجر Finio الذكي",
    "smart_store_desc": "فعّل تجربة تسوق بدون دفع عند الكاشير باستخدام كاميرات مدعومة بالذكاء الاصطناعي، أجهزة الشراء الذاتي، والكشف الآلي عن السرقة.",
    "smart_store_feature_1_title": "كاميرات مدعومة بالذكاء الاصطناعي",
    "smart_store_feature_1_desc": "رؤية حاسوبية متقدمة تتتبع حركات العملاء واختيارات المنتجات في الوقت الفعلي.",
    "smart_store_feature_2_title": "أجهزة الدفع الذاتي",
    "smart_store_feature_2_desc": "يقوم العملاء بمسح ودفع ثمن العناصر بشكل مستقل، مما يقلل من أوقات الانتظار ومتطلبات الموظفين.",
    "smart_store_feature_3_title": "الكشف الآلي عن السرقة",
    "smart_store_feature_3_desc": "خوارزميات التعلم الآلي تحدد السلوك المشبوه وتنبه الأمن في الوقت الفعلي.",
    "smart_store_feature_4_title": "التتبع الذكي للمخزون",
    "smart_store_feature_4_desc": "تتبع مستويات المخزون تلقائيًا عند اختيار العناصر، مما يضمن دقة أعداد المخزون.",
    "smart_airbnb_title": "إدارة العقارات الذكية (Finio Smart Airbnb)",
    "smart_airbnb_desc": "أتمتة إدارة الممتلكات الخاصة بك مع تأكيدات الحجز بالذكاء الاصطناعي، دخول القفل الذكي، ورموز الوصول الديناميكية.",
    "smart_airbnb_feature_1_title": "مساعد الحجز الذكي",
    "smart_airbnb_feature_1_desc": "ردود آلية على الاستفسارات، تأكيدات حجز فورية، واقتراحات تسعير ذكية.",
    "smart_airbnb_feature_2_title": "تكامل القفل الذكي",
    "smart_airbnb_feature_2_desc": "توليد رموز وصول فريدة لكل ضيف، يتم تفعيلها وإلغاء تفعيلها تلقائيًا بناءً على الحجوزات.",
    "smart_airbnb_feature_3_title": "إدارة الوصول الديناميكية",
    "smart_airbnb_feature_3_desc": "تتغير الرموز تلقائيًا بعد المغادرة، مما يضمن أقصى قدر من الأمان لممتلكاتك.",
    "smart_airbnb_feature_4_title": "مركز تواصل الضيوف",
    "smart_airbnb_feature_4_desc": "مراسلة مركزية، تعليمات تسجيل الدخول، ومتابعة آلية للتقييمات.",
    "early_adopter_title": "كن من المتبنين الأوائل",
    "early_adopter_intro": "انضم إلى برنامج المتبنين الأوائل الحصري واحصل على أسعار خاصة، دعم ذو أولوية، وفرصة للتأثير على تطوير المنتج.",
    "early_adopter_benefit_1": "أسعار إطلاق خاصة",
    "early_adopter_benefit_2": "أولوية في التنفيذ والدعم",
    "early_adopter_benefit_3": "مساهمة مباشرة في الميزات وخارطة الطريق",
    "early_adopter_benefit_4": "وصول حصري لميزات بيتا",
    "early_adopter_form_title": "عبر عن اهتمامك",
    "early_adopter_form_intro": "املأ النموذج أدناه لتكون من بين الأوائل الذين يختبرون هذه الحلول المبتكرة.",
    "solution_interested": "الحل الذي يهمك",
    "select_solution": "اختر حلًا",
    "business_type": "نوع العمل",
    "current_challenges": "التحديات الحالية",
    "early_adopter_submit": "انضم لبرنامج المتبنين الأوائل",

    "// Other keys from previous version...": "",
    "footer_finio_description": "نظام نقاط البيع الأكثر تطورًا لإدارة وتنمية عملك بكفاءة واحترافية.",
    "quick_links": "روابط سريعة",
    "support": "الدعم",
    "help_center": "مركز المساعدة",
    "terms_conditions": "الشروط والأحكام",
    "privacy_policy": "سياسة الخصوصية",
    "contact_us_footer": "اتصل بنا",
    "contact_phone": "+212 808-646672",
    "contact_email": "email",
    "contact_address": "شارع أوسو وباسلام، تطوان",
    "copyright": "© 2025 Finio Solutions. جميع الحقوق محفوظة.",
    "company_name": "Finio Solutions",
    "company_ice": "ICE: 003689594000059",
    "company_siege": "المقر: تطوان، المغرب",
    "company_address_full": "شارع أوسو وباسلام، تطوان، المغرب",
    "contact_page_title": "اتصل بنا",
    "contact_page_intro": "نحن هنا للإجابة على جميع استفساراتكم وتقديم الدعم اللازم. لا تترددوا في الاتصال بنا.",
    "send_message": "أرسل لنا رسالة",
    "full_name": "الاسم الكامل",
    "email_address": "البريد الإلكتروني",
    "phone_number": "رقم الهاتف",
    "subject": "الموضوع",
    "your_message": "رسالتك",
    "send_button": "إرسال الرسالة",
    "contact_info_title": "معلومات الاتصال",

    "// --- Navigation Links ---": "",
    "nav_documentation": "التوثيق",
    "nav_faq": "الأسئلة الشائعة",
    "nav_security": "الأمان وحماية البيانات",
    "nav_changelog": "سجل التغييرات",
    "nav_about_us": "من نحن",
    "nav_compliance": "الامتثال والتدقيق",

    "// Footer Columns": "",
    "footer_col_product": "المنتج",
    "footer_col_resources": "الموارد",
    "footer_col_terms": "الشروط",
    "footer_col_connect": "تواصل",

    "// --- About Us Page ---": "",
    "about_hero_title": "مصمم للحقيقة المالية",
    "about_hero_subtitle": "نحن لا نبني مجرد برنامج نقاط بيع. نحن نبني محركات مالية بدرجة تدقيق تمنع الاحتيال، تقضي على الأخطاء، وتضمن اتساق البيانات.",
    "about_mission_title": "مهمتنا",
    "about_mission_text": "القضاء على الغموض المالي في قطاع التجزئة والضيافة. نحن نوفر اليقين الرياضي بأن كل سنت مكتسب يتم حسابه وتتبعه وتأمينه.",
    "about_vision_title": "معيارنا الهندسي",
    "about_vision_text": "نحن نرفض معيار الصناعة المتمثل في 'جيد بما فيه الكفاية'. نتصور مستقبلاً تكون فيه برمجيات الأعمال موثوقة مثل خزانة البنك ودقيقة مثل ساعة سويسرية.",
    "about_values_title": "الأخلاقيات التشغيلية",
    "about_values_text": "الدقة قبل السرعة. النزاهة قبل الميزات. نحن نمنح الأولوية لسلامة بياناتك فوق كل الادعاءات التسويقية.",
    "about_story_title": "أصل عدم التسامح مطلقًا",
    "about_story_paragraph_1": "بدأ Finio عندما قمنا بتدقيق سلسلة مطاعم تستخدم نظام POS سحابي شهير. وجدنا طلبات وهمية، وفواتير محذوفة، ومخزونًا عائمًا يختفي دون أثر. النظام سمح بذلك. قاعدة البيانات سمحت بذلك.",
    "about_story_paragraph_2": "قمنا ببناء Finio لإغلاق تلك الثغرات. صممنا دفتر أستاذ غير قابل للتغيير حيث الحذف مستحيل - فقط العكس. قمنا ببناء محرك مزامنة ينجو من أعطال الشبكة دون إتلاف بايت واحد.",
    "about_story_paragraph_3": "اليوم، Finio هو المعيار للمشغلين الذين يطلبون الحقيقة. نحن لا نساعدك فقط على البيع؛ نحن نساعدك على إثبات ما بعته. من المتاجر الفردية إلى سلاسل المؤسسات، نوفر العمود الفقري لعمليات جاهزة للتدقيق.",
    "about_why_choose_title": "لماذا يثق المشغلون بـ Finio",
    "about_reason_1_title": "دفتر أستاذ غير قابل للتغيير",
    "about_reason_1_desc": "الفواتير والمدفوعات للإضافة فقط. لا أحد - ولا حتى أنت - يمكنه حذف سجل معاملة بصمت.",
    "about_reason_2_title": "تشفير عسكري",
    "about_reason_2_desc": "البيانات مشفرة في حالة السكون وأثناء النقل. تظل ذكاء أعمالك خاصًا بصرامة.",
    "about_reason_3_title": "السيادة المحلية (Offline Sovereignty)",
    "about_reason_3_desc": "نحن لا نؤجرك الوصول لبياناتك. إنها تعيش على جهازك، وتعمل بكامل طاقتها دون أي اعتماد على خادم خارجي.",
    "about_reason_4_title": "تقارير جاهزة للتدقيق",
    "about_reason_4_desc": "تقاريرنا ليست مجرد رسوم بيانية جميلة. إنها بيانات مالية مُسواة رياضيًا وجاهزة لمحاسبك.",
    "about_reason_5_title": "مزامنة خالية من التعارض",
    "about_reason_5_desc": "محرك المزامنة الخاص بنا يعالج تصادمات الأجهزة المتعددة بمنطق حتمي، مما يضمن عدم فقدان أي بيانات.",
    "about_reason_6_title": "نزاهة النظام البيئي",
    "about_reason_6_desc": "كل وحدة - KDS، المخزون، لوحة الويب - تغذي نفس المصدر الواحد للحقيقة. لا توجد صوامع بيانات.",
    "about_stats_title": "مقاييس موثوقية النظام",
    "about_stat_1": "تعارضات البيانات",
    "about_stat_2": "وقت التشغيل",
    "about_stat_3": "درجة التدقيق",
    "about_stat_4": "اتفاقية مستوى الخدمة للدعم",
    "about_team_title": "الفريق الهندسي",
    "about_team_intro": "نحن لسنا مسوقين. نحن مهندسو أنظمة، ومتخصصو أمن، ومهندسو برمجيات مالية مكرسون لعلم إدارة الأعمال.",
    "about_team_member_1_name": "هندسة الأنظمة",
    "about_team_member_1_role": "البنية التحتية الأساسية",
    "about_team_member_1_bio": "تصميم دفتر الأستاذ غير القابل للتغيير وطوبولوجيا قاعدة البيانات التي تعطي الأولوية للعمل دون اتصال والتي تشغل كل نسخة من Finio.",
    "about_team_member_2_name": "الأمن والامتثال",
    "about_team_member_2_role": "نزاهة البيانات",
    "about_team_member_2_bio": "ضمان أن كل معاملة تلبي معايير التدقيق الصارمة ومحمية ضد التلاعب.",
    "about_team_member_3_name": "عمليات العملاء",
    "about_team_member_3_role": "نجاح التنفيذ",
    "about_team_member_3_bio": "مساعدة المؤسسات على ترحيل هياكل بياناتها المعقدة إلى بيئة Finio الصارمة.",
    "about_team_member_4_name": "تصميم المنتج",
    "about_team_member_4_role": "تجربة المستخدم وسير العمل",
    "about_team_member_4_bio": "ترجمة المنطق المحاسبي المعقد إلى سير عمل سريع وبديهي لموظفي الواجهة الأمامية.",
    "about_core_values_title": "البروتوكولات الأساسية",
    "about_value_1_title": "الحقيقة أولاً",
    "about_value_1_desc": "نحن لا نتلاعب بالأرقام. إذا لم تتوازن الرياضيات، يتوقف النظام. الغموض هو خلل برمجي.",
    "about_value_2_title": "المحلي أولاً",
    "about_value_2_desc": "الجهاز الذي في يدك هو مصدر الحقيقة. السحابة هي مجرد نسخة احتياطية.",
    "about_value_3_title": "صفر فقدان للبيانات",
    "about_value_3_desc": "نحن مهووسون بسلامة البيانات. نفترض أن الشبكات تفشل، والتيار ينقطع، والأجهزة تتعطل.",
    "about_value_4_title": "منطق شفاف",
    "about_value_4_desc": "لا نخفي الحسابات. يمكن تتبع كل إجمالي إلى معاملاته الذرية الأصلية.",
    "about_value_5_title": "الشراكة",
    "about_value_5_desc": "نحن نبني أدوات للمشغلين الجادين. إذا كنت تهتم بالدقة، فنحن شريكك.",
    "about_growth_title": "مستقبل النزاهة",
    "about_growth_text_1": "مع توسعنا، يظل التزامنا بالصلابة الأساسية للمحاسبة دون تغيير. نحن نتوسع في الذكاء الاصطناعي والأتمتة، ولكن ليس على حساب الدقة أبدًا.",
    "about_growth_text_2": "تتضمن خارطة طريقنا وحدات 'المتجر الذكي' و'الممتلكات الذكية'، ولكن بخلاف الآخرين، فإن وحداتنا مبنية على نفس الأساس غير القابل للتغيير. اقتراحات الذكاء الاصطناعي ستكون مسجلة للتدقيق.",
    "about_growth_text_3": "يقوم Finio ببناء نظام التشغيل للجيل القادم من الشركات الشفافة والفعالة والآمنة للتدقيق في شمال إفريقيا وخارجها.",
    "about_cta_title": "هل أنت مستعد لعمل جاد؟",
    "about_cta_subtitle": "انضم إلى المشغلين الذين توقفوا عن القلق بشأن تناقضات البيانات وبدأوا التركيز على النمو.",
    "explore_products": "عرض الوحدات التقنية",

    "// --- Checkout Page ---": "",
    "checkout_title": "أكمل اشتراكك",
    "checkout_intro": "املأ التفاصيل أدناه لإكمال اشتراكك. سنتصل بك خلال 24 ساعة لتأكيد خطتك.",
    "complete_payment": "إتمام الدفع",
    "subscription_success": "تم بنجاح! تم تفعيل اشتراكك.",
    "fill_personal_info": "يرجى ملء المعلومات الشخصية أولاً.",
    "email_required_card": "عنوان البريد الإلكتروني مطلوب للدفع بالبطاقة.",
    "personal_info_title": "المعلومات الشخصية",
    "company_name_optional": "اسم الشركة (اختياري)",
    "city": "المدينة",
    "country": "الدولة",
    "payment_method": "طريقة الدفع",
    "select_payment": "اختر طريقة الدفع",
    "payment_card": "بالبطاقة",
    "payment_cash": "نقدًا",
    "payment_cheque": "بشيك",
    "payment_bank_cheque": "بشيك بنكي",
    "subscription_details_title": "تفاصيل الاشتراك",
    "selected_plan": "الخطة المختارة",
    "subscription_duration": "مدة الاشتراك",
    "select_duration": "اختر المدة",
    "duration_3months": "3 أشهر",
    "duration_6months": "6 أشهر (شهر مجاني)",
    "duration_12months": "12 شهرًا (2.5 شهر مجاني)",
    "total_amount": "المبلغ الإجمالي",
    "complete_subscription": "إتمام الاشتراك",
    "redirecting_to_payment": "جارٍ التحويل إلى الدفع الآمن...",
    "sub_recurring_note": "سيتم خصم {amount} درهم مقابل المدة المختارة الآن، ثم يتجدد الاشتراك تلقائيًا بسعر {yearly} درهم سنويًا حتى تقوم بالإلغاء.",
    "payment_success_title": "تم الدفع بنجاح",
    "payment_success_desc": "شكرًا لك! تم استلام دفعتك ويجري تفعيل اشتراكك.",
    "payment_failed_title": "فشل الدفع",
    "payment_failed_desc": "تعذر إتمام دفعتك ولم يتم خصم أي مبلغ. يمكنك المحاولة مرة أخرى أو التواصل مع فريقنا.",
    "order_reference": "مرجع الطلب",
    "amount_paid": "المبلغ المدفوع",
    "try_again": "حاول مرة أخرى",

    // --- صفحة المعدات (المغرب فقط) ---
    "nav_hardware": "المعدات",
    "hw_title": "حزم أجهزة نقاط البيع",
    "hw_subtitle": "حزم نقاط بيع كاملة — مجمّعة ومختبرة ومسلّمة في جميع أنحاء المغرب.",
    "hw_morocco_badge": "متوفرة في المغرب فقط",
    "hw_delivery_badge": "التوصيل ابتداءً من 50 درهم",
    "hw_packs_title": "اختر الحزمة المناسبة",
    "hw_includes": "ماذا تشمل",
    "hw_hardware_price": "سعر الأجهزة",
    "hw_with_scanner": "مع ماسح ضوئي 2D",
    "hw_get_quote": "اطلب هذه الحزمة",
    "hw_pricelist_title": "المكونات المتوفرة",
    "hw_pricelist_intro": "كل المكونات التي يمكننا توفيرها لتجهيز منشأتك.",
    "hw_item_col": "القطعة",
    "hw_price_col": "السعر",
    "hw_software_title": "برنامج Finio يُحتسب بشكل منفصل",
    "hw_software_desc": "أسعار الأجهزة تُدفع مرة واحدة، وبرنامج Finio يُحتسب بشكل منفصل. عند إضافة اشتراك Finio السنوي إلى طلب الأجهزة، تحصل على خصم إضافي 30% للسنة الأولى فقط — على سبيل المثال خطة البداية (1900 درهم/سنة) تصبح 1330 درهمًا للسنة الأولى، ثم تتجدد بسعر 1900 درهم/سنة.",
    "hw_cta_title": "تحتاج مساعدة في الاختيار؟",
    "hw_cta_desc": "أخبرنا عن نشاطك التجاري وسنرشح لك الحزمة المناسبة.",
    "hw_pack_starter_name": "حزمة البداية",
    "hw_pack_starter_desc": "التجهيز الأساسي للمتاجر الصغيرة والمقاهي.",
    "hw_pack_android_name": "حزمة أندرويد",
    "hw_pack_android_desc": "نقطة بيع بشاشة لمس تعمل بنظام أندرويد — مدمجة ومرنة.",
    "hw_pack_pro_name": "الحزمة الاحترافية",
    "hw_pack_pro_desc": "تجهيز بجهاز الكل في واحد للمحلات ذات الحركة العالية.",
    "hw_item_touch": "شاشة لمس 17 بوصة",
    "hw_item_printer": "طابعة حرارية 80 مم",
    "hw_item_scanner": "ماسح ضوئي 2D",
    "hw_item_drawer": "صندوق نقود (Tiroir)",
    "hw_item_tablet": "جهاز لوحي G-Tab T12",
    "hw_item_support": "حامل عالي الجودة",
    "hw_item_pc": "حاسوب i5",
    "hw_item_aio": "جهاز الكل في واحد",
    "hw_item_tvbox": "AndroidBox",
    "hw_order_title": "هيّئ حزمتك",
    "hw_order_subtitle": "اختر الخيارات وطريقة الدفع ثم أكمل طلبك.",
    "hw_order_back": "رجوع إلى الحزم",
    "hw_order_selected_pack": "الحزمة المختارة",
    "hw_scanner_title": "ماسح ضوئي 2D",
    "hw_scanner_without": "بدون ماسح ضوئي",
    "hw_scanner_with": "مع ماسح ضوئي 2D",
    "hw_subscription_title": "برنامج Finio",
    "hw_sub_hardware_only": "الأجهزة فقط",
    "hw_sub_add": "إضافة اشتراك Finio",
    "hw_sub_plan": "خطة البرنامج",
    "hw_sub_first_year": "السنة الأولى (خصم 30%)",
    "hw_sub_renews": "يتجدد بسعر",
    "hw_sub_per_year": "/سنة",
    "hw_payment_title": "طريقة الدفع",
    "hw_pay_card": "بالبطاقة",
    "hw_pay_card_desc": "دفع إلكتروني آمن عبر Chari Pay",
    "hw_pay_cod": "الدفع عند التسليم",
    "hw_pay_transfer": "تحويل بنكي",
    "hw_pay_transfer_note": "سنرسل لك المعلومات البنكية مباشرة بعد إتمام الطلب.",
    "hw_details_title": "معلوماتك",
    "hw_field_name": "الاسم الكامل",
    "hw_field_phone": "الهاتف",
    "hw_field_email": "البريد الإلكتروني",
    "hw_field_company": "الشركة (اختياري)",
    "hw_field_city": "المدينة",
    "hw_field_address": "العنوان",
    "hw_summary_title": "ملخص الطلب",
    "hw_sum_pack": "حزمة الأجهزة",
    "hw_sum_scanner": "ماسح ضوئي 2D",
    "hw_sum_subscription": "برنامج Finio (السنة الأولى)",
    "hw_sum_delivery": "التوصيل",
    "hw_sum_total": "الإجمالي",
    "hw_place_order": "تأكيد الطلب",
    "hw_processing": "جارٍ المعالجة...",
    "hw_order_success_title": "تم استلام الطلب!",
    "hw_order_success_desc": "شكرًا لك. سيتواصل معك فريقنا قريبًا لتأكيد التوصيل والدفع.",
    "hw_new_order": "اطلب حزمة أخرى",
    "hw_required_missing": "يرجى ملء الحقول المطلوبة.",
    "months": "أشهر",
    "month_free": "شهر مجاني",
    "months_free": "أشهر مجانية",
    "subscriptions_page_title": "خطط شفافة لعملك",
    "subscriptions_intro": "اختر الخطة التي تناسب احتياجاتك دون رسوم خفية.",
    "pricing_basic_title": "البداية (Starter)",
    "pricing_basic_price": "200",
    "pricing_pro_title": "الأعمال (Business)",
    "pricing_pro_price": "350",
    "pricing_custom_title": "مخصص",
    "pricing_custom_contact": "اتصل بنا",
    "pricing_currency": "درهم/شهر",
    "pricing_basic_yearly": "سنويًا 1900 درهم/سنة",
    "pricing_basic_yearly_savings": "2.5 شهر مجاني",
    "pricing_pro_yearly": "سنويًا 3325 درهم/سنة",
    "pricing_pro_yearly_savings": "2.5 شهر مجاني",
    "pricing_most_popular": "الأكثر شيوعًا",
    "pricing_basic_feature_1": "تطبيق كامل الميزات",
    "pricing_basic_feature_2": "إدارة المخزون والعملاء",
    "pricing_basic_feature_3": "تحليلات ذكية",
    "pricing_basic_feature_4": "دعم فني 24/7",
    "pricing_pro_feature_1": "جميع ميزات البداية",
    "pricing_pro_feature_2": "لوحة تحكم ويب للمحاسب",
    "pricing_pro_feature_3": "إدارة الوثائق (عروض أسعار، فواتير)",
    "pricing_pro_feature_4": "تقارير متقدمة",
    "pricing_pro_feature_5": "دعم فني ذو أولوية",
    "pricing_custom_feature_1": "نظام المطاعم (KDS)",
    "pricing_custom_feature_2": "نظام الفنادق (PMS)",
    "pricing_custom_feature_3": "تخصيص كامل",
    "pricing_custom_feature_4": "تكامل مع أنظمتك الحالية",
    "pricing_custom_feature_5": "سعر مصمم حسب احتياجاتك",
    "pricing_get_started": "ابدأ الآن",
    "pricing_choose_plan": "اختر خطة الأعمال",
    "pricing_request_quote": "اطلب عرض سعر",
    "contact_plans_title": "لتفاصيل الخطط والأسعار",
    "contact_plans_text": "يرجى الاتصال بنا مباشرة للحصول على عرض سعر مخصص يلبي متطلبات عملك.",
    "contact_us_plans": "اتصل بنا",
    "learn_more": "اعرف المزيد",

    "// --- Custom Plan Content ---": "",
    "sub_custom_title": "Finio مخصص",
    "sub_custom_tagline": "حلول مؤسسية مصممة للبيئات المعقدة",
    "sub_custom_pricing_contact": "اتصل بنا للحصول على عرض",
    "sub_custom_everything_pro": "كل شيء في خطة الأعمال، بالإضافة إلى:",

    "sub_custom_f1_title": "نظام فندقي كامل (Enterprise PMS)",
    "sub_custom_f1_desc": "الغرف، الإشغال، الخدمات، إدارة الأقسام المتعددة والممتلكات المتعددة.",
    "sub_custom_f2_title": "تكامل Enterprise ERP",
    "sub_custom_f2_desc": "اربط Finio بأنظمة ERP الحالية للمحاسبة، المخزون، الموارد البشرية، والتقارير.",
    "sub_custom_f3_title": "حلول المتجر الذكي (Smart Store)",
    "sub_custom_f3_desc": "أتمتة تجزئة متقدمة، أجهزة متصلة، وسير عمل تشغيلي مخصص.",
    "sub_custom_f4_title": "تكامل المنزل الذكي والممتلكات الذكية",
    "sub_custom_f4_desc": "التكامل مع أنظمة إنترنت الأشياء (IoT)، التحكم في الوصول، الأتمتة، والبنية التحتية الذكية.",
    "sub_custom_f5_title": "تخصيص كامل للنظام",
    "sub_custom_f5_desc": "وحدات مخصصة، سير عمل، ومنطق عمل مكيف تمامًا مع احتياجاتك.",
    "sub_custom_f6_title": "اتصال متقدم للنظام و API",
    "sub_custom_f6_desc": "تكاملات آمنة مع منصات الطرف الثالث والأنظمة الداخلية للمؤسسة.",
    "sub_custom_f7_title": "قابلية التوسع للمواقع المتعددة والمؤسسات",
    "sub_custom_f7_desc": "مصمم للشركات الكبيرة، السلاسل، والهياكل التنظيمية المعقدة.",
    "sub_custom_f8_title": "تحليل ونشر مشروع مخصص",
    "sub_custom_f8_desc": "دراسة فنية، تكوين، ونشر يتماشى مع متطلبات عملك.",
    "sub_custom_f9_title": "تسعير مخصص ودعم طويل الأجل",
    "sub_custom_f9_desc": "نموذج تسعير مصمم حسب النطاق، التكاملات، ومستوى الدعم.",

    "sub_custom_contact_text": "تواصل معنا للحصول على عرض مخصص.",

    "// --- Subscription Page Enhanced Content ---": "",
    "sub_safety_header": "السلامة والنزاهة المحاسبية مضمنة افتراضيًا.",
    "sub_safety_desc": "تم بناء Finio بأساس قوي للمحاسبة وحماية البيانات في جوهره. تشترك كلتا الخطتين في نفس الأساس الآمن — الفرق يكمن في مدى التحكم والرؤية وقابلية التوسع التي تحتاجها.",
    "sub_safety_note": "نحن لا نبيع الأمان كترقية إضافية.",

    "sub_basic_title": "Finio البداية (Starter)",
    "sub_basic_tagline": "كل ما تحتاجه للبيع، والإدارة، وإغلاق يومك بثقة.",
    "sub_basic_desc": "تشمل خطة البداية تجربة POS الكاملة مع حمايات مدمجة تضمن أن بياناتك صحيحة وموثوقة دائمًا.",
    "sub_included_features": "الميزات المضمنة",
    "sub_basic_f1": "نقطة بيع كاملة (تجزئة، وجبات سريعة، مطاعم)",
    "sub_basic_f2": "إدارة المنتجات، العملاء، والموردين",
    "sub_basic_f3": "مسح الباركود ومستويات أسعار متعددة",
    "sub_basic_f4": "خصومات، كوبونات، وعربات محفوظة",
    "sub_basic_f5": "فتح الصندوق وإغلاق نهاية اليوم (Z / clôture)",
    "sub_basic_f6": "إنشاء الفواتير، طباعتها، وتتبعها",
    "sub_basic_f7": "استرداد آمن وإدارة رصيد العملاء",
    "sub_basic_f8": "تشغيل دون اتصال (Offline-first) مع مزامنة تلقائية",
    "sub_basic_f9": "فواتير غير قابلة للتغيير ومحاسبة مقاومة للتلاعب",
    "sub_basic_f10": "مزامنة آمنة متعددة الأجهزة",
    "sub_best_for": "الأفضل لـ",
    "sub_basic_best_for": "الشركات الصغيرة والمتوسطة التي تدير العمليات اليومية مباشرة من التطبيق وتريد نقطة بيع موثوقة ومتوافقة دون تعقيد.",

    "sub_pro_title": "Finio الأعمال (Business)",
    "sub_pro_tagline": "مصممة للنمو، الهيكلة، وسير العمل المحاسبي الاحترافي.",
    "sub_pro_desc": "تعتمد خطة الأعمال على كل ما في خطة البداية وتضيف وحدات متقدمة للإدارة، المحاسبة، والأتمتة.",
    "sub_everything_basic_plus": "كل شيء في البداية، بالإضافة إلى:",
    "sub_pro_f1": "لوحة ويب للمحاسب والإدارة",
    "sub_pro_f1_detail": "إدارة رواتب الموظفين، المصروفات، الفواتير (B2C & B2B)، والإحصاءات المحاسبية من واجهة ويب مخصصة.",
    "sub_pro_f2": "تقارير مالية وتحليلات متقدمة",
    "sub_pro_f3": "نظام عرض المطبخ (KDS) مع محطات ومؤقتات",
    "sub_pro_f4": "وحدات الفنادق / PMS (الغرف، الإشغال، الخدمات)",
    "sub_pro_f5": "الطلب عبر الويب (أجهزة لوحية للنوادل والطلب الذاتي للعملاء)",
    "sub_pro_f6": "WhatsApp وإشعارات آلية",
    "sub_pro_f7": "API للمنتجات والتكامل",
    "sub_pro_f8": "مزامنة WooCommerce",
    "sub_pro_f9": "أدوات للتوسع وعمليات سير العمل المتعددة",
    "sub_pro_f10": "نظام طباعة متقدم (ملصقات وإيصالات)",
    "sub_pro_best_for": "الشركات النامية، المطاعم ذات المطابخ، الفنادق، البيئات متعددة الأجهزة، والشركات التي تعمل مع محاسبين أو أنظمة خارجية.",

    "sub_safety_compliance_title": "ملاحظة حول الأمان والامتثال",
    "sub_safety_compliance_intro": "نحن لا نبيع الأمان كترقية. تشمل كل من خطة البداية والأعمال:",
    "sub_compliance_1": "فواتير غير قابلة للتغيير",
    "sub_compliance_2": "سجل مدفوعات للإضافة فقط (Append-only)",
    "sub_compliance_3": "مدفوعات آمنة من الأعطال (Crash-safe)",
    "sub_compliance_4": "محاسبة بفترات مقفلة",
    "sub_compliance_5": "معالجة صحيحة للنقد، البنك، وسجل العملاء",
    "sub_compliance_6": "أسس جاهزة للمحاسبة متعددة البلدان",
    "sub_safety_difference": "الفرق بين الخطط هو في الإمكانيات، وليس في المخاطر.",

    "sub_compare_title": "مقارنة الميزات",
    "sub_compare_feature": "الميزة",
    "sub_cat_core_pos": "ميزات POS الأساسية",
    "sub_cmp_pos": "نظام نقطة بيع كامل",
    "sub_cmp_products": "إدارة المنتجات، العملاء والموردين",
    "sub_cmp_barcode": "مسح الباركود ومستويات الأسعار",
    "sub_cmp_discounts": "الخصومات، الكوبونات والعربات المحفوظة",
    "sub_cmp_eod": "نهاية اليوم (Z / clôture)",
    "sub_cmp_invoices": "إنشاء وتتبع الفواتير",
    "sub_cmp_refunds": "استرداد آمن ورصيد العملاء",
    "sub_cmp_offline": "Offline-first مع مزامنة تلقائية",
    "sub_cat_safety": "الأمان والامتثال",
    "sub_cmp_immutable": "فواتير غير قابلة للتغيير",
    "sub_cmp_ledger": "سجل مدفوعات للإضافة فقط",
    "sub_cmp_crash_safe": "مدفوعات آمنة من الأعطال",
    "sub_cmp_period_lock": "محاسبة بفترات مقفلة",
    "sub_cat_advanced": "ميزات متقدمة (خطة الأعمال فقط)",
    "sub_cmp_web_panel": "لوحة ويب للمحاسب والمدير",
    "sub_cmp_advanced_reports": "تقارير مالية متقدمة",
    "sub_cmp_kds": "نظام عرض المطبخ (KDS)",
    "sub_cmp_hotel": "وحدات الفنادق / PMS",
    "sub_cmp_web_ordering": "الطلب عبر الويب (أجهزة لوحية وطلب ذاتي)",
    "sub_cmp_whatsapp": "إشعارات WhatsApp",
    "sub_cmp_api": "API للمنتجات والتكامل",
    "sub_cmp_woocommerce": "مزامنة WooCommerce",
    "sub_cmp_print": "نظام طباعة متقدم",
    "sub_cmp_remote": "وصول عن بُعد (iOS & Android)",
    "sub_cmp_auth": "مصادقة متعددة المستأجرين وأذونات",

    "sub_which_plan_title": "أي خطة يجب أن أختار؟",
    "sub_choose_basic": "اختر 'البداية' إذا كنت تريد نقطة بيع كاملة وآمنة وتدير كل شيء من التطبيق.",
    "sub_choose_pro": "اختر 'الأعمال' إذا كنت بحاجة إلى إدارة محاسبية، وحدات متقدمة، أتمتة، أو تكاملات.",
    "sub_choose_custom": "حل Finio مخصص بالكامل مصمم حول أنظمتك الحالية، عملياتك، واحتياجات مؤسستك. Finio المخصص يتكيف مع عملك — وليس العكس.",

    "// --- 404 Page ---": "",
    "page_not_found_title": "الصفحة غير موجودة",
    "page_not_found_desc": "الصفحة التي تبحث عنها قد تكون حُذفت، أو تم تغيير اسمها، أو أنها غير متاحة مؤقتًا.",
    "back_to_home": "العودة للرئيسية",

    "// --- Help Center ---": "",
    "help_welcome": "مرحبًا بك في مركز مساعدة Finio",
    "help_intro": "اعثر على إجابات للأسئلة الشائعة واحصل على الدعم الذي تحتاجه للاستفادة القصوى من Finio PRO.",
    "help_faq_title": "الأسئلة الشائعة",
    "help_faq_1_q": "ما هو Finio PRO؟",
    "help_faq_1_a": "Finio PRO هو منصة كاملة لإدارة المطاعم والضيافة تشمل POS، KDS، إدارة المخزون، خادم الويب، PMS، وأنظمة طباعة متقدمة.",
    "help_faq_2_q": "هل يعمل Finio PRO دون اتصال بالإنترنت؟",
    "help_faq_2_a": "نعم! يتميز Finio PRO ببنية Offline-first تحافظ على سير عملياتك بسلاسة حتى بدون إنترنت. تتم مزامنة البيانات تلقائيًا عند عودتك للاتصال.",
    "help_faq_3_q": "ما هي الأجهزة التي أحتاجها؟",
    "help_faq_3_a": "يعمل Finio PRO مع أجهزة POS القياسية بما في ذلك طابعات الإيصالات، ماسحات الباركود، وأدراج النقود. نقدم أيضًا خدمات استشارات وتركيب الأجهزة.",
    "help_faq_4_q": "ما مدى أمان بياناتي؟",
    "help_faq_4_a": "بياناتك محمية بأمان من الدرجة المؤسسية بما في ذلك التشفير، النسخ الاحتياطي التلقائي، الأذونات القائمة على الأدوار، وعزل البيانات متعدد المستأجرين.",
    "help_faq_5_q": "هل يمكنني الوصول إلى لوحة المحاسب عن بُعد؟",
    "help_faq_5_a": "بالتأكيد! راقب وأدر عملك من أي مكان في العالم باستخدام تطبيقات الهاتف المحمول المخصصة لنظامي iOS و Android.",
    "help_contact_support": "تحتاج للمزيد من المساعدة؟",
    "help_contact_text": "فريق الدعم لدينا هنا لمساعدتك. اتصل بنا عبر:",
    "help_search_placeholder": "ابحث عن إجابات...",
    "help_docs_desc": "أدلة مفصلة حول كيفية استخدام كل ميزة في Finio.",
    "help_faq_desc": "إجابات سريعة للأسئلة الشائعة.",
    "help_security_desc": "كيف نحافظ على أمان بياناتك وامتثالها.",
    "help_changelog_desc": "أحدث التحديثات والتحسينات.",
    "contact_email_plain": "",
    "contact_phone_plain": "+212808646672",

    "// --- Terms & Conditions ---": "",
    "terms_last_updated": "آخر تحديث",
    "terms_date": "نوفمبر 2025",
    "terms_acceptance_title": "قبول الشروط",
    "terms_acceptance_text": "من خلال الوصول إلى خدمات Finio PRO واستخدامها، فإنك تقبل وتوافق على الالتزام بهذه الشروط والأحكام. إذا كنت لا توافق على هذه الشروط، يرجى عدم استخدام خدماتنا.",
    "terms_services_title": "وصف الخدمات",
    "terms_services_text": "توفر Finio Solutions منصة شاملة لإدارة الأعمال تشمل نقاط البيع، إدارة المخزون، إدارة علاقات العملاء، والخدمات ذات الصلة. نحتفظ بالحق في تعديل أو تعليق أو إيقاف أي جزء من خدماتنا في أي وقت.",
    "terms_account_title": "حسابات المستخدمين والمسؤوليات",
    "terms_account_text": "أنت مسؤول عن الحفاظ على سرية بيانات اعتماد حسابك وعن جميع الأنشطة التي تحدث تحت حسابك. أنت توافق على إخطارنا فورًا بأي استخدام غير مصرح به لحسابك.",
    "terms_offline_title": "الوضع غير المتصل ومسؤولية البيانات",
    "terms_offline_text": "يخزن الوضع غير المتصل لـ Finio PRO البيانات محليًا على جهازك. أنت مسؤول عن ضمان مزامنة جهازك مع السحابة بانتظام. Finio Solutions غير مسؤولة عن فقدان البيانات الناتج عن فشل الجهاز أو فقده قبل المزامنة.",
    "terms_payment_title": "الدفع والاشتراك",
    "terms_payment_text": "يتم فوترة رسوم الاشتراك مقدمًا على أساس متكرر. جميع الرسوم غير قابلة للاسترداد إلا ما يقتضيه القانون. نحتفظ بالحق في تغيير أسعارنا مع إشعار لمدة 30 يومًا للمشتركين النشطين.",
    "terms_hardware_title": "توافق الأجهزة والطابعات",
    "terms_hardware_text": "يعمل Finio مع طابعات ESC/POS القياسية والأجهزة اللوحية. نقدم قائمة بالأجهزة الموصى بها ولكننا لا نضمن التوافق مع جميع أجهزة الطرف الثالث ما لم يتم اعتمادها صراحة من قبلنا.",
    "terms_intellectual_title": "الملكية الفكرية",
    "terms_intellectual_text": "جميع المحتويات والميزات والوظائف الخاصة بـ Finio PRO مملوكة لشركة Finio Solutions ومحمية بموجب قوانين حقوق النشر والعلامات التجارية والملكية الفكرية الدولية الأخرى.",
    "terms_limitation_title": "حدود المسؤولية",
    "terms_limitation_text": "لن تكون Finio Solutions مسؤولة عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية ناتجة عن استخدامك أو عدم قدرتك على استخدام الخدمة.",
    "terms_termination_title": "الإنهاء",
    "terms_termination_text": "نحتفظ بالحق في إنهاء أو تعليق حسابك والوصول إلى خدماتنا وفقًا لتقديرنا الخاص، دون إشعار، للسلوك الذي نعتقد أنه ينتهك هذه الشروط أو يضر بالمستخدمين الآخرين أو بنا أو بأطراف ثالثة، أو لأي سبب آخر.",
    "terms_governing_title": "القانون الحاكم",
    "terms_governing_text": "تخضع هذه الشروط وتفسر وفقًا لقوانين المملكة المغربية، دون النظر إلى تعارض نصوص القوانين.",
    "terms_changes_title": "تغييرات الشروط",
    "terms_changes_text": "نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سنقوم بإخطار المستخدمين بأي تغييرات جوهرية عبر البريد الإلكتروني أو من خلال خدمتنا. الاستخدام المستمر للخدمة بعد هذه التعديلات يشكل قبولًا للشروط المحدثة.",
    "terms_contact_title": "معلومات الاتصال",
    "terms_contact_text": "لأي أسئلة حول هذه الشروط والأحكام، يرجى الاتصال بنا على:",

    "// --- Privacy Policy ---": "",
    "privacy_last_updated": "آخر تحديث",
    "privacy_date": "نوفمبر 2025",
    "privacy_intro_title": "مقدمة",
    "privacy_intro_text": "تحترم Finio Solutions ('نحن' أو 'نا') خصوصيتك وتلتزم بحماية بياناتك الشخصية. تشرح سياسة الخصوصية هذه كيفية جمعنا واستخدامنا وحماية معلوماتك عند استخدام خدمات Finio PRO.",
    "privacy_collection_title": "المعلومات التي نجمعها",
    "privacy_collection_text": "نقوم بجمع عدة أنواع من المعلومات لتقديم وتحسين خدماتنا:",
    "privacy_collection_item1": "معلومات الهوية الشخصية (الاسم، عنوان البريد الإلكتروني، رقم الهاتف)",
    "privacy_collection_item2": "معلومات العمل (اسم الشركة، العنوان، التعريف الضريبي)",
    "privacy_collection_item3": "بيانات الاستخدام (كيفية تفاعلك مع خدماتنا، الميزات المستخدمة)",
    "privacy_collection_item4": "البيانات التقنية (عنوان IP، نوع المتصفح، معلومات الجهاز)",
    "privacy_pos_data_item": "البيانات المتعلقة بالمعاملات (سجل المبيعات، مستويات المخزون، مشتريات العملاء) التي تم إنشاؤها من خلال نظام نقاط البيع",
    "privacy_pos_title": "بيانات نقاط البيع والمعاملات",
    "privacy_pos_text": "نقوم بجمع ومعالجة بيانات المعاملات لتزويدك بتقارير المبيعات وتتبع المخزون وتحليلات الأعمال. هذه البيانات ملك لك. نحن لا نستخدم بيانات المبيعات الخاصة بك لأغراض التسويق أو نشاركها مع أطراف ثالثة.",
    "privacy_offline_storage_title": "التخزين المحلي دون اتصال",
    "privacy_offline_storage_text": "عند استخدام Finio PRO دون اتصال بالإنترنت، يتم تخزين البيانات بشكل آمن على جهازك المحلي باستخدام قاعدة بيانات محلية مشفرة. تظل هذه البيانات فعليًا تحت سيطرتك المادية حتى يعيد الجهاز الاتصال بالإنترنت ويتزامن مع خوادمنا السحابية الآمنة.",
    "privacy_use_title": "كيف نستخدم معلوماتك",
    "privacy_use_text": "نستخدم المعلومات التي تم جمعها للأغراض التالية:",
    "privacy_use_item1": "لتوفير وصيانة وتحسين خدماتنا",
    "privacy_use_item2": "لمعالجة المعاملات وإرسال المعلومات ذات الصلة",
    "privacy_use_item3": "لإرسال المعلومات الإدارية والتحديثات وتنبيهات الأمان",
    "privacy_use_item4": "للرد على طلباتك وأسئلتك وتقديم دعم العملاء",
    "privacy_sharing_title": "مشاركة المعلومات والإفصاح عنها",
    "privacy_sharing_text": "نحن لا نبيع أو نتاجر أو نؤجر معلوماتك الشخصية لأطراف ثالثة. قد نشارك معلوماتك فقط في الظروف التالية: بموافقتك، للامتثال للالتزامات القانونية، لحماية حقوقنا وسلامتنا، أو مع مزودي الخدمة الذين يساعدون في عملياتنا بموجب اتفاقيات سرية صارمة.",
    "privacy_security_title": "أمن البيانات",
    "privacy_security_text": "ننفذ تدابير أمنية متوافقة مع معايير الصناعة لحماية معلوماتك الشخصية، بما في ذلك التشفير والخوادم الآمنة وعمليات التدقيق الأمني المنتظمة وضوابط الوصول. ومع ذلك، لا توجد طريقة نقل عبر الإنترنت آمنة بنسبة 100٪.",
    "privacy_retention_title": "الاحتفاظ بالبيانات",
    "privacy_retention_text": "نحتفظ بمعلوماتك الشخصية فقط طالما كان ذلك ضروريًا لتحقيق الأغراض الموضحة في سياسة الخصوصية هذه، ما لم يقتض القانون فترة احتفاظ أطول.",
    "privacy_rights_title": "حقوق الخصوصية الخاصة بك",
    "privacy_rights_text": "لديك الحق في:",
    "privacy_rights_item1": "الوصول والحصول على نسخة من بياناتك الشخصية",
    "privacy_rights_item2": "طلب تصحيح البيانات غير الدقيقة أو غير الكاملة",
    "privacy_rights_item3": "طلب حذف بياناتك الشخصية",
    "privacy_rights_item4": "الاعتراض على أو تقييد معالجة معينة لبياناتك",
    "privacy_cookies_title": "ملفات تعريف الارتباط وتقنيات التتبع",
    "privacy_cookies_text": "نستخدم ملفات تعريف الارتباط وتقنيات تتبع مماثلة لتتبع النشاط على خدمتنا والاحتفاظ بمعلومات معينة. يمكنك توجيه متصفحك لرفض جميع ملفات تعريف الارتباط أو الإشارة إلى وقت إرسال ملف تعريف الارتباط.",
    "privacy_children_title": "خصوصية الأطفال",
    "privacy_children_text": "خدماتنا غير مخصصة للأفراد الذين تقل أعمارهم عن 18 عامًا. نحن لا نجمع عن قصد معلومات شخصية من الأطفال. إذا كنت تعتقد أننا جمعنا معلومات من طفل، يرجى الاتصال بنا على الفور.",
    "privacy_changes_title": "تغييرات على سياسة الخصوصية هذه",
    "privacy_changes_text": "قد نقوم بتحديث سياسة الخصوصية الخاصة بنا من وقت لآخر. سنقوم بإخطارك بأي تغييرات عن طريق نشر سياسة الخصوصية الجديدة على هذه الصفحة وتحديث تاريخ 'آخر تحديث'.",
    "privacy_contact_title": "اتصل بنا",
    "privacy_contact_text": "إذا كان لديك أي أسئلة حول سياسة الخصوصية هذه، يرجى الاتصال بنا على:",

    // --- Documentation ---
    // Docs Navigation
    "docs_nav_title": "الوثائق",
    "docs_resources_title": "الموارد",
    "docs_nav_getting_started": "البداية",
    "docs_nav_pos_usage": "دليل استخدام نقاط البيع",
    "docs_nav_kds_workflow": "سير عمل شاشات المطبخ",
    "docs_nav_pms_overview": "نظام الفنادق",
    "docs_nav_offline_sync": "غير متصل والمزامنة",
    "docs_nav_roles_permissions": "الأدوار والصلاحيات",
    "docs_nav_finance_closures": "الفواتير والإغلاقات",
    "docs_nav_hardware_printers": "الطابعات والأجهزة",
    "docs_nav_integrations": "التكاملات",
    "docs_nav_compliance": "الامتثال والضريبة",
    "docs_nav_troubleshooting": "استكشاف الأخطاء وإصلاحها",

    // Getting Started
    "docs_gs_title": "البداية",
    "docs_gs_lead": "مرحبًا بك في Finio، نظام نقاط بيع وإدارة متقدم يعمل دون اتصال بالإنترنت، مصمم لتجارة التجزئة، المطاعم، والمقاهي.",
    "docs_gs_intro": "يجمع Finio بين نقاط البيع، شاشات المطبخ (KDS)، المخزون، المحاسبة، ونظام الفنادق في منصة موحدة، مع نزاهة بيانات قوية ومزامنة متعددة الأجهزة.",
    "docs_gs_access_title": "1. الوصول إلى Finio",
    "docs_gs_step_1": "<strong>ثبّت Finio</strong> على جهازك (أندرويد / سطح المكتب / تابلت).",
    "docs_gs_step_2": "<strong>افتح التطبيق.</strong>",
    "docs_gs_step_3": "<strong>سجل الدخول</strong> باستخدام حساب المستأجر الخاص بك.",
    "docs_gs_step_4": "<strong>اختر متجرك / فرعك</strong> إذا كان ذلك ينطبق.",
    "docs_gs_offline_note": "<em>يعمل Finio دون اتصال بالإنترنت بشكل افتراضي. الإنترنت مطلوب فقط للمزامنة السحابية والمشاركة بين الأجهزة.</em>",
    "docs_gs_config_title": "2. إعداد المتجر",
    "docs_gs_config_intro": "قبل البدء في البيع، قم بتهيئة إعدادات متجرك:",
    "docs_gs_company_title": "تفاصيل الشركة",
    "docs_gs_company_go_to": "اذهب إلى: <strong>الإعدادات ← الشركة</strong>",
    "docs_gs_company_configure": "قم بتهيئة:",
    "docs_gs_company_list_1": "اسم العمل",
    "docs_gs_company_list_2": "الشعار",
    "docs_gs_company_list_3": "العنوان",
    "docs_gs_company_list_4": "الرقم الضريبي (ICE / VAT)",
    "docs_gs_company_list_5": "العملة",
    "docs_gs_company_list_6": "الدولة",
    "docs_gs_company_note": "تظهر هذه التفاصيل على الفواتير، الإيصالات، وتصديرات المحاسبة.",
    "docs_gs_taxes_title": "الضرائب",
    "docs_gs_taxes_go_to": "اذهب إلى: <strong>الإعدادات ← الضرائب</strong>",
    "docs_gs_taxes_list_1": "إنشاء معدل ضريبة واحد أو أكثر",
    "docs_gs_taxes_list_2": "تعيين الضرائب للمنتجات",
    "docs_gs_taxes_list_3": "يدعم: التسعير الشامل للضريبة، التسعير غير الشامل للضريبة، فئات ضريبية متعددة",
    "docs_gs_taxes_note": "<strong>ملاحظة:</strong> يتم تجميد الضرائب عند إنشاء الفاتورة لضمان النزاهة المحاسبية.",
    "docs_gs_hardware_title": "الطابعات والأجهزة",
    "docs_gs_hardware_go_to": "اذهب إلى: <strong>الإعدادات ← الأجهزة</strong>",
    "docs_gs_hardware_list_1": "اكتشاف طابعات الشبكة",
    "docs_gs_hardware_list_2": "تعيين الطابعات لـ: الإيصالات، تذاكر المطبخ، البار / المحطات",
    "docs_gs_hardware_list_3": "تهيئة قوالب الطباعة لكل محطة",
    "docs_gs_hardware_note": "<em>قوالب الطابعة ذات إصدارات ويمكن استعادتها بأمان.</em>",
    "docs_gs_adding_title": "3. إضافة المنتجات",
    "docs_gs_adding_go_to": "اذهب إلى: <strong>المخزون ← المنتجات</strong>",
    "docs_gs_adding_intro": "يمكنك إضافة المنتجات بثلاث طرق:",
    "docs_gs_manual_title": "الإدخال اليدوي",
    "docs_gs_manual_list_1": "الاسم، الفئة، السعر (دعم متعدد الأسعار)",
    "docs_gs_manual_list_2": "الضريبة، تتبع المخزون",
    "docs_gs_manual_list_3": "الصورة، المعدلات والمتغيرات",
    "docs_gs_csv_title": "استيراد CSV (خطة Pro)",
    "docs_gs_csv_list_1": "استيراد المنتجات بالجملة",
    "docs_gs_csv_list_2": "إنشاء الفئات تلقائيًا",
    "docs_gs_csv_list_3": "يدعم المتغيرات والأسعار",
    "docs_gs_types_title": "أنواع منتجات متقدمة",
    "docs_gs_types_list_1": "منتجات بسيطة",
    "docs_gs_types_list_2": "منتجات متغيرة (الحجم، اللون، إلخ)",
    "docs_gs_types_list_3": "منتجات مركبة (وصفات / حزم)",
    "docs_gs_types_list_4": "منتجات مخزنية فقط (غير قابلة للبيع)",

    // POS Usage
    "docs_pos_title": "أول عملية بيع (POS)",
    "docs_pos_lead": "معالجة المبيعات في Finio سريعة وآمنة. اتبع هذه الخطوات لإتمام معاملتك الأولى.",
    "docs_pos_steps_title": "خطوات البيع",
    "docs_pos_step_1": "<strong>افتح نقاط البيع</strong> من القائمة الرئيسية.",
    "docs_pos_step_2": "<strong>اختر المنتجات</strong> من الشبكة أو امسح الباركود.",
    "docs_pos_step_3": "<strong>اختر العميل</strong> (اختياري) من الشريط العلوي.",
    "docs_pos_step_4": "اضغط <strong>دفع</strong>.",
    "docs_pos_step_5": "<strong>اختر طريقة الدفع:</strong>",
    "docs_pos_method_1": "نقد",
    "docs_pos_method_2": "بطاقة",
    "docs_pos_method_3": "بنك",
    "docs_pos_method_4": "رصيد العميل",
    "docs_pos_method_5": "شيك",
    "docs_pos_method_6": "مدفوعات مختلطة",
    "docs_pos_step_6": "<strong>تأكيد الدفع</strong>.",
    "docs_pos_step_7": "<strong>طباعة الإيصال تلقائيًا</strong> (إذا تم تكوينه).",
    "docs_pos_note": "<strong>ملاحظة:</strong> كل عملية بيع تنشئ فاتورة غير قابلة للتغيير لضمان الأمان المحاسبي.",
    "docs_pos_features_title": "الميزات الرئيسية",
    "docs_pos_feat_multi": "<strong>دعم متعدد المدفوعات:</strong> تقسيم الفواتير بسهولة بين النقد والبطاقة.",
    "docs_pos_feat_hold": "<strong>تعليق واسترجاع:</strong> حفظ الطلبات لوقت لاحق إذا لم يكن العميل جاهزًا.",
    "docs_pos_feat_offline": "<strong>وضع غير متصل:</strong> استمر في البيع حتى بدون إنترنت؛ تتم مزامنة البيانات تلقائيًا عند الاتصال.",



    // Hardware
    "docs_hardware_title": "الطابعات والطباعة",
    "docs_hardware_lead": "قم بتهيئة الإيصالات، تذاكر المطبخ، وتوجيه الطابعات لعمليات سلسة.",
    "docs_hardware_features_title": "ميزات الطباعة",
    "docs_hardware_feat_receipts": "<strong>الإيصالات:</strong> إيصالات عملاء تلقائية عند الدفع.",
    "docs_hardware_feat_kitchen": "<strong>تذاكر المطبخ:</strong> إرسال الطلبات مباشرة إلى المطبخ أو البار.",
    "docs_hardware_feat_routing": "<strong>توجيه المحطات:</strong> توجيه عناصر محددة (مثل المشروبات مقابل الطعام) إلى طابعات مختلفة.",
    "docs_hardware_feat_templates": "<strong>القوالب:</strong> تخصيص تخطيطات الطباعة باستخدام محرر القوالب.",
    "docs_hardware_feat_queue": "<strong>مراقبة قائمة الانتظار:</strong> تتبع مهام الطباعة وحالتها.",
    "docs_hardware_supported_title": "الأجهزة المدعومة",
    "docs_hardware_bt": "<strong>طابعات بلوتوث:</strong> للإعدادات المتنقلة واللوحية.",
    "docs_hardware_network": "<strong>طابعات الشبكة (LAN):</strong> اتصال إيثرنت قياسي للموثوقية.",
    "docs_hardware_recovery_note": "<strong>ميزة:</strong> يضمن الاسترداد التلقائي عدم ضياع مهام الطباعة إذا توقفت الطابعة مؤقتًا.",

    // Integrations
    "docs_int_title": "التكاملات",
    "docs_int_lead": "اربط Finio بأدوات الطرف الثالث لتوسيع نظامك البيئي.",
    "docs_int_web_title": "تكاملات الويب",
    "docs_int_api": "<strong>API المنتج:</strong> مزامنة المنتجات والمخزون مع الأنظمة الخارجية.",
    "docs_int_kds": "<strong>ويب KDS:</strong> الوصول إلى نظام عرض المطبخ عبر المتصفح.",
    "docs_int_pms": "<strong>ويب PMS:</strong> واجهة نظام إدارة الممتلكات.",
    "docs_int_feedback": "<strong>ويب الملاحظات:</strong> جمع ملاحظات العملاء.",
    "docs_int_waiter": "<strong>نادل / طلب ذاتي:</strong> طلبات عبر الهاتف للموظفين أو العملاء.",
    "docs_int_arch_title": "بنية الخدمة",
    "docs_int_arch_intro": "تم تصميم جميع خدمات الويب للأمان والأداء:",
    "docs_int_local": "<strong>تنفيذ محلي:</strong> تشغيل الخدمات محليًا لتقليل التأخير.",
    "docs_int_auth": "<strong>مصادق عليه:</strong> وصول آمن عبر بيانات اعتماد المستأجر.",
    "docs_int_rate": "<strong>محدود المعدل:</strong> حماية الهوية ضد إساءة الاستخدام.",
    "docs_int_limited": "<strong>معزول للمستأجر:</strong> فصل البيانات لبيئات متعددة المستأجرين.",

    // Compliance
    "docs_comp_title": "المحاسبة والامتثال",
    "docs_comp_lead": "نموذج المحاسبة الأساسي: كيف يحمي Finio بياناتك المالية.",
    "docs_comp_model_title": "نموذج المحاسبة الأساسي",
    "docs_comp_immutable_title": "فواتير غير قابلة للتغيير",
    "docs_comp_immutable_desc": "بمجرد الانتهاء من الفاتورة، لا يمكن تعديلها أو حذفها. توجد بشكل دائم في دفتر الأستاذ.",
    "docs_comp_append_title": "دفتر أستاذ للإضافة فقط",
    "docs_comp_append_desc": "تحديثات المدفوعات والأرصدة هي إضافة فقط. نحن لا نستبدل أي سجل أبدًا؛ نحن نضيف فقط إدخالات تعويضية جديدة.",
    "docs_comp_safety_title": "أمان المعاملات",
    "docs_comp_safety_desc": "جميع العمليات ذرية. إذا حدث عطل، يتعافى النظام إلى حالة متسقة دون فقدان البيانات.",

    // Security
    "docs_sec_title": "الأمان وحماية البيانات",
    "docs_sec_lead": "يستخدم Finio نموذج أمان \"التدقيق أولاً\" المصمم للامتثال المالي ونزاهة البيانات.",
    "docs_sec_enc_title": "تشفير البيانات",
    "docs_sec_enc_intro": "نحن نتعامل مع بيانات عملك كأصول مالية حساسة.",
    "docs_sec_table_state": "الحالة",
    "docs_sec_table_method": "الطريقة",
    "docs_sec_table_detail": "التفاصيل",
    "docs_sec_rest_cloud": "<strong>في الراحة (سحابة)</strong>",
    "docs_sec_rest_cloud_desc": "تخزين مشفر في Google Cloud Firestore.",
    "docs_sec_rest_device": "<strong>في الراحة (جهاز)</strong>",
    "docs_sec_rest_device_desc": "ملفات قاعدة البيانات المحلية مشفرة لمنع الاستخراج من الأجهزة المسروقة.",
    "docs_sec_transit": "<strong>في النقل</strong>",
    "docs_sec_transit_desc": "تتدفق جميع حركة المزامنة عبر قنوات HTTPS الآمنة.",
    "docs_sec_rbac_title": "التحكم في الوصول القائم على الأدوار (RBAC)",
    "docs_sec_rbac_intro": "يتم فرض الأذونات بصرامة على مستوى واجهة المستخدم و API.",
    "docs_sec_role_owner": "<strong>المالك:</strong> وصول كامل إلى جميع الإعدادات، المحاسبة، والحذف.",
    "docs_sec_role_manager": "<strong>المدير:</strong> يمكنه إلغاء العناصر وتشغيل تقارير نهاية اليوم، لكن لا يمكنه حذف التاريخ.",
    "docs_sec_role_cashier": "<strong>كاشير:</strong> يمكنه إنشاء مبيعات وتلقي المدفوعات. لا يمكنه إلغاء الطلبات المرسلة دون موافقة.",
    "docs_sec_role_waiter": "<strong>نادل:</strong> مقتصر على إنشاء الطلبات فقط. لا يوجد وصول للدفع أو الدرج.",
    "docs_sec_audit_title": "سجلات تدقيق غير قابلة للتغيير",
    "docs_sec_audit_intro": "كل إجراء حاسم يطلق إدخالاً دائمًا في مجموعة <code>audit_log</code>. هذا السجل مرئي في لوحة ويب المحاسب.",
    "docs_sec_audit_events": "<strong>الأحداث المسجلة:</strong> الإلغاءات، الاسترداد، الخصومات (>10%)، فتح الدرج، تغييرات الأسعار.",
    "docs_sec_audit_meta": "<strong>البيانات الوصفية:</strong> معرف المستخدم، الطابع الزمني الدقيق، معرف الجهاز، موقع GPS (اختياري).",
    "docs_sec_alert_box": "<strong>نظام التنبيه:</strong> الأنماط المشبوهة (مثل 5 إلغاءات في دقيقة واحدة) تطلق إشعارات \"علم أحمر\" تلقائية لمالك العمل عبر واتساب.",

    // Troubleshooting
    "docs_ts_title": "استكشاف الأخطاء وإصلاحها",
    "docs_ts_lead": "ابحث عن حلول سريعة للمشاكل التشغيلية الشائعة.",
    "docs_ts_issues_title": "مشاكل شائعة",
    "docs_ts_printer_title": "الطابعة لا تطبع",
    "docs_ts_printer_1": "<strong>تحقق من الشبكة:</strong> تأكد من أن الطابعة متصلة بنفس شبكة Wi-Fi/LAN.",
    "docs_ts_printer_2": "<strong>إعادة تشغيل الخدمة:</strong> أعد تشغيل خدمة طابعة Finio أو الطابعة نفسها.",
    "docs_ts_printer_3": "<strong>إعادة تعيين القالب:</strong> اذهب إلى الإعدادات ← الأجهزة وتحقق من تعيين الطابعة.",
    "docs_ts_sync_title": "المزامنة لا تعمل",
    "docs_ts_sync_1": "<strong>تحقق من الإنترنت:</strong> تحقق من أن جهازك لديه اتصال نشط.",
    "docs_ts_sync_2": "<strong>مزامنة تلقائية:</strong> تُستأنف المزامنة تلقائيًا بمجرد الاتصال؛ لا حاجة لعمل يدوي.",
    "docs_ts_sync_3": "<strong>أمان البيانات:</strong> بياناتك آمنة محليًا وسيتم تحميلها عندما يكون ذلك ممكنًا.",
    "docs_ts_balance_title": "رصيد نقد خاطئ",
    "docs_ts_balance_1": "<strong>الجلسات:</strong> تحقق مما إذا كانت جلسة السجل الصحيحة مفتوحة.",
    "docs_ts_balance_2": "<strong>مبالغ مستردة:</strong> تحقق مما إذا تمت معالجة أي مبالغ مستردة بعد بدء الجلسة.",
    "docs_ts_balance_3": "<strong>نهاية اليوم:</strong> راجع تقارير إغلاق نهاية اليوم بحثًا عن الفروقات.",
    "docs_ts_help_box": "<strong>هل تحتاج إلى مزيد من المساعدة؟</strong> اتصل بالدعم على <a href=\"mailto:support\u0040finio.pro\">support\u0040finio.pro</a>.",

    // Compliance Extended (Arabic)
    "docs_comp_intro_title": "كيف تعمل المحاسبة في فينيو",
    "docs_comp_intro_p1": "فينيو ليس \"نقطة بيع بسيطة\". إنه نظام معاملات آمن محاسبيًا مصمم لحماية البيانات المالية من التلاعب والأخطاء والتعارضات بين الأجهزة.",
    "docs_comp_intro_p2": "يشرح هذا القسم كيفية عمل محاسبة فينيو، ولماذا يتم تقييد بعض الإجراءات، وكيف يحمي ذلك عملك أثناء التدقيق.",
    "docs_comp_invoice_title": "ما هي الفاتورة؟",
    "docs_comp_invoice_p1": "الفاتورة في فينيو هي وثيقة محاسبية قانونية وليست مسودة.",
    "docs_comp_invoice_frozen": "عند إنشاء فاتورة، يتم تجميد ما يلي بشكل دائم:",
    "docs_comp_invoice_list_1": "أسعار المنتجات",
    "docs_comp_invoice_list_2": "الضرائب",
    "docs_comp_invoice_list_3": "الخصومات",
    "docs_comp_invoice_list_4": "الإجماليات",
    "docs_comp_immutability_title": "عدم قابلية الفاتورة للتغيير (قاعدة حرجة)",
    "docs_comp_immutability_intro": "بمجرد الإنشاء:",
    "docs_comp_immutability_list_1": "لا يمكنك تعديل الإجماليات",
    "docs_comp_immutability_list_2": "لا يمكنك تغيير الضريبة",
    "docs_comp_immutability_list_3": "لا يمكنك تعديل العناصر",
    "docs_comp_immutability_why": "<strong>لماذا؟</strong> لأن تعديل الفواتير بعد إنشائها يكسر مسارات التدقيق ويبطل التقارير الضريبية ويخلق مخاطر الاحتيال.",
    "docs_comp_immutability_compliance": "هذا مقصود ومطلوب للامتثال.",
    "docs_comp_payments_title": "المدفوعات: سجل الإضافة فقط",
    "docs_comp_payments_intro": "فينيو لا يعدل المدفوعات. بدلاً من ذلك، يستخدم سجل مدفوعات للإضافة فقط.",
    "docs_comp_payments_how_title": "كيف تعمل المدفوعات",
    "docs_comp_payments_list_1": "تتم إضافة كل دفعة كسجل جديد",
    "docs_comp_payments_list_2": "لا يتم الكتابة فوق المدفوعات أبدًا",
    "docs_comp_payments_list_3": "المبالغ المستردة هي إدخالات دفتر الأستاذ الجديدة وليست تعديلات",
    "docs_comp_payments_guarantee": "يضمن هذا تاريخ دفع كامل ومقاومة للتلاعب ومزامنة آمنة متعددة الأجهزة.",
    "docs_comp_example_title": "مثال",
    "docs_comp_table_action": "الإجراء",
    "docs_comp_table_result": "ما يفعله فينيو",
    "docs_comp_table_row_1_a": "العميل يدفع نقدًا",
    "docs_comp_table_row_1_b": "يضيف إدخال دفع",
    "docs_comp_table_row_2_a": "تم التحقق من الشيك لاحقًا",
    "docs_comp_table_row_2_b": "يضيف إدخال تغيير الحالة",
    "docs_comp_table_row_3_a": "تم إصدار استرداد",
    "docs_comp_table_row_3_b": "يضيف إدخال استرداد",
    "docs_comp_table_row_4_a": "تعارض المزامنة",
    "docs_comp_table_row_4_b": "يدمج الإدخالات بأمان",
    "docs_comp_refunds_title": "المبالغ المستردة (لا تعديل للفاتورة)",
    "docs_comp_refunds_intro": "المبالغ المستردة في فينيو:",
    "docs_comp_refunds_list_1": "لا تعدل الفاتورة",
    "docs_comp_refunds_list_2": "لا تزيل المدفوعات",
    "docs_comp_refunds_list_3": "قم بإنشاء إدخال دفتر الأستاذ السلبي",
    "docs_comp_refunds_why": "<strong>لماذا يهم هذا:</strong> يظل البيع الأصلي مرئيًا، ويتم تتبع الاسترداد بوضوح، ويمكن للمدققين إعادة بناء التاريخ الكامل.",
    "docs_comp_refunds_box": "<strong>التوجيه:</strong> المبالغ المستردة النقدية تقلل من درج النقود. المبالغ المستردة بالبطاقة تقلل من رصيد البنك. رصيد المتجر يزيد من رصيد العميل.",
    "docs_comp_eod_title": "الفترات المغلقة وحماية التدقيق",
    "docs_comp_eod_intro": "بمجرد اكتمال نهاية اليوم (EOD):",
    "docs_comp_eod_list_1": "يتم قفل الفترة المحاسبية",
    "docs_comp_eod_list_2": "لا يمكن تعديل المعاملات السابقة",
    "docs_comp_eod_list_3": "يتم تسجيل المبالغ المستردة بعد EOD كأحداث جديدة",
    "docs_comp_eod_prevent": "هذا يمنع التلاعب بتاريخ سابق والتصحيحات المخفية وتناقضات التدقيق.",
    "docs_comp_trust_title": "لماذا يثق المحاسبون في فينيو",
    "docs_comp_trust_intro": "يطبق فينيو ضوابط محاسبة المؤسسة، وليس قيود نقطة البيع:",
    "docs_comp_trust_list_1": "فواتير غير قابلة للتغيير",
    "docs_comp_trust_list_2": "مدفوعات للإضافة فقط",
    "docs_comp_trust_list_3": "قفل الفترة",
    "docs_comp_trust_list_4": "النقد المرتبط بالسجل",
    "docs_comp_trust_list_5": "حماية وقت الخادم",
    "docs_comp_reg_title": "سجلات النقد والمصالحة",
    "docs_comp_reg_what_title": "ما هو سجل النقد في فينيو؟",
    "docs_comp_reg_what_desc": "يمثل سجل النقد درج نقود فعلي. يجب ربط كل حركة نقدية بسجل، ومرتبطة بجلسة مفتوحة، وتتبعها لكل فترة. هذا يضمن المساءلة النقدية.",
    "docs_comp_float_title": "الرصيد الافتتاحي",
    "docs_comp_float_step_1": "افتح السجل",
    "docs_comp_float_step_2": "أدخل المبلغ النقدي الافتتاحي",
    "docs_comp_float_desc": "يصبح هذا خط الأساس للمصالحة.",
    "docs_comp_z_title": "نهاية اليوم (إغلاق Z)",
    "docs_comp_z_intro": "في نهاية اليوم أو الوردية:",
    "docs_comp_z_step_1": "قم بإجراء إغلاق نهاية اليوم",
    "docs_comp_z_step_2": "يحسب فينيو: النقد المتوقع، النقد الفعلي، الفرق، الهامش",
    "docs_comp_z_step_3": "الفترة مغلقة ومقفلة",
    "docs_comp_z_after": "بعد EOD:",
    "docs_comp_z_list_1": "لا يمكنك تغيير المبيعات النقدية السابقة",
    "docs_comp_z_list_2": "لا يمكنك تأريخ المدفوعات بأثر رجعي",
    "docs_comp_z_list_3": "يبدأ النشاط الجديد فترة جديدة",
    "docs_comp_reg_box": "<strong>لماذا النقد ملزم بصرامة:</strong> بدون ربط السجل، يذهب النقد دون تعقب ولا يمكن اكتشاف السرقة. يحظر فينيو المدفوعات النقدية بدون سجل مفتوح وسياق جلسة.",
    "docs_comp_offline_title": "سلوك عدم الاتصال والأجهزة المتعددة",
    "docs_comp_offline_first_title": "العمل دون اتصال حسب التصميم",
    "docs_comp_offline_first_desc": "يعمل فينيو بدون إنترنت. يتم حفظ جميع الإجراءات محليًا أولاً، والمزامنة بأمان لاحقًا، ولا تمنع المبيعات أبدًا.",
    "docs_comp_drops_title": "ماذا يحدث إذا انقطع الإنترنت؟",
    "docs_comp_drops_desc": "لا شيء ينكسر. تستمر المبيعات، ويتم تسجيل المدفوعات، وتستأنف المزامنة تلقائيًا. لا فقدان للبيانات.",
    "docs_comp_multi_title": "سلامة الأجهزة المتعددة (هام جدًا)",
    "docs_comp_multi_scenario": "<strong>السيناريو:</strong> جهازان، نفس الفاتورة.",
    "docs_comp_multi_list_1": "الجهاز أ يضيف دفعة",
    "docs_comp_multi_list_2": "الجهاز ب يضيف دفعة أخرى",
    "docs_comp_multi_list_3": "كلاهما يتزامن لاحقًا",
    "docs_comp_multi_merge": "يدمج فينيو المدفوعات، ولا يقوم بالكتابة فوقها. هذا ممكن لأن المدفوعات للإضافة فقط والفواتير لها إصدارات.",
    "docs_comp_time_title": "حماية وقت الجهاز",
    "docs_comp_time_desc": "يستخدم فينيو وقتًا متوافقًا مع الخادم لاكتشاف التلاعب بالساعة ومنع التأريخ الرجعي وحماية الفترات المحاسبية.",
    "docs_comp_faq_title": "الأسئلة الشائعة (المحاسبة والثقة)",
    "docs_comp_faq_1_q": "هل يمكنني تعديل فاتورة بعد الدفع؟",
    "docs_comp_faq_1_a": "لا. الفواتير غير قابلة للتغيير لحماية النزاهة المحاسبية والتدقيق.",
    "docs_comp_faq_2_q": "هل يمكنني حذف دفعة؟",
    "docs_comp_faq_2_a": "لا. المدفوعات هي إدخالات دفتر الأستاذ ولا يمكن إزالتها.",
    "docs_comp_faq_3_q": "ماذا يحدث إذا قمت بالاسترداد بعد نهاية اليوم؟",
    "docs_comp_faq_3_a": "يتم إنشاء سجل استرداد جديد في الفترة التالية. تظل الفترات السابقة مقفلة.",
    "docs_comp_faq_4_q": "هل يمكن لاثنين من الصرافين استخدام نفس السجل؟",
    "docs_comp_faq_4_a": "نعم، ولكن ليس في نفس الوقت. كل سجل له جلسات خاضعة للرقابة.",
    "docs_comp_faq_5_q": "ماذا لو كان وقت الجهاز خاطئًا؟",
    "docs_comp_faq_5_a": "يكتشف فينيو التلاعب بالوقت ويمنعه.",
    "docs_comp_faq_6_q": "هل فينيو آمن للتدقيق الضريبي؟",
    "docs_comp_faq_6_a": "نعم. يطبق القواعد المحاسبية التي يطلبها المدققون والسلطات المالية.",
    "docs_comp_faq_7_q": "هل يمكن للمحاسبين الوصول إلى البيانات عن بُعد؟",
    "docs_comp_faq_7_a": "نعم. من خلال لوحة ويب المحاسب، وليس نقطة البيع.",
    "docs_comp_panel_title": "لوحة ويب المحاسب",
    "docs_comp_panel_desc": "منصة ويب منفصلة للمحاسبين والمديرين. إنها ليست نقطة بيع.",
    "docs_comp_panel_what": "ما يمكن للمحاسبين القيام به",
    "docs_comp_panel_list_1": "عرض الفواتير",
    "docs_comp_panel_list_2": "مراجعة المدفوعات والمستردات",
    "docs_comp_panel_list_3": "تتبع المصاريف",
    "docs_comp_panel_list_4": "إدارة الرواتب والأجور",
    "docs_comp_panel_list_5": "تحليل الهوامش والتدفق النقدي",
    "docs_comp_panel_list_6": "تصدير البيانات",
    "docs_comp_panel_list_7": "مراقبة فروع متعددة",
    "docs_comp_panel_remote": "كل ذلك عن بُعد، دون الوصول إلى أجهزة نقطة البيع.",
    "docs_comp_panel_box": "<strong>لماذا يهم هذا:</strong> فصل الواجبات (عدم وصول الصراف إلى أدوات المحاسبة) وسير عمل التدقيق المهني. هذه ميزة مؤسسية رئيسية.",
    "docs_comp_int_title": "التكامل وموارد المطورين",
    "docs_comp_api_title": "واجهة برمجة تطبيقات المنتج",
    "docs_comp_api_desc": "لأنظمة ERP، وماسحات المخزون، والتطبيقات الخارجية، وأنظمة المستودعات.",
    "docs_comp_api_sec": "<strong>نموذج الأمان:</strong> إقران الجهاز، المصادقة المستندة إلى الرمز المميز، عزل المستأجر، تحديد المعدل. لا يمكن الوصول عبر المستأجرين.",
    "docs_comp_woo_title": "تكامل WooCommerce",
    "docs_comp_woo_list_1": "مزامنة المنتجات من فينيو ← WooCommerce",
    "docs_comp_woo_list_2": "إرسال الطلبات من WooCommerce ← فينيو",
    "docs_comp_woo_limits": "<strong>قيود مهمة:</strong> مزامنة المنتج في اتجاه واحد، تتطلب الطلبات الموافقة، تعتمد مزامنة المخزون على التكوين.",
    "docs_comp_local_title": "خوادم الويب المحلية (متقدم)",
    "docs_comp_local_desc": "يتضمن فينيو خوادم ويب مدمجة لأجهزة الكمبيوتر اللوحي للنادل، وأكشاك الطلب الذاتي، والوصول إلى الويب PMS، ونماذج التعليقات. جميعها مصادق عليها، وتستند إلى الشبكة المحلية، ومحدودة المعدل.",
    "docs_comp_sec_title": "نموذج الأمان",
    "docs_comp_sec_list_1": "عزل المستأجر",
    "docs_comp_sec_list_2": "أذونات تستند إلى الدور",
    "docs_comp_sec_list_3": "إنفاذ وقت الخادم",
    "docs_comp_sec_list_4": "التشفير دون اتصال",
    "docs_comp_sec_list_5": "لا توجد بيانات مشتركة بين الشركات",

    // Offline Sync (Arabic)
    "docs_offline_title": "بنية العمل دون اتصال والمزامنة",
    "docs_offline_lead": "يضمن نظام Finio الموزع وقت تشغيل بنسبة 100٪ من خلال بنية قاعدة بيانات Hive المحلية أولاً.",
    "docs_offline_hive_title": "قاعدة بيانات محلية أولاً (Hive)",
    "docs_offline_hive_desc": "لا يعتمد Finio على اتصال سحابي دائم. بدلاً من ذلك، يدير قاعدة بيانات NoSQL كاملة (Hive) مباشرة على جهازك.",
    "docs_offline_hive_list_1": "<strong>سرعة القراءة/الكتابة:</strong> عمليات فورية بدون زمن انتقال (< 16 مللي ثانية).",
    "docs_offline_hive_list_2": "<strong>سيادة البيانات:</strong> يوجد كتالوج المنتجات الكامل وقائمة العملاء وسجل المعاملات على جهازك.",
    "docs_offline_hive_list_3": "<strong>التشفير:</strong> يتم تشفير البيانات المحلية في حالة الراحة باستخدام معايير AES-256.",
    "docs_offline_why_box": "<strong>لماذا هذا مهم:</strong> تتوقف معظم أنظمة نقاط البيع المستندة إلى الويب عن العمل عند انقطاع WiFi. يستمر Finio في معالجة المبيعات وطباعة الإيصالات وفتح أدراج النقود لأن المنطق محلي.",
    "docs_offline_queue_title": "قائمة انتظار مزامنة دلتا",
    "docs_offline_queue_desc": "عندما تقوم بإجراء (على سبيل المثال، إغلاق فاتورة)، يكتبها Finio في \"قائمة انتظار مزامنة\" دائمة.",
    "docs_offline_queue_step_1": "<strong>التزام الإجراء:</strong> يتم حفظ المعاملة في دفتر الأستاذ المحلي على الفور.",
    "docs_offline_queue_step_2": "<strong>إدخال قائمة الانتظار:</strong> تتم إضافة حمولة JSON للطفرة إلى جدول <code>sync_queue</code>.",
    "docs_offline_queue_step_3": "<strong>عامل الخلفية:</strong> تحاول عملية في الخلفية تفريغ قائمة الانتظار إلى السحابة.",
    "docs_offline_queue_step_4": "<strong>منطق إعادة المحاولة:</strong> إذا كانت الشبكة معطلة، ينام العامل ويعيد المحاولة مع التراجع الأسي.",
    "docs_offline_conflict_title": "استراتيجية حل النزاعات",
    "docs_offline_conflict_desc": "في بيئة متعددة الأجهزة، يستخدم Finio <strong>استراتيجية دمج حتمية</strong> للتعامل مع التصادمات.",
    "docs_offline_append_title": "دفتر أستاذ للإضافة فقط",
    "docs_offline_append_desc": "نحن لا نكتب فوق سجلات الدفع أبدًا. إذا تلقى الجهاز A 50 دولارًا واستلم الجهاز B 20 دولارًا لنفس الفاتورة:",
    "docs_offline_append_list_1": "يلتزم الجهاز A بمعرف الدفع #101 (50 دولارًا).",
    "docs_offline_append_list_2": "يلتزم الجهاز B بمعرف الدفع #102 (20 دولارًا).",
    "docs_offline_append_list_3": "يقبل الخادم السحابي كليهما.",
    "docs_offline_append_list_4": "يتم تحديث رصيد الفاتورة ليعكس دفع 70 دولارًا.",
    "docs_offline_time_title": "الوقت المعتمد من الخادم",
    "docs_offline_time_desc": "لمنع الاحتيال (على سبيل المثال، تغيير ساعة الجهاز لتغيير تاريخ المبيعات)، يتم ختم جميع أحداث المزامنة بـ <code>server_timestamp</code> عند الوصول. تستخدم التقارير المالية وقت الخادم هذا لقول الحقيقة.",

    // KDS Workflow (Arabic)
    "docs_kds_title": "نظام عرض المطبخ (KDS)",
    "docs_kds_lead": "قم بتحسين عمليات مطبخك مع تتبع الطلبات في الوقت الفعلي.",
    "docs_kds_overview_title": "نظرة عامة",
    "docs_kds_overview_desc": "انتقل إلى: <strong>KDS</strong> من القائمة الرئيسية.",
    "docs_kds_list_1": "<strong>طلبات في الوقت الفعلي:</strong> تظهر الطلبات على الفور عند وضعها.",
    "docs_kds_list_2": "<strong>تجميع المحطات:</strong> يتم توجيه الطلبات إلى محطات محددة (مطبخ، بار، شواية).",
    "docs_kds_list_3": "<strong>المؤقتات:</strong> تتبع وقت التحضير لكل عنصر.",
    "docs_kds_list_4": "<strong>شاشات متعددة:</strong> دعم لشاشات عرض متعددة عبر محطات مختلفة.",
    "docs_kds_flow_title": "تدفق الحالة",
    "docs_kds_flow_desc": "تتحرك الطلبات عبر المراحل التالية:",
    "docs_kds_flow_step_1": "<strong>جديد:</strong> تم استلام الطلب.",
    "docs_kds_flow_step_2": "<strong>قيد التحضير:</strong> يقر الموظفون بالطلب.",
    "docs_kds_flow_step_3": "<strong>جاهز:</strong> العنصر مطبوخ وجاهز للتقديم.",
    "docs_kds_flow_step_4": "<strong>تم التقديم:</strong> اكتمل الطلب.",
    "docs_kds_note_box": "<strong>ملاحظة:</strong> يعمل KDS محليًا عبر شبكة LAN، مما يضمن الوظائف حتى بدون الإنترنت.",

    // PMS Overview (Arabic)
    "docs_pms_title": "نظام إدارة الممتلكات (PMS) ووحدة الفنادق",
    "docs_pms_lead": "إدارة الغرف والحجوزات والضيوف بكفاءة من خلال نظام إدارة الممتلكات المتكامل لدينا.",
    "docs_pms_key_title": "الميزات الرئيسية",
    "docs_pms_key_desc": "انتقل إلى: <strong>PMS</strong> من القائمة الرئيسية.",
    "docs_pms_list_1": "<strong>إدارة الغرف:</strong> نظرة عامة مرئية على حالة الغرفة وتوافرها.",
    "docs_pms_list_2": "<strong>تسجيل الوصول / تسجيل للمغادرة:</strong> تدفقات وصول ومغادرة الضيوف المبسطة.",
    "docs_pms_list_3": "<strong>تتبع الإشغال:</strong> مراقبة معدلات الإشغال في الوقت الحقيقي.",
    "docs_pms_list_4": "<strong>الفواتير المتكاملة:</strong> يتم ربط رسوم الغرفة تلقائيًا بفواتير الضيوف.",
    "docs_pms_list_5": "<strong>تدبير منزلي:</strong> إدارة جداول التنظيف وصيانة الغرف.",
    "docs_pms_designed_title": "مصمم لـ",
    "docs_pms_designed_1": "الفنادق",
    "docs_pms_designed_2": "الرياض",
    "docs_pms_designed_3": "بيوت الضيافة",
    "docs_pms_designed_4": "إيجارات الإقامة القصيرة",

    // Roles & Permissions (Arabic)
    "docs_roles_title": "الأدوار والأذونات",
    "docs_roles_lead": "تحكم في الوصول وضمان الأمان مع إدارة الأدوار الدقيقة.",
    "docs_roles_manage_title": "إدارة الأدوار",
    "docs_roles_manage_desc": "انتقل إلى: <strong>الإعدادات -> الأدوار والأذونات</strong>",
    "docs_roles_list_1": "<strong>إنشاء أدوار مخصصة:</strong> حدد أدوارًا فريدة مصممة خصيصًا لاحتياجات عملك (على سبيل المثال، أمين الصندوق، المدير، المحاسب).",
    "docs_roles_list_2": "<strong>أذونات دقيقة:</strong> تعيين حقوق محددة لكل دور.",
    "docs_roles_scopes_title": "نطاقات الأذونات",
    "docs_roles_scopes_desc": "يمكن تطبيق الأذونات عبر وحدات مختلفة:",
    "docs_roles_scopes_1": "<strong>نقاط البيع:</strong> بيع الوصول، وإلغاء العناصر، وتطبيق الخصومات.",
    "docs_roles_scopes_2": "<strong>المخزون:</strong> إضافة المنتجات وتعديلات المخزون.",
    "docs_roles_scopes_3": "<strong>المحاسبة:</strong> عرض التقارير وإغلاق الورديات.",
    "docs_roles_scopes_4": "<strong>الإعدادات:</strong> تغيير تكوينات النظام.",

    // Finance Closures (Arabic)
    "docs_finance_title": "الفواتير والإغلاقات",
    "docs_finance_lead": "تتبع أموالك، وقم بإدارة الفواتير، وتأمين تدفقك النقدي.",
    "docs_faq_title": "الأسئلة الشائعة",
    "docs_faq_subtitle": "إجابات على الأسئلة الشائعة حول نظام نقاط البيع Finio و KDS ومنصة إدارة الأعمال.",
    "docs_faq_cat_pos": "نقاط البيع والمبيعات",
    "docs_faq_q_offline_sell": "هل يمكنني بيع المنتجات بدون اتصال بالإنترنت؟",
    "docs_faq_a_offline_sell": "نعم. Finio هو نظام <strong>يعمل دون اتصال أولاً</strong>. يتم حفظ جميع المبيعات وإغلاقات Z وحركات الطاولات محلياً على جهازك. يقوم النظام بمزامنة كل شيء تلقائياً إلى السحابة بمجرد استعادة الاتصال.",
    "docs_faq_q_prices": "هل يدعم نقاط البيع مستويات أسعار متعددة؟",
    "docs_faq_a_prices": "نعم، يمكنك تكوين مستويات أسعار متعددة (مثل ساعة التخفيضات، VIP، الموظفين) والتبديل بينها فوراً أثناء البيع.",
    "docs_faq_q_split": "هل يمكنني تقسيم الفواتير أو دمج الطاولات؟",
    "docs_faq_a_split": "بالتأكيد. يمكنك تقسيم المدفوعات حسب المبلغ أو العنصر، ودمج الطاولات أو نقل العناصر بينها بسهولة.",
    "docs_faq_cat_sync": "الوضع غير المتصل والمزامنة",
    "docs_faq_q_break": "ماذا يحدث إذا تعطل جهازي اللوحي أثناء عدم الاتصال؟",
    "docs_faq_a_break": "تم تصميم Finio لحماية بياناتك حتى في البيئات غير المتصلة بالإنترنت. عندما تكون أجهزة متعددة متصلة بنفس الشبكة المحلية، فإنها تتشارك وتزامن البيانات تلقائيًا مع بعضها البعض دون اتصال. هذا يعني أن بياناتك مخزنة على أكثر من جهاز محلياً. إذا تعطل أحد الأجهزة قبل المزامنة، تظل البيانات متاحة على الأجهزة الأخرى في الشبكة. بمجرد إعادة اتصال أي جهاز بالإنترنت، تتم مزامنة جميع البيانات تلقائيًا مع السحابة.",
    "docs_faq_q_status": "كيف أعرف ما إذا كنت متصلاً أم لا؟",
    "docs_faq_a_status": "يظهر مؤشر الحالة في الزاوية العلوية اليمنى من شاشة نقاط البيع حالة الاتصال الخاصة بك: أخضر للأونلاين، ورمادي/أحمر للأوفلاين.",
    "docs_faq_cat_billing": "الاشتراكات والفوترة",
    "docs_faq_q_plan": "هل يمكنني تغيير خطتي لاحقاً؟",
    "docs_faq_a_plan": "نعم، يمكنك الترقية من Starter إلى Business في أي وقت. سيتم احتساب الرصيد المتبقي من اشتراكك الحالي بالتناسب.",
    "docs_faq_q_cancel": "هل توجد رسوم إلغاء؟",
    "docs_faq_a_cancel": "لا. يمكنك إلغاء اشتراكك في أي وقت. سيظل وصولك نشطاً حتى نهاية فترة الفوترة الحالية.",
    "docs_faq_cat_hardware": "الأجهزة",
    "docs_faq_q_printers": "ما هي طابعات الإيصالات المتوافقة؟",
    "docs_faq_a_printers": "يدعم Finio معظم الطابعات الشبكية (Ethernet/WiFi) المتوافقة مع ESC/POS، بما في ذلك العلامات التجارية الكبرى مثل Epson و Star Micronics و Xprinter.",
    "docs_faq_q_tablet": "هل يمكنني استخدام جهازي اللوحي الحالي؟",
    "docs_faq_a_tablet": "نعم، يعمل Finio على أي جهاز لوحي حديث يعمل بنظام iOS (iPad) أو Android. نوصي بإمكانيات لا تقل عن 3 جيجابايت من ذاكرة الوصول العشوائي.",
    "docs_faq_cat_security": "الأمان والبيانات",
    "docs_faq_q_access": "من لديه حق الوصول إلى بيانات مبيعاتي؟",
    "docs_faq_a_access": "أنت والمستخدمون المصرح لهم فقط. لا يمكن لموظفي Finio الوصول إلى بياناتك التشغيلية دون إذن صريح للدعم.",
    "docs_faq_q_legal": "هل فواتيري متوافقة قانونياً؟",
    "docs_faq_a_legal": "نعم. ينشئ Finio فواتير غير قابلة للتغيير ومرقمة بالتسلسل تتوافق مع اللوائح الضريبية. بمجرد الانتهاء، لا يمكن تغيير الفاتورة أو حذفها.",
    "docs_finance_invoices_title": "الفواتير",
    "docs_finance_invoices_go_to": "انتقل إلى: <strong>الفواتير</strong>",
    "docs_finance_invoices_history": "<strong>عرض السجل:</strong> الوصول إلى جميع الفواتير السابقة.",
    "docs_finance_invoices_filtering": "<strong>الفلترة:</strong> الفلترة حسب التاريخ أو الحالة أو السجل أو المستخدم.",
    "docs_finance_invoices_actions": "<strong>الإجراءات:</strong> طباعة أو تصدير ملفات PDF للفواتير. يتم دعم مشاركة الفواتير العامة أيضًا.",
    "docs_finance_important_rules": "قواعد مهمة:",
    "docs_finance_rule_1": "مجاميع الفاتورة غير قابلة للتغيير بمجرد إنشائها.",
    "docs_finance_rule_2": "المدفوعات للإضافة فقط (لا يمكن حذفها، فقط تعويضها).",
    "docs_finance_rule_3": "لا تعيد المبالغ المستردة كتابة التاريخ أبدًا؛ فهي تخلق معاملات عكسية جديدة.",
    "docs_finance_registers_title": "صناديق النقود",
    "docs_finance_registers_sessions": "<strong>الجلسات:</strong> يعمل كل صندوق نقود في جلسات.",
    "docs_finance_registers_tracking": "<strong>التتبع:</strong> يجب ربط المدفوعات النقدية بجلسة تسجيل مفتوحة.",
    "docs_finance_registers_security": "<strong>الأمان:</strong> يمنع الحركات النقدية غير المتعقبة ويعزز المساءلة.",
    "docs_finance_closures_title": "الرصيد الافتتاحي وإغلاق نهاية اليوم (Z Closure)",
    "docs_finance_closures_start": "<strong>بداية الوردية:</strong> تعيين مبلغ الرصيد الافتتاحي.",
    "docs_finance_closures_end": "<strong>نهاية الوردية:</strong> إجراء إغلاق نهاية اليوم.",
    "docs_finance_closures_reconciliation": "<strong>المصالحة:</strong> يحسب النظام النقد المتوقع مقابل النقد الفعلي ويسلط الضوء على أي اختلافات.",
    "docs_finance_closures_locking": "<strong>القفل:</strong> يتم قفل الفترات المغلقة ولا يمكن تعديلها.",

    // --- Changelog ---
    "nav_changelog_title": "سجل تغييرات Finio",
    "changelog_version": "الإصدار 1.0 ← الإعداد 2.0",
    "changelog_overview_title": "نظرة عامة",
    "changelog_overview_p1": "كان إصدار Finio 1.0 إصدارًا تجريبيًا (Pilot)، تم توفيره لعدد محدود من العملاء المختارين عبر قطاعات مختلفة (مطاعم، تجزئة، ضيافة، وخدمات).",
    "changelog_overview_p2": "لم يكن الهدف من v1.0 هو التوسع، بل التعلم.",
    "changelog_overview_p3": "من خلال التعاون المباشر مع شركات حقيقية، لاحظنا كيف تتصرف أنظمة نقاط البيع تحت ضغط العمل الفعلي: الاستخدام دون اتصال بالإنترنت، أخطاء الموظفين، المبالغ المستردة، فقدان المخزون، الفروقات النقدية، واستخدام أجهزة متعددة.",
    "changelog_overview_p4": "الإصدار 2.0 هو نتيجة لهذا التعلم.",
    "changelog_overview_p5": "إنه ليس مجرد تحديث — إنه ترقية هيكلية شاملة، صممت من أجل:",
    "changelog_overview_list_1": "تقليل المخاطر التشغيلية",
    "changelog_overview_list_2": "منع التلاعب الصامت بالبيانات",
    "changelog_overview_list_3": "تقليل السرقة والأخطاء المحاسبية",
    "changelog_overview_list_4": "تحديد نقاط الضعف في العمليات التجارية",
    "changelog_overview_list_5": "إعداد الشركات للتوسع على أساس متين",

    "changelog_v1_title": "الإصدار 1.0 — المرحلة التجريبية (التعلم التأسيسي)",
    "changelog_v1_purpose_title": "الغرض",
    "changelog_v1_purpose_1": "التحقق من صحة الاستخدام دون اتصال بالإنترنت (Offline-first)",
    "changelog_v1_purpose_2": "مراقبة سلوك الموظفين الحقيقي",
    "changelog_v1_purpose_3": "فهم نقاط الفشل في سير عمل نقاط البيع الحالية",
    "changelog_v1_purpose_4": "تحديد أين تخسر الشركات الأموال دون أن تدرك ذلك",

    "changelog_v1_char_title": "الخصائص",
    "changelog_v1_char_1": "الوظائف الأساسية لنقاط البيع",
    "changelog_v1_char_2": "دعم أساسي لوضع عدم الاتصال",
    "changelog_v1_char_3": "تتبع المخزون والمبيعات",
    "changelog_v1_char_4": "سير عمل التسويات اليدوية",
    "changelog_v1_char_5": "حماية محدودة ضد تغيير البيانات",
    "changelog_v1_char_6": "تقارير تفاعلية (ماذا حدث، وليس لماذا)",

    "changelog_findings_title": "النتائج الرئيسية من العملاء التجريبيين",
    "changelog_findings_intro": "خلال الاستخدام الفعلي، حددنا مشاكل متكررة عبر القطاعات:",
    "changelog_findings_1": "تعديل الفواتير بعد الدفع",
    "changelog_findings_2": "المدفوعات التي يتم استبدالها أو فقدانها أثناء المزامنة",
    "changelog_findings_3": "المنتجات كثيرة الاسترجاع تخفي مشاكل في المخزون أو الجودة",
    "changelog_findings_4": "فروقات نقدية دون سبب واضح",
    "changelog_findings_5": "تعارضات الأجهزة المتعددة التي تسبب عدم تطابق البيانات",
    "changelog_findings_6": "توسع الشركات قبل إصلاح نقاط الضعف الداخلية",
    "changelog_findings_outro": "هذه الرؤى شكلت الإصدار 2.0 بشكل مباشر.",

    "changelog_v2_title": "الإصدار 2.0 — الإنتاج والتحكم في المخاطر",
    "changelog_v2_desc": "تم إعادة بناء الإصدار 2.0 مع وضع التحكم، والنزاهة، والوقاية كمبادئ أساسية.",

    "changelog_acc_title": "المحاسبة ونزاهة البيانات (ترقية كبرى)",
    "changelog_acc_inv_title": "فواتير غير قابلة للتغيير",
    "changelog_acc_inv_1": "بمجرد إصدار الفاتورة، لا يمكن تغيير الإجماليات المالية.",
    "changelog_acc_inv_2": "يمنع التعديلات الصامتة، التلاعب، أو التغييرات بعد الدفع.",
    "changelog_acc_inv_3": "يضمن سجلات محاسبية آمنة للتدقيق.",

    "changelog_acc_pay_title": "سجل مدفوعات للإضافة فقط",
    "changelog_acc_pay_1": "لا يتم استبدال المدفوعات أو حذفها أبدًا.",
    "changelog_acc_pay_2": "يتم تسجيل كل عملية دفع، استرجاع، أو تغيير حالة كإدخال جديد في السجل.",
    "changelog_acc_pay_3": "تتبع كامل لجميع الإجراءات المالية.",

    "changelog_acc_atomic_title": "معاملات ذرية (Atomic Transactions)",
    "changelog_acc_atomic_1": "تتم معالجة المدفوعات في عمليات آمنة من التعطل.",
    "changelog_acc_atomic_2": "لا توجد حالات جزئية إذا توقف الجهاز أثناء المعاملة.",
    "changelog_acc_atomic_3": "يقضي على المدفوعات الوهمية والأرصدة غير المتطابقة.",

    "changelog_acc_time_title": "فرض وقت الخادم",
    "changelog_acc_time_1": "يمنع التلاعب بساعة الجهاز.",
    "changelog_acc_time_2": "تتبع جميع الأحداث المالية جدولًا زمنيًا موثوقًا وتصاعديًا.",

    "changelog_off_title": "الموثوقية دون اتصال وتعدد الأجهزة",
    "changelog_off_design_title": "Offline-First بالتصميم",
    "changelog_off_design_1": "تشغيل كامل دون إنترنت.",
    "changelog_off_design_2": "لا تراجع في الميزات عند انقطاع الاتصال.",

    "changelog_off_local_title": "المزامنة عبر الشبكة المحلية (Offline)",
    "changelog_off_local_1": "تتشارك الأجهزة البيانات عبر الشبكة المحلية دون إنترنت.",
    "changelog_off_local_2": "يخلق تكرارًا محليًا عبر الأجهزة.",
    "changelog_off_local_3": "تنجو البيانات حتى إذا تضرر أحد الأجهزة.",

    "changelog_off_sync_title": "مزامنة آمنة للتعارضات",
    "changelog_off_sync_1": "يتم دمج المدفوعات والفواتير بأمان عبر الأجهزة.",
    "changelog_off_sync_2": "لا معاملات مفقودة عند استخدام محطات متعددة.",
    "changelog_off_sync_3": "تحل سلطة الخادم التعارضات دون استبدال التاريخ.",

    "changelog_off_cloud_title": "استرداد سحابي تلقائي",
    "changelog_off_cloud_1": "بمجرد إعادة اتصال أي جهاز بالإنترنت، تتم مزامنة جميع البيانات تلقائيًا مع السحابة.",

    "changelog_inv_title": "المخزون، الاسترجاع ومنع الخسارة",
    "changelog_inv_refund_title": "ذكاء عمليات الاسترجاع",
    "changelog_inv_refund_1": "تُعامل عمليات الاسترجاع كعكس مالي، وليس كمصروفات عامة.",
    "changelog_inv_refund_2": "ربط واضح بين الاسترجاع، المنتجات، وطرق الدفع.",

    "changelog_inv_risk_title": "كشف مخاطر المنتجات",
    "changelog_inv_risk_1": "تحديد المنتجات ذات: معدلات استرجاع عالية، شكاوى متكررة، أو فروقات في المخزون.",
    "changelog_inv_risk_2": "يساعد في الكشف عن: موردين سيئين، مشاكل جودة، أو احتيال/سوء استخدام.",

    "changelog_inv_stock_title": "تحسينات نزاهة المخزون",
    "changelog_inv_stock_1": "تتبع أفضل للعناصر التالفة، المرتجعة، وغير القابلة للبيع.",
    "changelog_inv_stock_2": "تقليل انحراف المخزون بمرور الوقت.",

    "changelog_health_title": "صحة الأعمال ودعم القرار (جديد)",
    "changelog_health_ind_title": "مؤشرات صحة الأعمال",
    "changelog_health_ind_1": "إشارات تحذير مبكر لـ: استرجاع مفرط، هوامش ربح متقلصة، عدم توازن نقدي، تسرب المخزون.",
    "changelog_health_ind_2": "مصممة لإصلاح المشاكل قبل التوسع، وليس بعد الفشل.",

    "changelog_health_ops_title": "الرؤية التشغيلية",
    "changelog_health_ops_1": "فصل واضح بين: أداء المبيعات، المشاكل التشغيلية، والتناقضات المحاسبية.",

    "changelog_cash_title": "التحكم النقدي والتسوية",
    "changelog_cash_bind_title": "ربط صندوق النقد",
    "changelog_cash_bind_1": "يجب ربط كل حركة نقدية بجلسة تسجيل نشطة.",
    "changelog_cash_bind_2": "يمنع العمليات النقدية غير القابلة للتعقب.",

    "changelog_cash_eod_title": "فترات محاسبية مغلقة (EOD Locking)",
    "changelog_cash_eod_1": "بمجرد إغلاق اليوم، يتم حماية البيانات التاريخية.",
    "changelog_cash_eod_2": "يمنع التلاعب بأثر رجعي.",
    "changelog_cash_eod_3": "يضمن تسوية يومية نظيفة.",

    "changelog_plat_title": "تحسينات المنصة والهندسة",
    "changelog_plat_mod_title": "نضج النظام المعياري",
    "changelog_plat_mod_1": "توافق كامل بين POS، KDS، المخزون، PMS، التوصيل، ولوحة المحاسبة.",
    "changelog_plat_mod_2": "فصل واضح للأدوار (أمين صندوق، مدير، محاسب، مسؤول).",

    "changelog_plat_web_title": "لوحات ويب محسنة",
    "changelog_plat_web_1": "وصول المحاسب والإدارة لـ: المصاريف، الرواتب، فواتير B2B، الإحصاءات المالية، الإشراف متعدد الفروع.",

    "changelog_reps_title": "ماذا يمثل الإصدار 2.0",
    "changelog_reps_intro": "تم بناء Finio v2.0 للشركات التي تريد:",
    "changelog_reps_list_1": "النمو دون فقدان السيطرة",
    "changelog_reps_list_2": "منع السرقة والأخطاء الصامتة",
    "changelog_reps_list_3": "الكشف عن المنتجات السيئة مبكرًا",
    "changelog_reps_list_4": "الوثوق بأرقامهم",
    "changelog_reps_list_5": "التوسع على أساس تشغيلي صحي",
    "changelog_reps_outro": "إنه لم يعد مجرد نقطة بيع — إنه منصة لنزاهة الأعمال.",

    "changelog_future_title": "نظرة إلى المستقبل",
    "changelog_future_1": "يؤسس الإصدار 2.0 الأساس غير القابل للتفاوض.",
    "changelog_future_2": "ستبني الإصدارات المستقبلية فوق هذه النزاهة، وليس حولها.",

  },
  fr: {
    "home": "Accueil",
    "products": "Nos Modules",
    "subscriptions": "Abonnements",
    "features": "Fonctionnalités",
    "coming_soon": "Innovations",
    "contact_us": "Contact",
    "hero_title": "Le logiciel de caisse Offline-First à l'intégrité comptable certifiée",
    "hero_subtitle": "Conçu pour les entreprises qui exigent la précision. Avec factures inaltérables, registre de paiements en ajout uniquement et synchronisation multi-appareils sans conflit.",
    "book_demo": "Explorer la plateforme",
    "hero_badge_new": "Finio 2.0 : Maintenant avec sécurité d'audit renforcée",

    "// --- Value Pillars ---": "",
    "pillar_offline_title": "Véritable Architecture Offline-First",
    "pillar_offline_desc": "Pas seulement un support 'intermittent'. Finio stocke 100% des données localement et synchronise sans conflit une fois en ligne. Zéro temps d'arrêt.",
    "pillar_integrity_title": "Cœur Comptable Inaltérable",
    "pillar_integrity_desc": "Les factures ne peuvent être modifiées après création. Les paiements sont en ajout uniquement. Votre intégrité financière est mathématiquement garantie.",
    "pillar_conflict_title": "Transactions Atomiques",
    "pillar_conflict_desc": "Les opérations résistantes aux pannes garantissent que si un appareil échoue en pleine transaction, vos données restent cohérentes et non corrompues.",
    "pillar_modular_title": "Périodes Comptables Clôturées",
    "pillar_modular_desc": "Le verrouillage strict de fin de journée empêche toute modification rétroactive des périodes financières historiques.",

    "// --- Why Finio Section ---": "",
    "why_finio_title": "Pourquoi Finio n'est pas un POS typique",
    "why_finio_subtitle": "La plupart des systèmes de caisse sont juste des calculatrices. Finio est un moteur comptable.",

    "why_finio_card_1_title": "Calculatrices vs Registres",
    "why_finio_card_1_desc": "Les systèmes POS génériques traitent les ventes comme des chiffres temporaires. Finio traite chaque vente comme un enregistrement financier permanent et immuable.",

    "why_finio_card_2_title": "Dépendant du Cloud vs Offline-First",
    "why_finio_card_2_desc": "Les autres s'arrêtent quand Internet coupe. Finio fonctionne localement avec une intégrité de niveau serveur, ne synchronisant que lorsque c'est stable.",

    "why_finio_card_3_title": "Surcharge de fonctions vs Précision",
    "why_finio_card_3_desc": "Nous ne courons pas après les fonctionnalités qui compromettent la fiabilité. Chaque module est conçu pour garantir que vos chiffres correspondent à la réalité.",

    "// --- Special Offer Section ---": "",
    "home_offer_badge": "Offre limitée pour les premiers abonnés",
    "home_offer_title": "Soyez parmi les premiers & obtenez 3 mois gratuits",
    "home_offer_desc": "Rejoignez les 100 premiers abonnés et obtenez 3 mois gratuits sur n'importe quel forfait.",
    "home_offer_btn": "Profiter de l'offre",

    "// --- Solutions Section ---": "",
    "solutions_title": "Des solutions adaptées à chaque secteur",
    "solution_restaurant_title": "Restaurants & Cafés",
    "solution_restaurant_desc": "Gérez les commandes, la cuisine, la livraison et les réservations depuis une plateforme unique.",
    "solution_retail_title": "Magasins de détail",
    "solution_retail_desc": "Optimisez les stocks, les ventes et la fidélité client en toute simplicité.",
    "solution_service_title": "Entreprises de services",
    "solution_service_desc": "Solutions de réservation, facturation et CRM pour prestataires de services et hôtels.",

    "// --- Modules Section ---": "",
    "our_modules_title": "Un écosystème modulaire prêt pour la production",

    "// 1. Sales & Operations": "",
    "mod_sales_title": "Ventes & Opérations",
    "mod_pos_title": "Système de Caisse (POS)",
    "mod_pos_desc": "Point de vente ultra-rapide offline-first avec scan code-barres, gestion multi-paniers et synchronisation sans conflit.",
    "mod_kds_title": "Système d'Affichage Cuisine (KDS)",
    "mod_kds_desc": "Lien direct entre la salle et la cuisine. Routage des postes en temps réel, minuteurs et suivi du statut des commandes.",
    "mod_delivery_title": "Livraison & Commandes",
    "mod_delivery_desc": "Gérez efficacement les plateformes de livraison, les livreurs et les commandes téléphoniques depuis un tableau de bord unique.",

    "// 2. Accounting & Finance": "",
    "mod_finance_title": "Comptabilité & Finance",
    "mod_invoice_title": "Moteur de Facturation",
    "mod_invoice_desc": "Générez des factures immuables et conformes fiscalement. Suivez les paiements, les acomptes et l'historique de crédit client.",
    "mod_cash_title": "Gestion de Trésorerie",
    "mod_cash_desc": "Fonds de caisse sécurisé, clôtures aveugles et réconciliation précise des espèces pour prévenir le vol et les erreurs.",
    "mod_expense_title": "Dépenses & Capital",
    "mod_expense_desc": "Suivez les petites dépenses, les paiements fournisseurs et les coûts opérationnels directement depuis la caisse.",

    "// 3. Management & Back-Office": "",
    "mod_mgmt_title": "Gestion & Back-Office",
    "mod_inventory_title": "Contrôle des Stocks",
    "mod_inventory_desc": "Suivi des stocks en temps réel avec journaux de mouvements, alertes de stock bas et bons de commande fournisseurs.",
    "mod_crm_title": "Clients & Fournisseurs",
    "mod_crm_desc": "Profils CRM détaillés avec historique d'achats, points de fidélité et grands livres fournisseurs.",
    "mod_admin_title": "Portail Web Comptable",
    "mod_admin_desc": "Accès à distance pour les comptables afin d'auditer les ventes, exporter les journaux et gérer la configuration.",

    "// 4. Hospitality & Advanced": "",
    "mod_hosp_title": "Hôtellerie & Avancé",
    "mod_floor_title": "Gestion de Salle",
    "mod_floor_desc": "Plan de table visuel avec statut d'occupation, gestion des réservations et affectation des serveurs.",
    "mod_pms_title": "Module Hôtel & PMS",
    "mod_pms_desc": "Gestion hôtelière complète : réservation de chambres, ménage et intégration check-in/out des invités.",
    "mod_api_title": "Écosystème API Ouvert",
    "mod_api_desc": "Connectez des applis tierces, des bornes de commande et des plateformes de commande web via notre API sécurisée.",

    "// --- Legacy Keys (Restored for Compatibility) ---": "",
    "pos_module_title": "Moteur POS Principal",
    "pos_module_description": "Cœur transactionnel natif hors ligne. Gère les paiements partagés, l'audit des annulations et la gestion de session sans dépendance internet.",
    "kds_module_title": "Système de Production",
    "kds_module_description": "Routage à latence zéro. Les commandes sont envoyées depuis le POS et garanties d'arriver aux postes de préparation avec minuteurs visuels.",
    "inventory_module_title": "Inventaire Permanent",
    "inventory_module_description": "Suivi des coûts FIFO avec déduction des recettes. Prend en charge les composés imbriqués complexes et les transferts d'économat.",
    "pms_module_title": "Gestion Immobilière",
    "pms_module_description": "Gestion de folios intégrée au POS. Les frais de chambre sont des transactions atomiques validées par rapport au statut de l'invité.",
    "printing_module_title": "Serveur de Routage d'Impression",
    "printing_module_description": "Routage d'impression basé sur des règles. Prend en charge le re-routage en cas d'échec, le matériel ESC/POS hérité et l'injection de modèles personnalisés.",
    "web_server_module_title": "Commande Centrale",
    "web_server_module_description": "Agrégation en temps réel des données multi-sites. Poussez les mises à jour de menu, tirez les journaux d'audit et gérez les permissions utilisateurs globalement.",

    "// --- Services Section ---": "",
    "professional_services_title": "Services Professionnels",
    "custom_software_title": "Développement Logiciel Sur Mesure",
    "custom_software_description": "Nous offrons des solutions logicielles adaptées à vos besoins uniques, du web aux applications mobiles.",
    "hardware_software_title": "Solutions Matérielles & Logicielles",
    "hardware_software_description": "Nous assurons une intégration transparente du matériel et du logiciel pour faire tourner vos systèmes efficacement, incluant support et installation.",
    "consulting_title": "Conseil & Solutions Intégrées",
    "consulting_description": "Notre équipe offre un conseil complet pour vous aider à choisir et implémenter les meilleures solutions technologiques pour votre projet.",

    "// --- Core Features Section ---": "",
    "core_features_title": "Architecture Système & Capacités",

    "// 1. Accounting-Grade Protection": "",
    "feat_protect_title": "Cœur Financier Inaltérable",
    "feat_protect_desc": "Enregistrement en partie double avec vérification en ajout uniquement. Les données historiques sont mathématiquement verrouillées contre toute altération.",

    "// 2. Offline-First Sync Engine": "",
    "feat_sync_engine_title": "Réplication Sans Conflit",
    "feat_sync_engine_desc": "Architecture de base de données locale (Hive). Les opérations sont validées localement instantanément et répliquées vers le cloud avec cohérence éventuelle.",

    "// 3. Trusted Time & Audit Safety": "",
    "feat_audit_title": "Temps Faisant Foi (Serveur)",
    "feat_audit_desc": "Les transactions sont horodatées par l'autorité du serveur pour empêcher la manipulation de l'horloge locale et la fraude à la clôture.",

    "// 4. Professional Closures": "",
    "feat_closure_title": "Clôtures Journalières Professionnelles",
    "feat_closure_desc": "Gérez les fonds de caisse, effectuez des clôtures Z aveugles et réconciliez les espèces avec un suivi automatisé des écarts.",

    "// 5. Roles & Permissions": "",
    "feat_roles_title": "Contrôle d'Accès Basé sur les Rôles (RBAC)",
    "feat_roles_desc": "Définissez des niveaux d'accès exacts pour les caissiers, managers, comptables et le personnel de cuisine avec des portes de permission granulaires.",

    "// 6. Integrations & APIs": "",
    "feat_integrations_title": "Intégrations & APIs",
    "feat_integrations_desc": "Connectez-vous aux ERP, WooCommerce et passerelles de paiement via notre API développeur étendue.",

    // --- Tout est inclus ---
    "all_features_title": "Tout est inclus",
    "all_features_subtitle": "Des capacités puissantes déjà intégrées à Finio — au-delà de l'essentiel.",
    "all_group_revenue": "Revenus & Croissance",
    "all_revenue_giftcards_title": "Cartes cadeaux",
    "all_revenue_giftcards_desc": "Émettez, rechargez et annulez des cartes cadeaux, avec suivi de caisse et panneau de passif en temps réel.",
    "all_revenue_loyalty_title": "Programme de fidélité",
    "all_revenue_loyalty_desc": "Niveaux Bronze, Argent et Or avec cumul, utilisation et expiration des points.",
    "all_revenue_coupons_title": "Coupons & Promotions",
    "all_revenue_coupons_desc": "Limites d'utilisation, campagnes planifiées et statistiques de ROI par coupon.",
    "all_revenue_loans_title": "Crédit client & Prêts",
    "all_revenue_loans_desc": "Prêts et limites de crédit B2B avec suivi des soldes.",
    "all_group_ops": "Opérations & Prévention des pertes",
    "all_ops_shifts_title": "Équipes & Planification",
    "all_ops_shifts_desc": "Shifts récurrents hebdomadaires, détection des conflits et rappels push.",
    "all_ops_loss_title": "Suite anti-pertes",
    "all_ops_loss_desc": "Journal des pertes, inventaire/cycle count et remboursement + remplacement — arrêtez de perdre de l'argent.",
    "all_ops_fefo_title": "FEFO & Suivi des lots",
    "all_ops_fefo_desc": "Suivi des dates d'expiration et des lots avec valorisation premier expiré, premier sorti.",
    "all_ops_transfers_title": "Transferts multi-sites",
    "all_ops_transfers_desc": "Déplacez le stock entre sites et entrepôts avec une traçabilité totale.",
    "all_ops_barcode_title": "Impression d'étiquettes code-barres",
    "all_ops_barcode_desc": "Générez et imprimez des étiquettes code-barres et des étiquettes de prix.",
    "all_ops_purchasing_title": "Achats & Fournisseurs",
    "all_ops_purchasing_desc": "Bons de commande et comptes fournisseurs pour un back-office complet.",
    "all_group_ai": "IA & Expérience client",
    "all_ai_translation_title": "Traduction produit par IA",
    "all_ai_translation_desc": "Traduisez automatiquement les noms et descriptions de produits.",
    "all_ai_enrichment_title": "Enrichissement ChatGPT",
    "all_ai_enrichment_desc": "Enrichissez le catalogue avec descriptions, catégories et attributs.",
    "all_ai_perplexity_title": "Recherche Perplexity",
    "all_ai_perplexity_desc": "Importez des informations produits et marché en direct dans Finio.",
    "all_ai_waiter_title": "Appeler le serveur",
    "all_ai_waiter_desc": "Les clients appellent le personnel en temps réel avec une vue de salle en alerte rouge.",
    "all_ai_feedback_title": "Avis client + QR",
    "all_ai_feedback_desc": "Recueillez les avis via un formulaire web et un QR code.",
    "all_ai_kiosk_title": "Borne / Commande autonome",
    "all_ai_kiosk_desc": "Borne libre-service et mode commande autonome pour les lieux très fréquentés.",
    "all_ai_map_title": "Plan de salle",
    "all_ai_map_desc": "Plans par glisser-déposer, multi-étages et multi-sites.",
    "all_ai_glovo_title": "Marketplace Glovo",
    "all_ai_glovo_desc": "Importez directement les commandes Glovo dans votre caisse.",
    "all_group_finance": "Back-office & Finance",
    "all_fin_fiscal_title": "Conformité fiscale",
    "all_fin_fiscal_desc": "fiskaly SIGN ES et fiskaltrust (AT/DE/FR) pour une fiscalisation certifiée.",
    "all_fin_dunning_title": "Relances de paiement",
    "all_fin_dunning_desc": "Rappels de paiement automatiques pour les soldes clients en retard.",
    "all_fin_payroll_title": "Paie & Salaires",
    "all_fin_payroll_desc": "Gérez les salaires et générez les PDF de paie depuis le panneau comptable.",
    "all_fin_docs_title": "Chaîne documentaire",
    "all_fin_docs_desc": "Remboursement + remplacement, quotas de documents et chaîne BC → BL → FA → AV.",
    "all_group_platform": "Plateforme & Fiabilité",
    "all_plat_lan_title": "Redondance LAN hors ligne",
    "all_plat_lan_desc": "Fonctionnement hors ligne multi-appareils sur votre réseau local, avec synchronisation automatique.",
    "all_plat_desktop_title": "Applications de bureau",
    "all_plat_desktop_desc": "Applications natives pour Windows, macOS et Linux, en plus du Web, iOS et Android.",

    // --- Comparaison : vrais avantages PRO ---
    "sub_cat_pro_only": "PRO uniquement",
    "sub_cmp_refund_replace": "Remboursement + Remplacement",
    "sub_cmp_quota": "Gestion des quotas de documents",
    "sub_cmp_doc_chain": "Chaîne documentaire (BC → BL → FA → AV)",
    "sub_cmp_whatsapp_invoice": "Factures WhatsApp",
    "sub_cmp_max_registers": "Nombre maximum de caisses",
    "sub_cmp_reconciliation": "Rapprochement de caisse",
    "sub_cmp_customer_pdf": "Relevés clients PDF",
    "sub_cmp_stock_reports": "Rapports de stock",
    "sub_cmp_delivery_orders": "Commandes de livraison",
    "sub_cmp_store_orders": "Commandes web / boutique",
    "sub_cmp_health_date": "Rapports par période (santé)",
    "sub_cmp_liquid_capital": "PDF capital liquide",

    "// Legacy keys": "",
    "feature_auth_title": "Authentification Multi-Locataire",
    "feature_auth_desc": "Authentification sécurisée basée sur les rôles conçue pour une architecture multi-locataire, assurant l'isolation et la sécurité des données.",
    "feature_offline_title": "Engagement Local à Latence Zéro",
    "feature_offline_desc": "Gardez vos opérations fluides, même sans connexion internet. Les données se synchronisent automatiquement une fois en ligne.",
    "feature_sync_title": "Synchro Delta en Arrière-plan",
    "feature_sync_desc": "Exploite Firebase et un service de synchro puissant pour assurer que les données sont toujours à jour sur tous les appareils et modules.",
    "feature_remote_title": "Panneau Web Admin & Comptable",
    "feature_remote_desc": "Accès à distance sécurisé pour les comptables et admins pour auditer les ventes, journaux et configurations.",
    "feature_reporting_title": "États Financiers Réconciliés",
    "feature_reporting_desc": "Obtenez des informations critiques avec des analyses complètes sur les ventes, la performance produit et le comportement client.",
    "feature_security_title": "Contrôle d'Accès (RBAC)",
    "feature_security_desc": "Des permissions dans l'interface aux sauvegardes automatiques et au cryptage, vos données sont toujours protégées.",
    "feature_pms_rooms": "Gestion des Chambres & Occupation",
    "feature_pms_booking": "Réservation Invités & CRM",
    "feature_print_network": "Découverte d'Imprimantes Réseau",
    "feature_print_custom": "Modèles de Reçus Personnalisables",

    "// --- Innovations Section ---": "",
    "innovations_title": "Solutions prêtes pour le futur",
    "innovations_intro": "Soyez parmi les premiers à expérimenter le futur de l'automatisation commerciale. Rejoignez notre programme d'adoption précoce et façonnez la prochaine génération de solutions intelligentes.",
    "smart_store_title": "Finio Smart Store",
    "smart_store_desc": "Activez une expérience sans caisse avec des caméras IA, des appareils d'auto-achat et une détection automatique du vol.",
    "smart_store_feature_1_title": "Caméras alimentées par l'IA",
    "smart_store_feature_1_desc": "La vision par ordinateur avancée suit les mouvements des clients et les sélections de produits en temps réel.",
    "smart_store_feature_2_title": "Appareils d'auto-encaissement",
    "smart_store_feature_2_desc": "Les clients scannent et paient les articles indépendamment, réduisant les temps d'attente et les besoins en personnel.",
    "smart_store_feature_3_title": "Détection Automatique du Vol",
    "smart_store_feature_3_desc": "Des algorithmes de machine learning identifient les comportements suspects et alertent la sécurité en temps réel.",
    "smart_store_feature_4_title": "Suivi Intelligent des Stocks",
    "smart_store_feature_4_desc": "Suivi automatique des niveaux de stock au fur et à mesure que les articles sont pris, assurant des comptes d'inventaire précis.",
    "smart_airbnb_title": "Finio Smart Airbnb",
    "smart_airbnb_desc": "Automatisez votre gestion immobilière avec confirmations de réservation IA, serrures intelligentes et codes d'accès dynamiques.",
    "smart_airbnb_feature_1_title": "Assistant de Réservation IA",
    "smart_airbnb_feature_1_desc": "Réponses automatisées aux demandes, confirmations instantanées et suggestions de prix intelligentes.",
    "smart_airbnb_feature_2_title": "Intégration Serrure Intelligente",
    "smart_airbnb_feature_2_desc": "Générez des codes d'accès uniques pour chaque invité, activés et désactivés automatiquement selon les réservations.",
    "smart_airbnb_feature_3_title": "Gestion d'Accès Dynamique",
    "smart_airbnb_feature_3_desc": "Les codes changent automatiquement après le départ, assurant une sécurité maximale pour votre propriété.",
    "smart_airbnb_feature_4_title": "Hub de Communication Invité",
    "smart_airbnb_feature_4_desc": "Messagerie centralisée, instructions d'arrivée et suivis automatisés pour les avis.",
    "early_adopter_title": "Devenez un Early Adopter",
    "early_adopter_intro": "Rejoignez notre programme exclusif et obtenez des tarifs spéciaux, un support prioritaire et l'opportunité d'influencer le développement du produit.",
    "early_adopter_benefit_1": "Prix de lancement spécial",
    "early_adopter_benefit_2": "Implémentation et support prioritaires",
    "early_adopter_benefit_3": "Contribution directe aux fonctionnalités et à la roadmap",
    "early_adopter_benefit_4": "Accès exclusif aux fonctionnalités bêta",
    "early_adopter_form_title": "Exprimez votre intérêt",
    "early_adopter_form_intro": "Remplissez le formulaire ci-dessous pour être parmi les premiers à découvrir ces solutions innovantes.",
    "solution_interested": "Solution d'intérêt",
    "select_solution": "Sélectionnez une solution",
    "business_type": "Type d'entreprise",
    "current_challenges": "Défis actuels",
    "early_adopter_submit": "Rejoindre le programme Early Adopter",

    "// Other keys from previous version...": "",
    "footer_finio_description": "Le système POS le plus avancé pour gérer et faire croître votre entreprise efficacement et professionnellement.",
    "quick_links": "Liens Rapides",
    "support": "Support",
    "help_center": "Centre d'Aide",
    "terms_conditions": "Conditions Générales",
    "privacy_policy": "Politique de Confidentialité",
    "contact_us_footer": "Contactez-nous",
    "contact_phone": "+212 808-646672",
    "contact_email": "email",
    "contact_address": "Av. Aoussou Ou Basselam, Tétouan, Morocco",
    "copyright": "© 2025 Finio Solutions. Tous droits réservés.",
    "company_name": "Finio Solutions",
    "company_ice": "ICE: 003689594000059",
    "company_siege": "Basé à : Tétouan, Maroc",
    "company_address_full": "Av. Aoussou Ou Basselam, Tétouan, Maroc",
    "contact_page_title": "Contactez-nous",
    "contact_page_intro": "Nous sommes là pour répondre à toutes vos demandes et fournir le support nécessaire. N'hésitez pas à nous contacter.",
    "send_message": "Envoyez-nous un message",
    "full_name": "Nom Complet",
    "email_address": "Adresse Email",
    "phone_number": "Numéro de Téléphone",
    "subject": "Sujet",
    "your_message": "Votre Message",
    "send_button": "Envoyer le Message",
    "contact_info_title": "Informations de Contact",

    "// --- Navigation Links ---": "",
    "nav_documentation": "Documentation",
    "nav_faq": "FAQ",
    "nav_security": "Sécurité & Protection des Données",
    "nav_changelog": "Journal des Modifications",
    "nav_about_us": "À Propos",
    "nav_compliance": "Conformité & Audit",

    "// Footer Columns": "",
    "footer_col_product": "Produit",
    "footer_col_resources": "Ressources",
    "footer_col_terms": "Conditions",
    "footer_col_connect": "Connexion",

    "// --- About Us Page ---": "",
    "about_hero_title": "Conçu pour la vérité financière",
    "about_hero_subtitle": "Nous ne créons pas juste un logiciel de caisse. Nous bâtissons des moteurs financiers de qualité audit qui préviennent la fraude, éliminent l'erreur et garantissent la cohérence des données.",
    "about_mission_title": "Notre Mandat",
    "about_mission_text": "Éliminer l'ambiguïté financière dans le commerce et l'hôtellerie. Nous fournissons la certitude mathématique que chaque centime gagné est comptabilisé, suivi et sécurisé.",
    "about_vision_title": "Notre Standard d'Ingénierie",
    "about_vision_text": "Nous rejetons le standard industriel du 'suffisant'. Nous envisageons un futur où le logiciel d'entreprise est aussi fiable qu'un coffre-fort de banque et aussi précis qu'une montre suisse.",
    "about_values_title": "Éthique Opérationnelle",
    "about_values_text": "La précision avant la vitesse. L'intégrité avant les fonctionnalités. Nous priorisons la sécurité de vos données au-dessus de toute promesse marketing.",
    "about_story_title": "L'origine de la tolérance zéro",
    "about_story_paragraph_1": "Finio a commencé lorsque nous avons audité une chaîne de restaurants utilisant un POS cloud populaire. Nous avons trouvé des commandes fantômes, des factures supprimées et un stock flottant disparu sans trace. Le système le permettait. La base de données le permettait.",
    "about_story_paragraph_2": "Nous avons construit Finio pour fermer ces brèches. Nous avons conçu un registre immuable où la suppression est impossible — seulement l'annulation (contre-passation). Nous avons bâti un moteur de synchro qui survit aux pannes réseau sans corrompre un seul octet.",
    "about_story_paragraph_3": "Aujourd'hui, Finio est le standard pour les opérateurs qui exigent la vérité. Nous ne vous aidons pas seulement à vendre ; nous vous aidons à prouver ce que vous avez vendu. Du magasin unique aux chaînes d'entreprise, nous fournissons l'ossature pour des opérations prêtes pour l'audit.",
    "about_why_choose_title": "Pourquoi les opérateurs font confiance à Finio",
    "about_reason_1_title": "Registre Immuable",
    "about_reason_1_desc": "Les factures et paiements sont en ajout uniquement. Personne — pas même vous — ne peut supprimer silencieusement un enregistrement de transaction.",
    "about_reason_2_title": "Cryptage Militaire",
    "about_reason_2_desc": "Les données sont cryptées au repos et en transit. Votre intelligence économique reste rigoureusement privée.",
    "about_reason_3_title": "Souveraineté Offline",
    "about_reason_3_desc": "Nous ne vous louons pas l'accès à vos données. Elles vivent sur votre appareil, pleinement fonctionnelles sans dépendance serveur externe.",
    "about_reason_4_title": "Rapports Prêts pour l'Audit",
    "about_reason_4_desc": "Nos rapports ne sont pas juste de jolis graphiques. Ce sont des états financiers mathématiquement réconciliés prêts pour votre comptable.",
    "about_reason_5_title": "Synchro Sans Conflit",
    "about_reason_5_desc": "Notre moteur de synchro propriétaire gère les collisions multi-appareils avec une logique déterministe, assurant zéro perte de données.",
    "about_reason_6_title": "Intégrité de l'Écosystème",
    "about_reason_6_desc": "Chaque module — KDS, Inventaire, Panneau Web — alimente la même source unique de vérité. Pas de silos de données.",
    "about_stats_title": "Métriques de Fiabilité Système",
    "about_stat_1": "Conflits de Données",
    "about_stat_2": "Disponibilité (Uptime)",
    "about_stat_3": "Score d'Audit",
    "about_stat_4": "SLA Support",
    "about_team_title": "L'Équipe d'Ingénierie",
    "about_team_intro": "Nous ne sommes pas des marketeurs. Nous sommes des architectes systèmes, des spécialistes en sécurité et des ingénieurs en logiciels financiers dédiés à la science de la gestion d'entreprise.",
    "about_team_member_1_name": "Architecture Systèmes",
    "about_team_member_1_role": "Infrastructure Cœur",
    "about_team_member_1_bio": "Conception du registre immuable et de la topologie de base de données offline-first qui propulse chaque instance Finio.",
    "about_team_member_2_name": "Sécurité & Conformité",
    "about_team_member_2_role": "Intégrité des Données",
    "about_team_member_2_bio": "Assure que chaque transaction répond aux standards d'audit stricts et est protégée contre l'altération.",
    "about_team_member_3_name": "Opérations Clients",
    "about_team_member_3_role": "Succès de Déploiement",
    "about_team_member_3_bio": "Aide les entreprises à migrer leurs structures de données complexes vers l'environnement rigoureux de Finio.",
    "about_team_member_4_name": "Design Produit",
    "about_team_member_4_role": "UX & Flux de Travail",
    "about_team_member_4_bio": "Traduction de la logique comptable complexe en flux de travail rapides et intuitifs pour le personnel de terrain.",
    "about_core_values_title": "Protocoles Fondamentaux",
    "about_value_1_title": "La Vérité d'Abord",
    "about_value_1_desc": "Nous ne truquons jamais les chiffres. Si les maths ne s'équilibrent pas, le système s'arrête. L'ambiguïté est un bug.",
    "about_value_2_title": "Le Local d'Abord",
    "about_value_2_desc": "L'appareil dans votre main est la source de vérité. Le cloud est juste une sauvegarde.",
    "about_value_3_title": "Zéro Perte de Données",
    "about_value_3_desc": "Nous sommes paranoïaques sur la sécurité des données. Nous supposons que les réseaux échouent, le courant coupe et le matériel casse.",
    "about_value_4_title": "Logique Transparente",
    "about_value_4_desc": "Nous ne cachons pas les calculs. Chaque total peut être retracé jusqu'à ses transactions atomiques.",
    "about_value_5_title": "Partenariat",
    "about_value_5_desc": "Nous créons des outils pour les opérateurs sérieux. Si vous vous souciez de la précision, nous sommes votre partenaire.",
    "about_growth_title": "Le Futur de l'Intégrité",
    "about_growth_text_1": "Alors que nous grandissons, notre engagement envers la rigidité centrale de la comptabilité reste inchangé. Nous nous étendons vers l'IA et l'automatisation, mais jamais au prix de la précision.",
    "about_growth_text_2": "Notre roadmap inclut les modules 'Smart Store' et 'Smart Property', mais contrairement aux autres, les nôtres sont bâtis sur la même fondation immuable. Les suggestions IA seront auditées.",
    "about_growth_text_3": "Finio construit le système d'exploitation pour la prochaine génération d'entreprises transparentes, efficaces et sûres pour l'audit en Afrique du Nord et au-delà.",
    "about_cta_title": "Prêt pour le sérieux ?",
    "about_cta_subtitle": "Rejoignez les opérateurs qui ont arrêté de s'inquiéter des écarts de données et commencé à se concentrer sur la croissance.",
    "explore_products": "Voir les Modules Techniques",

    "// --- Checkout Page ---": "",
    "checkout_title": "Complétez votre abonnement",
    "checkout_intro": "Remplissez vos détails ci-dessous pour finaliser votre abonnement. Nous vous contacterons sous 24h pour confirmer votre forfait.",
    "complete_payment": "Effectuer le paiement",
    "subscription_success": "Succès ! Votre abonnement a été activé.",
    "fill_personal_info": "Veuillez d'abord remplir vos Informations Personnelles.",
    "email_required_card": "L'adresse email est requise pour les paiements par carte.",
    "personal_info_title": "Informations Personnelles",
    "company_name_optional": "Nom de l'entreprise (Optionnel)",
    "city": "Ville",
    "country": "Pays",
    "payment_method": "Méthode de Paiement",
    "select_payment": "Sélectionnez la méthode",
    "payment_card": "Par Carte",
    "payment_cash": "En Espèces",
    "payment_cheque": "Par Chèque",
    "payment_bank_cheque": "Par Chèque Bancaire",
    "subscription_details_title": "Détails de l'Abonnement",
    "selected_plan": "Forfait Sélectionné",
    "subscription_duration": "Durée d'Abonnement",
    "select_duration": "Sélectionnez la durée",
    "duration_3months": "3 Mois",
    "duration_6months": "6 Mois (1 Mois Gratuit)",
    "duration_12months": "12 Mois (2.5 Mois Gratuits)",
    "total_amount": "Montant Total",
    "complete_subscription": "Valider l'Abonnement",
    "redirecting_to_payment": "Redirection vers le paiement sécurisé...",
    "sub_recurring_note": "Le montant de {amount} MAD pour la période choisie sera débité maintenant, puis renouvelé automatiquement à {yearly} MAD/an jusqu'à résiliation.",
    "payment_success_title": "Paiement réussi",
    "payment_success_desc": "Merci ! Votre paiement a été reçu et votre abonnement est en cours d'activation.",
    "payment_failed_title": "Paiement échoué",
    "payment_failed_desc": "Votre paiement n'a pas abouti et aucun montant n'a été débité. Vous pouvez réessayer ou contacter notre équipe.",
    "order_reference": "Référence de commande",
    "amount_paid": "Montant payé",
    "try_again": "Réessayer",

    // --- Page Matériel (Maroc uniquement) ---
    "nav_hardware": "Matériel",
    "hw_title": "Packs de matériel de caisse",
    "hw_subtitle": "Des configurations de point de vente complètes — assemblées, testées et livrées partout au Maroc.",
    "hw_morocco_badge": "Disponible au Maroc uniquement",
    "hw_delivery_badge": "Livraison à partir de 50 MAD",
    "hw_packs_title": "Choisissez votre pack",
    "hw_includes": "Ce qui est inclus",
    "hw_hardware_price": "Prix du matériel",
    "hw_with_scanner": "Avec scanner 2D",
    "hw_get_quote": "Choisir ce pack",
    "hw_pricelist_title": "Composants disponibles",
    "hw_pricelist_intro": "Tous les composants que nous pouvons fournir pour votre installation.",
    "hw_item_col": "Article",
    "hw_price_col": "Prix",
    "hw_software_title": "Le logiciel Finio est facturé séparément",
    "hw_software_desc": "Les prix du matériel sont uniques et le logiciel Finio est facturé séparément. Lorsque vous ajoutez un abonnement annuel Finio à votre commande de matériel, vous bénéficiez de 30% de réduction supplémentaires la première année uniquement — par exemple, la formule Starter (normalement 1900 MAD/an) passe à 1330 MAD la première année, puis renouvelée à 1900 MAD/an.",
    "hw_cta_title": "Besoin d'aide pour choisir ?",
    "hw_cta_desc": "Parlez-nous de votre activité et nous vous recommanderons le pack idéal.",
    "hw_pack_starter_name": "Pack Starter",
    "hw_pack_starter_desc": "La configuration essentielle pour les petits commerces et cafés.",
    "hw_pack_android_name": "Pack Android",
    "hw_pack_android_desc": "Caisse tactile sous Android — compacte et flexible.",
    "hw_pack_pro_name": "Pack Professionnel",
    "hw_pack_pro_desc": "La configuration tout-en-un pour les commerces à fort volume.",
    "hw_item_touch": "Écran tactile 17\"",
    "hw_item_printer": "Imprimante thermique 80mm",
    "hw_item_scanner": "Scanner 2D",
    "hw_item_drawer": "Tiroir-caisse",
    "hw_item_tablet": "Tablette G-Tab T12",
    "hw_item_support": "Support haut de gamme",
    "hw_item_pc": "PC i5",
    "hw_item_aio": "Terminal tout-en-un",
    "hw_item_tvbox": "AndroidBox",
    "hw_order_title": "Configurez votre pack",
    "hw_order_subtitle": "Choisissez vos options et votre moyen de paiement, puis validez votre commande.",
    "hw_order_back": "Retour aux packs",
    "hw_order_selected_pack": "Pack sélectionné",
    "hw_scanner_title": "Scanner 2D",
    "hw_scanner_without": "Sans scanner",
    "hw_scanner_with": "Avec scanner 2D",
    "hw_subscription_title": "Logiciel Finio",
    "hw_sub_hardware_only": "Matériel uniquement",
    "hw_sub_add": "Ajouter l'abonnement Finio",
    "hw_sub_plan": "Formule logiciel",
    "hw_sub_first_year": "1ère année (-30%)",
    "hw_sub_renews": "Renouvellement à",
    "hw_sub_per_year": "/an",
    "hw_payment_title": "Moyen de paiement",
    "hw_pay_card": "Par carte",
    "hw_pay_card_desc": "Paiement en ligne sécurisé via Chari Pay",
    "hw_pay_cod": "Paiement à la livraison",
    "hw_pay_transfer": "Virement bancaire",
    "hw_pay_transfer_note": "Nous vous enverrons les coordonnées bancaires juste après votre commande.",
    "hw_details_title": "Vos coordonnées",
    "hw_field_name": "Nom complet",
    "hw_field_phone": "Téléphone",
    "hw_field_email": "Email",
    "hw_field_company": "Société (optionnel)",
    "hw_field_city": "Ville",
    "hw_field_address": "Adresse",
    "hw_summary_title": "Récapitulatif",
    "hw_sum_pack": "Pack matériel",
    "hw_sum_scanner": "Scanner 2D",
    "hw_sum_subscription": "Logiciel Finio (1ère année)",
    "hw_sum_delivery": "Livraison",
    "hw_sum_total": "Total",
    "hw_place_order": "Valider la commande",
    "hw_processing": "Traitement...",
    "hw_order_success_title": "Commande reçue !",
    "hw_order_success_desc": "Merci. Notre équipe vous contactera bientôt pour confirmer la livraison et le paiement.",
    "hw_new_order": "Passer une autre commande",
    "hw_required_missing": "Veuillez remplir les champs obligatoires.",
    "months": "mois",
    "month_free": "mois offert",
    "months_free": "mois offerts",
    "subscriptions_page_title": "Des forfaits transparents pour votre entreprise",
    "subscriptions_intro": "Choisissez le plan qui correspond à vos besoins, sans frais cachés.",
    "pricing_basic_title": "Starter",
    "pricing_basic_price": "200",
    "pricing_pro_title": "Business",
    "pricing_pro_price": "350",
    "pricing_custom_title": "Sur Mesure",
    "pricing_custom_contact": "Contactez-nous",
    "pricing_currency": "MAD/mois",
    "pricing_basic_yearly": "Annuel 1900 MAD/an",
    "pricing_basic_yearly_savings": "2.5 mois offerts",
    "pricing_pro_yearly": "Annuel 3325 MAD/an",
    "pricing_pro_yearly_savings": "2.5 mois offerts",
    "pricing_most_popular": "Le Plus Populaire",
    "pricing_basic_feature_1": "Application complète",
    "pricing_basic_feature_2": "Gestion stocks et clients",
    "pricing_basic_feature_3": "Analyses intelligentes",
    "pricing_basic_feature_4": "Support technique 24/7",
    "pricing_pro_feature_1": "Toutes les fonctions Starter",
    "pricing_pro_feature_2": "Tableau de bord web comptable",
    "pricing_pro_feature_3": "Gestion documentaire (Devis, Factures)",
    "pricing_pro_feature_4": "Rapports avancés",
    "pricing_pro_feature_5": "Support technique prioritaire",
    "pricing_custom_feature_1": "Système Restaurant (KDS)",
    "pricing_custom_feature_2": "Système Hôtel (PMS)",
    "pricing_custom_feature_3": "Personnalisation complète",
    "pricing_custom_feature_4": "Intégration avec vos systèmes existants",
    "pricing_custom_feature_5": "Un prix adapté à vos besoins",
    "pricing_get_started": "Commencer",
    "pricing_choose_plan": "Choisir Forfait Business",
    "pricing_request_quote": "Demander un Devis",
    "contact_plans_title": "Pour les détails et tarifs",
    "contact_plans_text": "Veuillez nous contacter directement pour obtenir un devis personnalisé répondant à vos exigences professionnelles.",
    "contact_us_plans": "Contactez-nous",
    "learn_more": "En savoir plus",

    "// --- Custom Plan Content ---": "",
    "sub_custom_title": "Finio Custom",
    "sub_custom_tagline": "Solutions d'entreprise sur mesure pour environnements complexes",
    "sub_custom_pricing_contact": "Contactez-nous pour un devis",
    "sub_custom_everything_pro": "TOUT CE QUI EST DANS BUSINESS, PLUS :",

    "sub_custom_f1_title": "Système Hôtelier Complet (PMS Entreprise)",
    "sub_custom_f1_desc": "Chambres, occupation, services, gestion multi-départements et multi-propriétés.",
    "sub_custom_f2_title": "Intégration ERP Entreprise",
    "sub_custom_f2_desc": "Connectez Finio à vos systèmes ERP existants pour la comptabilité, l'inventaire, les RH et le reporting.",
    "sub_custom_f3_title": "Solutions Smart Store",
    "sub_custom_f3_desc": "Automatisation avancée du retail, appareils connectés et flux opérationnels personnalisés.",
    "sub_custom_f4_title": "Intégration Smart House & Propriété",
    "sub_custom_f4_desc": "Intégration avec systèmes IoT, contrôle d'accès, automatisation et infrastructure intelligente.",
    "sub_custom_f5_title": "Personnalisation Complète du Système",
    "sub_custom_f5_desc": "Modules, flux de travail et logique métier adaptés exactement à vos besoins.",
    "sub_custom_f6_title": "Connectivité Système & API Avancée",
    "sub_custom_f6_desc": "Intégrations sécurisées avec plateformes tierces et systèmes internes d'entreprise.",
    "sub_custom_f7_title": "Évolutivité Multi-sites & Entreprise",
    "sub_custom_f7_desc": "Conçu pour les grandes entreprises, chaînes et structures organisationnelles complexes.",
    "sub_custom_f8_title": "Analyse de Projet & Déploiement Dédiés",
    "sub_custom_f8_desc": "Étude technique, configuration et déploiement alignés sur vos exigences métier.",
    "sub_custom_f9_title": "Tarification Personnalisée & Support Long Terme",
    "sub_custom_f9_desc": "Un modèle de prix adapté à l'envergure, aux intégrations et au niveau de support.",

    "sub_custom_contact_text": "Contactez-nous pour une proposition personnalisée.",

    "// --- Subscription Page Enhanced Content ---": "",
    "sub_safety_header": "La sécurité et l'intégrité comptable sont incluses par défaut.",
    "sub_safety_desc": "Finio est bâti avec une comptabilité forte et une protection des données en son cœur. Les deux forfaits partagent la même fondation sécurisée — la différence réside dans le niveau de contrôle, de visibilité et d'évolutivité dont vous avez besoin.",
    "sub_safety_note": "Nous ne vendons pas la sécurité en option.",

    "sub_basic_title": "Finio Starter",
    "sub_basic_tagline": "Tout ce dont vous avez besoin pour vendre, gérer et clôturer votre journée en toute confiance.",
    "sub_basic_desc": "Finio Starter inclut l'expérience POS complète avec protections intégrées assurant que vos données sont toujours correctes et fiables.",
    "sub_included_features": "Fonctionnalités Incluses",
    "sub_basic_f1": "Point de Vente complet (retail, fast-food, restaurant)",
    "sub_basic_f2": "Gestion produits, clients et fournisseurs",
    "sub_basic_f3": "Scan code-barres et niveaux de prix multiples",
    "sub_basic_f4": "Remises, coupons et paniers sauvegardés",
    "sub_basic_f5": "Ouverture de caisse & fin de journée (Z / clôture)",
    "sub_basic_f6": "Création, impression et suivi de factures",
    "sub_basic_f7": "Remboursements sécurisés et gestion solde client",
    "sub_basic_f8": "Fonctionnement Offline-first avec synchro auto",
    "sub_basic_f9": "Factures immuables et comptabilité inviolable",
    "sub_basic_f10": "Synchronisation sécurisée multi-appareils",
    "sub_best_for": "Idéal Pour",
    "sub_basic_best_for": "Petites et moyennes entreprises qui gèrent les opérations quotidiennes directement depuis l'appli et veulent un POS fiable et conforme sans complexité.",

    "sub_pro_title": "Finio Business",
    "sub_pro_tagline": "Conçu pour la croissance, la structure et les flux comptables professionnels.",
    "sub_pro_desc": "Finio Business s'appuie sur tout ce qu'offre Starter et ajoute des modules avancés pour l'administration, la comptabilité et l'automatisation.",
    "sub_everything_basic_plus": "Tout ce qu'il y a dans Starter, plus :",
    "sub_pro_f1": "Panneau Web Comptable & Administration",
    "sub_pro_f1_detail": "Gérez salaires employés, dépenses, factures (B2C & B2B) et statistiques comptables depuis une interface web dédiée.",
    "sub_pro_f2": "Rapports financiers et analyses avancés",
    "sub_pro_f3": "Système d'Affichage Cuisine (KDS) avec postes et minuteurs",
    "sub_pro_f4": "Modules Hôtel / PMS (chambres, occupation, services)",
    "sub_pro_f5": "Commande Web (tablettes serveurs & auto-commande client)",
    "sub_pro_f6": "WhatsApp et notifications automatisées",
    "sub_pro_f7": "APIs Produits & intégration",
    "sub_pro_f8": "Synchronisation WooCommerce",
    "sub_pro_f9": "Outils pour la mise à l'échelle et opérations multi-flux",
    "sub_pro_f10": "Système d'Impression Avancé (Étiquette & Reçu)",
    "sub_pro_best_for": "Entreprises en croissance, restaurants avec cuisine, hôtels, environnements multi-appareils et sociétés travaillant avec comptables ou systèmes externes.",

    "sub_safety_compliance_title": "Note sur la sécurité & conformité",
    "sub_safety_compliance_intro": "Nous ne vendons pas la sécurité en option. BASIC et PRO incluent tous deux :",
    "sub_compliance_1": "Factures immuables",
    "sub_compliance_2": "Registre de paiement en ajout uniquement",
    "sub_compliance_3": "Paiements résistants aux pannes (Crash-safe)",
    "sub_compliance_4": "Comptabilité à période verrouillée",
    "sub_compliance_5": "Gestion correcte des espèces, banque et grand livre client",
    "sub_compliance_6": "Fondations prêtes pour comptabilité multi-pays",
    "sub_safety_difference": "La différence entre les forfaits est la capacité, pas le risque.",

    "sub_compare_title": "Comparaison des Fonctionnalités",
    "sub_compare_feature": "Fonctionnalité",
    "sub_cat_core_pos": "Fonctions POS Principales",
    "sub_cmp_pos": "Système Point de Vente Complet",
    "sub_cmp_products": "Gestion produits, clients & fournisseurs",
    "sub_cmp_barcode": "Scan code-barres & niveaux de prix",
    "sub_cmp_discounts": "Remises, coupons & paniers sauvegardés",
    "sub_cmp_eod": "Fin de journée (Z / clôture)",
    "sub_cmp_invoices": "Création & suivi factures",
    "sub_cmp_refunds": "Remboursements sécurisés & solde client",
    "sub_cmp_offline": "Offline-first avec synchro auto",
    "sub_cat_safety": "Sécurité & Conformité",
    "sub_cmp_immutable": "Factures immuables",
    "sub_cmp_ledger": "Registre de paiement en ajout uniquement",
    "sub_cmp_crash_safe": "Paiements résistants aux pannes",
    "sub_cmp_period_lock": "Comptabilité à période verrouillée",
    "sub_cat_advanced": "Fonctions Avancées (PRO Uniquement)",
    "sub_cmp_web_panel": "Panneau Web Comptable & Admin",
    "sub_cmp_advanced_reports": "Rapports financiers avancés",
    "sub_cmp_kds": "Système d'Affichage Cuisine (KDS)",
    "sub_cmp_hotel": "Modules Hôtel / PMS",
    "sub_cmp_web_ordering": "Commande Web (tablettes & auto-commande)",
    "sub_cmp_whatsapp": "Notifications WhatsApp",
    "sub_cmp_api": "APIs Produit & Intégration",
    "sub_cmp_woocommerce": "Synchronisation WooCommerce",
    "sub_cmp_print": "Système d'Impression Avancé",
    "sub_cmp_remote": "Accès à Distance (iOS & Android)",
    "sub_cmp_auth": "Auth Multi-locataire & Permissions",

    "sub_which_plan_title": "Quel forfait choisir ?",
    "sub_choose_basic": "Choisissez Starter si vous voulez un POS complet et sécurisé et gérez tout depuis l'appli.",
    "sub_choose_pro": "Choisissez Business si vous avez besoin d'administration comptable, modules avancés, automatisation ou intégrations.",
    "sub_choose_custom": "Une solution Finio entièrement personnalisée autour de vos systèmes, opérations et besoins d'entreprise. Finio Custom s'adapte à votre business — pas l'inverse.",

    "// --- 404 Page ---": "",
    "page_not_found_title": "Page Non Trouvée",
    "page_not_found_desc": "La page que vous recherchez a peut-être été supprimée, renommée ou est temporairement indisponible.",
    "back_to_home": "Retour à l'Accueil",

    "// --- Help Center ---": "",
    "help_welcome": "Bienvenue au Centre d'Aide Finio",
    "help_intro": "Trouvez des réponses aux questions fréquentes et obtenez le support nécessaire pour tirer le meilleur de Finio PRO.",
    "help_faq_title": "Questions Fréquentes (FAQ)",
    "help_faq_1_q": "Qu'est-ce que Finio PRO ?",
    "help_faq_1_a": "Finio PRO est une plateforme complète de gestion pour la restauration et l'hôtellerie incluant POS, KDS, gestion de stock, serveur web, PMS et systèmes d'impression avancés.",
    "help_faq_2_q": "Finio PRO fonctionne-t-il hors ligne ?",
    "help_faq_2_a": "Oui ! Finio PRO dispose d'une architecture offline-first qui maintient vos opérations fluides même sans internet. Les données se synchronisent automatiquement au retour de la connexion.",
    "help_faq_3_q": "De quel matériel ai-je besoin ?",
    "help_faq_3_a": "Finio PRO fonctionne avec le matériel POS standard incluant imprimantes tickets, scanners code-barres et tiroirs-caisses. Nous fournissons aussi des services de conseil et d'installation matériel.",
    "help_faq_4_q": "Mes données sont-elles sécurisées ?",
    "help_faq_4_a": "Vos données sont protégées avec une sécurité de niveau entreprise incluant cryptage, sauvegardes automatiques, permissions basées sur les rôles et isolation des données multi-locataires.",
    "help_faq_5_q": "Puis-je accéder au Panneau Comptable à distance ?",
    "help_faq_5_a": "Absolument ! Surveillez et gérez votre entreprise de n'importe où dans le monde grâce à nos applications mobiles iOS et Android dédiées.",
    "help_contact_support": "Besoin de plus d'aide ?",
    "help_contact_text": "Notre équipe support est là pour vous assister. Contactez-nous via :",
    "help_search_placeholder": "Rechercher des réponses...",
    "help_docs_desc": "Guides détaillés sur l'utilisation de chaque fonctionnalité Finio.",
    "help_faq_desc": "Réponses rapides aux questions courantes.",
    "help_security_desc": "Comment nous gardons vos données sûres et conformes.",
    "help_changelog_desc": "Dernières mises à jour et améliorations.",
    "contact_email_plain": "",
    "contact_phone_plain": "+212808646672",

    "// --- Terms & Conditions ---": "",
    "terms_last_updated": "Dernière mise à jour",
    "terms_date": "Novembre 2025",
    "terms_acceptance_title": "Acceptation des Conditions",
    "terms_acceptance_text": "En accédant et en utilisant les services Finio PRO, vous acceptez d'être lié par ces Conditions Générales. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser nos services.",
    "terms_services_title": "Description des Services",
    "terms_services_text": "Finio Solutions fournit une plateforme complète de gestion d'entreprise incluant point de vente, gestion d'inventaire, relation client et services associés. Nous nous réservons le droit de modifier, suspendre ou discontinuer toute partie de nos services à tout moment.",
    "terms_account_title": "Comptes Utilisateurs et Responsabilités",
    "terms_account_text": "Vous êtes responsable du maintien de la confidentialité de vos identifiants de compte et de toutes les activités qui se produisent sous votre compte. Vous acceptez de nous notifier immédiatement toute utilisation non autorisée de votre compte.",
    "terms_offline_title": "Mode Hors Ligne & Responsabilité des Données",
    "terms_offline_text": "Le mode hors ligne de Finio PRO stocke les données localement sur votre appareil. Vous êtes responsable d'assurer que votre appareil est synchronisé avec le cloud régulièrement. Finio Solutions n'est pas responsable des pertes de données causées par une panne ou perte d'appareil avant synchronisation.",
    "terms_payment_title": "Paiement et Abonnement",
    "terms_payment_text": "Les frais d'abonnement sont facturés à l'avance sur une base récurrente. Tous les frais sont non-remboursables sauf si la loi l'exige. Nous nous réservons le droit de changer nos tarifs avec un préavis de 30 jours aux abonnés actifs.",
    "terms_hardware_title": "Compatibilité Matériel & Imprimante",
    "terms_hardware_text": "Finio fonctionne avec les imprimantes ESC/POS standard et tablettes. Nous fournissons une liste de matériel recommandé mais ne garantissons pas la compatibilité avec tous les appareils tiers sauf certification explicite de notre part.",
    "terms_intellectual_title": "Propriété Intellectuelle",
    "terms_intellectual_text": "Tout contenu, fonctionnalité et fonction de Finio PRO sont la propriété de Finio Solutions et sont protégés par les lois internationales sur le droit d'auteur, les marques et autres propriétés intellectuelles.",
    "terms_limitation_title": "Limitation de Responsabilité",
    "terms_limitation_text": "Finio Solutions ne sera pas tenu responsable de tout dommage indirect, accessoire, spécial, consécutif ou punitif résultant de votre utilisation ou incapacité à utiliser le service.",
    "terms_termination_title": "Résiliation",
    "terms_termination_text": "Nous nous réservons le droit de résilier ou suspendre votre compte et l'accès à nos services à notre seule discrétion, sans préavis, pour une conduite que nous estimons violer ces Conditions ou être nuisible à d'autres utilisateurs, à nous ou à des tiers, ou pour toute autre raison.",
    "terms_governing_title": "Droit Applicable",
    "terms_governing_text": "Ces Conditions seront régies et interprétées conformément aux lois du Maroc, sans égard à ses dispositions sur les conflits de lois.",
    "terms_changes_title": "Modifications des Conditions",
    "terms_changes_text": "Nous nous réservons le droit de modifier ces conditions à tout moment. Nous notifierons les utilisateurs de tout changement matériel via email ou notre service. L'utilisation continue du service après de telles modifications constitue une acceptation des conditions mises à jour.",
    "terms_contact_title": "Informations de Contact",
    "terms_contact_text": "Pour toute question concernant ces Conditions Générales, veuillez nous contacter à :",

    "// --- Privacy Policy ---": "",
    "privacy_last_updated": "Dernière mise à jour",
    "privacy_date": "Novembre 2025",
    "privacy_intro_title": "Introduction",
    "privacy_intro_text": "Finio Solutions ('nous', 'notre' ou 'nos') respecte votre vie privée et s'engage à protéger vos données personnelles. Cette politique de confidentialité explique comment nous collectons, utilisons et sauvegardons vos informations lorsque vous utilisez les services Finio PRO.",
    "privacy_collection_title": "Informations que nous collectons",
    "privacy_collection_text": "Nous collectons plusieurs types d'informations pour fournir et améliorer nos services :",
    "privacy_collection_item1": "Informations d'identification personnelle (nom, adresse email, numéro de téléphone)",
    "privacy_collection_item2": "Informations d'entreprise (nom de l'entreprise, adresse, identification fiscale)",
    "privacy_collection_item3": "Données d'utilisation (comment vous interagissez avec nos services, fonctionnalités utilisées)",
    "privacy_collection_item4": "Données techniques (adresse IP, type de navigateur, informations sur l'appareil)",
    "privacy_pos_data_item": "Données transactionnelles (historique des ventes, niveaux de stock, achats clients) générées via le système POS",
    "privacy_pos_title": "Données POS & Transactionnelles",
    "privacy_pos_text": "Nous collectons et traitons les données transactionnelles pour vous fournir des rapports de vente, un suivi d'inventaire et des analyses commerciales. Ces données vous appartiennent. Nous n'utilisons pas vos données de vente à des fins marketing ni ne les partageons avec des tiers.",
    "privacy_offline_storage_title": "Stockage Local Hors Ligne",
    "privacy_offline_storage_text": "Lors de l'utilisation de Finio PRO hors ligne, les données sont stockées de manière sécurisée sur votre appareil local via une base de données cryptée. Ces données restent effectivement sous votre contrôle physique jusqu'à ce que l'appareil se reconnecte à internet et se synchronise avec nos serveurs cloud sécurisés.",
    "privacy_use_title": "Comment nous utilisons vos informations",
    "privacy_use_text": "Nous utilisons les informations collectées pour les objectifs suivants :",
    "privacy_use_item1": "Pour fournir, maintenir et améliorer nos services",
    "privacy_use_item2": "Pour traiter les transactions et envoyer les informations associées",
    "privacy_use_item3": "Pour envoyer des informations administratives, mises à jour et alertes de sécurité",
    "privacy_use_item4": "Pour répondre à vos demandes, questions et fournir le support client",
    "privacy_sharing_title": "Partage et Divulgation d'Informations",
    "privacy_sharing_text": "Nous ne vendons, n'échangeons ni ne louons vos informations personnelles à des tiers. Nous pouvons partager vos informations uniquement dans les circonstances suivantes : avec votre consentement, pour se conformer aux obligations légales, pour protéger nos droits et sécurité, ou avec des prestataires de services qui assistent nos opérations sous des accords de confidentialité stricts.",
    "privacy_security_title": "Sécurité des Données",
    "privacy_security_text": "Nous mettons en œuvre des mesures de sécurité aux normes industrielles pour protéger vos informations personnelles, incluant cryptage, serveurs sécurisés, audits de sécurité réguliers et contrôles d'accès. Cependant, aucune méthode de transmission sur internet n'est sécurisée à 100%.",
    "privacy_retention_title": "Conservation des Données",
    "privacy_retention_text": "Nous conservons vos informations personnelles uniquement aussi longtemps que nécessaire pour remplir les objectifs décrits dans cette politique de confidentialité, à moins qu'une période de conservation plus longue ne soit requise par la loi.",
    "privacy_rights_title": "Vos Droits de Confidentialité",
    "privacy_rights_text": "Vous avez le droit de :",
    "privacy_rights_item1": "Accéder et recevoir une copie de vos données personnelles",
    "privacy_rights_item2": "Demander la correction de données inexactes ou incomplètes",
    "privacy_rights_item3": "Demander la suppression de vos données personnelles",
    "privacy_rights_item4": "Vous opposer ou restreindre certains traitements de vos données",
    "privacy_cookies_title": "Cookies et Technologies de Suivi",
    "privacy_cookies_text": "Nous utilisons des cookies et technologies de suivi similaires pour suivre l'activité sur notre service et détenir certaines informations. Vous pouvez instruire votre navigateur pour refuser tous les cookies ou indiquer quand un cookie est envoyé.",
    "privacy_children_title": "Confidentialité des Enfants",
    "privacy_children_text": "Nos services ne sont pas destinés aux individus de moins de 18 ans. Nous ne collectons pas sciemment d'informations personnelles auprès d'enfants. Si vous pensez que nous avons collecté des informations d'un enfant, veuillez nous contacter immédiatement.",
    "privacy_changes_title": "Modifications de cette Politique de Confidentialité",
    "privacy_changes_text": "Nous pouvons mettre à jour notre Politique de Confidentialité de temps à autre. Nous vous notifierons de tout changement en publiant la nouvelle Politique sur cette page et en mettant à jour la date de 'Dernière mise à jour'.",
    "privacy_contact_title": "Contactez-nous",
    "privacy_contact_text": "Si vous avez des questions concernant cette Politique de Confidentialité, veuillez nous contacter à :",

    // --- Documentation ---
    // Docs Navigation
    "docs_nav_title": "Documentation",
    "docs_resources_title": "Ressources",
    "docs_nav_getting_started": "Commencer",
    "docs_nav_pos_usage": "Manuel d'utilisation POS",
    "docs_nav_kds_workflow": "Flux KDS",
    "docs_nav_pms_overview": "PMS & Hôtel",
    "docs_nav_offline_sync": "Hors ligne & Sync",
    "docs_nav_roles_permissions": "Rôles & Permissions",
    "docs_nav_finance_closures": "Factures & Clôtures",
    "docs_nav_hardware_printers": "Imprimantes & Matériel",
    "docs_nav_integrations": "Intégrations",
    "docs_nav_compliance": "Conformité & Juridique",
    "docs_nav_troubleshooting": "Dépannage",

    // Getting Started
    "docs_gs_title": "Commencer",
    "docs_gs_lead": "Bienvenue sur Finio, un système de point de vente et de gestion d'entreprise avancé hors ligne, conçu pour le commerce de détail, les restaurants, les cafés et l'hôtellerie.",
    "docs_gs_intro": "Finio combine le point de vente, l'affichage cuisine (KDS), l'inventaire, la comptabilité et le PMS hôtelier en une seule plateforme unifiée, avec une forte intégrité des données, une auditabilité et une synchronisation multi-appareils.",
    "docs_gs_access_title": "1. Accéder à Finio",
    "docs_gs_step_1": "<strong>Installez Finio</strong> sur votre appareil (Android / Bureau / Tablette).",
    "docs_gs_step_2": "<strong>Ouvrez l'application.</strong>",
    "docs_gs_step_3": "<strong>Connectez-vous</strong> avec votre compte locataire.",
    "docs_gs_step_4": "<strong>Sélectionnez votre magasin / succursale</strong> le cas échéant.",
    "docs_gs_offline_note": "<em>Finio fonctionne hors ligne par défaut. Internet n'est requis que pour la synchronisation cloud et le partage multi-appareils.</em>",
    "docs_gs_config_title": "2. Configuration du magasin",
    "docs_gs_config_intro": "Avant de commencer les ventes, configurez les paramètres de votre magasin :",
    "docs_gs_company_title": "Détails de l'entreprise",
    "docs_gs_company_go_to": "Aller à : <strong>Paramètres → Entreprise</strong>",
    "docs_gs_company_configure": "Configurer :",
    "docs_gs_company_list_1": "Nom de l'entreprise",
    "docs_gs_company_list_2": "Logo",
    "docs_gs_company_list_3": "Adresse",
    "docs_gs_company_list_4": "ID Fiscal (ICE / TVA)",
    "docs_gs_company_list_5": "Devise",
    "docs_gs_company_list_6": "Pays",
    "docs_gs_company_note": "Ces détails apparaissent sur les factures, les reçus et les exports comptables.",
    "docs_gs_taxes_title": "Taxes",
    "docs_gs_taxes_go_to": "Aller à : <strong>Paramètres → Taxes</strong>",
    "docs_gs_taxes_list_1": "Créer un ou plusieurs taux de taxe",
    "docs_gs_taxes_list_2": "Attribuer des taxes aux produits",
    "docs_gs_taxes_list_3": "Prend en charge : Prix TTC, Prix HT, Classes fiscales multiples",
    "docs_gs_taxes_note": "<strong>Note :</strong> Les taxes sont figées lors de la création de la facture pour garantir l'intégrité comptable.",
    "docs_gs_hardware_title": "Imprimantes et Matériel",
    "docs_gs_hardware_go_to": "Aller à : <strong>Paramètres → Matériel</strong>",
    "docs_gs_hardware_list_1": "Découvrir les imprimantes réseau",
    "docs_gs_hardware_list_2": "Attribuer des imprimantes pour : Reçus, Tickets cuisine, Bar / stations",
    "docs_gs_hardware_list_3": "Configurer les modèles d'impression par station",
    "docs_gs_hardware_note": "<em>Les modèles d'imprimante sont versionnés et récupérables en toute sécurité.</em>",
    "docs_gs_adding_title": "3. Ajout de produits",
    "docs_gs_adding_go_to": "Aller à : <strong>Inventaire → Produits</strong>",
    "docs_gs_adding_intro": "Vous pouvez ajouter des produits de trois manières :",
    "docs_gs_manual_title": "Saisie manuelle",
    "docs_gs_manual_list_1": "Nom, Catégorie, Prix (multi-prix pris en charge)",
    "docs_gs_manual_list_2": "Taxe, Suivi des stocks",
    "docs_gs_manual_list_3": "Image, Modificateurs et variantes",
    "docs_gs_csv_title": "Import CSV (Plan Pro)",
    "docs_gs_csv_list_1": "Importer des produits en masse",
    "docs_gs_csv_list_2": "Catégories créées automatiquement",
    "docs_gs_csv_list_3": "Prend en charge les variantes et les prix",
    "docs_gs_types_title": "Types de produits avancés",
    "docs_gs_types_list_1": "Produits simples",
    "docs_gs_types_list_2": "Produits à variantes (taille, couleur, etc.)",
    "docs_gs_types_list_3": "Produits composites (recettes / lots)",
    "docs_gs_types_list_4": "Produits de stock uniquement (non vendables)",
    "docs_pos_title": "Votre première vente (POS)",
    "docs_pos_lead": "Le traitement des ventes dans Finio est rapide et sécurisé. Suivez ces étapes pour effectuer votre première transaction.",
    "docs_pos_steps_title": "Étapes pour vendre",
    "docs_pos_step_1": "<strong>Ouvrez le POS</strong> depuis le menu principal.",
    "docs_pos_step_2": "<strong>Sélectionnez des produits</strong> depuis la grille ou scannez un code-barres.",
    "docs_pos_step_3": "<strong>Choisissez un client</strong> (optionnel) depuis la barre supérieure.",
    "docs_pos_step_4": "Appuyez sur <strong>Payer</strong>.",
    "docs_pos_step_5": "<strong>Sélectionnez le mode de paiement :</strong>",
    "docs_pos_method_1": "Espèces",
    "docs_pos_method_2": "Carte",
    "docs_pos_method_3": "Banque",
    "docs_pos_method_4": "Solde client",
    "docs_pos_method_5": "Chèque",
    "docs_pos_method_6": "Paiements mixtes",
    "docs_pos_step_6": "<strong>Confirmer le paiement</strong>.",
    "docs_pos_step_7": "<strong>Le reçu s'imprime automatiquement</strong> (si configuré).",
    "docs_pos_note": "<strong>Note :</strong> Chaque vente crée une facture immuable pour la sécurité comptable.",
    "docs_pos_features_title": "Fonctionnalités clés",
    "docs_pos_feat_multi": "<strong>Support multi-paiement :</strong> Divisez facilement les factures entre espèces et carte.",
    "docs_pos_feat_hold": "<strong>Mise en attente et récupération :</strong> Sauvegardez les commandes pour plus tard si un client n'est pas prêt.",
    "docs_pos_feat_offline": "<strong>Mode hors ligne :</strong> Continuez à vendre même sans internet ; les données se synchronisent automatiquement une fois en ligne.",

    "docs_hardware_title": "Imprimantes et Impression",
    "docs_hardware_lead": "Configurez les reçus, les tickets de cuisine et le routage des imprimantes pour des opérations fluides.",
    "docs_hardware_features_title": "Fonctionnalités d'impression",
    "docs_hardware_feat_receipts": "<strong>Reçus :</strong> Reçus client automatiques lors du paiement.",
    "docs_hardware_feat_kitchen": "<strong>Tickets cuisine :</strong> Envoyez les commandes directement à la cuisine ou au bar.",
    "docs_hardware_feat_routing": "<strong>Routage par station :</strong> Acheminez des articles spécifiques (ex : boissons vs nourriture) vers différentes imprimantes.",
    "docs_hardware_feat_templates": "<strong>Modèles :</strong> Personnalisez les mises en page d'impression avec l'éditeur de modèles.",
    "docs_hardware_feat_queue": "<strong>Surveillance de la file d'attente :</strong> Suivez les tâches d'impression et leur statut.",
    "docs_hardware_supported_title": "Matériel supporté",
    "docs_hardware_bt": "<strong>Imprimantes Bluetooth :</strong> Pour les configurations mobiles et sur tablette.",
    "docs_hardware_network": "<strong>Imprimantes réseau (LAN) :</strong> Connexion Ethernet standard pour la fiabilité.",
    "docs_hardware_recovery_note": "<strong>Fonctionnalité :</strong> La récupération automatique garantit que les tâches d'impression ne sont pas perdues si une imprimante est temporairement hors ligne.",
    "docs_int_title": "Intégrations",
    "docs_int_lead": "Connectez Finio à des outils tiers pour étendre votre écosystème.",
    "docs_int_web_title": "Intégrations Web",
    "docs_int_api": "<strong>API Produit :</strong> Synchronisez les produits et l'inventaire avec des systèmes externes.",
    "docs_int_kds": "<strong>KDS Web :</strong> Accès au système d'affichage cuisine via navigateur.",
    "docs_int_pms": "<strong>PMS Web :</strong> Interface du système de gestion immobilière.",
    "docs_int_feedback": "<strong>Feedback Web :</strong> Collecte des retours clients.",
    "docs_int_waiter": "<strong>Serveur / Self-Order :</strong> Commande mobile pour le personnel ou les clients.",
    "docs_int_arch_title": "Architecture de service",
    "docs_int_arch_intro": "Tous les services web sont conçus pour la sécurité et la performance :",
    "docs_int_local": "<strong>Exécution locale :</strong> Exécutez les services localement pour une faible latence.",
    "docs_int_auth": "<strong>Authentifié :</strong> Accès sécurisé via les identifiants du locataire.",
    "docs_int_rate": "<strong>Limité en taux :</strong> Protection de l'ID contre les abus.",
    "docs_int_limited": "<strong>Isolé par locataire :</strong> Séparation des données pour les environnements multi-locataires.",
    "docs_comp_title": "Comptabilité et Conformité",
    "docs_comp_lead": "Modèle comptable de base : Comment Finio protège vos données financières.",
    "docs_comp_model_title": "Modèle comptable de base",
    "docs_comp_immutable_title": "Factures immuables",
    "docs_comp_immutable_desc": "Une fois une facture finalisée, elle ne peut être ni modifiée ni supprimée. Elle existe en permanence dans le grand livre.",
    "docs_comp_append_title": "Grand livre en ajout seulement",
    "docs_comp_append_desc": "Les paiements et les mises à jour de solde sont additifs. Nous n'écrasons jamais un enregistrement ; nous ajoutons seulement de nouvelles entrées compensatoires.",
    "docs_comp_safety_title": "Sécurité des transactions",
    "docs_comp_safety_desc": "Toutes les opérations sont atomiques. En cas de panne, le système récupère un état cohérent sans perte de données.",
    "docs_sec_title": "Sécurité et Protection des Données",
    "docs_sec_lead": "Finio utilise un modèle de sécurité \"Audit-First\" conçu pour la conformité financière et l'intégrité des données.",
    "docs_sec_enc_title": "Chiffrement des données",
    "docs_sec_enc_intro": "Nous traitons vos données d'affaires comme des actifs financiers sensibles.",
    "docs_sec_table_state": "État",
    "docs_sec_table_method": "Méthode",
    "docs_sec_table_detail": "Détail",
    "docs_sec_rest_cloud": "<strong>Au repos (Cloud)</strong>",
    "docs_sec_rest_cloud_desc": "Stockage chiffré Google Cloud Firestore.",
    "docs_sec_rest_device": "<strong>Au repos (Appareil)</strong>",
    "docs_sec_rest_device_desc": "Les fichiers de base de données locaux sont chiffrés pour empêcher l'extraction depuis des tablettes volées.",
    "docs_sec_transit": "<strong>En transit</strong>",
    "docs_sec_transit_desc": "Tout le trafic de synchronisation circule via des canaux HTTPS sécurisés.",
    "docs_sec_rbac_title": "Contrôle d'accès basé sur les rôles (RBAC)",
    "docs_sec_rbac_intro": "Les permissions sont strictement appliquées au niveau de l'interface utilisateur et de l'API.",
    "docs_sec_role_owner": "<strong>Propriétaire :</strong> Accès complet à tous les paramètres, la comptabilité et les suppressions.",
    "docs_sec_role_manager": "<strong>Manager :</strong> Peut annuler des articles et lancer des rapports de fin de journée, mais ne peut pas supprimer l'historique.",
    "docs_sec_role_cashier": "<strong>Caissier :</strong> Peut créer des ventes et encaisser des paiements. Ne peut pas annuler des commandes envoyées sans approbation.",
    "docs_sec_role_waiter": "<strong>Serveur :</strong> Limité à la création de commandes uniquement. Pas d'accès au paiement ou au tiroir.",
    "docs_sec_audit_title": "Journaux d'audit immuables",
    "docs_sec_audit_intro": "Chaque action critique déclenche une entrée permanente dans la collection <code>audit_log</code>. Ce journal est visible dans le panneau web du comptable.",
    "docs_sec_audit_events": "<strong>Événements journalisés :</strong> Annulations, Remboursements, Remises (>10%), Ouvertures de tiroir, Changements de prix.",
    "docs_sec_audit_meta": "<strong>Métadonnées :</strong> ID utilisateur, Horodatage exact, ID appareil, Localisation GPS (optionnel).",
    "docs_sec_alert_box": "<strong>Système d'alerte :</strong> Les modèles suspects (ex : 5 annulations en 1 minute) déclenchent des notifications automatiques \"Drapeau Rouge\" au propriétaire de l'entreprise via WhatsApp.",
    "docs_ts_title": "Dépannage",
    "docs_ts_lead": "Trouvez des solutions rapides aux problèmes opérationnels courants.",
    "docs_ts_issues_title": "Problèmes courants",
    "docs_ts_printer_title": "L'imprimante n'imprime pas",
    "docs_ts_printer_1": "<strong>Vérifier le réseau :</strong> Assurez-vous que l'imprimante est connectée au même Wi-Fi/LAN.",
    "docs_ts_printer_2": "<strong>Redémarrer le service :</strong> Redémarrez le service d'impression Finio ou l'imprimante elle-même.",
    "docs_ts_printer_3": "<strong>Réassigner le modèle :</strong> Allez dans Paramètres → Matériel et vérifiez l'assignation de l'imprimante.",
    "docs_ts_sync_title": "La synchronisation ne fonctionne pas",
    "docs_ts_sync_1": "<strong>Vérifier internet :</strong> Vérifiez que votre appareil a une connexion active.",
    "docs_ts_sync_2": "<strong>Sync automatique :</strong> La synchronisation reprend automatiquement une fois en ligne ; aucune action manuelle nécessaire.",
    "docs_ts_sync_3": "<strong>Sécurité des données :</strong> Vos données sont en sécurité localement et seront téléchargées dès que possible.",
    "docs_ts_balance_title": "Solde de caisse erroné",
    "docs_ts_balance_1": "<strong>Sessions :</strong> Vérifiez si la bonne session de caisse est ouverte.",
    "docs_ts_balance_2": "<strong>Remboursements :</strong> Vérifiez si des remboursements ont été traités après le début de la session.",
    "docs_ts_balance_3": "<strong>Fin de journée :</strong> Consultez les rapports de clôture de fin de journée pour les écarts.",
    "docs_ts_help_box": "<strong>Besoin d'aide supplémentaire ?</strong> Contactez le support à <a href=\"mailto:support\u0040finio.pro\">support\u0040finio.pro</a>.",

    // Compliance Extended (French)
    "docs_comp_intro_title": "Comment fonctionne la comptabilité dans Finio",
    "docs_comp_intro_p1": "Finio n'est pas un \"simple POS\". C'est un système de transaction sécurisé sur le plan comptable, conçu pour protéger les données financières contre la manipulation, les erreurs, les pannes et les conflits entre appareils.",
    "docs_comp_intro_p2": "Cette section explique comment fonctionne la comptabilité Finio, pourquoi certaines actions sont limitées et comment cela protège votre entreprise lors des audits.",
    "docs_comp_invoice_title": "Qu'est-ce qu'une facture ?",
    "docs_comp_invoice_p1": "Une facture dans Finio est un document comptable légal, pas un brouillon.",
    "docs_comp_invoice_frozen": "Lorsqu'une facture est créée, les éléments suivants sont gelés définitivement :",
    "docs_comp_invoice_list_1": "Prix des produits",
    "docs_comp_invoice_list_2": "Taxes",
    "docs_comp_invoice_list_3": "Remises",
    "docs_comp_invoice_list_4": "Totaux",
    "docs_comp_immutability_title": "Immutabilité des factures (Règle critique)",
    "docs_comp_immutability_intro": "Une fois créé :",
    "docs_comp_immutability_list_1": "Vous ne pouvez pas modifier les totaux",
    "docs_comp_immutability_list_2": "Vous ne pouvez pas changer la taxe",
    "docs_comp_immutability_list_3": "Vous ne pouvez pas modifier les articles",
    "docs_comp_immutability_why": "<strong>Pourquoi ?</strong> Parce que la modification des factures après leur création brise les pistes d'audit, invalide les rapports fiscaux et crée un risque de fraude.",
    "docs_comp_immutability_compliance": "Ceci est intentionnel et requis pour la conformité.",
    "docs_comp_payments_title": "Paiements : Grand livre en ajout seul",
    "docs_comp_payments_intro": "Finio ne modifie pas les paiements. Au lieu de cela, il utilise un grand livre de paiement en ajout seul.",
    "docs_comp_payments_how_title": "Comment fonctionnent les paiements",
    "docs_comp_payments_list_1": "Chaque paiement est ajouté comme un nouvel enregistrement",
    "docs_comp_payments_list_2": "Les paiements ne sont jamais écrasés",
    "docs_comp_payments_list_3": "Les remboursements sont de nouvelles entrées dans le grand livre, pas des modifications",
    "docs_comp_payments_guarantee": "Cela garantit un historique complet des paiements, une résistance à la falsification et une synchronisation multi-appareils sécurisée.",
    "docs_comp_example_title": "Exemple",
    "docs_comp_table_action": "Action",
    "docs_comp_table_result": "Ce que fait Finio",
    "docs_comp_table_row_1_a": "Le client paie en espèces",
    "docs_comp_table_row_1_b": "Ajoute une entrée de paiement",
    "docs_comp_table_row_2_a": "Chèque validé plus tard",
    "docs_comp_table_row_2_b": "Ajoute une entrée de changement de statut",
    "docs_comp_table_row_3_a": "Remboursement émis",
    "docs_comp_table_row_3_b": "Ajoute une entrée de remboursement",
    "docs_comp_table_row_4_a": "Conflit de synchronisation",
    "docs_comp_table_row_4_b": "Fusionne les entrées en toute sécurité",
    "docs_comp_refunds_title": "Remboursements (Pas de modification de facture)",
    "docs_comp_refunds_intro": "Remboursements dans Finio :",
    "docs_comp_refunds_list_1": "Ne modifient PAS la facture",
    "docs_comp_refunds_list_2": "Ne suppriment PAS les paiements",
    "docs_comp_refunds_list_3": "Créent une entrée négative dans le grand livre",
    "docs_comp_refunds_why": "<strong>Pourquoi cela est important :</strong> La vente originale reste visible, le remboursement est clairement suivi et les auditeurs peuvent reconstituer l'historique complet.",
    "docs_comp_refunds_box": "<strong>Routage :</strong> Les remboursements en espèces réduisent le tiroir-caisse. Les remboursements par carte réduisent le solde bancaire. Le crédit magasin augmente le solde du client.",
    "docs_comp_eod_title": "Périodes clôturées et protection de l'audit",
    "docs_comp_eod_intro": "Une fois qu'une fin de journée (EOD) est terminée :",
    "docs_comp_eod_list_1": "La période comptable est verrouillée",
    "docs_comp_eod_list_2": "Les transactions passées ne peuvent pas être modifiées",
    "docs_comp_eod_list_3": "Les remboursements après EOD sont enregistrés comme de nouveaux événements",
    "docs_comp_eod_prevent": "Cela empêche la manipulation antidatée, les corrections cachées et les incohérences d'audit.",
    "docs_comp_trust_title": "Pourquoi les comptables font confiance à Finio",
    "docs_comp_trust_intro": "Finio applique des contrôles comptables d'entreprise, pas des limitations de POS :",
    "docs_comp_trust_list_1": "Factures immuables",
    "docs_comp_trust_list_2": "Paiements en ajout seul",
    "docs_comp_trust_list_3": "Verrouillage de période",
    "docs_comp_trust_list_4": "Espèces liées à la caisse",
    "docs_comp_trust_list_5": "Protection de l'heure serveur",
    "docs_comp_reg_title": "Caisses enregistreuses et rapprochement",
    "docs_comp_reg_what_title": "Qu'est-ce qu'une caisse enregistreuse dans Finio ?",
    "docs_comp_reg_what_desc": "Une caisse enregistreuse représente un tiroir-caisse physique. Chaque mouvement d'espèces doit être lié à une caisse, lié à une session ouverte et suivi par période. Cela garantit la responsabilité de la trésorerie.",
    "docs_comp_float_title": "Fond de caisse",
    "docs_comp_float_step_1": "Ouvrir la caisse",
    "docs_comp_float_step_2": "Entrer le montant d'ouverture",
    "docs_comp_float_desc": "Cela devient la base de référence pour le rapprochement.",
    "docs_comp_z_title": "Fin de journée (Clôture Z)",
    "docs_comp_z_intro": "À la fin de la journée ou du quart de travail :",
    "docs_comp_z_step_1": "Effectuer la clôture de fin de journée",
    "docs_comp_z_step_2": "Finio calcule : Espèces attendues, Espèces réelles, Différence, Marge",
    "docs_comp_z_step_3": "La période est fermée et verrouillée",
    "docs_comp_z_after": "Après EOD :",
    "docs_comp_z_list_1": "Vous ne pouvez pas modifier les ventes au comptant passées",
    "docs_comp_z_list_2": "Vous ne pouvez pas antidater les paiements",
    "docs_comp_z_list_3": "Une nouvelle activité commence une nouvelle période",
    "docs_comp_reg_box": "<strong>Pourquoi les espèces sont strictement liées :</strong> Sans lien de caisse, les espèces ne sont pas suivies et le vol ne peut pas être détecté. Finio bloque les paiements en espèces sans caisse ouverte et contexte de session.",
    "docs_comp_offline_title": "Comportement hors ligne et multi-appareils",
    "docs_comp_offline_first_title": "Hors ligne par conception",
    "docs_comp_offline_first_desc": "Finio fonctionne sans internet. Toutes les actions sont enregistrées localement d'abord, synchronisées en toute sécurité plus tard, et ne bloquent jamais les ventes.",
    "docs_comp_drops_title": "Que se passe-t-il si Internet coupe ?",
    "docs_comp_drops_desc": "Rien ne se casse. Les ventes continuent, les paiements sont enregistrés et la synchronisation reprend automatiquement. Aucune perte de données.",
    "docs_comp_multi_title": "Sécurité multi-appareils (Très important)",
    "docs_comp_multi_scenario": "<strong>Scénario :</strong> Deux appareils, même facture.",
    "docs_comp_multi_list_1": "L'appareil A ajoute un paiement",
    "docs_comp_multi_list_2": "L'appareil B ajoute un autre paiement",
    "docs_comp_multi_list_3": "Les deux se synchronisent plus tard",
    "docs_comp_multi_merge": "Finio fusionne les paiements, il n'écrase pas. Cela est possible car les paiements sont en ajout seul et les factures sont versionnées.",
    "docs_comp_time_title": "Protection de l'heure de l'appareil",
    "docs_comp_time_desc": "Finio utilise une heure alignée sur le serveur pour détecter la manipulation de l'horloge, empêcher l'antidatage et protéger les périodes comptables.",
    "docs_comp_faq_title": "FAQ (Comptabilité et Confiance)",
    "docs_comp_faq_1_q": "Puis-je modifier une facture après paiement ?",
    "docs_comp_faq_1_a": "Non. Les factures sont immuables pour protéger l'intégrité comptable et les audits.",
    "docs_comp_faq_2_q": "Puis-je supprimer un paiement ?",
    "docs_comp_faq_2_a": "Non. Les paiements sont des écritures du grand livre et ne peuvent pas être supprimés.",
    "docs_comp_faq_3_q": "Que se passe-t-il si je rembourse après la fin de la journée ?",
    "docs_comp_faq_3_a": "Un nouvel enregistrement de remboursement est créé dans la période suivante. Les périodes passées restent verrouillées.",
    "docs_comp_faq_4_q": "Deux caissiers peuvent-ils utiliser la même caisse ?",
    "docs_comp_faq_4_a": "Oui, mais pas en même temps. Chaque caisse a des sessions contrôlées.",
    "docs_comp_faq_5_q": "Que faire si l'heure de l'appareil est incorrecte ?",
    "docs_comp_faq_5_a": "Finio détecte et empêche la manipulation de l'heure.",
    "docs_comp_faq_6_q": "Finio est-il sûr pour les contrôles fiscaux ?",
    "docs_comp_faq_6_a": "Oui. Il applique les règles comptables requises par les auditeurs et les autorités fiscales.",
    "docs_comp_faq_7_q": "Les comptables peuvent-ils accéder aux données à distance ?",
    "docs_comp_faq_7_a": "Oui. Via le panneau Web comptable, pas le POS.",
    "docs_comp_panel_title": "Panneau Web Comptable",
    "docs_comp_panel_desc": "Une plateforme web séparée pour les comptables et les gestionnaires. Ce n'est PAS un POS.",
    "docs_comp_panel_what": "Ce que les comptables peuvent faire",
    "docs_comp_panel_list_1": "Voir les factures",
    "docs_comp_panel_list_2": "Examiner les paiements et les remboursements",
    "docs_comp_panel_list_3": "Suivre les dépenses",
    "docs_comp_panel_list_4": "Gérer la paie et les salaires",
    "docs_comp_panel_list_5": "Analyser les marges et les flux de trésorerie",
    "docs_comp_panel_list_6": "Exporter les données",
    "docs_comp_panel_list_7": "Surveiller plusieurs succursales",
    "docs_comp_panel_remote": "Tout à distance, sans accéder aux appareils POS.",
    "docs_comp_panel_box": "<strong>Pourquoi cela est important :</strong> Séparation des tâches (pas d'accès caissier aux outils comptables) et flux de travail d'audit professionnel. C'est une fonctionnalité d'entreprise majeure.",
    "docs_comp_int_title": "Intégrations et ressources pour développeurs",
    "docs_comp_api_title": "API Produit",
    "docs_comp_api_desc": "Pour les systèmes ERP, les scanners d'inventaire, les applications externes et les systèmes d'entrepôt.",
    "docs_comp_api_sec": "<strong>Modèle de sécurité :</strong> Appairage d'appareils, authentification par jeton, isolation des locataires, limitation du débit. Aucun accès inter-locataires n'est possible.",
    "docs_comp_woo_title": "Intégration WooCommerce",
    "docs_comp_woo_list_1": "Synchroniser les produits de Finio → WooCommerce",
    "docs_comp_woo_list_2": "Envoyer les commandes de WooCommerce → Finio",
    "docs_comp_woo_limits": "<strong>Limitations importantes :</strong> Synchronisation des produits unidirectionnelle, les commandes nécessitent une approbation, la synchronisation des stocks dépend de la configuration.",
    "docs_comp_local_title": "Serveurs Web locaux (Avancé)",
    "docs_comp_local_desc": "Finio inclut des serveurs web intégrés pour les tablettes serveurs, les bornes de commande automatique, l'accès web PMS et les formulaires de commentaires. Tous sont authentifiés, basés sur le réseau local et limités en débit.",
    "docs_comp_sec_title": "Modèle de sécurité",
    "docs_comp_sec_list_1": "Isolation des locataires",
    "docs_comp_sec_list_2": "Permissions basées sur les rôles",
    "docs_comp_sec_list_3": "Application de l'heure serveur",
    "docs_comp_sec_list_4": "Chiffrement hors ligne",
    "docs_comp_sec_list_5": "Aucune donnée partagée entre les entreprises",

    // Offline Sync (French)
    "docs_offline_title": "Architecture Hors Ligne et Synchronisation",
    "docs_offline_lead": "Le système distribué de Finio assure une disponibilité de 100% grâce à une architecture de base de données Hive locale.",
    "docs_offline_hive_title": "Base de données locale (Hive)",
    "docs_offline_hive_desc": "Finio ne dépend pas d'une connexion cloud constante. Au lieu de cela, il exécute une base de données NoSQL complète (Hive) directement sur votre matériel.",
    "docs_offline_hive_list_1": "<strong>Vitesse de lecture/écriture :</strong> Opérations instantanées sans latence (< 16 ms).",
    "docs_offline_hive_list_2": "<strong>Souveraineté des données :</strong> Votre catalogue complet de produits, liste de clients et historique des transactions résident sur votre appareil.",
    "docs_offline_hive_list_3": "<strong>Chiffrement :</strong> Les données locales sont chiffrées au repos selon les normes AES-256.",
    "docs_offline_why_box": "<strong>Pourquoi c'est important :</strong> La plupart des systèmes POS web cessent de fonctionner lorsque le WiFi tombe. Finio continue de traiter les ventes, d'imprimer des reçus et d'ouvrir les tiroirs-caisses car la logique est locale.",
    "docs_offline_queue_title": "La file d'attente de synchronisation Delta",
    "docs_offline_queue_desc": "Lorsque vous effectuez une action (par exemple, Clôturer une facture), Finio l'écrit dans une \"File d'attente de synchronisation\" persistante.",
    "docs_offline_queue_step_1": "<strong>Engagement de l'action :</strong> La transaction est enregistrée instantanément dans le grand livre local.",
    "docs_offline_queue_step_2": "<strong>Entrée en file d'attente :</strong> Une charge utile JSON de la mutation est ajoutée à la table <code>sync_queue</code>.",
    "docs_offline_queue_step_3": "<strong>Worker d'arrière-plan :</strong> Un processus d'arrière-plan tente de vider la file d'attente vers le cloud.",
    "docs_offline_queue_step_4": "<strong>Logique de réessai :</strong> Si le réseau est en panne, le worker dort et réessaie avec un backoff exponentiel.",
    "docs_offline_conflict_title": "Stratégie de résolution de conflits",
    "docs_offline_conflict_desc": "Dans un environnement multi-appareils, Finio utilise une <strong>Stratégie de fusion déterministe</strong> pour gérer les collisions.",
    "docs_offline_append_title": "Grand livre en ajout seul",
    "docs_offline_append_desc": "Nous n'écrasons jamais les enregistrements de paiement. Si l'appareil A reçoit 50 $ et l'appareil B reçoit 20 $ pour la même facture :",
    "docs_offline_append_list_1": "L'appareil A engage l'ID de paiement #101 (50 $).",
    "docs_offline_append_list_2": "L'appareil B engage l'ID de paiement #102 (20 $).",
    "docs_offline_append_list_3": "Le serveur Cloud accepte les deux.",
    "docs_offline_append_list_4": "Le solde de la facture est mis à jour pour refléter 70 $ payés.",
    "docs_offline_time_title": "Heure faisant autorité sur le serveur",
    "docs_offline_time_desc": "Pour prévenir la fraude (par exemple, changer l'horloge de l'appareil pour antidater les ventes), tous les événements de synchronisation sont marqués avec <code>server_timestamp</code> à l'arrivée. Les rapports financiers utilisent cette heure serveur pour la vérité.",

    // KDS Workflow (French)
    "docs_kds_title": "Système d'affichage de cuisine (KDS)",
    "docs_kds_lead": "Optimisez vos opérations de cuisine avec un suivi des commandes en temps réel.",
    "docs_kds_overview_title": "Aperçu",
    "docs_kds_overview_desc": "Allez à : <strong>KDS</strong> depuis le menu principal.",
    "docs_kds_list_1": "<strong>Commandes en temps réel :</strong> Les commandes apparaissent instantanément dès qu'elles sont passées.",
    "docs_kds_list_2": "<strong>Groupement par station :</strong> Les commandes sont acheminées vers des stations spécifiques (Cuisine, Bar, Grill).",
    "docs_kds_list_3": "<strong>Minuteurs :</strong> Suivez le temps de préparation pour chaque article.",
    "docs_kds_list_4": "<strong>Multi-écran :</strong> Prise en charge de plusieurs écrans d'affichage sur différentes stations.",
    "docs_kds_flow_title": "Flux de statut",
    "docs_kds_flow_desc": "Les commandes passent par les étapes suivantes :",
    "docs_kds_flow_step_1": "<strong>Nouveau :</strong> Commande reçue.",
    "docs_kds_flow_step_2": "<strong>En préparation :</strong> Le personnel accuse réception de la commande.",
    "docs_kds_flow_step_3": "<strong>Prêt :</strong> L'article est cuit et prêt à servir.",
    "docs_kds_flow_step_4": "<strong>Servi :</strong> Commande terminée.",
    "docs_kds_note_box": "<strong>Note :</strong> KDS fonctionne localement via LAN, assurant la fonctionnalité même sans internet.",

    // PMS Overview (French)
    "docs_pms_title": "Module PMS & Hôtel",
    "docs_pms_lead": "Gérez efficacement les chambres, les réservations et les clients grâce à notre système de gestion immobilière intégré.",
    "docs_pms_key_title": "Fonctionnalités clés",
    "docs_pms_key_desc": "Allez à : <strong>PMS</strong> depuis le menu principal.",
    "docs_pms_list_1": "<strong>Gestion des chambres :</strong> Aperçu visuel de l'état et de la disponibilité des chambres.",
    "docs_pms_list_2": "<strong>Arrivée / Départ :</strong> Flux d'arrivée et de départ des clients simplifiés.",
    "docs_pms_list_3": "<strong>Suivi de l'occupation :</strong> Surveillez les taux d'occupation en temps réel.",
    "docs_pms_list_4": "<strong>Facturation intégrée :</strong> Les frais de chambre sont automatiquement liés aux factures des clients.",
    "docs_pms_list_5": "<strong>Ménage :</strong> Gérez les horaires de nettoyage et l'entretien des chambres.",
    "docs_pms_designed_title": "Conçu pour",
    "docs_pms_designed_1": "Hôtels",
    "docs_pms_designed_2": "Riads",
    "docs_pms_designed_3": "Maisons d'hôtes",
    "docs_pms_designed_4": "Locations de courte durée",

    // Roles & Permissions (French)
    "docs_roles_title": "Rôles et autorisations",
    "docs_roles_lead": "Contrôlez l'accès et assurez la sécurité avec une gestion granulaire des rôles.",
    "docs_roles_manage_title": "Gestion des rôles",
    "docs_roles_manage_desc": "Allez dans : <strong>Paramètres → Rôles et autorisations</strong>",
    "docs_roles_list_1": "<strong>Créer des rôles personnalisés :</strong> Définissez des rôles uniques adaptés aux besoins de votre entreprise (par exemple, Caissier, Manager, Comptable).",
    "docs_roles_list_2": "<strong>Autorisations granulaires :</strong> Attribuez des droits spécifiques à chaque rôle.",
    "docs_roles_scopes_title": "Portées des autorisations",
    "docs_roles_scopes_desc": "Les autorisations peuvent être appliquées à divers modules :",
    "docs_roles_scopes_1": "<strong>PDV :</strong> Accès à la vente, annulation d'articles, application de remises.",
    "docs_roles_scopes_2": "<strong>Inventaire :</strong> Ajout de produits, ajustements de stocks.",
    "docs_roles_scopes_3": "<strong>Comptabilité :</strong> Consultation des rapports, clôture des quarts de travail.",
    "docs_roles_scopes_4": "<strong>Paramètres :</strong> Modification des configurations du système.",

    // Finance Closures (French)
    "docs_finance_title": "Factures et Clôtures",
    "docs_finance_lead": "Suivez vos finances, gérez les factures et sécurisez votre trésorerie.",
    "docs_faq_title": "Foire Aux Questions",
    "docs_faq_subtitle": "Réponses aux questions courantes sur Finio POS, KDS et la plateforme de gestion.",
    "docs_faq_cat_pos": "POS & Ventes",
    "docs_faq_q_offline_sell": "Puis-je vendre des produits sans connexion Internet ?",
    "docs_faq_a_offline_sell": "Oui. Finio est un système <strong>offline-first</strong>. Toutes les ventes, clôtures Z et mouvements de table sont enregistrés localement. Le système synchronise automatiquement tout vers le cloud une fois la connexion rétablie.",
    "docs_faq_q_prices": "Le POS supporte-t-il plusieurs niveaux de prix ?",
    "docs_faq_a_prices": "Oui, vous pouvez configurer plusieurs niveaux de prix (ex: Happy Hour, VIP, Personnel) et basculer instantanément lors d'une vente.",
    "docs_faq_q_split": "Puis-je diviser l'addition ou fusionner des tables ?",
    "docs_faq_a_split": "Absolument. Vous pouvez diviser les paiements par montant ou par article, et fusionner facilement des tables ou transférer des articles.",
    "docs_faq_cat_sync": "Mode Hors Ligne & Sync",
    "docs_faq_q_break": "Que se passe-t-il si ma tablette casse hors ligne ?",
    "docs_faq_a_break": "Finio est conçu pour protéger vos données même hors ligne. Lorsque plusieurs appareils sont connectés sur le même réseau local, ils partagent et synchronisent automatiquement les données entre eux hors ligne. Cela signifie que vos données sont stockées localement sur plus d'un appareil. Si un appareil est endommagé avant la synchronisation, les données restent disponibles sur les autres appareils du réseau. Une fois qu'un appareil se reconnecte à Internet, toutes les données sont automatiquement synchronisées vers le cloud.",
    "docs_faq_q_status": "Comment savoir si je suis en ligne ou hors ligne ?",
    "docs_faq_a_status": "Un indicateur en haut à droite de l'écran POS montre votre statut : Vert pour En ligne, et Gris/Rouge pour Hors ligne.",
    "docs_faq_cat_billing": "Abonnements & Facturation",
    "docs_faq_q_plan": "Puis-je changer mon forfait plus tard ?",
    "docs_faq_a_plan": "Oui, vous pouvez passer de Starter à Business à tout moment. Le solde restant de votre abonnement actuel sera calculé au prorata.",
    "docs_faq_q_cancel": "Y a-t-il des frais d'annulation ?",
    "docs_faq_a_cancel": "Non. Vous pouvez annuler votre abonnement à tout moment. Votre accès restera actif jusqu'à la fin de la période de facturation en cours.",
    "docs_faq_cat_hardware": "Matériel",
    "docs_faq_q_printers": "Quelles imprimantes sont compatibles ?",
    "docs_faq_a_printers": "Finio supporte la plupart des imprimantes réseau ESC/POS (Ethernet/WiFi), incluant des marques comme Epson, Star Micronics et Xprinter.",
    "docs_faq_q_tablet": "Puis-je utiliser ma tablette actuelle ?",
    "docs_faq_a_tablet": "Oui, Finio fonctionne sur toute tablette moderne iOS (iPad) ou Android. Nous recommandons des appareils avec au moins 3 Go de RAM.",
    "docs_faq_cat_security": "Sécurité & Données",
    "docs_faq_q_access": "Qui a accès à mes données de vente ?",
    "docs_faq_a_access": "Seulement vous et les utilisateurs autorisés. Le personnel Finio ne peut accéder à vos données opérationnelles sans permission explicite pour le support.",
    "docs_faq_q_legal": "Mes factures sont-elles conformes légalement ?",
    "docs_faq_a_legal": "Oui. Finio génère des factures immuables et numérotées séquentiellement conformes aux régulations fiscales. Une fois finalisée, une facture ne peut être modifiée ou supprimée.",
    "docs_finance_invoices_title": "Factures",
    "docs_finance_invoices_go_to": "Allez à : <strong>Factures</strong>",
    "docs_finance_invoices_history": "<strong>Voir l'historique :</strong> Accédez à toutes les factures passées.",
    "docs_finance_invoices_filtering": "<strong>Filtrage :</strong> Filtrez par date, statut, caisse ou utilisateur.",
    "docs_finance_invoices_actions": "<strong>Actions :</strong> Imprimer ou exporter des factures PDF. Le partage public de factures est également pris en charge.",
    "docs_finance_important_rules": "Règles importantes :",
    "docs_finance_rule_1": "Les totaux des factures sont immuables une fois créés.",
    "docs_finance_rule_2": "Les paiements sont en ajout seul (ne peuvent pas être supprimés, seulement compensés).",
    "docs_finance_rule_3": "Les remboursements ne réécrivent jamais l'historique ; ils créent de nouvelles contre-transactions.",
    "docs_finance_registers_title": "Caisses enregistreuses",
    "docs_finance_registers_sessions": "<strong>Sessions :</strong> Chaque caisse fonctionne par sessions.",
    "docs_finance_registers_tracking": "<strong>Suivi :</strong> Les paiements en espèces doivent être liés à une session de caisse ouverte.",
    "docs_finance_registers_security": "<strong>Sécurité :</strong> Empêche les mouvements d'espèces non suivis et renforce la responsabilité.",
    "docs_finance_closures_title": "Fond de caisse et Clôture de fin de journée (Z Closure)",
    "docs_finance_closures_start": "<strong>Début de quart :</strong> Définissez le montant du fond de caisse.",
    "docs_finance_closures_end": "<strong>Fin de quart :</strong> Effectuez la clôture de fin de journée.",
    "docs_finance_closures_reconciliation": "<strong>Rapprochement :</strong> Le système calcule le montant attendu vs réel et met en évidence les différences.",
    "docs_finance_closures_locking": "<strong>Verrouillage :</strong> Les périodes clôturées sont verrouillées et ne peuvent pas être modifiées.",

    // --- Changelog ---
    "nav_changelog_title": "Journal des Modifications",
    "changelog_version": "Version 1.0 → Version 2.0",
    "changelog_overview_title": "Aperçu",
    "changelog_overview_p1": "Finio Version 1.0 était une version pilote, déployée auprès d'un nombre limité de clients sélectionnés dans divers secteurs (restaurants, commerce de détail, hôtellerie et services).",
    "changelog_overview_p2": "L'objectif de la v1.0 n'était pas l'échelle, mais l'apprentissage.",
    "changelog_overview_p3": "Grâce à une collaboration directe avec de vraies entreprises, nous avons observé le comportement des systèmes de caisse sous pression opérationnelle : utilisation hors ligne, erreurs du personnel, remboursements, perte de stock, écarts de caisse et utilisation multi-appareils.",
    "changelog_overview_p4": "La Version 2.0 est le résultat de cet apprentissage.",
    "changelog_overview_p5": "Ce n'est pas une itération — c'est une mise à niveau structurelle, conçue pour :",
    "changelog_overview_list_1": "Minimiser le risque opérationnel",
    "changelog_overview_list_2": "Empêcher la manipulation silencieuse des données",
    "changelog_overview_list_3": "Réduire le vol et les erreurs comptables",
    "changelog_overview_list_4": "Identifier les points faibles des opérations",
    "changelog_overview_list_5": "Préparer les entreprises à croître sur des bases solides",

    "changelog_v1_title": "Version 1.0 — Phase Pilote (Apprentissage Fondamental)",
    "changelog_v1_purpose_title": "Objectif",
    "changelog_v1_purpose_1": "Valider l'utilisation Offline-first",
    "changelog_v1_purpose_2": "Observer le comportement réel du personnel",
    "changelog_v1_purpose_3": "Comprendre les points de défaillance des flux POS existants",
    "changelog_v1_purpose_4": "Identifier où les entreprises perdent de l'argent sans s'en rendre compte",

    "changelog_v1_char_title": "Caractéristiques",
    "changelog_v1_char_1": "Fonctionnalités POS de base",
    "changelog_v1_char_2": "Support hors ligne basique",
    "changelog_v1_char_3": "Suivi des stocks et des ventes",
    "changelog_v1_char_4": "Flux de réconciliation manuelle",
    "changelog_v1_char_5": "Protection limitée contre la mutation des données",
    "changelog_v1_char_6": "Rapports réactifs (ce qui s'est passé, pas pourquoi)",

    "changelog_findings_title": "Principales constatations des clients pilotes",
    "changelog_findings_intro": "En usage réel, nous avons identifié des problèmes récurrents :",
    "changelog_findings_1": "Factures modifiées après paiement",
    "changelog_findings_2": "Paiements écrasés ou perdus lors de la synchronisation",
    "changelog_findings_3": "Produits à fort taux de remboursement masquant des problèmes de stock ou de qualité",
    "changelog_findings_4": "Écarts de caisse sans cause claire",
    "changelog_findings_5": "Conflits multi-appareils causant des incohérences de données",
    "changelog_findings_6": "Entreprises passant à l'échelle avant de corriger les faiblesses internes",
    "changelog_findings_outro": "Ces observations ont directement façonné la Version 2.0.",

    "changelog_v2_title": "Version 2.0 — Production & Contrôle des Risques",
    "changelog_v2_desc": "La Version 2.0 a été reconstruite avec le contrôle, l'intégrité et la prévention comme principes fondamentaux.",

    "changelog_acc_title": "Comptabilité et Intégrité des Données (Mise à jour majeure)",
    "changelog_acc_inv_title": "Factures Immuables",
    "changelog_acc_inv_1": "Une fois une facture émise, les totaux financiers ne peuvent être modifiés.",
    "changelog_acc_inv_2": "Empêche les modifications silencieuses, la manipulation ou les changements post-paiement.",
    "changelog_acc_inv_3": "Garantit des registres comptables sécurisés pour l'audit.",

    "changelog_acc_pay_title": "Registre de Paiement en Ajout Uniquement",
    "changelog_acc_pay_1": "Les paiements ne sont jamais écrasés ou supprimés.",
    "changelog_acc_pay_2": "Chaque paiement, remboursement ou changement de statut est enregistré comme une nouvelle entrée.",
    "changelog_acc_pay_3": "Traçabilité complète de toutes les actions financières.",

    "changelog_acc_atomic_title": "Transactions Atomiques",
    "changelog_acc_atomic_1": "Les paiements sont traités via des opérations atomiques sécurisées.",
    "changelog_acc_atomic_2": "Pas d'états partiels si l'appareil s'éteint en pleine transaction.",
    "changelog_acc_atomic_3": "Élimine les paiements fantômes et les soldes incohérents.",

    "changelog_acc_time_title": "Application de l'Heure Serveur",
    "changelog_acc_time_1": "Empêche la manipulation de l'horloge de l'appareil.",
    "changelog_acc_time_2": "Tous les événements financiers suivent une chronologie fiable et monotone.",

    "changelog_off_title": "Fiabilité Hors Ligne et Multi-Appareils",
    "changelog_off_design_title": "Offline-First par Conception",
    "changelog_off_design_1": "Fonctionnement complet sans internet.",
    "changelog_off_design_2": "Aucune dégradation des fonctionnalités hors ligne.",

    "changelog_off_local_title": "Synchronisation Réseau Locale",
    "changelog_off_local_1": "Les appareils sur le même réseau local partagent les données hors ligne.",
    "changelog_off_local_2": "Crée une redondance locale entre les appareils.",
    "changelog_off_local_3": "Les données survivent même si un appareil est endommagé.",

    "changelog_off_sync_title": "Synchronisation Multi-Appareils Sans Conflit",
    "changelog_off_sync_1": "Paiements et factures sont fusionnés en toute sécurité entre les appareils.",
    "changelog_off_sync_2": "Aucune transaction perdue lors de l'utilisation de plusieurs terminaux.",
    "changelog_off_sync_3": "L'autorité du serveur résout les conflits sans écraser l'historique.",

    "changelog_off_cloud_title": "Récupération Cloud Automatique",
    "changelog_off_cloud_1": "Dès qu'un appareil se reconnecte à internet, toutes les données se synchronisent automatiquement.",

    "changelog_inv_title": "Inventaire, Remboursements et Prévention des Pertes",
    "changelog_inv_refund_title": "Intelligence des Remboursements",
    "changelog_inv_refund_1": "Les remboursements sont traités comme des annulations financières, pas comme des dépenses génériques.",
    "changelog_inv_refund_2": "Lien clair entre remboursements, produits et moyens de paiement.",

    "changelog_inv_risk_title": "Détection des Risques Produits",
    "changelog_inv_risk_1": "Identification des produits avec : Taux de remboursement élevés, plaintes fréquentes ou écarts de stock.",
    "changelog_inv_risk_2": "Aide à détecter : Mauvais fournisseurs, problèmes de qualité ou fraude.",

    "changelog_inv_stock_title": "Amélioration de l'Intégrité du Stock",
    "changelog_inv_stock_1": "Meilleur suivi des articles endommagés, retournés et non vendables.",
    "changelog_inv_stock_2": "Réduction de la dérive des stocks au fil du temps.",

    "changelog_health_title": "Santé de l'Entreprise et Aide à la Décision (Nouveau)",
    "changelog_health_ind_title": "Indicateurs de Santé",
    "changelog_health_ind_1": "Signaux d'alerte pour : remboursements excessifs, marges réduites, déséquilibre de trésorerie, fuite de stock.",
    "changelog_health_ind_2": "Conçu pour corriger les problèmes avant l'échelle, pas après l'échec.",

    "changelog_health_ops_title": "Visibilité Opérationnelle",
    "changelog_health_ops_1": "Séparation claire entre : performance des ventes, problèmes opérationnels, incohérences comptables.",

    "changelog_cash_title": "Contrôle de Caisse et Réconciliation",
    "changelog_cash_bind_title": "Liaison de Caisse",
    "changelog_cash_bind_1": "Tout mouvement d'espèces doit être lié à une session de caisse active.",
    "changelog_cash_bind_2": "Empêche les opérations en espèces non traçables.",

    "changelog_cash_eod_title": "Périodes Comptables Fermées (Verrouillage EOD)",
    "changelog_cash_eod_1": "Une fois la journée clôturée, les données historiques sont protégées.",
    "changelog_cash_eod_2": "Empêche la manipulation rétroactive.",
    "changelog_cash_eod_3": "Garantit une réconciliation quotidienne propre.",

    "changelog_plat_title": "Améliorations de la Plateforme et de l'Architecture",
    "changelog_plat_mod_title": "Maturité du Système Modulaire",
    "changelog_plat_mod_1": "POS, KDS, Inventaire, PMS, Livraison, Panneau Comptable entièrement alignés.",
    "changelog_plat_mod_2": "Séparation claire des rôles (Caissier, Manager, Comptable, Admin).",

    "changelog_plat_web_title": "Panneaux Web Améliorés",
    "changelog_plat_web_1": "Accès comptable et gestion pour : Dépenses, Salaires, Factures B2B, Statistiques financières, Supervision multi-sites.",

    "changelog_reps_title": "Ce que représente la Version 2.0",
    "changelog_reps_intro": "Finio v2.0 est conçu pour les entreprises qui veulent :",
    "changelog_reps_list_1": "Croître sans perdre le contrôle",
    "changelog_reps_list_2": "Prévenir le vol et les erreurs silencieuses",
    "changelog_reps_list_3": "Détecter les mauvais produits tôt",
    "changelog_reps_list_4": "Faire confiance à leurs chiffres",
    "changelog_reps_list_5": "Grandir sur une base opérationnelle saine",
    "changelog_reps_outro": "Ce n'est plus juste un POS — c'est une plateforme d'intégrité commerciale.",

    "changelog_future_title": "Vers l'Avenir",
    "changelog_future_1": "La Version 2.0 établit une fondation non négociable.",
    "changelog_future_2": "Les futures versions se construiront sur cette intégrité, pas autour.",

  },
  es: {
    "home": "Inicio",
    "products": "Nuestros Módulos",
    "subscriptions": "Suscripciones",
    "features": "Funcionalidades",
    "coming_soon": "Innovaciones",
    "contact_us": "Contacto",
    "hero_title": "El POS Offline-First con Integridad de Grado Contable",
    "hero_subtitle": "Creado para empresas que exigen precisión. Cuenta con facturas inmutables, libro de pagos de solo adición y sincronización multidispositivo a prueba de conflictos.",
    "book_demo": "Explorar la Plataforma",
    "hero_badge_new": "Finio 2.0: Ahora con Seguridad de Auditoría Mejorada",

    "// --- Value Pillars ---": "",
    "pillar_offline_title": "Verdadera Arquitectura Offline-First",
    "pillar_offline_desc": "No es solo soporte 'intermitente'. Finio almacena el 100% de los datos localmente y sincroniza sin conflictos cuando está en línea. Cero tiempo de inactividad.",
    "pillar_integrity_title": "Núcleo Contable Inmutable",
    "pillar_integrity_desc": "Las facturas no pueden alterarse tras su creación. Los pagos son de solo adición. Su integridad financiera está garantizada matemáticamente.",
    "pillar_conflict_title": "Transacciones Atómicas",
    "pillar_conflict_desc": "Las operaciones a prueba de fallos aseguran que si un dispositivo falla a mitad de una transacción, sus datos permanezcan consistentes y sin corrupción.",
    "pillar_modular_title": "Periodos Contables Cerrados",
    "pillar_modular_desc": "El bloqueo estricto de Fin del Día evita la manipulación retroactiva de periodos financieros históricos.",

    "// --- Why Finio Section ---": "",
    "why_finio_title": "Por qué Finio no es un POS típico",
    "why_finio_subtitle": "La mayoría de los sistemas POS son solo calculadoras. Finio es un motor contable.",

    "why_finio_card_1_title": "Calculadoras vs. Libros Mayores",
    "why_finio_card_1_desc": "Los sistemas POS genéricos tratan las ventas como números temporales. Finio trata cada venta como un registro financiero permanente e inmutable.",

    "why_finio_card_2_title": "Dependiente de la Nube vs. Offline-First",
    "why_finio_card_2_desc": "Otros dejan de funcionar cuando cae internet. Finio opera localmente con integridad de grado servidor, sincronizando solo cuando es estable.",

    "why_finio_card_3_title": "Exceso de Funciones vs. Precisión",
    "why_finio_card_3_desc": "No perseguimos funciones que comprometan la fiabilidad. Cada módulo está construido para asegurar que sus números coincidan con la realidad.",

    "// --- Special Offer Section ---": "",
    "home_offer_badge": "Oferta Limitada para Primeros Suscriptores",
    "home_offer_title": "Sea de los primeros y obtenga 3 meses gratis",
    "home_offer_desc": "Únase a los primeros 100 suscriptores y obtenga 3 meses gratis en cualquier plan.",
    "home_offer_btn": "Obtener la Oferta",

    "// --- Solutions Section ---": "",
    "solutions_title": "Soluciones a medida para cada industria",
    "solution_restaurant_title": "Restaurantes y Cafeterías",
    "solution_restaurant_desc": "Gestione pedidos, cocina, entregas y reservas desde una única plataforma.",
    "solution_retail_title": "Tiendas Minoristas",
    "solution_retail_desc": "Optimice el inventario, las ventas y la fidelidad del cliente con facilidad.",
    "solution_service_title": "Empresas de Servicios",
    "solution_service_desc": "Soluciones de reservas, facturación y CRM para proveedores de servicios y hoteles.",

    "// --- Modules Section ---": "",
    "our_modules_title": "Un ecosistema modular listo para producción",

    "// 1. Sales & Operations": "",
    "mod_sales_title": "Ventas y Operaciones",
    "mod_pos_title": "Sistema POS",
    "mod_pos_desc": "Punto de venta offline-first de alta velocidad con escaneo de códigos de barras, gestión de múltiples carritos y sincronización sin conflictos.",
    "mod_kds_title": "Sistema de Pantalla de Cocina (KDS)",
    "mod_kds_desc": "Enlace directo entre la sala y la cocina. Enrutamiento de estaciones en tiempo real, temporizadores y seguimiento del estado del pedido.",
    "mod_delivery_title": "Entregas y Pedidos",
    "mod_delivery_desc": "Gestione plataformas de entrega, conductores y pedidos telefónicos eficientemente desde un único panel.",

    "// 2. Accounting & Finance": "",
    "mod_finance_title": "Contabilidad y Finanzas",
    "mod_invoice_title": "Motor de Facturación",
    "mod_invoice_desc": "Genere facturas inmutables y fiscalmente conformes. Rastree pagos, depósitos e historial de crédito del cliente.",
    "mod_cash_title": "Gestión de Efectivo",
    "mod_cash_desc": "Fondo de caja seguro, cierres ciegos y conciliación precisa de efectivo para prevenir robos y errores.",
    "mod_expense_title": "Gastos y Capital",
    "mod_expense_desc": "Rastree gastos de caja chica, pagos a proveedores y costos operativos directamente desde la caja registradora.",

    "// 3. Management & Back-Office": "",
    "mod_mgmt_title": "Gestión y Back-Office",
    "mod_inventory_title": "Control de Inventario",
    "mod_inventory_desc": "Seguimiento de stock en tiempo real con registros de movimiento, alertas de stock bajo y órdenes de compra a proveedores.",
    "mod_crm_title": "Clientes y Proveedores",
    "mod_crm_desc": "Perfiles CRM detallados con historial de compras, puntos de fidelidad y libros mayores de proveedores.",
    "mod_admin_title": "Panel Web para Contadores",
    "mod_admin_desc": "Acceso remoto para contadores para auditar ventas, exportar libros diarios y gestionar la configuración.",

    "// 4. Hospitality & Advanced": "",
    "mod_hosp_title": "Hostelería y Avanzado",
    "mod_floor_title": "Gestión de Sala",
    "mod_floor_desc": "Mapa visual de mesas con estado de ocupación, gestión de reservas y asignación de camareros.",
    "mod_pms_title": "Módulo de Hotel y PMS",
    "mod_pms_desc": "Gestión hotelera completa: reserva de habitaciones, limpieza e integración de check-in/out de huéspedes.",
    "mod_api_title": "Ecosistema de API Abierta",
    "mod_api_desc": "Conecte aplicaciones de terceros, quioscos de autopedido y plataformas de pedidos web a través de nuestra API segura.",

    "// --- Legacy Keys (Restored for Compatibility) ---": "",
    "pos_module_title": "Motor POS Central",
    "pos_module_description": "Núcleo transaccional nativo offline. Maneja pagos divididos, auditoría de anulaciones y gestión de sesiones sin dependencia de internet.",
    "kds_module_title": "Sistema de Producción",
    "kds_module_description": "Enrutamiento de latencia cero. Los pedidos se envían desde el POS y se garantiza su entrega a las estaciones de preparación con temporizadores visuales.",
    "inventory_module_title": "Inventario Perpetuo",
    "inventory_module_description": "Seguimiento de costos FIFO con deducción de recetas. Soporta compuestos anidados complejos y flujos de trabajo de transferencia de economato.",
    "pms_module_title": "Gestión de Propiedades",
    "pms_module_description": "Gestión de folios integrada con POS. Los cargos a la habitación son transacciones atómicas validadas contra el estado del huésped.",
    "printing_module_title": "Servidor de Enrutamiento de Impresión",
    "printing_module_description": "Enrutamiento de impresión basado en reglas. Soporta re-enrutamiento por fallos, hardware ESC/POS heredado e inyección de plantillas personalizadas.",
    "web_server_module_title": "Comando Central",
    "web_server_module_description": "Agregación en tiempo real de datos multisitio. Envíe actualizaciones de menú, extraiga registros de auditoría y gestione permisos de usuario globalmente.",

    "// --- Services Section ---": "",
    "professional_services_title": "Servicios Profesionales",
    "custom_software_title": "Desarrollo de Software a Medida",
    "custom_software_description": "Ofrecemos soluciones de software personalizadas adaptadas a las necesidades únicas de su negocio, desde aplicaciones web hasta móviles.",
    "hardware_software_title": "Soluciones de Hardware y Software",
    "hardware_software_description": "Proporcionamos una integración perfecta de hardware y software para ejecutar sus sistemas de manera eficiente, incluyendo soporte e instalación.",
    "consulting_title": "Consultoría y Soluciones Integradas",
    "consulting_description": "Nuestro equipo ofrece consultoría integral para ayudarle a elegir e implementar las mejores soluciones tecnológicas para su proyecto.",

    "// --- Core Features Section ---": "",
    "core_features_title": "Arquitectura del Sistema y Capacidades",

    "// 1. Accounting-Grade Protection": "",
    "feat_protect_title": "Núcleo Financiero Inmutable",
    "feat_protect_desc": "Registro de libro mayor de doble entrada con verificación de solo adición. Los datos históricos están bloqueados matemáticamente contra la manipulación.",

    "// 2. Offline-First Sync Engine": "",
    "feat_sync_engine_title": "Replicación Sin Conflictos",
    "feat_sync_engine_desc": "Arquitectura de base de datos local-primero (Hive). Las operaciones se confirman localmente al instante y se replican en la nube con consistencia eventual.",

    "// 3. Trusted Time & Audit Safety": "",
    "feat_audit_title": "Tiempo Autorizado por el Servidor",
    "feat_audit_desc": "Las transacciones llevan marca de tiempo por autoridad del servidor para prevenir la manipulación del reloj local y el fraude en el cierre.",

    "// 4. Professional Closures": "",
    "feat_closure_title": "Cierres de Día Profesionales",
    "feat_closure_desc": "Gestione fondos de apertura, realice cierres Z ciegos y concilie efectivo con seguimiento automatizado de variaciones.",

    "// 5. Roles & Permissions": "",
    "feat_roles_title": "Control de Acceso Basado en Roles (RBAC)",
    "feat_roles_desc": "Defina niveles de acceso exactos para cajeros, gerentes, contadores y personal de cocina con puertas de permiso granulares.",

    "// 6. Integrations & APIs": "",
    "feat_integrations_title": "Integraciones y APIs",
    "feat_integrations_desc": "Conéctese con ERPs, WooCommerce y pasarelas de pago a través de nuestra extensa API para desarrolladores.",

    // --- Todo incluido ---
    "all_features_title": "Todo incluido",
    "all_features_subtitle": "Funciones potentes ya integradas en Finio — más allá de lo esencial.",
    "all_group_revenue": "Ingresos y Crecimiento",
    "all_revenue_giftcards_title": "Tarjetas de regalo",
    "all_revenue_giftcards_desc": "Emita, recargue y anule tarjetas de regalo, con control de caja y panel de pasivos en vivo.",
    "all_revenue_loyalty_title": "Programa de fidelidad",
    "all_revenue_loyalty_desc": "Niveles Bronce, Plata y Oro con acumulación, canje y caducidad de puntos.",
    "all_revenue_coupons_title": "Cupones y Promociones",
    "all_revenue_coupons_desc": "Límites de uso, campañas programadas y estadísticas de ROI por cupón.",
    "all_revenue_loans_title": "Crédito y Préstamos",
    "all_revenue_loans_desc": "Préstamos y límites de crédito B2B con seguimiento de saldos.",
    "all_group_ops": "Operaciones y Prevención de Pérdidas",
    "all_ops_shifts_title": "Turnos y Horarios",
    "all_ops_shifts_desc": "Turnos semanales recurrentes, detección de conflictos y recordatorios push.",
    "all_ops_loss_title": "Suite antipérdidas",
    "all_ops_loss_desc": "Registro de mermas, inventario/recuento cíclico y reembolso + reemplazo — deje de perder dinero.",
    "all_ops_fefo_title": "FEFO y Lotes",
    "all_ops_fefo_desc": "Seguimiento de caducidad y lotes con costeo primero en caducar, primero en salir.",
    "all_ops_transfers_title": "Transferencias multi-sede",
    "all_ops_transfers_desc": "Mueva stock entre sedes y almacenes con trazabilidad total.",
    "all_ops_barcode_title": "Impresión de etiquetas",
    "all_ops_barcode_desc": "Genere e imprima etiquetas de código de barras y precios.",
    "all_ops_purchasing_title": "Compras y Proveedores",
    "all_ops_purchasing_desc": "Órdenes de compra y libros de proveedores para un back-office completo.",
    "all_group_ai": "IA y Experiencia del Cliente",
    "all_ai_translation_title": "Traducción con IA",
    "all_ai_translation_desc": "Traduzca automáticamente nombres y descripciones de productos.",
    "all_ai_enrichment_title": "Enriquecimiento con ChatGPT",
    "all_ai_enrichment_desc": "Enriquezca el catálogo con descripciones, categorías y atributos.",
    "all_ai_perplexity_title": "Investigación con Perplexity",
    "all_ai_perplexity_desc": "Obtenga información de productos y mercado directamente en Finio.",
    "all_ai_waiter_title": "Llamar al camarero",
    "all_ai_waiter_desc": "Los clientes llaman al personal en tiempo real con vista de sala en alerta roja.",
    "all_ai_feedback_title": "Opiniones + QR",
    "all_ai_feedback_desc": "Recoja reseñas mediante formulario web y código QR.",
    "all_ai_kiosk_title": "Kiosco / Autopedido",
    "all_ai_kiosk_desc": "Kiosco de autoservicio y modo de autopedido para locales concurridos.",
    "all_ai_map_title": "Mapa del restaurante",
    "all_ai_map_desc": "Planos arrastrar y soltar con soporte multi-planta y multi-sede.",
    "all_ai_glovo_title": "Marketplace Glovo",
    "all_ai_glovo_desc": "Reciba pedidos de Glovo directamente en su POS.",
    "all_group_finance": "Back-office y Finanzas",
    "all_fin_fiscal_title": "Cumplimiento fiscal",
    "all_fin_fiscal_desc": "fiskaly SIGN ES y fiskaltrust (AT/DE/FR) para fiscalización certificada.",
    "all_fin_dunning_title": "Recordatorios de cobro",
    "all_fin_dunning_desc": "Recordatorios de pago automáticos para saldos vencidos.",
    "all_fin_payroll_title": "Nóminas y Salarios",
    "all_fin_payroll_desc": "Gestione salarios y genere PDF de nóminas desde el panel del contable.",
    "all_fin_docs_title": "Cadena documental",
    "all_fin_docs_desc": "Reembolso + reemplazo, cuotas de documentos y la cadena BC → BL → FA → AV.",
    "all_group_platform": "Plataforma y Fiabilidad",
    "all_plat_lan_title": "Redundancia LAN sin conexión",
    "all_plat_lan_desc": "Operación multidispositivo sin conexión en su red local, con sincronización automática.",
    "all_plat_desktop_title": "Aplicaciones de escritorio",
    "all_plat_desktop_desc": "Aplicaciones nativas para Windows, macOS y Linux, además de Web, iOS y Android.",

    // --- Comparativa: ventajas reales PRO ---
    "sub_cat_pro_only": "Solo PRO",
    "sub_cmp_refund_replace": "Reembolso + Reemplazo",
    "sub_cmp_quota": "Gestión de cuota de documentos",
    "sub_cmp_doc_chain": "Cadena documental (BC → BL → FA → AV)",
    "sub_cmp_whatsapp_invoice": "Facturas por WhatsApp",
    "sub_cmp_max_registers": "Máximo de cajas registradoras",
    "sub_cmp_reconciliation": "Conciliación de caja",
    "sub_cmp_customer_pdf": "Estados de cuenta PDF",
    "sub_cmp_stock_reports": "Informes de stock",
    "sub_cmp_delivery_orders": "Pedidos de reparto",
    "sub_cmp_store_orders": "Pedidos web / tienda",
    "sub_cmp_health_date": "Informes por rango (salud)",
    "sub_cmp_liquid_capital": "PDF de capital líquido",

    "// Legacy keys": "",
    "feature_auth_title": "Autenticación Multi-Inquilino",
    "feature_auth_desc": "Autenticación segura basada en roles diseñada para una arquitectura multi-inquilino, asegurando el aislamiento y la seguridad de los datos.",
    "feature_offline_title": "Confirmación Local de Latencia Cero",
    "feature_offline_desc": "Mantenga sus operaciones funcionando sin problemas, incluso sin conexión a internet. Los datos se sincronizan automáticamente una vez que vuelve a estar en línea.",
    "feature_sync_title": "Sincronización Delta en Segundo Plano",
    "feature_sync_desc": "Aprovecha Firebase y un potente servicio de sincronización para asegurar que los datos estén siempre actualizados en todos los dispositivos y módulos.",
    "feature_remote_title": "Panel Web de Contador y Admin",
    "feature_remote_desc": "Acceso remoto seguro para contadores y administradores para auditar ventas, diarios y configuración.",
    "feature_reporting_title": "Estados Financieros Conciliados",
    "feature_reporting_desc": "Obtenga información crítica con análisis completos sobre ventas, rendimiento de productos y comportamiento del cliente.",
    "feature_security_title": "Control de Acceso Basado en Roles (RBAC)",
    "feature_security_desc": "Desde puertas de permiso en la UI hasta copias de seguridad automáticas y encriptación, sus datos siempre están protegidos.",
    "feature_pms_rooms": "Gestión de Habitaciones y Ocupación",
    "feature_pms_booking": "Reserva de Huéspedes y CRM",
    "feature_print_network": "Descubrimiento de Impresoras en Red",
    "feature_print_custom": "Plantillas de Recibos Personalizables",

    "// --- Innovations Section ---": "",
    "innovations_title": "Soluciones Preparadas para el Futuro",
    "innovations_intro": "Sea de los primeros en experimentar el futuro de la automatización empresarial. Únase a nuestro programa de adopción temprana y dé forma a la próxima generación de soluciones empresariales inteligentes.",
    "smart_store_title": "Tienda Inteligente Finio",
    "smart_store_desc": "Habilite una experiencia sin pasar por caja con cámaras impulsadas por IA, dispositivos de autocompra y detección automatizada de robos.",
    "smart_store_feature_1_title": "Cámaras Impulsadas por IA",
    "smart_store_feature_1_desc": "La visión por computadora avanzada rastrea los movimientos de los clientes y las selecciones de productos en tiempo real.",
    "smart_store_feature_2_title": "Dispositivos de Autocobro",
    "smart_store_feature_2_desc": "Los clientes escanean y pagan los artículos independientemente, reduciendo tiempos de espera y requisitos de personal.",
    "smart_store_feature_3_title": "Detección Automatizada de Robos",
    "smart_store_feature_3_desc": "Algoritmos de aprendizaje automático identifican comportamientos sospechosos y alertan a la seguridad en tiempo real.",
    "smart_store_feature_4_title": "Seguimiento Inteligente de Inventario",
    "smart_store_feature_4_desc": "Rastree automáticamente los niveles de stock a medida que se recogen los artículos, asegurando recuentos de inventario precisos.",
    "smart_airbnb_title": "Finio Smart Airbnb",
    "smart_airbnb_desc": "Automatice la gestión de su propiedad con confirmaciones de reserva por IA, entrada con cerradura inteligente y códigos de acceso dinámicos.",
    "smart_airbnb_feature_1_title": "Asistente de Reservas con IA",
    "smart_airbnb_feature_1_desc": "Respuestas automatizadas a consultas, confirmaciones de reserva instantáneas y sugerencias de precios inteligentes.",
    "smart_airbnb_feature_2_title": "Integración de Cerradura Inteligente",
    "smart_airbnb_feature_2_desc": "Genere códigos de acceso únicos para cada huésped, activados y desactivados automáticamente según las reservas.",
    "smart_airbnb_feature_3_title": "Gestión de Acceso Dinámico",
    "smart_airbnb_feature_3_desc": "Los códigos cambian automáticamente después del check-out, asegurando la máxima seguridad para su propiedad.",
    "smart_airbnb_feature_4_title": "Centro de Comunicación con Huéspedes",
    "smart_airbnb_feature_4_desc": "Mensajería centralizada, instrucciones de check-in y seguimientos automatizados para reseñas.",
    "early_adopter_title": "Sea un Adoptador Temprano",
    "early_adopter_intro": "Únase a nuestro exclusivo programa de adopción temprana y obtenga precios especiales, soporte prioritario y la oportunidad de influir en el desarrollo del producto.",
    "early_adopter_benefit_1": "Precios especiales de lanzamiento",
    "early_adopter_benefit_2": "Implementación y soporte prioritarios",
    "early_adopter_benefit_3": "Aporte directo en características y hoja de ruta",
    "early_adopter_benefit_4": "Acceso exclusivo a funciones beta",
    "early_adopter_form_title": "Exprese su Interés",
    "early_adopter_form_intro": "Complete el formulario a continuación para ser de los primeros en experimentar estas soluciones innovadoras.",
    "solution_interested": "Solución de Interés",
    "select_solution": "Seleccione una solución",
    "business_type": "Tipo de Negocio",
    "current_challenges": "Desafíos Actuales",
    "early_adopter_submit": "Unirse al Programa Early Adopter",

    "// Other keys from previous version...": "",
    "footer_finio_description": "El sistema POS más avanzado para gestionar y hacer crecer su negocio de manera eficiente y profesional.",
    "quick_links": "Enlaces Rápidos",
    "support": "Soporte",
    "help_center": "Centro de Ayuda",
    "terms_conditions": "Términos y Condiciones",
    "privacy_policy": "Política de Privacidad",
    "contact_us_footer": "Contáctenos",
    "contact_phone": "+212 808-646672",
    "contact_email": "email",
    "contact_address": "Av. Aoussou Ou Basselam, Tetuán",
    "copyright": "© 2025 Finio Solutions. Todos los derechos reservados.",
    "company_name": "Finio Solutions",
    "company_ice": "ICE: 003689594000059",
    "company_siege": "Sede: Tetuán, Marruecos",
    "company_address_full": "Av. Aoussou Ou Basselam, Tetuán, Marruecos",
    "contact_page_title": "Contáctenos",
    "contact_page_intro": "Estamos aquí para responder a todas sus consultas y proporcionar el soporte necesario. No dude en contactarnos.",
    "send_message": "Envíenos un Mensaje",
    "full_name": "Nombre Completo",
    "email_address": "Dirección de Correo Electrónico",
    "phone_number": "Número de Teléfono",
    "subject": "Asunto",
    "your_message": "Su Mensaje",
    "send_button": "Enviar Mensaje",
    "contact_info_title": "Información de Contacto",

    "// --- Navigation Links ---": "",
    "nav_documentation": "Documentación",
    "nav_faq": "Preguntas Frecuentes",
    "nav_security": "Seguridad y Protección de Datos",
    "nav_changelog": "Registro de Cambios",
    "nav_about_us": "Sobre Nosotros",
    "nav_compliance": "Cumplimiento y Auditoría",

    "// Footer Columns": "",
    "footer_col_product": "Producto",
    "footer_col_resources": "Recursos",
    "footer_col_terms": "Términos",
    "footer_col_connect": "Conectar",

    "// --- About Us Page ---": "",
    "about_hero_title": "Ingeniería para la Verdad Financiera",
    "about_hero_subtitle": "No solo construimos software POS. Construimos motores financieros de grado de auditoría que previenen fraudes, eliminan errores y garantizan la consistencia de los datos.",
    "about_mission_title": "Nuestro Mandato",
    "about_mission_text": "Eliminar la ambigüedad financiera en el comercio minorista y la hostelería. Proporcionamos la certeza matemática de que cada centavo ganado es contabilizado, rastreado y asegurado.",
    "about_vision_title": "Nuestro Estándar de Ingeniería",
    "about_vision_text": "Rechazamos el estándar de la industria de 'suficientemente bueno'. Imaginamos un futuro donde el software empresarial sea tan confiable como una bóveda bancaria y tan preciso como un reloj suizo.",
    "about_values_title": "Ética Operacional",
    "about_values_text": "Precisión sobre velocidad. Integridad sobre características. Priorizamos la seguridad de sus datos por encima de cualquier reclamo de marketing.",
    "about_story_title": "El Origen de Tolerancia Cero",
    "about_story_paragraph_1": "Finio comenzó cuando auditamos una cadena de restaurantes que usaba un POS en la nube popular. Encontramos pedidos fantasma, facturas eliminadas e inventario flotante que desaparecía sin rastro. El sistema lo permitía. La base de datos lo permitía.",
    "about_story_paragraph_2": "Construimos Finio para cerrar esas brechas. Diseñamos un libro mayor inmutable donde la eliminación es imposible—solo la reversión. Construimos un motor de sincronización que sobrevive a caídas de red sin corromper un solo byte.",
    "about_story_paragraph_3": "Hoy, Finio es el estándar para operadores que exigen la verdad. No solo le ayudamos a vender; le ayudamos a probar lo que vendió. Desde tiendas individuales hasta cadenas empresariales, proporcionamos la columna vertebral para operaciones listas para auditoría.",
    "about_why_choose_title": "Por qué los Operadores Confían en Finio",
    "about_reason_1_title": "Libro Mayor Inmutable",
    "about_reason_1_desc": "Las facturas y pagos son de solo adición. Nadie—ni siquiera usted—puede eliminar silenciosamente un registro de transacción.",
    "about_reason_2_title": "Encriptación de Grado Militar",
    "about_reason_2_desc": "Los datos están encriptados en reposo y en tránsito. Su inteligencia empresarial permanece rigurosamente privada.",
    "about_reason_3_title": "Soberanía Offline",
    "about_reason_3_desc": "No le alquilamos el acceso a sus datos. Viven en su dispositivo, totalmente funcionales sin ninguna dependencia de servidor externo.",
    "about_reason_4_title": "Informes Listos para Auditoría",
    "about_reason_4_desc": "Nuestros informes no son solo gráficos bonitos. Son estados financieros matemáticamente conciliados listos para su contador.",
    "about_reason_5_title": "Sincronización Sin Conflictos",
    "about_reason_5_desc": "Nuestro motor de sincronización propietario maneja colisiones de múltiples dispositivos con lógica determinista, asegurando cero pérdida de datos.",
    "about_reason_6_title": "Integridad del Ecosistema",
    "about_reason_6_desc": "Cada módulo—KDS, Inventario, Panel Web—alimenta la misma fuente única de verdad. Sin silos de datos.",
    "about_stats_title": "Métricas de Fiabilidad del Sistema",
    "about_stat_1": "Conflictos de Datos",
    "about_stat_2": "Tiempo de Actividad (Uptime)",
    "about_stat_3": "Puntuación de Auditoría",
    "about_stat_4": "SLA de Soporte",
    "about_team_title": "El Equipo de Ingeniería",
    "about_team_intro": "No somos especialistas en marketing. Somos arquitectos de sistemas, especialistas en seguridad e ingenieros de software financiero dedicados a la ciencia de la gestión empresarial.",
    "about_team_member_1_name": "Arquitectura de Sistemas",
    "about_team_member_1_role": "Infraestructura Central",
    "about_team_member_1_bio": "Diseñando el libro mayor inmutable y la topología de base de datos offline-first que impulsa cada instancia de Finio.",
    "about_team_member_2_name": "Seguridad y Cumplimiento",
    "about_team_member_2_role": "Integridad de Datos",
    "about_team_member_2_bio": "Asegurando que cada transacción cumpla con estrictos estándares de auditoría y esté protegida contra la manipulación.",
    "about_team_member_3_name": "Operaciones de Clientes",
    "about_team_member_3_role": "Éxito de Despliegue",
    "about_team_member_3_bio": "Ayudando a empresas a migrar sus complejas estructuras de datos al entorno riguroso de Finio.",
    "about_team_member_4_name": "Diseño de Producto",
    "about_team_member_4_role": "UX y Flujo de Trabajo",
    "about_team_member_4_bio": "Traduciendo lógica contable compleja en flujos de trabajo rápidos e intuitivos para el personal de primera línea.",
    "about_core_values_title": "Protocolos Centrales",
    "about_value_1_title": "La Verdad Primero",
    "about_value_1_desc": "Nunca maquillamos los números. Si las matemáticas no cuadran, el sistema se detiene. La ambigüedad es un error (bug).",
    "about_value_2_title": "Local Primero",
    "about_value_2_desc": "El dispositivo en su mano es la fuente de la verdad. La nube es solo una copia de seguridad.",
    "about_value_3_title": "Cero Pérdida de Datos",
    "about_value_3_desc": "Somos paranoicos sobre la seguridad de los datos. Asumimos que las redes fallan, hay cortes de energía y el hardware se rompe.",
    "about_value_4_title": "Lógica Transparente",
    "about_value_4_desc": "No ocultamos los cálculos. Cada total puede rastrearse hasta sus transacciones atómicas.",
    "about_value_5_title": "Asociación",
    "about_value_5_desc": "Construimos herramientas para operadores serios. Si le importa la precisión, somos su socio.",
    "about_growth_title": "El Futuro de la Integridad",
    "about_growth_text_1": "A medida que escalamos, nuestro compromiso con la rigidez central de la contabilidad permanece sin cambios. Nos estamos expandiendo hacia la IA y la automatización, pero nunca a costa de la precisión.",
    "about_growth_text_2": "Nuestra hoja de ruta incluye módulos de 'Tienda Inteligente' y 'Propiedad Inteligente', pero a diferencia de otros, los nuestros están construidos sobre la misma base inmutable. Las sugerencias de IA se registrarán en auditoría.",
    "about_growth_text_3": "Finio está construyendo el sistema operativo para la próxima generación de empresas transparentes, eficientes y seguras para auditoría en el norte de África y más allá.",
    "about_cta_title": "¿Listo para Negocios Serios?",
    "about_cta_subtitle": "Únase a los operadores que han dejado de preocuparse por las discrepancias de datos y han comenzado a centrarse en el crecimiento.",
    "explore_products": "Ver Módulos Técnicos",

    "// --- Checkout Page ---": "",
    "checkout_title": "Complete su Suscripción",
    "checkout_intro": "Complete sus datos a continuación para finalizar su suscripción. Nos pondremos en contacto con usted dentro de las 24 horas para confirmar su plan.",
    "complete_payment": "Completar Pago",
    "subscription_success": "¡Éxito! Su suscripción ha sido activada.",
    "fill_personal_info": "Por favor, complete su Información Personal primero.",
    "email_required_card": "La dirección de correo electrónico es obligatoria para pagos con tarjeta.",
    "personal_info_title": "Información Personal",
    "company_name_optional": "Nombre de la Empresa (Opcional)",
    "city": "Ciudad",
    "country": "País",
    "payment_method": "Método de Pago",
    "select_payment": "Seleccione método de pago",
    "payment_card": "Con Tarjeta",
    "payment_cash": "En Efectivo",
    "payment_cheque": "Con Cheque",
    "payment_bank_cheque": "Con Cheque Bancario",
    "subscription_details_title": "Detalles de la Suscripción",
    "selected_plan": "Plan Seleccionado",
    "subscription_duration": "Duración de la Suscripción",
    "select_duration": "Seleccione duración",
    "duration_3months": "3 Meses",
    "duration_6months": "6 Meses (1 Mes Gratis)",
    "duration_12months": "12 Meses (2.5 Meses Gratis)",
    "total_amount": "Monto Total",
    "complete_subscription": "Completar Suscripción",
    "redirecting_to_payment": "Redirigiendo al pago seguro...",
    "payment_success_title": "Pago exitoso",
    "payment_success_desc": "¡Gracias! Hemos recibido su pago y su suscripción se está activando.",
    "payment_failed_title": "Pago fallido",
    "payment_failed_desc": "Su pago no se pudo completar y no se realizó ningún cargo. Puede intentarlo de nuevo o contactar con nuestro equipo.",
    "order_reference": "Referencia del pedido",
    "amount_paid": "Monto pagado",
    "try_again": "Intentar de nuevo",
    "sub_recurring_note": "Se cobrarán {amount} MAD por el período seleccionado ahora y se renovará automáticamente a {yearly} MAD/año hasta que cancele.",

    // --- Página de Hardware (solo Marruecos) ---
    "nav_hardware": "Equipos",
    "hw_title": "Packs de Hardware POS",
    "hw_subtitle": "Configuraciones completas de punto de venta — ensambladas, probadas y entregadas en todo Marruecos.",
    "hw_morocco_badge": "Disponible solo en Marruecos",
    "hw_delivery_badge": "Entrega desde 50 MAD",
    "hw_packs_title": "Elija su pack",
    "hw_includes": "Qué incluye",
    "hw_hardware_price": "Precio del hardware",
    "hw_with_scanner": "Con escáner 2D",
    "hw_get_quote": "Elegir este pack",
    "hw_pricelist_title": "Componentes que ofrecemos",
    "hw_pricelist_intro": "Todos los componentes que podemos suministrar para su instalación.",
    "hw_item_col": "Artículo",
    "hw_price_col": "Precio",
    "hw_software_title": "El software Finio se factura por separado",
    "hw_software_desc": "Los precios del hardware son únicos y el software Finio se factura por separado. Al añadir una suscripción anual Finio a su pedido de hardware, obtiene un 30% de descuento adicional solo el primer año — por ejemplo, el plan Starter (normalmente 1900 MAD/año) pasa a 1330 MAD el primer año y luego se renueva a 1900 MAD/año.",
    "hw_cta_title": "¿Necesita ayuda para elegir?",
    "hw_cta_desc": "Cuéntenos sobre su negocio y le recomendaremos el pack adecuado.",
    "hw_pack_starter_name": "Pack Starter",
    "hw_pack_starter_desc": "La configuración esencial para tiendas pequeñas y cafeterías.",
    "hw_pack_android_name": "Pack Android",
    "hw_pack_android_desc": "POS con pantalla táctil bajo Android — compacto y flexible.",
    "hw_pack_pro_name": "Pack Profesional",
    "hw_pack_pro_desc": "La configuración todo en uno para negocios de alto volumen.",
    "hw_item_touch": "Pantalla táctil 17\"",
    "hw_item_printer": "Impresora térmica 80mm",
    "hw_item_scanner": "Escáner 2D",
    "hw_item_drawer": "Cajón de dinero (Tiroir)",
    "hw_item_tablet": "Tableta G-Tab T12",
    "hw_item_support": "Soporte de alta gama",
    "hw_item_pc": "PC i5",
    "hw_item_aio": "Terminal todo en uno",
    "hw_item_tvbox": "AndroidBox",
    "hw_order_title": "Configure su pack",
    "hw_order_subtitle": "Elija sus opciones y método de pago y realice su pedido.",
    "hw_order_back": "Volver a los packs",
    "hw_order_selected_pack": "Pack seleccionado",
    "hw_scanner_title": "Escáner 2D",
    "hw_scanner_without": "Sin escáner",
    "hw_scanner_with": "Con escáner 2D",
    "hw_subscription_title": "Software Finio",
    "hw_sub_hardware_only": "Solo hardware",
    "hw_sub_add": "Añadir suscripción Finio",
    "hw_sub_plan": "Plan de software",
    "hw_sub_first_year": "Primer año (30% dto.)",
    "hw_sub_renews": "Se renueva a",
    "hw_sub_per_year": "/año",
    "hw_payment_title": "Método de pago",
    "hw_pay_card": "Con tarjeta",
    "hw_pay_card_desc": "Pago en línea seguro vía Chari Pay",
    "hw_pay_cod": "Pago contra entrega",
    "hw_pay_transfer": "Transferencia bancaria",
    "hw_pay_transfer_note": "Le enviaremos los datos bancarios justo después de realizar el pedido.",
    "hw_details_title": "Sus datos",
    "hw_field_name": "Nombre completo",
    "hw_field_phone": "Teléfono",
    "hw_field_email": "Correo electrónico",
    "hw_field_company": "Empresa (opcional)",
    "hw_field_city": "Ciudad",
    "hw_field_address": "Dirección",
    "hw_summary_title": "Resumen del pedido",
    "hw_sum_pack": "Pack de hardware",
    "hw_sum_scanner": "Escáner 2D",
    "hw_sum_subscription": "Software Finio (1er año)",
    "hw_sum_delivery": "Entrega",
    "hw_sum_total": "Total",
    "hw_place_order": "Realizar pedido",
    "hw_processing": "Procesando...",
    "hw_order_success_title": "¡Pedido recibido!",
    "hw_order_success_desc": "Gracias. Nuestro equipo le contactará en breve para confirmar la entrega y el pago.",
    "hw_new_order": "Realizar otro pedido",
    "hw_required_missing": "Por favor, complete los campos obligatorios.",
    "months": "meses",
    "month_free": "mes gratis",
    "months_free": "meses gratis",
    "subscriptions_page_title": "Planes Transparentes para su Negocio",
    "subscriptions_intro": "Elija el plan que se adapte a sus necesidades sin tarifas ocultas.",
    "pricing_basic_title": "Inicial (Starter)",
    "pricing_basic_price": "200",
    "pricing_pro_title": "Empresarial (Business)",
    "pricing_pro_price": "350",
    "pricing_custom_title": "A Medida",
    "pricing_custom_contact": "Contáctenos",
    "pricing_currency": "MAD/mes",
    "pricing_basic_yearly": "Anual 1900 MAD/año",
    "pricing_basic_yearly_savings": "2.5 meses gratis",
    "pricing_pro_yearly": "Anual 3325 MAD/año",
    "pricing_pro_yearly_savings": "2.5 meses gratis",
    "pricing_most_popular": "Más Popular",
    "pricing_basic_feature_1": "Aplicación con todas las funciones",
    "pricing_basic_feature_2": "Gestión de inventario y clientes",
    "pricing_basic_feature_3": "Análisis inteligentes",
    "pricing_basic_feature_4": "Soporte técnico 24/7",
    "pricing_pro_feature_1": "Todas las características del Inicial",
    "pricing_pro_feature_2": "Panel web para contador",
    "pricing_pro_feature_3": "Gestión documental (Presupuestos, Facturas)",
    "pricing_pro_feature_4": "Informes avanzados",
    "pricing_pro_feature_5": "Soporte técnico prioritario",
    "pricing_custom_feature_1": "Sistema de Restaurante (KDS)",
    "pricing_custom_feature_2": "Sistema de Hotel (PMS)",
    "pricing_custom_feature_3": "Personalización completa",
    "pricing_custom_feature_4": "Integración con sus sistemas existentes",
    "pricing_custom_feature_5": "Un precio adaptado a sus necesidades",
    "pricing_get_started": "Empezar",
    "pricing_choose_plan": "Elegir Plan Empresarial",
    "pricing_request_quote": "Solicitar Presupuesto",
    "contact_plans_title": "Para detalles del plan y precios",
    "contact_plans_text": "Por favor contáctenos directamente para obtener un presupuesto personalizado que cumpla con sus requisitos comerciales.",
    "contact_us_plans": "Contáctenos",
    "learn_more": "Más Información",

    "// --- Custom Plan Content ---": "",
    "sub_custom_title": "Finio Personalizado",
    "sub_custom_tagline": "Soluciones empresariales a medida para entornos complejos",
    "sub_custom_pricing_contact": "Contáctenos para un presupuesto",
    "sub_custom_everything_pro": "TODO LO DE EMPRESARIAL, MÁS:",

    "sub_custom_f1_title": "Sistema Hotelero Completo (PMS Empresarial)",
    "sub_custom_f1_desc": "Habitaciones, ocupación, servicios, gestión multi-departamento y multi-propiedad.",
    "sub_custom_f2_title": "Integración ERP Empresarial",
    "sub_custom_f2_desc": "Conecte Finio con sus sistemas ERP existentes para contabilidad, inventario, RRHH e informes.",
    "sub_custom_f3_title": "Soluciones Smart Store",
    "sub_custom_f3_desc": "Automatización minorista avanzada, dispositivos conectados y flujos de trabajo operativos personalizados.",
    "sub_custom_f4_title": "Integración Smart House y Propiedad",
    "sub_custom_f4_desc": "Integración con sistemas IoT, control de acceso, automatización e infraestructura inteligente.",
    "sub_custom_f5_title": "Personalización Completa del Sistema",
    "sub_custom_f5_desc": "Módulos personalizados, flujos de trabajo y lógica empresarial adaptada exactamente a sus necesidades.",
    "sub_custom_f6_title": "Conectividad Avanzada de API y Sistema",
    "sub_custom_f6_desc": "Integraciones seguras con plataformas de terceros y sistemas internos empresariales.",
    "sub_custom_f7_title": "Escalabilidad Multi-ubicación y Empresarial",
    "sub_custom_f7_desc": "Diseñado para grandes empresas, cadenas y estructuras organizativas complejas.",
    "sub_custom_f8_title": "Análisis de Proyecto y Despliegue Dedicado",
    "sub_custom_f8_desc": "Estudio técnico, configuración y despliegue alineado con sus requisitos comerciales.",
    "sub_custom_f9_title": "Precios Personalizados y Soporte a Largo Plazo",
    "sub_custom_f9_desc": "Un modelo de precios adaptado al alcance, integraciones y nivel de soporte.",

    "sub_custom_contact_text": "Póngase en contacto para una propuesta personalizada.",

    "// --- Subscription Page Enhanced Content ---": "",
    "sub_safety_header": "La seguridad y la integridad contable están incluidas por defecto.",
    "sub_safety_desc": "Finio está construido con una fuerte contabilidad y protección de datos en su núcleo. Ambos planes comparten la misma base segura — la diferencia es cuánto control, visibilidad y escalabilidad necesita.",
    "sub_safety_note": "No vendemos la seguridad como una mejora (upgrade).",

    "sub_basic_title": "Finio Inicial (Starter)",
    "sub_basic_tagline": "Todo lo que necesita para vender, gestionar y cerrar su día con confianza.",
    "sub_basic_desc": "Finio Inicial incluye la experiencia POS completa con protecciones integradas que aseguran que sus datos sean siempre correctos y fiables.",
    "sub_included_features": "Características Incluidas",
    "sub_basic_f1": "Punto de Venta completo (minorista, comida rápida, restaurante)",
    "sub_basic_f2": "Gestión de productos, clientes y proveedores",
    "sub_basic_f3": "Escaneo de códigos de barras y múltiples niveles de precios",
    "sub_basic_f4": "Descuentos, cupones y carritos guardados",
    "sub_basic_f5": "Apertura de caja y cierre de fin de día (Z / cierre)",
    "sub_basic_f6": "Creación, impresión y seguimiento de facturas",
    "sub_basic_f7": "Reembolsos seguros y manejo de saldo de clientes",
    "sub_basic_f8": "Operación offline-first con sincronización automática",
    "sub_basic_f9": "Facturas inmutables y contabilidad resistente a la manipulación",
    "sub_basic_f10": "Sincronización segura multidispositivo",
    "sub_best_for": "Mejor Para",
    "sub_basic_best_for": "Pequeñas y medianas empresas que gestionan operaciones diarias directamente desde la aplicación y desean un POS fiable y compatible sin complejidad.",

    "sub_pro_title": "Finio Empresarial (Business)",
    "sub_pro_tagline": "Diseñado para el crecimiento, la estructura y los flujos de trabajo contables profesionales.",
    "sub_pro_desc": "Finio Empresarial se basa en todo lo del Inicial y agrega módulos avanzados para administración, contabilidad y automatización.",
    "sub_everything_basic_plus": "Todo lo del Inicial, más:",
    "sub_pro_f1": "Panel Web de Contador y Administración",
    "sub_pro_f1_detail": "Gestione salarios de empleados, gastos, facturas (B2C y B2B) y estadísticas contables desde una interfaz web dedicada.",
    "sub_pro_f2": "Informes financieros y análisis avanzados",
    "sub_pro_f3": "Sistema de Pantalla de Cocina (KDS) con estaciones y temporizadores",
    "sub_pro_f4": "Módulos de Hotel / PMS (habitaciones, ocupación, servicios)",
    "sub_pro_f5": "Pedidos web (tabletas de camareros y autopedido de clientes)",
    "sub_pro_f6": "WhatsApp y notificaciones automatizadas",
    "sub_pro_f7": "APIs de productos e integración",
    "sub_pro_f8": "Sincronización con WooCommerce",
    "sub_pro_f9": "Herramientas para escalado y operaciones de flujo de trabajo múltiple",
    "sub_pro_f10": "Sistema de Impresión Avanzado (Etiquetas y Recibos)",
    "sub_pro_best_for": "Empresas en crecimiento, restaurantes con cocinas, hoteles, entornos multidispositivo y empresas que trabajan con contadores o sistemas externos.",

    "sub_safety_compliance_title": "Una nota sobre seguridad y cumplimiento",
    "sub_safety_compliance_intro": "No vendemos la seguridad como una mejora. Tanto el INICIAL como el EMPRESARIAL incluyen:",
    "sub_compliance_1": "Facturas inmutables",
    "sub_compliance_2": "Libro de pagos de solo adición",
    "sub_compliance_3": "Pagos a prueba de fallos (Crash-safe)",
    "sub_compliance_4": "Contabilidad con bloqueo de periodo",
    "sub_compliance_5": "Manejo adecuado de efectivo, banco y libro mayor de clientes",
    "sub_compliance_6": "Cimientos listos para contabilidad multi-país",
    "sub_safety_difference": "La diferencia entre planes es la capacidad, no el riesgo.",

    "sub_compare_title": "Comparación de Características",
    "sub_compare_feature": "Característica",
    "sub_cat_core_pos": "Características Principales del POS",
    "sub_cmp_pos": "Sistema Completo de Punto de Venta",
    "sub_cmp_products": "Gestión de productos, clientes y proveedores",
    "sub_cmp_barcode": "Escaneo de códigos de barras y niveles de precios",
    "sub_cmp_discounts": "Descuentos, cupones y carritos guardados",
    "sub_cmp_eod": "Fin de día (Z / cierre)",
    "sub_cmp_invoices": "Creación y seguimiento de facturas",
    "sub_cmp_refunds": "Reembolsos seguros y saldo de clientes",
    "sub_cmp_offline": "Offline-first con sincronización automática",
    "sub_cat_safety": "Seguridad y Cumplimiento",
    "sub_cmp_immutable": "Facturas inmutables",
    "sub_cmp_ledger": "Libro de pagos de solo adición",
    "sub_cmp_crash_safe": "Pagos a prueba de fallos",
    "sub_cmp_period_lock": "Contabilidad con bloqueo de periodo",
    "sub_cat_advanced": "Características Avanzadas (Solo EMPRESARIAL)",
    "sub_cmp_web_panel": "Panel Web de Contador y Admin",
    "sub_cmp_advanced_reports": "Informes financieros avanzados",
    "sub_cmp_kds": "Sistema de Pantalla de Cocina (KDS)",
    "sub_cmp_hotel": "Módulos de Hotel / PMS",
    "sub_cmp_web_ordering": "Pedidos web (tabletas y autopedido)",
    "sub_cmp_whatsapp": "Notificaciones de WhatsApp",
    "sub_cmp_api": "APIs de Producto e Integración",
    "sub_cmp_woocommerce": "Sincronización con WooCommerce",
    "sub_cmp_print": "Sistema de Impresión Avanzado",
    "sub_cmp_remote": "Acceso Remoto (iOS y Android)",
    "sub_cmp_auth": "Autenticación Multi-Inquilino y Permisos",

    "sub_which_plan_title": "¿Qué plan debo elegir?",
    "sub_choose_basic": "Elija INICIAL si desea un POS completo y seguro y gestiona todo desde la aplicación.",
    "sub_choose_pro": "Elija EMPRESARIAL si necesita administración contable, módulos avanzados, automatización o integraciones.",
    "sub_choose_custom": "Una solución Finio totalmente personalizada diseñada en torno a sus sistemas, operaciones y necesidades empresariales existentes. Finio Personalizado se adapta a su negocio — no al revés.",

    "// --- 404 Page ---": "",
    "page_not_found_title": "Página No Encontrada",
    "page_not_found_desc": "La página que busca podría haber sido eliminada, haber cambiado de nombre o no estar disponible temporalmente.",
    "back_to_home": "Volver al Inicio",

    "// --- Help Center ---": "",
    "help_welcome": "Bienvenido al Centro de Ayuda de Finio",
    "help_intro": "Encuentre respuestas a preguntas frecuentes y obtenga el soporte que necesita para aprovechar al máximo Finio PRO.",
    "help_faq_title": "Preguntas Frecuentes",
    "help_faq_1_q": "¿Qué es Finio PRO?",
    "help_faq_1_a": "Finio PRO es una plataforma completa de gestión para restaurantes y hostelería que incluye POS, KDS, gestión de inventario, servidor web, PMS y sistemas de impresión avanzados.",
    "help_faq_2_q": "¿Finio PRO funciona sin conexión?",
    "help_faq_2_a": "¡Sí! Finio PRO cuenta con una arquitectura offline-first que mantiene sus operaciones funcionando sin problemas incluso sin internet. Los datos se sincronizan automáticamente cuando vuelve a estar en línea.",
    "help_faq_3_q": "¿Qué hardware necesito?",
    "help_faq_3_a": "Finio PRO funciona con hardware POS estándar, incluyendo impresoras de recibos, escáneres de códigos de barras y cajones de efectivo. También proporcionamos servicios de consultoría e instalación de hardware.",
    "help_faq_4_q": "¿Qué tan seguros están mis datos?",
    "help_faq_4_a": "Sus datos están protegidos con seguridad de grado empresarial, incluyendo encriptación, copias de seguridad automáticas, permisos basados en roles y aislamiento de datos multi-inquilino.",
    "help_faq_5_q": "¿Puedo acceder al Panel Web del Contador de forma remota?",
    "help_faq_5_a": "¡Absolutamente! Monitoree y gestione su negocio desde cualquier lugar del mundo utilizando nuestras aplicaciones móviles dedicadas para iOS y Android.",
    "help_contact_support": "¿Necesita Más Ayuda?",
    "help_contact_text": "Nuestro equipo de soporte está aquí para asistirle. Contáctenos a través de:",
    "help_search_placeholder": "Buscar respuestas...",
    "help_docs_desc": "Guías detalladas sobre cómo usar cada función de Finio.",
    "help_faq_desc": "Respuestas rápidas a preguntas comunes.",
    "help_security_desc": "Cómo mantenemos sus datos seguros y en cumplimiento.",
    "help_changelog_desc": "Últimas actualizaciones y mejoras.",
    "contact_email_plain": "",
    "contact_phone_plain": "+212808646672",

    "// --- Terms & Conditions ---": "",
    "terms_last_updated": "Última Actualización",
    "terms_date": "Noviembre 2025",
    "terms_acceptance_title": "Aceptación de Términos",
    "terms_acceptance_text": "Al acceder y utilizar los servicios de Finio PRO, usted acepta y acuerda estar sujeto a estos Términos y Condiciones. Si no está de acuerdo con estos términos, por favor no utilice nuestros servicios.",
    "terms_services_title": "Descripción de Servicios",
    "terms_services_text": "Finio Solutions proporciona una plataforma integral de gestión empresarial que incluye punto de venta, gestión de inventario, gestión de relaciones con el cliente y servicios relacionados. Nos reservamos el derecho de modificar, suspender o discontinuar cualquier parte de nuestros servicios en cualquier momento.",
    "terms_account_title": "Cuentas de Usuario y Responsabilidades",
    "terms_account_text": "Usted es responsable de mantener la confidencialidad de las credenciales de su cuenta y de todas las actividades que ocurran bajo su cuenta. Usted acepta notificarnos inmediatamente sobre cualquier uso no autorizado de su cuenta.",
    "terms_offline_title": "Modo Offline y Responsabilidad de Datos",
    "terms_offline_text": "El modo offline de Finio PRO almacena datos localmente en su dispositivo. Usted es responsable de asegurar que su dispositivo se sincronice con la nube regularmente. Finio Solutions no es responsable por la pérdida de datos causada por fallo o pérdida del dispositivo antes de la sincronización.",
    "terms_payment_title": "Pago y Suscripción",
    "terms_payment_text": "Las tarifas de suscripción se facturan por adelantado de forma recurrente. Todas las tarifas no son reembolsables excepto según lo requiera la ley. Nos reservamos el derecho de cambiar nuestros precios con un aviso de 30 días a los suscriptores activos.",
    "terms_hardware_title": "Compatibilidad de Hardware e Impresoras",
    "terms_hardware_text": "Finio funciona con impresoras y tabletas estándar ESC/POS. Proporcionamos una lista de hardware recomendado pero no garantizamos la compatibilidad con todos los dispositivos de terceros a menos que sean certificados explícitamente por nosotros.",
    "terms_intellectual_title": "Propiedad Intelectual",
    "terms_intellectual_text": "Todo el contenido, características y funcionalidad de Finio PRO son propiedad de Finio Solutions y están protegidos por leyes internacionales de derechos de autor, marcas registradas y otras leyes de propiedad intelectual.",
    "terms_limitation_title": "Limitación de Responsabilidad",
    "terms_limitation_text": "Finio Solutions no será responsable de ningún daño indirecto, incidental, especial, consecuente o punitivo resultante de su uso o incapacidad para usar el servicio.",
    "terms_termination_title": "Terminación",
    "terms_termination_text": "Nos reservamos el derecho de terminar o suspender su cuenta y acceso a nuestros servicios a nuestra sola discreción, sin previo aviso, por conducta que creemos viola estos Términos o es perjudicial para otros usuarios, nosotros o terceros, o por cualquier otra razón.",
    "terms_governing_title": "Ley Aplicable",
    "terms_governing_text": "Estos Términos se regirán e interpretarán de acuerdo con las leyes de Marruecos, sin tener en cuenta sus disposiciones sobre conflictos de leyes.",
    "terms_changes_title": "Cambios a los Términos",
    "terms_changes_text": "Nos reservamos el derecho de modificar estos términos en cualquier momento. Notificaremos a los usuarios sobre cualquier cambio material por correo electrónico o a través de nuestro servicio. El uso continuado del servicio después de tales modificaciones constituye la aceptación de los términos actualizados.",
    "terms_contact_title": "Información de Contacto",
    "terms_contact_text": "Para cualquier pregunta sobre estos Términos y Condiciones, por favor contáctenos en:",

    "// --- Privacy Policy ---": "",
    "privacy_last_updated": "Última Actualización",
    "privacy_date": "Noviembre 2025",
    "privacy_intro_title": "Introducción",
    "privacy_intro_text": "Finio Solutions ('nosotros', 'nuestro' o 'nos') respeta su privacidad y se compromete a proteger sus datos personales. Esta política de privacidad explica cómo recopilamos, usamos y protegemos su información cuando utiliza los servicios de Finio PRO.",
    "privacy_collection_title": "Información que Recopilamos",
    "privacy_collection_text": "Recopilamos varios tipos de información para proporcionar y mejorar nuestros servicios:",
    "privacy_collection_item1": "Información de identificación personal (nombre, dirección de correo electrónico, número de teléfono)",
    "privacy_collection_item2": "Información comercial (nombre de la empresa, dirección, identificación fiscal)",
    "privacy_collection_item3": "Datos de uso (cómo interactúa con nuestros servicios, características utilizadas)",
    "privacy_collection_item4": "Datos técnicos (dirección IP, tipo de navegador, información del dispositivo)",
    "privacy_pos_data_item": "Datos transaccionales (historial de ventas, niveles de inventario, compras de clientes) generados a través del sistema POS",
    "privacy_pos_title": "Datos Transaccionales y POS",
    "privacy_pos_text": "Recopilamos y procesamos datos transaccionales para proporcionarle informes de ventas, seguimiento de inventario y análisis comerciales. Estos datos le pertenecen. No utilizamos sus datos de ventas con fines de marketing ni los compartimos con terceros.",
    "privacy_offline_storage_title": "Almacenamiento Local Offline",
    "privacy_offline_storage_text": "Al usar Finio PRO sin conexión, los datos se almacenan de forma segura en su dispositivo local utilizando una base de datos local encriptada. Estos datos permanecen efectivamente bajo su control físico hasta que el dispositivo se reconecta a internet y se sincroniza con nuestros servidores seguros en la nube.",
    "privacy_use_title": "Cómo Usamos Su Información",
    "privacy_use_text": "Usamos la información recopilada para los siguientes propósitos:",
    "privacy_use_item1": "Para proporcionar, mantener y mejorar nuestros servicios",
    "privacy_use_item2": "Para procesar transacciones y enviar información relacionada",
    "privacy_use_item3": "Para enviar información administrativa, actualizaciones y alertas de seguridad",
    "privacy_use_item4": "Para responder a sus solicitudes, preguntas y proporcionar soporte al cliente",
    "privacy_sharing_title": "Intercambio y Divulgación de Información",
    "privacy_sharing_text": "No vendemos, intercambiamos ni alquilamos su información personal a terceros. Podemos compartir su información solo en las siguientes circunstancias: con su consentimiento, para cumplir con obligaciones legales, para proteger nuestros derechos y seguridad, o con proveedores de servicios que ayudan en nuestras operaciones bajo estrictos acuerdos de confidencialidad.",
    "privacy_security_title": "Seguridad de Datos",
    "privacy_security_text": "Implementamos medidas de seguridad estándar de la industria para proteger su información personal, incluyendo encriptación, servidores seguros, auditorías de seguridad regulares y controles de acceso. Sin embargo, ningún método de transmisión por internet es 100% seguro.",
    "privacy_retention_title": "Retención de Datos",
    "privacy_retention_text": "Retenemos su información personal solo durante el tiempo necesario para cumplir con los propósitos descritos en esta política de privacidad, a menos que la ley exija un período de retención más largo.",
    "privacy_rights_title": "Sus Derechos de Privacidad",
    "privacy_rights_text": "Usted tiene derecho a:",
    "privacy_rights_item1": "Acceder y recibir una copia de sus datos personales",
    "privacy_rights_item2": "Solicitar la corrección de datos inexactos o incompletos",
    "privacy_rights_item3": "Solicitar la eliminación de sus datos personales",
    "privacy_rights_item4": "Oponerse o restringir cierto procesamiento de sus datos",
    "privacy_cookies_title": "Cookies y Tecnologías de Seguimiento",
    "privacy_cookies_text": "Utilizamos cookies y tecnologías de seguimiento similares para rastrear la actividad en nuestro servicio y mantener cierta información. Puede indicar a su navegador que rechace todas las cookies o que indique cuándo se envía una cookie.",
    "privacy_children_title": "Privacidad de los Niños",
    "privacy_children_text": "Nuestros servicios no están destinados a personas menores de 18 años. No recopilamos a sabiendas información personal de niños. Si cree que hemos recopilado información de un niño, por favor contáctenos inmediatamente.",
    "privacy_changes_title": "Cambios a Esta Política de Privacidad",
    "privacy_changes_text": "Podemos actualizar nuestra Política de Privacidad de vez en cuando. Le notificaremos sobre cualquier cambio publicando la nueva Política de Privacidad en esta página y actualizando la fecha de 'Última Actualización'.",
    "privacy_contact_title": "Contáctenos",
    "privacy_contact_text": "Si tiene alguna pregunta sobre esta Política de Privacidad, por favor contáctenos en:",

    // --- Documentation ---
    // Docs Navigation
    "docs_nav_title": "Documentación",
    "docs_resources_title": "Recursos",
    "docs_nav_getting_started": "Comenzando",
    "docs_nav_pos_usage": "Manual de uso del TPV",
    "docs_nav_kds_workflow": "Flujo de trabajo KDS",
    "docs_nav_pms_overview": "PMS & Hotel",
    "docs_nav_offline_sync": "Sin conexión & Sincronización",
    "docs_nav_roles_permissions": "Roles & Permisos",
    "docs_nav_finance_closures": "Facturas & Cierres",
    "docs_nav_hardware_printers": "Impresoras & Hardware",
    "docs_nav_integrations": "Integraciones",
    "docs_nav_compliance": "Cumplimiento & Fiscal",
    "docs_nav_troubleshooting": "Solución de problemas",

    // Getting Started
    "docs_gs_title": "Comenzando",
    "docs_gs_lead": "Bienvenido a Finio, un avanzado sistema de punto de venta y gestión empresarial fuera de línea, diseñado para comercio, restaurantes, cafeterías y hostelería.",
    "docs_gs_intro": "Finio combina Punto de Venta (POS), Pantalla de Cocina (KDS), Inventario, Contabilidad y PMS Hotelero en una plataforma unificada, con sólida integridad de datos, auditabilidad y sincronización multidispositivo.",
    "docs_gs_access_title": "1. Acceder a Finio",
    "docs_gs_step_1": "<strong>Instale Finio</strong> en su dispositivo (Android / Escritorio / Tablet).",
    "docs_gs_step_2": "<strong>Abra la aplicación.</strong>",
    "docs_gs_step_3": "<strong>Inicie sesión</strong> usando su cuenta de inquilino.",
    "docs_gs_step_4": "<strong>Seleccione su tienda / sucursal</strong> si corresponde.",
    "docs_gs_offline_note": "<em>Finio funciona sin conexión por defecto. Solo se requiere Internet para la sincronización en la nube y compartir entre dispositivos.</em>",
    "docs_gs_config_title": "2. Configuración de la tienda",
    "docs_gs_config_intro": "Antes de comenzar las ventas, configure los ajustes de su tienda:",
    "docs_gs_company_title": "Detalles de la empresa",
    "docs_gs_company_go_to": "Vaya a: <strong>Configuración → Empresa</strong>",
    "docs_gs_company_configure": "Configurar:",
    "docs_gs_company_list_1": "Nombre del negocio",
    "docs_gs_company_list_2": "Logotipo",
    "docs_gs_company_list_3": "Dirección",
    "docs_gs_company_list_4": "ID Fiscal (ICE / IVA)",
    "docs_gs_company_list_5": "Moneda",
    "docs_gs_company_list_6": "País",
    "docs_gs_company_note": "Estos detalles aparecen en facturas, recibos y exportaciones contables.",
    "docs_gs_taxes_title": "Impuestos",
    "docs_gs_taxes_go_to": "Vaya a: <strong>Configuración → Impuestos</strong>",
    "docs_gs_taxes_list_1": "Crear una o más tasas de impuestos",
    "docs_gs_taxes_list_2": "Asignar impuestos a productos",
    "docs_gs_taxes_list_3": "Soporta: Precios con impuestos incluidos, Precios sin impuestos incluidos, Clases impositivas múltiples",
    "docs_gs_taxes_note": "<strong>Nota:</strong> Los impuestos se congelan al crear la factura para garantizar la integridad contable.",
    "docs_gs_hardware_title": "Impresoras y Hardware",
    "docs_gs_hardware_go_to": "Vaya a: <strong>Configuración → Hardware</strong>",
    "docs_gs_hardware_list_1": "Descubrir impresoras de red",
    "docs_gs_hardware_list_2": "Asignar impresoras para: Recibos, Tickets de cocina, Barra / estaciones",
    "docs_gs_hardware_list_3": "Configurar plantillas de impresión por estación",
    "docs_gs_hardware_note": "<em>Las plantillas de impresora están versionadas y son recuperables de forma segura.</em>",
    "docs_gs_adding_title": "3. Agregar productos",
    "docs_gs_adding_go_to": "Vaya a: <strong>Inventario → Productos</strong>",
    "docs_gs_adding_intro": "Puede agregar productos de tres maneras:",
    "docs_gs_manual_title": "Entrada manual",
    "docs_gs_manual_list_1": "Nombre, Categoría, Precio (soporte para múltiples precios)",
    "docs_gs_manual_list_2": "Impuesto, Seguimiento de stock",
    "docs_gs_manual_list_3": "Imagen, Modificadores y variantes",
    "docs_gs_csv_title": "Importación CSV (Plan Pro)",
    "docs_gs_csv_list_1": "Importar productos de forma masiva",
    "docs_gs_csv_list_2": "Categorías creadas automáticamente",
    "docs_gs_csv_list_3": "Soporta variantes y precios",
    "docs_gs_types_title": "Tipos de productos avanzados",
    "docs_gs_types_list_1": "Productos simples",
    "docs_gs_types_list_2": "Productos con variantes (talla, color, etc.)",
    "docs_gs_types_list_3": "Productos compuestos (recetas / paquetes)",
    "docs_gs_types_list_4": "Productos solo de stock (no vendibles)",
    "docs_pos_title": "Su primera venta (TPV)",
    "docs_pos_lead": "Procesar ventas en Finio es rápido y seguro. Siga estos pasos para completar su primera transacción.",
    "docs_pos_steps_title": "Pasos para vender",
    "docs_pos_step_1": "<strong>Abra el TPV</strong> desde el menú principal.",
    "docs_pos_step_2": "<strong>Seleccione productos</strong> de la cuadrícula o escanee un código de barras.",
    "docs_pos_step_3": "<strong>Elija cliente</strong> (opcional) desde la barra superior.",
    "docs_pos_step_4": "Toque <strong>Pagar</strong>.",
    "docs_pos_step_5": "<strong>Seleccione método de pago:</strong>",
    "docs_pos_method_1": "Efectivo",
    "docs_pos_method_2": "Tarjeta",
    "docs_pos_method_3": "Banco",
    "docs_pos_method_4": "Saldo del cliente",
    "docs_pos_method_5": "Cheque",
    "docs_pos_method_6": "Pagos mixtos",
    "docs_pos_step_6": "<strong>Confirmar pago</strong>.",
    "docs_pos_step_7": "<strong>El recibo se imprime automáticamente</strong> (si está configurado).",
    "docs_pos_note": "<strong>Nota:</strong> Cada venta crea una factura inmutable para la seguridad contable.",
    "docs_pos_features_title": "Características Clave",
    "docs_pos_feat_multi": "<strong>Soporte multipago:</strong> Divida facturas fácilmente entre efectivo y tarjeta.",
    "docs_pos_feat_hold": "<strong>Retener y Recuperar:</strong> Guarde pedidos para más tarde si un cliente no está listo.",
    "docs_pos_feat_offline": "<strong>Modo sin conexión:</strong> Continúe vendiendo incluso sin internet; los datos se sincronizan automáticamente una vez en línea.",

    "docs_hardware_title": "Impresoras e Impresión",
    "docs_hardware_lead": "Configure recibos, tickets de cocina y enrutamiento de impresoras para operaciones fluidas.",
    "docs_hardware_features_title": "Funciones de impresión",
    "docs_hardware_feat_receipts": "<strong>Recibos:</strong> Recibos de clientes automáticos al pagar.",
    "docs_hardware_feat_kitchen": "<strong>Tickets de cocina:</strong> Envíe pedidos directamente a la cocina o barra.",
    "docs_hardware_feat_routing": "<strong>Enrutamiento de estación:</strong> Enrute artículos específicos (por ejemplo, bebidas vs comida) a diferentes impresoras.",
    "docs_hardware_feat_templates": "<strong>Plantillas:</strong> Personalice diseños de impresión con el editor de plantillas.",
    "docs_hardware_feat_queue": "<strong>Supervisión de cola:</strong> Rastree trabajos de impresión y estado.",
    "docs_hardware_supported_title": "Hardware compatible",
    "docs_hardware_bt": "<strong>Impresoras Bluetooth:</strong> Para configuraciones móviles y de tableta.",
    "docs_hardware_network": "<strong>Impresoras de red (LAN):</strong> Conexión ethernet estándar para confiabilidad.",
    "docs_hardware_recovery_note": "<strong>Característica:</strong> La recuperación automática asegura que los trabajos de impresión no se pierdan si una impresora se desconecta temporalmente.",
    "docs_int_title": "Integraciones",
    "docs_int_lead": "Conecte Finio con herramientas de terceros para ampliar su ecosistema.",
    "docs_int_web_title": "Integraciones Web",
    "docs_int_api": "<strong>API de Producto:</strong> Sincronice productos e inventario con sistemas externos.",
    "docs_int_kds": "<strong>Web KDS:</strong> Acceso al sistema de pantalla de cocina basado en navegador.",
    "docs_int_pms": "<strong>Web PMS:</strong> Interfaz del sistema de gestión de propiedades.",
    "docs_int_feedback": "<strong>Web de Comentarios:</strong> Recopilación de comentarios de clientes.",
    "docs_int_waiter": "<strong>Camarero / Autopedido:</strong> Pedidos móviles para personal o clientes.",
    "docs_int_arch_title": "Arquitectura de servicio",
    "docs_int_arch_intro": "Todos los servicios web están diseñados para seguridad y rendimiento:",
    "docs_int_local": "<strong>Ejecución local:</strong> Ejecute servicios localmente para baja latencia.",
    "docs_int_auth": "<strong>Autenticado:</strong> Acceso seguro a través de credenciales de inquilino.",
    "docs_int_rate": "<strong>Limitado por tasa:</strong> Protección de ID contra abuso.",
    "docs_int_limited": "<strong>Aislado por inquilino:</strong> Separación de datos para entornos multiinquilino.",
    "docs_comp_title": "Contabilidad y Cumplimiento",
    "docs_comp_lead": "Modelo de Contabilidad Central: Cómo Finio protege sus datos financieros.",
    "docs_comp_model_title": "Modelo de Contabilidad Central",
    "docs_comp_immutable_title": "Facturas inmutables",
    "docs_comp_immutable_desc": "Una vez finalizada una factura, no se puede editar ni eliminar. Existe permanentemente en el libro mayor.",
    "docs_comp_append_title": "Libro mayor de solo adición",
    "docs_comp_append_desc": "Los pagos y actualizaciones de saldo son aditivos. Nunca sobrescribimos un registro; solo agregamos nuevas entradas compensatorias.",
    "docs_comp_safety_title": "Seguridad de transacciones",
    "docs_comp_safety_desc": "Todas las operaciones son atómicas. Si ocurre un fallo, el sistema se recupera a un estado consistente sin pérdida de datos.",
    "docs_sec_title": "Seguridad y Protección de Datos",
    "docs_sec_lead": "Finio emplea un modelo de seguridad \"Audit-First\" diseñado para el cumplimiento financiero y la integridad de los datos.",
    "docs_sec_enc_title": "Cifrado de datos",
    "docs_sec_enc_intro": "Tratamos los datos de su negocio como activos financieros confidenciales.",
    "docs_sec_table_state": "Estado",
    "docs_sec_table_method": "Método",
    "docs_sec_table_detail": "Detalle",
    "docs_sec_rest_cloud": "<strong>En reposo (Nube)</strong>",
    "docs_sec_rest_cloud_desc": "Almacenamiento cifrado de Google Cloud Firestore.",
    "docs_sec_rest_device": "<strong>En reposo (Dispositivo)</strong>",
    "docs_sec_rest_device_desc": "Los archivos de base de datos locales están cifrados para evitar la extracción de tabletas robadas.",
    "docs_sec_transit": "<strong>En tránsito</strong>",
    "docs_sec_transit_desc": "Todo el tráfico de sincronización fluye a través de canales HTTPS seguros.",
    "docs_sec_rbac_title": "Control de acceso basado en roles (RBAC)",
    "docs_sec_rbac_intro": "Los permisos se aplican estrictamente a nivel de interfaz de usuario y API.",
    "docs_sec_role_owner": "<strong>Propietario:</strong> Acceso total a todos los ajustes, contabilidad y eliminaciones.",
    "docs_sec_role_manager": "<strong>Gerente:</strong> Puede anular artículos y ejecutar informes de fin de día, pero no puede eliminar historial.",
    "docs_sec_role_cashier": "<strong>Cajero:</strong> Puede crear ventas y aceptar pagos. No puede anular pedidos enviados sin aprobación.",
    "docs_sec_role_waiter": "<strong>Camarero:</strong> Restringido a la creación de pedidos solamente. Sin acceso a pagos o cajón.",
    "docs_sec_audit_title": "Registros de auditoría inmutables",
    "docs_sec_audit_intro": "Cada acción crítica activa una entrada permanente en la colección <code>audit_log</code>. Este registro es visible en el Panel Web de Contador.",
    "docs_sec_audit_events": "<strong>Eventos registrados:</strong> Anulaciones, Reembolsos, Descuentos (>10%), Aperturas de cajón, Cambios de precios.",
    "docs_sec_audit_meta": "<strong>Metadatos:</strong> ID de usuario, Marca de tiempo exacta, ID de dispositivo, Ubicación GPS (opcional).",
    "docs_sec_alert_box": "<strong>Sistema de alerta:</strong> Patrones sospechosos (por ejemplo, 5 anulaciones en 1 minuto) activan notificaciones automáticas de \"Bandera Roja\" al propietario del negocio a través de WhatsApp.",
    "docs_ts_title": "Solución de problemas",
    "docs_ts_lead": "Encuentre soluciones rápidas a problemas operativos comunes.",
    "docs_ts_issues_title": "Problemas comunes",
    "docs_ts_printer_title": "La impresora no imprime",
    "docs_ts_printer_1": "<strong>Verificar red:</strong> Asegúrese de que la impresora esté conectada al mismo Wi-Fi/LAN.",
    "docs_ts_printer_2": "<strong>Reiniciar servicio:</strong> Reinicie el Servicio de Impresora Finio o la impresora misma.",
    "docs_ts_printer_3": "<strong>Reasignar plantilla:</strong> Vaya a Configuración → Hardware y verifique la asignación de la impresora.",
    "docs_ts_sync_title": "La sincronización no funciona",
    "docs_ts_sync_1": "<strong>Verificar internet:</strong> Verifique que su dispositivo tenga una conexión activa.",
    "docs_ts_sync_2": "<strong>Sincronización automática:</strong> La sincronización se reanuda automáticamente una vez en línea; no es necesaria ninguna acción manual.",
    "docs_ts_sync_3": "<strong>Seguridad de datos:</strong> Sus datos están seguros localmente y se cargarán cuando sea posible.",
    "docs_ts_balance_title": "Saldo de efectivo incorrecto",
    "docs_ts_balance_1": "<strong>Sesiones:</strong> Verifique si la sesión de registro correcta está abierta.",
    "docs_ts_balance_2": "<strong>Reembolsos:</strong> Verifique si se procesaron reembolsos después de que comenzó la sesión.",
    "docs_ts_balance_3": "<strong>Fin de día:</strong> Revise los informes de cierre de fin de día en busca de discrepancias.",
    "docs_ts_help_box": "<strong>¿Necesita más ayuda?</strong> Contacte al soporte en <a href=\"mailto:support\u0040finio.pro\">support\u0040finio.pro</a>.",

    // Compliance Extended (Spanish)
    "docs_comp_intro_title": "Cómo funciona la contabilidad en Finio",
    "docs_comp_intro_p1": "Finio no es un \"simple POS\". Es un sistema de transacciones contablemente seguro diseñado para proteger los datos financieros contra manipulación, errores, fallos y conflictos entre dispositivos.",
    "docs_comp_intro_p2": "Esta sección explica cómo funciona la contabilidad de Finio, por qué algunas acciones están restringidas y cómo esto protege su negocio durante las auditorías.",
    "docs_comp_invoice_title": "¿Qué es una factura?",
    "docs_comp_invoice_p1": "Una factura en Finio es un documento contable legal, no un borrador.",
    "docs_comp_invoice_frozen": "Cuando se crea una factura, lo siguiente se congela permanentemente:",
    "docs_comp_invoice_list_1": "Precios de productos",
    "docs_comp_invoice_list_2": "Impuestos",
    "docs_comp_invoice_list_3": "Descuentos",
    "docs_comp_invoice_list_4": "Totales",
    "docs_comp_immutability_title": "Inmutabilidad de la factura (Regla crítica)",
    "docs_comp_immutability_intro": "Una vez creada:",
    "docs_comp_immutability_list_1": "No puede editar los totales",
    "docs_comp_immutability_list_2": "No puede cambiar el impuesto",
    "docs_comp_immutability_list_3": "No puede modificar los artículos",
    "docs_comp_immutability_why": "<strong>¿Por qué?</strong> Porque editar facturas después de su creación rompe las pistas de auditoría, invalida los informes fiscales y crea riesgo de fraude.",
    "docs_comp_immutability_compliance": "Esto es intencional y requerido para el cumplimiento.",
    "docs_comp_payments_title": "Pagos: Libro mayor de solo adición",
    "docs_comp_payments_intro": "Finio no modifica los pagos. En su lugar, utiliza un libro mayor de pagos de solo adición.",
    "docs_comp_payments_how_title": "Cómo funcionan los pagos",
    "docs_comp_payments_list_1": "Cada pago se agrega como un nuevo registro",
    "docs_comp_payments_list_2": "Los pagos nunca se sobrescriben",
    "docs_comp_payments_list_3": "Los reembolsos son nuevas entradas en el libro mayor, no ediciones",
    "docs_comp_payments_guarantee": "Esto garantiza un historial de pagos completo, resistencia a la manipulación y sincronización segura entre dispositivos.",
    "docs_comp_example_title": "Ejemplo",
    "docs_comp_table_action": "Acción",
    "docs_comp_table_result": "Lo que hace Finio",
    "docs_comp_table_row_1_a": "El cliente paga en efectivo",
    "docs_comp_table_row_1_b": "Agrega una entrada de pago",
    "docs_comp_table_row_2_a": "Cheque validado más tarde",
    "docs_comp_table_row_2_b": "Agrega una entrada de cambio de estado",
    "docs_comp_table_row_3_a": "Reembolso emitido",
    "docs_comp_table_row_3_b": "Agrega una entrada de reembolso",
    "docs_comp_table_row_4_a": "Conflicto de sincronización",
    "docs_comp_table_row_4_b": "Fusiona las entradas de forma segura",
    "docs_comp_refunds_title": "Reembolsos (Sin edición de factura)",
    "docs_comp_refunds_intro": "Reembolsos en Finio:",
    "docs_comp_refunds_list_1": "NO editan la factura",
    "docs_comp_refunds_list_2": "NO eliminan pagos",
    "docs_comp_refunds_list_3": "Crean una entrada negativa en el libro mayor",
    "docs_comp_refunds_why": "<strong>Por qué esto importa:</strong> La venta original permanece visible, el reembolso se rastrea claramente y los auditores pueden reconstruir el historial completo.",
    "docs_comp_refunds_box": "<strong>Enrutamiento:</strong> Los reembolsos en efectivo reducen el cajón de efectivo. Los reembolsos con tarjeta reducen el saldo bancario. El crédito de la tienda aumenta el saldo del cliente.",
    "docs_comp_eod_title": "Períodos cerrados y protección de auditoría",
    "docs_comp_eod_intro": "Una vez que se completa un cierre de día (EOD):",
    "docs_comp_eod_list_1": "El período contable está bloqueado",
    "docs_comp_eod_list_2": "Las transacciones pasadas no se pueden modificar",
    "docs_comp_eod_list_3": "Los reembolsos después del EOD se registran como nuevos eventos",
    "docs_comp_eod_prevent": "Esto evita la manipulación con fecha anterior, las correcciones ocultas y las inconsistencias de auditoría.",
    "docs_comp_trust_title": "Por qué los contadores confían en Finio",
    "docs_comp_trust_intro": "Finio aplica controles contables empresariales, no limitaciones de POS:",
    "docs_comp_trust_list_1": "Facturas inmutables",
    "docs_comp_trust_list_2": "Pagos de solo adición",
    "docs_comp_trust_list_3": "Bloqueo de período",
    "docs_comp_trust_list_4": "Efectivo vinculado al registro",
    "docs_comp_trust_list_5": "Protección de hora del servidor",
    "docs_comp_reg_title": "Cajas registradoras y conciliación",
    "docs_comp_reg_what_title": "¿Qué es una caja registradora en Finio?",
    "docs_comp_reg_what_desc": "Una caja registradora representa un cajón de efectivo físico. Cada movimiento de efectivo debe estar vinculado a un registro, vinculado a una sesión abierta y rastreado por período. Esto garantiza la responsabilidad del efectivo.",
    "docs_comp_float_title": "Fondo de apertura",
    "docs_comp_float_step_1": "Abrir el registro",
    "docs_comp_float_step_2": "Ingresar el monto de efectivo de apertura",
    "docs_comp_float_desc": "Esto se convierte en la línea base para la conciliación.",
    "docs_comp_z_title": "Fin de día (Cierre Z)",
    "docs_comp_z_intro": "Al final del día o turno:",
    "docs_comp_z_step_1": "Realizar el cierre de fin de día",
    "docs_comp_z_step_2": "Finio calcula: Efectivo esperado, Efectivo real, Diferencia, Margen",
    "docs_comp_z_step_3": "El período está cerrado y bloqueado",
    "docs_comp_z_after": "Después de EOD:",
    "docs_comp_z_list_1": "No puede cambiar las ventas en efectivo pasadas",
    "docs_comp_z_list_2": "No puede cambiar la fecha de los pagos",
    "docs_comp_z_list_3": "La nueva actividad inicia un nuevo período",
    "docs_comp_reg_box": "<strong>Por qué el efectivo está estrictamente vinculado:</strong> Sin vinculación de registro, el efectivo no se rastrea y el robo no se puede detectar. Finio bloquea los pagos en efectivo sin un registro abierto y contexto de sesión.",
    "docs_comp_offline_title": "Comportamiento fuera de línea y multidispositivo",
    "docs_comp_offline_first_title": "Fuera de línea por diseño",
    "docs_comp_offline_first_desc": "Finio funciona sin internet. Todas las acciones se guardan localmente primero, se sincronizan de forma segura más tarde y nunca bloquean las ventas.",
    "docs_comp_drops_title": "¿Qué sucede si se corta Internet?",
    "docs_comp_drops_desc": "Nada se rompe. Las ventas continúan, los pagos se registran y la sincronización se reanuda automáticamente. Sin pérdida de datos.",
    "docs_comp_multi_title": "Seguridad multidispositivo (Muy importante)",
    "docs_comp_multi_scenario": "<strong>Escenario:</strong> Dos dispositivos, misma factura.",
    "docs_comp_multi_list_1": "Dispositivo A agrega pago",
    "docs_comp_multi_list_2": "Dispositivo B agrega otro pago",
    "docs_comp_multi_list_3": "Ambos se sincronizan más tarde",
    "docs_comp_multi_merge": "Finio fusiona los pagos, no sobrescribe. Esto es posible porque los pagos son de solo adición y las facturas tienen versiones.",
    "docs_comp_time_title": "Protección de hora del dispositivo",
    "docs_comp_time_desc": "Finio utiliza una hora alineada con el servidor para detectar la manipulación del reloj, evitar el cambio de fecha y proteger los períodos contables.",
    "docs_comp_faq_title": "Preguntas frecuentes (Contabilidad y Confianza)",
    "docs_comp_faq_1_q": "¿Puedo editar una factura después del pago?",
    "docs_comp_faq_1_a": "No. Las facturas son inmutables para proteger la integridad contable y las auditorías.",
    "docs_comp_faq_2_q": "¿Puedo eliminar un pago?",
    "docs_comp_faq_2_a": "No. Los pagos son entradas del libro mayor y no se pueden eliminar.",
    "docs_comp_faq_3_q": "¿Qué sucede si reembolso después del cierre del día?",
    "docs_comp_faq_3_a": "Se crea un nuevo registro de reembolso en el siguiente período. Los períodos anteriores permanecen bloqueados.",
    "docs_comp_faq_4_q": "¿Pueden dos cajeros usar el mismo registro?",
    "docs_comp_faq_4_a": "Sí, pero no al mismo tiempo. Cada registro tiene sesiones controladas.",
    "docs_comp_faq_5_q": "¿Qué pasa si la hora del dispositivo es incorrecta?",
    "docs_comp_faq_5_a": "Finio detecta y previene la manipulación de la hora.",
    "docs_comp_faq_6_q": "¿Es Finio seguro para auditorías fiscales?",
    "docs_comp_faq_6_a": "Sí. Aplica las reglas contables requeridas por los auditores y las autoridades fiscales.",
    "docs_comp_faq_7_q": "¿Pueden los contadores acceder a los datos de forma remota?",
    "docs_comp_faq_7_a": "Sí. A través del Panel Web Contador, no del POS.",
    "docs_comp_panel_title": "Panel Web Contador",
    "docs_comp_panel_desc": "Una plataforma web separada para contadores y gerentes. NO es un POS.",
    "docs_comp_panel_what": "Lo que los contadores pueden hacer",
    "docs_comp_panel_list_1": "Ver facturas",
    "docs_comp_panel_list_2": "Revisar pagos y reembolsos",
    "docs_comp_panel_list_3": "Rastrear gastos",
    "docs_comp_panel_list_4": "Gestionar nóminas y salarios",
    "docs_comp_panel_list_5": "Analizar márgenes y flujo de efectivo",
    "docs_comp_panel_list_6": "Exportar datos",
    "docs_comp_panel_list_7": "Monitorear múltiples sucursales",
    "docs_comp_panel_remote": "Todo de forma remota, sin acceder a los dispositivos POS.",
    "docs_comp_panel_box": "<strong>Por qué esto importa:</strong> Separación de funciones (sin acceso de cajero a herramientas contables) y flujos de trabajo de auditoría profesional. Esta es una característica empresarial importante.",
    "docs_comp_int_title": "Integraciones y recursos para desarrolladores",
    "docs_comp_api_title": "API de producto",
    "docs_comp_api_desc": "Para sistemas ERP, escáneres de inventario, aplicaciones externas y sistemas de almacén.",
    "docs_comp_api_sec": "<strong>Modelo de seguridad:</strong> Emparejamiento de dispositivos, autenticación basada en tokens, aislamiento de inquilinos, limitación de velocidad. No es posible el acceso entre inquilinos.",
    "docs_comp_woo_title": "Integración WooCommerce",
    "docs_comp_woo_list_1": "Sincronizar productos de Finio → WooCommerce",
    "docs_comp_woo_list_2": "Enviar pedidos de WooCommerce → Finio",
    "docs_comp_woo_limits": "<strong>Limitaciones importantes:</strong> Sincronización de productos unidireccional, los pedidos requieren aprobación, la sincronización de stock depende de la configuración.",
    "docs_comp_local_title": "Servidores web locales (Avanzado)",
    "docs_comp_local_desc": "Finio incluye servidores web integrados para tabletas de camareros, quioscos de autopedido, acceso web PMS y formularios de comentarios. Todos están autenticados, basados en LAN y con velocidad limitada.",
    "docs_comp_sec_title": "Modelo de seguridad",
    "docs_comp_sec_list_1": "Aislamiento de inquilinos",
    "docs_comp_sec_list_2": "Permisos basados en roles",
    "docs_comp_sec_list_3": "Cumplimiento de la hora del servidor",
    "docs_comp_sec_list_4": "Cifrado fuera de línea",
    "docs_comp_sec_list_5": "No hay datos compartidos entre empresas",

    // Offline Sync (Spanish)
    "docs_offline_title": "Arquitectura fuera de línea y Sincronización",
    "docs_offline_lead": "El sistema distribuido de Finio garantiza un 100% de tiempo de actividad a través de una arquitectura de base de datos Hive local.",
    "docs_offline_hive_title": "Base de datos local (Hive)",
    "docs_offline_hive_desc": "Finio no depende de una conexión constante a la nube. En su lugar, ejecuta una base de datos NoSQL completa (Hive) directamente en el hardware de su dispositivo.",
    "docs_offline_hive_list_1": "<strong>Velocidad de lectura/escritura:</strong> Operaciones instantáneas de latencia cero (< 16 ms).",
    "docs_offline_hive_list_2": "<strong>Soberanía de datos:</strong> Su catálogo completo de productos, lista de clientes e historial de transacciones residen en su dispositivo.",
    "docs_offline_hive_list_3": "<strong>Cifrado:</strong> Los datos locales se cifran en reposo utilizando los estándares AES-256.",
    "docs_offline_why_box": "<strong>Por qué esto importa:</strong> La mayoría de los sistemas POS web dejan de funcionar cuando se cae el WiFi. Finio continúa procesando ventas, imprimiendo recibos y abriendo cajones de efectivo porque la lógica es local.",
    "docs_offline_queue_title": "La cola de sincronización Delta",
    "docs_offline_queue_desc": "Cuando realiza una acción (por ejemplo, Cerrar una cuenta), Finio la escribe en una \"Cola de sincronización\" persistente.",
    "docs_offline_queue_step_1": "<strong>Confirmación de acción:</strong> La transacción se guarda en el libro mayor local al instante.",
    "docs_offline_queue_step_2": "<strong>Entrada en cola:</strong> Se agrega una carga útil JSON de la mutación a la tabla <code>sync_queue</code>.",
    "docs_offline_queue_step_3": "<strong>Trabajador en segundo plano:</strong> Un proceso en segundo plano intenta vaciar la cola a la nube.",
    "docs_offline_queue_step_4": "<strong>Lógica de reintento:</strong> Si la red está inactiva, el trabajador duerme y vuelve a intentarlo con retroceso exponencial.",
    "docs_offline_conflict_title": "Estrategia de resolución de conflictos",
    "docs_offline_conflict_desc": "En un entorno multidispositivo, Finio utiliza una <strong>Estrategia de fusión determinista</strong> para manejar colisiones.",
    "docs_offline_append_title": "Libro mayor de solo adición",
    "docs_offline_append_desc": "Nunca sobrescribimos los registros de pago. Si el Dispositivo A recibe $50 y el Dispositivo B recibe $20 para la misma factura:",
    "docs_offline_append_list_1": "El Dispositivo A confirma el ID de pago #101 ($50).",
    "docs_offline_append_list_2": "El Dispositivo B confirma el ID de pago #102 ($20).",
    "docs_offline_append_list_3": "El servidor en la nube acepta ambos.",
    "docs_offline_append_list_4": "El saldo de la factura se actualiza para reflejar $70 pagados.",
    "docs_offline_time_title": "Hora autorizada por el servidor",
    "docs_offline_time_desc": "Para evitar fraudes (por ejemplo, cambiar el reloj del dispositivo para cambiar la fecha de las ventas), todos los eventos de sincronización se marcan con <code>server_timestamp</code> al llegar. Los informes financieros utilizan esta hora del servidor para decir la verdad.",

    // KDS Workflow (Spanish)
    "docs_kds_title": "Sistema de visualización de cocina (KDS)",
    "docs_kds_lead": "Optimice sus operaciones de cocina con seguimiento de pedidos en tiempo real.",
    "docs_kds_overview_title": "Visión general",
    "docs_kds_overview_desc": "Vaya a: <strong>KDS</strong> desde el menú principal.",
    "docs_kds_list_1": "<strong>Pedidos en tiempo real:</strong> Los pedidos aparecen instantáneamente a medida que se realizan.",
    "docs_kds_list_2": "<strong>Agrupación de estaciones:</strong> Los pedidos se envían a estaciones específicas (Cocina, Bar, Parrilla).",
    "docs_kds_list_3": "<strong>Temporizadores:</strong> Rastree el tiempo de preparación de cada artículo.",
    "docs_kds_list_4": "<strong>Pantalla múltiple:</strong> Soporte para múltiples pantallas de visualización en diferentes estaciones.",
    "docs_kds_flow_title": "Flujo de estado",
    "docs_kds_flow_desc": "Los pedidos pasan por las siguientes etapas:",
    "docs_kds_flow_step_1": "<strong>Nuevo:</strong> Pedido recibido.",
    "docs_kds_flow_step_2": "<strong>En preparación:</strong> El personal reconoce el pedido.",
    "docs_kds_flow_step_3": "<strong>Listo:</strong> El artículo está cocinado y listo para servir.",
    "docs_kds_flow_step_4": "<strong>Servido:</strong> Pedido completado.",
    "docs_kds_note_box": "<strong>Nota:</strong> KDS funciona localmente a través de LAN, garantizando la funcionalidad incluso sin internet.",

    // PMS Overview (Spanish)
    "docs_pms_title": "Módulo PMS y Hotel",
    "docs_pms_lead": "Administre habitaciones, reservas e invitados de manera eficiente con nuestro Sistema de Gestión de Propiedades integrado.",
    "docs_pms_key_title": "Características clave",
    "docs_pms_key_desc": "Vaya a: <strong>PMS</strong> desde el menú principal.",
    "docs_pms_list_1": "<strong>Gestión de habitaciones:</strong> Descripción visual del estado y disponibilidad de las habitaciones.",
    "docs_pms_list_2": "<strong>Check-in / Check-out:</strong> Flujos simplificados de llegada y salida de huéspedes.",
    "docs_pms_list_3": "<strong>Seguimiento de ocupación:</strong> Supervise las tasas de ocupación en tiempo real.",
    "docs_pms_list_4": "<strong>Facturación integrada:</strong> Los cargos de la habitación se vinculan automáticamente a las facturas de los huéspedes.",
    "docs_pms_list_5": "<strong>Limpieza:</strong> Gestione los horarios de limpieza y el mantenimiento de las habitaciones.",
    "docs_pms_designed_title": "Diseñado para",
    "docs_pms_designed_1": "Hoteles",
    "docs_pms_designed_2": "Riads",
    "docs_pms_designed_3": "Casas de huéspedes",
    "docs_pms_designed_4": "Alquileres de corta duración",

    // Roles & Permissions (Spanish)
    "docs_roles_title": "Roles y permisos",
    "docs_roles_lead": "Controle el acceso y garantice la seguridad con una gestión de roles granular.",
    "docs_roles_manage_title": "Gestión de roles",
    "docs_roles_manage_desc": "Vaya a: <strong>Configuración → Roles y permisos</strong>",
    "docs_roles_list_1": "<strong>Crear roles personalizados:</strong> Defina roles únicos adaptados a las necesidades de su negocio (por ejemplo, Cajero, Gerente, Contador).",
    "docs_roles_list_2": "<strong>Permisos granulares:</strong> Asigne derechos específicos a cada rol.",
    "docs_roles_scopes_title": "Inmuebles de permisos",
    "docs_roles_scopes_desc": "Los permisos se pueden aplicar en varios módulos:",
    "docs_roles_scopes_1": "<strong>POS:</strong> Acceso a ventas, anulación de artículos, aplicación de descuentos.",
    "docs_roles_scopes_2": "<strong>Inventario:</strong> Agregar productos, ajustes de stock.",
    "docs_roles_scopes_3": "<strong>Contabilidad:</strong> Ver informes, cerrar turnos.",
    "docs_roles_scopes_4": "<strong>Configuración:</strong> Cambio de configuraciones del sistema.",

    // Finance & Closures
    "docs_faq_title": "Preguntas Frecuentes",
    "docs_faq_subtitle": "Respuestas a preguntas comunes sobre Finio POS, KDS y la plataforma de gestión.",
    "docs_faq_cat_pos": "POS y Ventas",
    "docs_faq_q_offline_sell": "¿Puedo vender productos sin conexión a Internet?",
    "docs_faq_a_offline_sell": "Sí. Finio es un sistema <strong>offline-first</strong>. Todas las ventas, cierres Z y movimientos de mesa se guardan localmente. El sistema sincroniza todo automáticamente con la nube una vez restaurada la conexión.",
    "docs_faq_q_prices": "¿El POS admite múltiples niveles de precios?",
    "docs_faq_a_prices": "Sí, puede configurar múltiples niveles de precios (ej: Happy Hour, VIP, Personal) y cambiar instantáneamente durante una venta.",
    "docs_faq_q_split": "¿Puedo dividir cuentas o fusionar mesas?",
    "docs_faq_a_split": "Absolutamente. Puede dividir pagos por monto o por artículo, y fusionar mesas o transferir artículos fácilmente.",
    "docs_faq_cat_sync": "Modo Offline y Sincronización",
    "docs_faq_q_break": "¿Qué pasa si mi tableta se rompe estando offline?",
    "docs_faq_a_break": "Finio está diseñado para proteger sus datos incluso en entornos sin conexión. Cuando varios dispositivos están conectados en la misma red local, comparten y sincronizan datos automáticamente entre sí sin conexión. Esto significa que sus datos se almacenan localmente en más de un dispositivo. Si un dispositivo se daña antes de sincronizar, los datos permanecen disponibles en los otros dispositivos de la red. Una vez que cualquier dispositivo se vuelve a conectar a Internet, todos los datos se sincronizan automáticamente con la nube.",
    "docs_faq_q_status": "¿Cómo sé si estoy en línea o no?",
    "docs_faq_a_status": "Un indicador en la esquina superior derecha de la pantalla POS muestra su estado: Verde para Online, y Gris/Rojo para Offline.",
    "docs_faq_cat_billing": "Suscripciones y Facturación",
    "docs_faq_q_plan": "¿Puedo cambiar mi plan más tarde?",
    "docs_faq_a_plan": "Sí, puede actualizar de Starter a Business en cualquier momento. El saldo restante de su suscripción actual se prorrateará.",
    "docs_faq_q_cancel": "¿Hay tarifa de cancelación?",
    "docs_faq_a_cancel": "No. Puede cancelar su suscripción en cualquier momento. Su acceso permanecerá activo hasta el final del ciclo de facturación.",
    "docs_faq_cat_hardware": "Hardware",
    "docs_faq_q_printers": "¿Qué impresoras son compatibles?",
    "docs_faq_a_printers": "Finio admite la mayoría de impresoras de red ESC/POS (Ethernet/WiFi), incluidas marcas como Epson, Star Micronics y Xprinter.",
    "docs_faq_q_tablet": "¿Puedo usar mi tableta actual?",
    "docs_faq_a_tablet": "Sí, Finio funciona en cualquier tableta moderna iOS (iPad) o Android. Recomendamos dispositivos con al menos 3GB de RAM.",
    "docs_faq_cat_security": "Seguridad y Datos",
    "docs_faq_q_access": "¿Quién tiene acceso a mis datos de ventas?",
    "docs_faq_a_access": "Solo usted y usuarios autorizados. El personal de Finio no puede acceder a sus datos sin permiso explícito para soporte.",
    "docs_faq_q_legal": "¿Son mis facturas legalmente compatibles?",
    "docs_faq_a_legal": "Sí. Finio genera facturas inmutables y numeradas secuencialmente que cumplen con las regulaciones fiscales. Una vez finalizada, una factura no puede ser alterada o eliminada.",
    "docs_finance_title": "Facturas y Cierres",
    "docs_finance_lead": "Rastree sus finanzas, gestione facturas y asegure su flujo de efectivo.",
    "docs_finance_invoices_title": "Facturas",
    "docs_finance_invoices_go_to": "Vaya a: <strong>Facturas</strong>",
    "docs_finance_invoices_history": "<strong>Ver historial:</strong> Acceda a todas las facturas pasadas.",
    "docs_finance_invoices_filtering": "<strong>Filtrado:</strong> Filtrar por fecha, estado, caja o usuario.",
    "docs_finance_invoices_actions": "<strong>Acciones:</strong> Imprimir o exportar facturas en PDF. También se admite compartir facturas públicamente.",
    "docs_finance_important_rules": "Reglas importantes:",
    "docs_finance_rule_1": "Los totales de las facturas son inmutables una vez creados.",
    "docs_finance_rule_2": "Los pagos son de solo adición (no se pueden eliminar, solo compensar).",
    "docs_finance_rule_3": "Los reembolsos nunca reescriben el historial; crean nuevas contra-transacciones.",
    "docs_finance_registers_title": "Cajas registradoras",
    "docs_finance_registers_sessions": "<strong>Sesiones:</strong> Cada caja registradora opera en sesiones.",
    "docs_finance_registers_tracking": "<strong>Seguimiento:</strong> Los pagos en efectivo deben estar vinculados a una sesión de registro abierta.",
    "docs_finance_registers_security": "<strong>Seguridad:</strong> Previene movimientos de efectivo no rastreados y mejora la responsabilidad.",
    "docs_finance_closures_title": "Fondo de apertura y Cierre de fin de día (Z)",
    "docs_finance_closures_start": "<strong>Inicio de turno:</strong> Establecer el monto del fondo de apertura.",
    "docs_finance_closures_end": "<strong>Fin de turno:</strong> Realizar el cierre de fin de día.",
    "docs_finance_closures_reconciliation": "<strong>Conciliación:</strong> El sistema calcula el efectivo esperado frente al real y destaca cualquier diferencia.",
    "docs_finance_closures_locking": "<strong>Bloqueo:</strong> Los períodos cerrados están bloqueados y no se pueden modificar.",

    // --- Changelog ---
    "nav_changelog_title": "Registro de Cambios",
    "changelog_version": "Versión 1.0 → Versión 2.0",
    "changelog_overview_title": "Descripción General",
    "changelog_overview_p1": "La Versión 1.0 de Finio fue un lanzamiento piloto, entregado a un número limitado de clientes seleccionados en diferentes sectores comerciales (restaurantes, comercio minorista, hostelería y servicios).",
    "changelog_overview_p2": "El objetivo de la v1.0 no era la escala, sino el aprendizaje.",
    "changelog_overview_p3": "A través de la colaboración directa con empresas reales, observamos cómo se comportent los sistemas POS bajo presión operativa real: uso sin conexión, errores del personal, reembolsos, pérdida de inventario, discrepancias de efectivo y uso multidispositivo.",
    "changelog_overview_p4": "La Versión 2.0 es el resultado de ese aprendizaje.",
    "changelog_overview_p5": "No es una iteración — es una actualización estructural, diseñada para:",
    "changelog_overview_list_1": "Minimizar el riesgo operativo",
    "changelog_overview_list_2": "Prevenir la manipulación silenciosa de datos",
    "changelog_overview_list_3": "Reducir el robo y los errores contables",
    "changelog_overview_list_4": "Identificar puntos débiles en las operaciones comerciales",
    "changelog_overview_list_5": "Preparar a las empresas para escalar sobre una base sólida",

    "changelog_v1_title": "Versión 1.0 — Fase Piloto (Aprendizaje Fundamental)",
    "changelog_v1_purpose_title": "Propósito",
    "changelog_v1_purpose_1": "Validar uso first-offline (sin conexión)",
    "changelog_v1_purpose_2": "Observar el comportamiento real del personal",
    "changelog_v1_purpose_3": "Entender puntos de falla en flujos de trabajo POS existentes",
    "changelog_v1_purpose_4": "Identificar dónde pierden dinero las empresas sin darse cuenta",

    "changelog_v1_char_title": "Características",
    "changelog_v1_char_1": "Funcionalidad central de POS",
    "changelog_v1_char_2": "Soporte básico sin conexión",
    "changelog_v1_char_3": "Seguimiento de inventario y ventas",
    "changelog_v1_char_4": "Flujos de conciliación manual",
    "changelog_v1_char_5": "Protección limitada contra mutación de datos",
    "changelog_v1_char_6": "Informes reactivos (qué pasó, no por qué)",

    "changelog_findings_title": "Hallazgos Clave de Clientes Piloto",
    "changelog_findings_intro": "Durante el uso en el mundo real, identificamos problemas recurrentes:",
    "changelog_findings_1": "Facturas modificadas después del pago",
    "changelog_findings_2": "Pagos sobrescritos o perdidos durante la sincronización",
    "changelog_findings_3": "Productos con muchos reembolsos que ocultan problemas de inventario o calidad",
    "changelog_findings_4": "Discrepancias de efectivo sin causa clara",
    "changelog_findings_5": "Conflictos multidispositivo que causan inconsistencia de datos",
    "changelog_findings_6": "Empresas escalando antes de corregir debilidades internas",
    "changelog_findings_outro": "Estos conocimientos formaron directamente la Versión 2.0.",

    "changelog_v2_title": "Versión 2.0 — Producción y Control de Riesgos",
    "changelog_v2_desc": "La Versión 2.0 fue reconstruida con control, integridad y prevención como principios de primera clase.",

    "changelog_acc_title": "Contabilidad e Integridad de Datos (Mejora Mayor)",
    "changelog_acc_inv_title": "Facturas Inmutables",
    "changelog_acc_inv_1": "Una vez emitida una factura, los totales financieros no pueden alterarse.",
    "changelog_acc_inv_2": "Previene ediciones silenciosas, manipulación o cambios posteriores al pago.",
    "changelog_acc_inv_3": "Garantiza registros contables a prueba de auditorías.",

    "changelog_acc_pay_title": "Libro de Pagos de Solo Adición",
    "changelog_acc_pay_1": "Los pagos nunca se sobrescriben ni eliminan.",
    "changelog_acc_pay_2": "Cada pago, reembolso o cambio de estado se registra como una nueva entrada.",
    "changelog_acc_pay_3": "Trazabilidad completa de todas las acciones financieras.",

    "changelog_acc_atomic_title": "Transacciones Atómicas",
    "changelog_acc_atomic_1": "Los pagos se procesan en operaciones atómicas a prueba de fallos.",
    "changelog_acc_atomic_2": "Sin estados parciales si el dispositivo se apaga a mitad de la transacción.",
    "changelog_acc_atomic_3": "Elimina pagos fantasma y saldos no coincidentes.",

    "changelog_acc_time_title": "Cumplimiento de Hora del Servidor",
    "changelog_acc_time_1": "Previene la manipulación del reloj del dispositivo.",
    "changelog_acc_time_2": "Todos los eventos financieros siguen una línea de tiempo confiable y monótona.",

    "changelog_off_title": "Fiabilidad Offline y Multidispositivo",
    "changelog_off_design_title": "Diseño Offline-First",
    "changelog_off_design_1": "Operación completa sin internet.",
    "changelog_off_design_2": "Sin degradación de funciones cuando está desconectado.",

    "changelog_off_local_title": "Sincronización de Red Local",
    "changelog_off_local_1": "Dispositivos en la misma red local comparten datos sin conexión.",
    "changelog_off_local_2": "Crea redundancia local entre dispositivos.",
    "changelog_off_local_3": "Los datos sobreviven incluso si un dispositivo se daña.",

    "changelog_off_sync_title": "Sincronización Multidispositivo Segura",
    "changelog_off_sync_1": "Pagos y facturas se fusionan de forma segura entre dispositivos.",
    "changelog_off_sync_2": "Sin transacciones perdidas al usar múltiples terminales.",
    "changelog_off_sync_3": "La autoridad del servidor resuelve conflictos sin sobrescribir el historial.",

    "changelog_off_cloud_title": "Recuperación Automática en la Nube",
    "changelog_off_cloud_1": "Tan pronto como cualquier dispositivo se reconecta, todos los datos se sincronizan automáticamente.",

    "changelog_inv_title": "Inventario, Reembolsos y Prevención de Pérdidas",
    "changelog_inv_refund_title": "Inteligencia de Reembolsos",
    "changelog_inv_refund_1": "Los reembolsos se tratan como reversiones financieras, no gastos genéricos.",
    "changelog_inv_refund_2": "Vínculo claro entre reembolsos, productos y métodos de pago.",

    "changelog_inv_risk_title": "Detección de Riesgo de Producto",
    "changelog_inv_risk_1": "Identificación de productos con: Altas tasas de reembolso, quejas frecuentes o discrepancias de stock.",
    "changelog_inv_risk_2": "Ayuda a detectar: Malos proveedores, problemas de calidad o fraude.",

    "changelog_inv_stock_title": "Mejoras en Integridad de Stock",
    "changelog_inv_stock_1": "Mejor seguimiento de artículos dañados, devueltos y no vendibles.",
    "changelog_inv_stock_2": "Reducción de la deriva de inventario con el tiempo.",

    "changelog_health_title": "Salud del Negocio y Apoyo a Decisiones (Nuevo)",
    "changelog_health_ind_title": "Indicadores de Salud del Negocio",
    "changelog_health_ind_1": "Señales de alerta temprana para: reembolsos excesivos, márgenes reducidos, desequilibrio de efectivo, fuga de stock.",
    "changelog_health_ind_2": "Diseñado para solucionar problemas antes de escalar, no después del fallo.",

    "changelog_health_ops_title": "Visibilidad Operativa",
    "changelog_health_ops_1": "Separación clara entre: rendimiento de ventas, problemas operativos e inconsistencias contables.",

    "changelog_cash_title": "Control de Efectivo y Conciliación",
    "changelog_cash_bind_title": "Vinculación de Caja Registradora",
    "changelog_cash_bind_1": "Cada movimiento de efectivo debe estar vinculado a una sesión activa.",
    "changelog_cash_bind_2": "Previene operaciones de efectivo no rastreables.",

    "changelog_cash_eod_title": "Períodos Contables Cerrados (Bloqueo EOD)",
    "changelog_cash_eod_1": "Una vez cerrado el día, los datos históricos están protegidos.",
    "changelog_cash_eod_2": "Previene manipulación retroactiva.",
    "changelog_cash_eod_3": "Garantiza una conciliación diaria limpia.",

    "changelog_plat_title": "Mejoras de Plataforma y Arquitectura",
    "changelog_plat_mod_title": "Madurez del Sistema Modular",
    "changelog_plat_mod_1": "POS, KDS, Inventario, PMS, Entrega, Panel Contable totalmente alineados.",
    "changelog_plat_mod_2": "Clara separación de roles (Cajero, Gerente, Contador, Admin).",

    "changelog_plat_web_title": "Paneles Web Mejorados",
    "changelog_plat_web_1": "Acceso de contables y gestión para: Gastos, Salarios, Facturas B2B, Estadísticas financieras, Supervisión multifilial.",

    "changelog_reps_title": "Lo que Representa la Versión 2.0",
    "changelog_reps_intro": "Finio v2.0 está construido para empresas que quieren:",
    "changelog_reps_list_1": "Crecer sin perder el control",
    "changelog_reps_list_2": "Prevenir robos y errores silenciosos",
    "changelog_reps_list_3": "Detectar malos productos temprano",
    "changelog_reps_list_4": "Confiar en sus números",
    "changelog_reps_list_5": "Escalar sobre una base operativa saludable",
    "changelog_reps_outro": "Ya no es solo un POS — es una plataforma de integridad empresarial.",

    "changelog_future_title": "Mirando Hacia Adelante",
    "changelog_future_1": "La Versión 2.0 establece la base no negociable.",
    "changelog_future_2": "Las versiones futuras se construirán sobre esta integridad, no alrededor de ella.",

  },
  el: {
    "home": "Αρχική",
    "products": "Οι Ενότητές μας",
    "subscriptions": "Συνδρομές",
    "features": "Βασικά Χαρακτηριστικά",
    "coming_soon": "Καινοτομίες",
    "contact_us": "Επικοινωνία",
    "hero_title": "Το Offline-First POS με Ακεραιότητα Λογιστικού Επιπέδου",
    "hero_subtitle": "Κατασκευασμένο για επιχειρήσεις που απαιτούν ακρίβεια. Διαθέτει αμετάβλητα τιμολόγια, καθολικό πληρωμών μόνο για προσθήκη και συγχρονισμό πολλαπλών συσκευών ασφαλή έναντι διενέξεων.",
    "book_demo": "Εξερευνήστε την Πλατφόρμα",
    "hero_badge_new": "Finio 2.0: Τώρα με Ενισχυμένη Ασφάλεια Ελέγχου",

    "// --- Value Pillars ---": "",
    "pillar_offline_title": "Πραγματική Αρχιτεκτονική Offline-First",
    "pillar_offline_desc": "Όχι απλώς 'διαλείπουσα' υποστήριξη. Το Finio αποθηκεύει το 100% των δεδομένων τοπικά και συγχρονίζει χωρίς διενέξεις όταν είναι online. Μηδενικός χρόνος διακοπής λειτουργίας.",
    "pillar_integrity_title": "Αμετάβλητος Λογιστικός Πυρήνας",
    "pillar_integrity_desc": "Τα τιμολόγια δεν μπορούν να τροποποιηθούν μετά τη δημιουργία τους. Οι πληρωμές είναι μόνο για προσθήκη (append-only). Η οικονομική σας ακεραιότητα είναι μαθηματικά εγγυημένη.",
    "pillar_conflict_title": "Ατομικές Συναλλαγές (Atomic Transactions)",
    "pillar_conflict_desc": "Οι λειτουργίες ανθεκτικές σε σφάλματα (crash-safe) διασφαλίζουν ότι εάν μια συσκευή αποτύχει στη μέση μιας συναλλαγής, τα δεδομένα σας παραμένουν συνεπή και χωρίς φθορές.",
    "pillar_modular_title": "Κλειστές Λογιστικές Περίοδοι",
    "pillar_modular_desc": "Το αυστηρό κλείδωμα στο τέλος της ημέρας αποτρέπει την αναδρομική παραποίηση ιστορικών οικονομικών περιόδων.",

    "// --- Why Finio Section ---": "",
    "why_finio_title": "Γιατί το Finio δεν είναι ένα τυπικό POS",
    "why_finio_subtitle": "Τα περισσότερα συστήματα POS είναι απλώς αριθμομηχανές. Το Finio είναι μια λογιστική μηχανή.",

    "why_finio_card_1_title": "Αριθμομηχανές vs. Καθολικά",
    "why_finio_card_1_desc": "Τα γενικά συστήματα POS αντιμετωπίζουν τις πωλήσεις ως προσωρινούς αριθμούς. Το Finio αντιμετωπίζει κάθε πώληση ως μόνιμο, αμετάβλητο οικονομικό αρχείο.",

    "why_finio_card_2_title": "Εξαρτώμενο από το Cloud vs. Offline-First",
    "why_finio_card_2_desc": "Άλλα σταματούν να λειτουργούν όταν πέφτει το διαδίκτυο. Το Finio λειτουργεί τοπικά με ακεραιότητα επιπέδου διακομιστή, συγχρονίζοντας μόνο όταν είναι σταθερό.",

    "why_finio_card_3_title": "Πληθώρα Χαρακτηριστικών vs. Ακρίβεια",
    "why_finio_card_3_desc": "Δεν κυνηγάμε χαρακτηριστικά που θέτουν σε κίνδυνο την αξιοπιστία. Κάθε ενότητα έχει κατασκευαστεί για να διασφαλίζει ότι οι αριθμοί σας ταιριάζουν με την πραγματικότητα.",

    "// --- Special Offer Section ---": "",
    "home_offer_badge": "Περιορισμένη Προσφορά για Πρώιμους Συνδρομητές",
    "home_offer_title": "Γίνετε από τους Πρώτους & Αποκτήστε 3 Μήνες Δωρεάν",
    "home_offer_desc": "Γίνετε μέλος στους πρώτους 100 συνδρομητές και κερδίστε 3 μήνες δωρεάν σε οποιοδήποτε πρόγραμμα.",
    "home_offer_btn": "Λήψη Προσφοράς",

    "// --- Solutions Section ---": "",
    "solutions_title": "Εξατομικευμένες Λύσεις για Κάθε Κλάδο",
    "solution_restaurant_title": "Εστιατόρια & Καφετέριες",
    "solution_restaurant_desc": "Διαχειριστείτε παραγγελίες, κουζίνα, διανομή (delivery) και κρατήσεις από μία ενιαία πλατφόρμα.",
    "solution_retail_title": "Καταστήματα Λιανικής",
    "solution_retail_desc": "Βελτιστοποιήστε τα αποθέματα, τις πωλήσεις και την αφοσίωση πελατών με ευκολία.",
    "solution_service_title": "Επιχειρήσεις Παροχής Υπηρεσιών",
    "solution_service_desc": "Λύσεις κρατήσεων, τιμολόγησης και CRM για παρόχους υπηρεσιών και ξενοδοχεία.",

    "// --- Modules Section ---": "",
    "our_modules_title": "Ένα Οικοσύστημα Έτοιμο για Παραγωγή",

    "// 1. Sales & Operations": "",
    "mod_sales_title": "Πωλήσεις & Λειτουργίες",
    "mod_pos_title": "Σύστημα POS",
    "mod_pos_desc": "POS υψηλής ταχύτητας offline-first με σάρωση barcode, διαχείριση πολλαπλών καλαθιών και συγχρονισμό χωρίς διενέξεις.",
    "mod_kds_title": "Σύστημα Προβολής Κουζίνας (KDS)",
    "mod_kds_desc": "Άμεση σύνδεση μεταξύ σάλας και κουζίνας. Δρομολόγηση σταθμών σε πραγματικό χρόνο, χρονόμετρα και παρακολούθηση κατάστασης παραγγελιών.",
    "mod_delivery_title": "Διανομή & Παραγγελίες",
    "mod_delivery_desc": "Διαχειριστείτε πλατφόρμες διανομής, οδηγούς και τηλεφωνικές παραγγελίες αποτελεσματικά από έναν ενιαίο πίνακα ελέγχου.",

    "// 2. Accounting & Finance": "",
    "mod_finance_title": "Λογιστική & Οικονομικά",
    "mod_invoice_title": "Μηχανή Τιμολόγησης",
    "mod_invoice_desc": "Δημιουργήστε αμετάβλητα, φορολογικά συμβατά τιμολόγια. Παρακολουθήστε πληρωμές, καταθέσεις και πιστωτικό ιστορικό πελατών.",
    "mod_cash_title": "Διαχείριση Μετρητών",
    "mod_cash_desc": "Ασφαλές ταμείο έναρξης, τυφλά κλεισίματα και ακριβής συμφωνία μετρητών για την αποτροπή κλοπής και σφαλμάτων.",
    "mod_expense_title": "Έξοδα & Κεφάλαιο",
    "mod_expense_desc": "Παρακολουθήστε μικροέξοδα, πληρωμές προμηθευτών και λειτουργικά κόστη απευθείας από το ταμείο.",

    "// 3. Management & Back-Office": "",
    "mod_mgmt_title": "Διαχείριση & Back-Office",
    "mod_inventory_title": "Έλεγχος Αποθεμάτων",
    "mod_inventory_desc": "Παρακολούθηση αποθεμάτων σε πραγματικό χρόνο με αρχεία κίνησης, ειδοποιήσεις χαμηλού αποθέματος και εντολές αγοράς προμηθευτών.",
    "mod_crm_title": "Πελάτες & Προμηθευτές",
    "mod_crm_desc": "Λεπτομερή προφίλ CRM με ιστορικό αγορών, πόντους επιβράβευσης και καθολικά προμηθευτών.",
    "mod_admin_title": "Web Panel Λογιστή",
    "mod_admin_desc": "Απομακρυσμένη πρόσβαση για λογιστές για έλεγχο πωλήσεων, εξαγωγή ημερολογίων και διαχείριση ρυθμίσεων.",

    "// 4. Hospitality & Advanced": "",
    "mod_hosp_title": "Φιλοξενία & Προηγμένα",
    "mod_floor_title": "Διαχείριση Ορόφου/Σάλας",
    "mod_floor_desc": "Οπτικός χάρτης τραπεζιών με κατάσταση πληρότητας, διαχείριση κρατήσεων και ανάθεση σερβιτόρων.",
    "mod_pms_title": "Μονάδα Ξενοδοχείου & PMS",
    "mod_pms_desc": "Πλήρης διαχείριση ξενοδοχείου: κράτηση δωματίων, καθαριότητα και ενσωμάτωση check-in/out επισκεπτών.",
    "mod_api_title": "Οικοσύστημα Open API",
    "mod_api_desc": "Συνδέστε εφαρμογές τρίτων, κιόσκια αυτοεξυπηρέτησης και πλατφόρμες διαδικτυακών παραγγελιών μέσω του ασφαλούς API μας.",

    "// --- Legacy Keys (Restored for Compatibility) ---": "",
    "pos_module_title": "Κεντρική Μηχανή POS",
    "pos_module_description": "Εγγενής offline συναλλακτικός πυρήνας. Χειρίζεται διαχωρισμένες πληρωμές, έλεγχο ακυρώσεων και διαχείριση συνεδριών χωρίς εξάρτηση από το διαδίκτυο.",
    "kds_module_title": "Σύστημα Παραγωγής",
    "kds_module_description": "Δρομολόγηση μηδενικής καθυστέρησης (Zero-latency). Οι παραγγελίες αποστέλλονται και ξεχνιούνται από το POS, με εγγυημένη παράδοση στους σταθμούς προετοιμασίας με οπτικά χρονόμετρα.",
    "inventory_module_title": "Διαρκής Απογραφή",
    "inventory_module_description": "Παρακολούθηση κόστους FIFO με αφαίρεση συνταγών. Υποστηρίζει σύνθετα εμφωλευμένα μίγματα και ροές εργασίας μεταφοράς από κεντρική αποθήκη.",
    "pms_module_title": "Διαχείριση Ακινήτων",
    "pms_module_description": "Διαχείριση φακέλων (folio) ενσωματωμένη με το POS. Οι χρεώσεις δωματίου είναι ατομικές συναλλαγές που επικυρώνονται έναντι της κατάστασης του επισκέπτη.",
    "printing_module_title": "Διακομιστής Δρομολόγησης Εκτύπωσης",
    "printing_module_description": "Δρομολόγηση εκτύπωσης βάσει κανόνων. Υποστηρίζει αναδρομολόγηση σε περίπτωση αποτυχίας (failover), παλαιού τύπου υλικό ESC/POS και εισαγωγή προσαρμοσμένων προτύπων.",
    "web_server_module_title": "Κεντρική Διοίκηση",
    "web_server_module_description": "Συγκέντρωση δεδομένων πολλαπλών τοποθεσιών σε πραγματικό χρόνο. Προωθήστε ενημερώσεις μενού, τραβήξτε αρχεία ελέγχου και διαχειριστείτε δικαιώματα χρηστών παγκοσμίως.",

    "// --- Services Section ---": "",
    "professional_services_title": "Επαγγελματικές Υπηρεσίες",
    "custom_software_title": "Ανάπτυξη Εξειδικευμένου Λογισμικού",
    "custom_software_description": "Προσφέρουμε λύσεις λογισμικού προσαρμοσμένες στις μοναδικές ανάγκες της επιχείρησής σας, από web έως mobile εφαρμογές.",
    "hardware_software_title": "Λύσεις Υλικού & Λογισμικού",
    "hardware_software_description": "Παρέχουμε απρόσκοπτη ενσωμάτωση υλικού και λογισμικού για την αποδοτική λειτουργία των συστημάτων σας, συμπεριλαμβανομένης της υποστήριξης και εγκατάστασης.",
    "consulting_title": "Συμβουλευτική & Ολοκληρωμένες Λύσεις",
    "consulting_description": "Η ομάδα μας προσφέρει ολοκληρωμένη συμβουλευτική για να σας βοηθήσει να επιλέξετε και να εφαρμόσετε τις καλύτερες τεχνολογικές λύσεις για το έργο σας.",

    "// --- Core Features Section ---": "",
    "core_features_title": "Αρχιτεκτονική Συστήματος & Δυνατότητες",

    "// 1. Accounting-Grade Protection": "",
    "feat_protect_title": "Αμετάβλητος Οικονομικός Πυρήνας",
    "feat_protect_desc": "Καταγραφή διπλογραφικού καθολικού με επαλήθευση μόνο για προσθήκη (append-only). Τα ιστορικά δεδομένα είναι μαθηματικά κλειδωμένα ενάντια σε παραποιήσεις.",

    "// 2. Offline-First Sync Engine": "",
    "feat_sync_engine_title": "Αναπαραγωγή Χωρίς Διενέξεις",
    "feat_sync_engine_desc": "Αρχιτεκτονική βάσης δεδομένων με προτεραιότητα στο τοπικό (Hive). Οι λειτουργίες δεσμεύονται τοπικά αμέσως και αναπαράγονται στο cloud με τελική συνέπεια.",

    "// 3. Trusted Time & Audit Safety": "",
    "feat_audit_title": "Χρόνος Εξουσιοδοτημένος από τον Διακομιστή",
    "feat_audit_desc": "Οι συναλλαγές φέρουν χρονική σήμανση από την αρχή του διακομιστή για να αποτραπεί η χειραγώγηση του τοπικού ρολογιού και η απάτη κατά το κλείσιμο.",

    "// 4. Professional Closures": "",
    "feat_closure_title": "Επαγγελματικά Κλεισίματα Ημέρας",
    "feat_closure_desc": "Διαχειριστείτε ταμεία έναρξης, πραγματοποιήστε τυφλά κλεισίματα Z και συμφωνήστε μετρητά με αυτοματοποιημένη παρακολούθηση αποκλίσεων.",

    "// 5. Roles & Permissions": "",
    "feat_roles_title": "Έλεγχος Πρόσβασης Βάσει Ρόλων (RBAC)",
    "feat_roles_desc": "Ορίστε ακριβή επίπεδα πρόσβασης για ταμίες, διευθυντές, λογιστές και προσωπικό κουζίνας με λεπτομερείς πύλη δικαιωμάτων.",

    "// 6. Integrations & APIs": "",
    "feat_integrations_title": "Ενσωματώσεις & APIs",
    "feat_integrations_desc": "Συνδεθείτε με ERPs, WooCommerce και πύλες πληρωμών μέσω του εκτεταμένου API προγραμματιστών μας.",

    // --- Όλα περιλαμβάνονται ---
    "all_features_title": "Όλα περιλαμβάνονται",
    "all_features_subtitle": "Ισχυρές δυνατότητες ήδη ενσωματωμένες στο Finio — πέρα από τα βασικά.",
    "all_group_revenue": "Έσοδα & Ανάπτυξη",
    "all_revenue_giftcards_title": "Δωροκάρτες",
    "all_revenue_giftcards_desc": "Έκδοση, επαναφόρτιση και ακύρωση δωροκαρτών, με ταμειακή παρακολούθηση και ζωντανό πίνακα υποχρεώσεων.",
    "all_revenue_loyalty_title": "Πρόγραμμα επιβράβευσης",
    "all_revenue_loyalty_desc": "Επίπεδα Bronze, Silver και Gold με συλλογή, εξαργύρωση και λήξη πόντων.",
    "all_revenue_coupons_title": "Κουπόνια & Προσφορές",
    "all_revenue_coupons_desc": "Όρια χρήσης, προγραμματισμένες καμπάνιες και στατιστικά ROI ανά κουπόνι.",
    "all_revenue_loans_title": "Πίστωση & Δάνεια Πελατών",
    "all_revenue_loans_desc": "Δάνεια και πιστωτικά όρια B2B με παρακολούθηση υπολοίπων.",
    "all_group_ops": "Λειτουργίες & Αποτροπή Απωλειών",
    "all_ops_shifts_title": "Βάρδιες & Προγραμματισμός",
    "all_ops_shifts_desc": "Εβδομαδιαίες επαναλαμβανόμενες βάρδιες, εντοπισμός συγκρούσεων και ειδοποιήσεις push.",
    "all_ops_loss_title": "Σουίτα αποτροπής απωλειών",
    "all_ops_loss_desc": "Καταγραφή φύρας, απογραφή/κυκλική καταμέτρηση και επιστροφή + αντικατάσταση — σταματήστε να χάνετε χρήματα.",
    "all_ops_fefo_title": "FEFO & Παρτίδες",
    "all_ops_fefo_desc": "Παρακολούθηση λήξης και παρτίδων με κοστολόγηση πρώτο-λήγει-πρώτο-βγαίνει.",
    "all_ops_transfers_title": "Μεταφορές πολλαπλών τοποθεσιών",
    "all_ops_transfers_desc": "Μετακινήστε απόθεμα μεταξύ τοποθεσιών και αποθηκών με πλήρη ιχνηλασιμότητα.",
    "all_ops_barcode_title": "Εκτύπωση ετικετών barcode",
    "all_ops_barcode_desc": "Δημιουργήστε και εκτυπώστε ετικέτες barcode και τιμών.",
    "all_ops_purchasing_title": "Αγορές & Προμηθευτές",
    "all_ops_purchasing_desc": "Εντολές αγοράς και καθολικά προμηθευτών για πλήρη back-office έλεγχο.",
    "all_group_ai": "AI & Εμπειρία Πελατών",
    "all_ai_translation_title": "Μετάφραση προϊόντων με AI",
    "all_ai_translation_desc": "Μεταφράστε αυτόματα ονόματα και περιγραφές προϊόντων.",
    "all_ai_enrichment_title": "Εμπλουτισμός ChatGPT",
    "all_ai_enrichment_desc": "Εμπλουτίστε τον κατάλογο με περιγραφές, κατηγορίες και χαρακτηριστικά.",
    "all_ai_perplexity_title": "Έρευνα Perplexity",
    "all_ai_perplexity_desc": "Αντλήστε ζωντανά δεδομένα προϊόντων και αγοράς απευθείας στο Finio.",
    "all_ai_waiter_title": "Κλήση σερβιτόρου",
    "all_ai_waiter_desc": "Οι πελάτες καλούν προσωπικό σε πραγματικό χρόνο με προβολή αίθουσας σε κόκκινο συναγερμό.",
    "all_ai_feedback_title": "Σχόλια πελατών + QR",
    "all_ai_feedback_desc": "Συλλέξτε κριτικές μέσω φόρμας web και κωδικού QR.",
    "all_ai_kiosk_title": "Kiosk / Αυτοεξυπηρέτηση",
    "all_ai_kiosk_desc": "Kiosk αυτοεξυπηρέτησης και λειτουργία αυτοπαραγγελίας για πολυσύχναστους χώρους.",
    "all_ai_map_title": "Χάρτης εστιατορίου",
    "all_ai_map_desc": "Σχέδια με σύρσιμο-απόθεση, πολλαπλών ορόφων και τοποθεσιών.",
    "all_ai_glovo_title": "Marketplace Glovo",
    "all_ai_glovo_desc": "Εισάγετε παραγγελίες διανομής Glovo απευθείας στο POS σας.",
    "all_group_finance": "Back-Office & Οικονομικά",
    "all_fin_fiscal_title": "Φορολογική συμμόρφωση",
    "all_fin_fiscal_desc": "fiskaly SIGN ES και fiskaltrust (AT/DE/FR) για πιστοποιημένη ηλεκτρονική τιμολόγηση.",
    "all_fin_dunning_title": "Υπενθυμίσεις οφειλών",
    "all_fin_dunning_desc": "Αυτόματες υπενθυμίσεις πληρωμής για ληξιπρόθεσμα υπόλοιπα.",
    "all_fin_payroll_title": "Μισθοδοσία",
    "all_fin_payroll_desc": "Διαχειριστείτε μισθούς και δημιουργήστε PDF μισθοδοσίας από τον πίνακα λογιστή.",
    "all_fin_docs_title": "Αλυσίδα εγγράφων",
    "all_fin_docs_desc": "Επιστροφή + αντικατάσταση, ποσοστώσεις εγγράφων και η αλυσίδα BC → BL → FA → AV.",
    "all_group_platform": "Πλατφόρμα & Αξιοπιστία",
    "all_plat_lan_title": "Πλεονασμός LAN εκτός σύνδεσης",
    "all_plat_lan_desc": "Λειτουργία πολλαπλών συσκευών εκτός σύνδεσης στο τοπικό δίκτυο, με αυτόματο συγχρονισμό.",
    "all_plat_desktop_title": "Εφαρμογές desktop",
    "all_plat_desktop_desc": "Εγγενείς εφαρμογές για Windows, macOS και Linux, πέρα από Web, iOS και Android.",

    // --- Σύγκριση: πραγματικά PRO ---
    "sub_cat_pro_only": "Μόνο PRO",
    "sub_cmp_refund_replace": "Επιστροφή + Αντικατάσταση",
    "sub_cmp_quota": "Διαχείριση ποσόστωσης εγγράφων",
    "sub_cmp_doc_chain": "Αλυσίδα εγγράφων (BC → BL → FA → AV)",
    "sub_cmp_whatsapp_invoice": "Τιμολόγια WhatsApp",
    "sub_cmp_max_registers": "Μέγιστος αριθμός ταμείων",
    "sub_cmp_reconciliation": "Συμφωνία ταμείου",
    "sub_cmp_customer_pdf": "Καταστάσεις πελατών PDF",
    "sub_cmp_stock_reports": "Αναφορές αποθέματος",
    "sub_cmp_delivery_orders": "Παραγγελίες διανομής",
    "sub_cmp_store_orders": "Παραγγελίες web / καταστήματος",
    "sub_cmp_health_date": "Αναφορές εύρους ημερομηνιών (υγεία)",
    "sub_cmp_liquid_capital": "PDF ρευστού κεφαλαίου",

    "// Legacy keys": "",
    "feature_auth_title": "Ταυτοποίηση Πολλαπλών Μισθωτών",
    "feature_auth_desc": "Ασφαλής ταυτοποίηση βάσει ρόλων, σχεδιασμένη για αρχιτεκτονική πολλαπλών μισθωτών, διασφαλίζοντας την απομόνωση και ασφάλεια δεδομένων.",
    "feature_offline_title": "Τοπική Δέσμευση Μηδενικής Καθυστέρησης",
    "feature_offline_desc": "Διατηρήστε τις λειτουργίες σας ομαλές, ακόμη και χωρίς σύνδεση στο διαδίκτυο. Τα δεδομένα συγχρονίζονται αυτόματα μόλις συνδεθείτε ξανά.",
    "feature_sync_title": "Συγχρονισμός Delta στο Παρασκήνιο",
    "feature_sync_desc": "Αξιοποιεί το Firebase και μια ισχυρή υπηρεσία συγχρονισμού για να διασφαλίζει ότι τα δεδομένα είναι πάντα ενημερωμένα σε όλες τις συσκευές και τις ενότητες.",
    "feature_remote_title": "Web Panel Λογιστή & Διαχειριστή",
    "feature_remote_desc": "Ασφαλής απομακρυσμένη πρόσβαση για λογιστές και διαχειριστές για έλεγχο πωλήσεων, ημερολογίων και ρυθμίσεων.",
    "feature_reporting_title": "Συμφωνημένες Οικονομικές Καταστάσεις",
    "feature_reporting_desc": "Αποκτήστε κρίσιμες πληροφορίες με ολοκληρωμένα αναλυτικά στοιχεία για τις πωλήσεις, την απόδοση προϊόντων και τη συμπεριφορά των πελατών.",
    "feature_security_title": "Έλεγχος Πρόσβασης Βάσει Ρόλων (RBAC)",
    "feature_security_desc": "Από τις πύλη δικαιωμάτων στο UI έως τα αυτόματα αντίγραφα ασφαλείας και την κρυπτογράφηση, τα δεδομένα σας είναι πάντα προστατευμένα.",
    "feature_pms_rooms": "Διαχείριση Δωματίων & Πληρότητας",
    "feature_pms_booking": "Κρατήσεις Επισκεπτών & CRM",
    "feature_print_network": "Ανακάλυψη Εκτυπωτών Δικτύου",
    "feature_print_custom": "Προσαρμόσιμα Πρότυπα Αποδείξεων",

    "// --- Innovations Section ---": "",
    "innovations_title": "Λύσεις Έτοιμες για το Μέλλον",
    "innovations_intro": "Γίνετε από τους πρώτους που θα βιώσουν το μέλλον του επιχειρηματικού αυτοματισμού. Εγγραφείτε στο πρόγραμμα early adopter και διαμορφώστε την επόμενη γενιά έξυπνων επιχειρηματικών λύσεων.",
    "smart_store_title": "Έξυπνο Κατάστημα Finio",
    "smart_store_desc": "Ενεργοποιήστε μια εμπειρία χωρίς ταμεία με κάμερες τεχνητής νοημοσύνης, συσκευές αυτόματης αγοράς και αυτοματοποιημένο εντοπισμό κλοπής.",
    "smart_store_feature_1_title": "Κάμερες με Τεχνητή Νοημοσύνη",
    "smart_store_feature_1_desc": "Προηγμένη όραση υπολογιστή παρακολουθεί τις κινήσεις των πελατών και τις επιλογές προϊόντων σε πραγματικό χρόνο.",
    "smart_store_feature_2_title": "Συσκευές Self-Checkout",
    "smart_store_feature_2_desc": "Οι πελάτες σαρώνουν και πληρώνουν τα είδη ανεξάρτητα, μειώνοντας τους χρόνους αναμονής και τις απαιτήσεις προσωπικού.",
    "smart_store_feature_3_title": "Αυτοματοποιημένος Εντοπισμός Κλοπής",
    "smart_store_feature_3_desc": "Αλγόριθμοι μηχανικής μάθησης εντοπίζουν ύποπτες συμπεριφορές και ειδοποιούν την ασφάλεια σε πραγματικό χρόνο.",
    "smart_store_feature_4_title": "Έξυπνη Παρακολούθηση Αποθεμάτων",
    "smart_store_feature_4_desc": "Αυτόματη παρακολούθηση επιπέδων αποθέματος καθώς επιλέγονται τα είδη, διασφαλίζοντας ακριβείς μετρήσεις.",
    "smart_airbnb_title": "Finio Smart Airbnb",
    "smart_airbnb_desc": "Αυτοματοποιήστε τη διαχείριση του ακινήτου σας με επιβεβαιώσεις κρατήσεων AI, έξυπνες κλειδαριές και δυναμικούς κωδικούς πρόσβασης.",
    "smart_airbnb_feature_1_title": "Βοηθός Κρατήσεων AI",
    "smart_airbnb_feature_1_desc": "Αυτοματοποιημένες απαντήσεις σε ερωτήματα, άμεσες επιβεβαιώσεις κρατήσεων και έξυπνες προτάσεις τιμολόγησης.",
    "smart_airbnb_feature_2_title": "Ενσωμάτωση Smart Lock",
    "smart_airbnb_feature_2_desc": "Δημιουργία μοναδικών κωδικών πρόσβασης για κάθε επισκέπτη, που ενεργοποιούνται και απενεργοποιούνται αυτόματα βάσει των κρατήσεων.",
    "smart_airbnb_feature_3_title": "Δυναμική Διαχείριση Πρόσβασης",
    "smart_airbnb_feature_3_desc": "Οι κωδικοί αλλάζουν αυτόματα μετά το checkout, εξασφαλίζοντας τη μέγιστη ασφάλεια για το ακίνητό σας.",
    "smart_airbnb_feature_4_title": "Κέντρο Επικοινωνίας Επισκεπτών",
    "smart_airbnb_feature_4_desc": "Κεντρική διαχείριση μηνυμάτων, οδηγίες check-in και αυτοματοποιημένα follow-up για κριτικές.",
    "early_adopter_title": "Γίνετε Early Adopter",
    "early_adopter_intro": "Γίνετε μέλος του αποκλειστικού προγράμματος early adopter και λάβετε ειδικές τιμές, προτεραιότητα στην υποστήριξη και την ευκαιρία να επηρεάσετε την ανάπτυξη του προϊόντος.",
    "early_adopter_benefit_1": "Ειδική τιμή λανσαρίσματος",
    "early_adopter_benefit_2": "Προτεραιότητα στην υλοποίηση και υποστήριξη",
    "early_adopter_benefit_3": "Άμεση συνεισφορά στα χαρακτηριστικά και τον οδικό χάρτη",
    "early_adopter_benefit_4": "Αποκλειστική πρόσβαση σε λειτουργίες beta",
    "early_adopter_form_title": "Εκδηλώστε το Ενδιαφέρον Σας",
    "early_adopter_form_intro": "Συμπληρώστε την παρακάτω φόρμα για να είστε από τους πρώτους που θα δοκιμάσουν αυτές τις καινοτόμες λύσεις.",
    "solution_interested": "Λύση που σας Ενδιαφέρει",
    "select_solution": "Επιλέξτε μια λύση",
    "business_type": "Τύπος Επιχείρησης",
    "current_challenges": "Τρέχουσες Προκλήσεις",
    "early_adopter_submit": "Συμμετοχή στο Πρόγραμμα Early Adopter",

    "// Other keys from previous version...": "",
    "footer_finio_description": "Το πιο προηγμένο σύστημα POS για τη διαχείριση και την ανάπτυξη της επιχείρησής σας αποδοτικά και επαγγελματικά.",
    "quick_links": "Γρήγοροι Σύνδεσμοι",
    "support": "Υποστήριξη",
    "help_center": "Κέντρο Βοήθειας",
    "terms_conditions": "Όροι & Προϋποθέσεις",
    "privacy_policy": "Πολιτική Απορρήτου",
    "contact_us_footer": "Επικοινωνήστε μαζί μας",
    "contact_phone": "+212 808-646672",
    "contact_email": "email",
    "contact_address": "Λεωφ. Aoussou Ou Basselam, Τετουάν",
    "copyright": "© 2025 Finio Solutions. Με επιφύλαξη παντός δικαιώματος.",
    "company_name": "Finio Solutions",
    "company_ice": "ICE: 003689594000059",
    "company_siege": "Έδρα: Τετουάν, Μαρόκο",
    "company_address_full": "Λεωφ. Aoussou Ou Basselam, Τετουάν, Μαρόκο",
    "contact_page_title": "Επικοινωνία",
    "contact_page_intro": "Είμαστε εδώ για να απαντήσουμε σε όλα τα ερωτήματά σας και να παρέχουμε την απαραίτητη υποστήριξη. Μη διστάσετε να επικοινωνήσετε μαζί μας.",
    "send_message": "Στείλτε μας Μήνυμα",
    "full_name": "Ονοματεπώνυμο",
    "email_address": "Διεύθυνση Email",
    "phone_number": "Αριθμός Τηλεφώνου",
    "subject": "Θέμα",
    "your_message": "Το Μήνυμά σας",
    "send_button": "Αποστολή Μηνύματος",
    "contact_info_title": "Πληροφορίες Επικοινωνίας",

    "// --- Navigation Links ---": "",
    "nav_documentation": "Τεκμηρίωση",
    "nav_faq": "Συχνές Ερωτήσεις",
    "nav_security": "Ασφάλεια & Προστασία Δεδομένων",
    "nav_changelog": "Ιστορικό Αλλαγών (Changelog)",
    "nav_about_us": "Σχετικά με Εμάς",
    "nav_compliance": "Συμμόρφωση & Έλεγχος",

    "// Footer Columns": "",
    "footer_col_product": "Προϊόν",
    "footer_col_resources": "Πόροι",
    "footer_col_terms": "Όροι",
    "footer_col_connect": "Σύνδεση",

    "// --- About Us Page ---": "",
    "about_hero_title": "Σχεδιασμένο για την Οικονομική Αλήθεια",
    "about_hero_subtitle": "Δεν φτιάχνουμε απλώς λογισμικό POS. Κατασκευάζουμε οικονομικές μηχανές επιπέδου ελέγχου που αποτρέπουν την απάτη, εξαλείφουν τα σφάλματα και εγγυώνται τη συνέπεια των δεδομένων.",
    "about_mission_title": "Η Εντολή μας",
    "about_mission_text": "Να εξαλείψουμε την οικονομική ασάφεια στο λιανεμπόριο και τη φιλοξενία. Παρέχουμε τη μαθηματική βεβαιότητα ότι κάθε σεντ που κερδίζεται καταγράφεται, παρακολουθείται και διασφαλίζεται.",
    "about_vision_title": "Το Μηχανικό μας Πρότυπο",
    "about_vision_text": "Απορρίπτουμε το βιομηχανικό πρότυπο του 'αρκετά καλού'. Οραματιζόμαστε ένα μέλλον όπου το λογισμικό επιχειρήσεων είναι τόσο αξιόπιστο όσο ένα τραπεζικό θησαυροφυλάκιο και τόσο ακριβές όσο ένα ελβετικό ρολόι.",
    "about_values_title": "Λειτουργική Ηθική",
    "about_values_text": "Η ακρίβεια πάνω από την ταχύτητα. Η ακεραιότητα πάνω από τα χαρακτηριστικά. Δίνουμε προτεραιότητα στην ασφάλεια των δεδομένων σας πάνω από όλους τους ισχυρισμούς μάρκετινγκ.",
    "about_story_title": "Η Προέλευση της Μηδενικής Ανοχής",
    "about_story_paragraph_1": "Το Finio ξεκίνησε όταν ελέγξαμε μια αλυσίδα εστιατορίων που χρησιμοποιούσε ένα δημοφιλές cloud POS. Βρήκαμε παραγγελίες-φαντάσματα, διαγραμμένα τιμολόγια και κινούμενα αποθέματα που εξαφανίζονταν χωρίς ίχνος. Το σύστημα το επέτρεπε. Η βάση δεδομένων το επέτρεπε.",
    "about_story_paragraph_2": "Φτιάξαμε το Finio για να κλείσουμε αυτά τα κενά. Σχεδιάσαμε ένα αμετάβλητο καθολικό όπου η διαγραφή είναι αδύνατη—μόνο η αντιστροφή. Φτιάξαμε μια μηχανή συγχρονισμού που επιβιώνει από διακοπές δικτύου χωρίς να καταστρέφει ούτε ένα byte.",
    "about_story_paragraph_3": "Σήμερα, το Finio είναι το πρότυπο για τους επιχειρηματίες που απαιτούν την αλήθεια. Δεν σας βοηθάμε απλώς να πουλήσετε, σας βοηθάμε να αποδείξετε τι πουλήσατε. Από μεμονωμένα καταστήματα έως αλυσίδες επιχειρήσεων, παρέχουμε τη ραχοκοκαλιά για λειτουργίες έτοιμες για έλεγχο.",
    "about_why_choose_title": "Γιατί οι Επιχειρηματίες Εμπιστεύονται το Finio",
    "about_reason_1_title": "Αμετάβλητο Καθολικό",
    "about_reason_1_desc": "Τα τιμολόγια και οι πληρωμές είναι μόνο για προσθήκη. Κανείς—ούτε καν εσείς—δεν μπορεί να διαγράψει σιωπηλά μια εγγραφή συναλλαγής.",
    "about_reason_2_title": "Κρυπτογράφηση Στρατιωτικού Επιπέδου",
    "about_reason_2_desc": "Τα δεδομένα κρυπτογραφούνται σε κατάσταση ηρεμίας και κατά τη μεταφορά. Η επιχειρηματική σας ευφυΐα παραμένει αυστηρά ιδιωτική.",
    "about_reason_3_title": "Κυριαρχία Offline",
    "about_reason_3_desc": "Δεν σας νοικιάζουμε την πρόσβαση στα δεδομένα σας. Ζουν στη συσκευή σας, πλήρως λειτουργικά χωρίς καμία εξάρτηση από εξωτερικό διακομιστή.",
    "about_reason_4_title": "Αναφορές Έτοιμες για Έλεγχο",
    "about_reason_4_desc": "Οι αναφορές μας δεν είναι απλώς όμορφα γραφήματα. Είναι μαθηματικά συμφωνημένες οικονομικές καταστάσεις έτοιμες για τον λογιστή σας.",
    "about_reason_5_title": "Συγχρονισμός Χωρίς Διενέξεις",
    "about_reason_5_desc": "Η ιδιόκτητη μηχανή συγχρονισμού μας χειρίζεται τις συγκρούσεις πολλαπλών συσκευών με ντετερμινιστική λογική, διασφαλίζοντας μηδενική απώλεια δεδομένων.",
    "about_reason_6_title": "Ακεραιότητα Οικοσυστήματος",
    "about_reason_6_desc": "Κάθε ενότητα—KDS, Απόθεμα, Web Panel—τροφοδοτεί την ίδια μοναδική πηγή αλήθειας. Χωρίς σιλό δεδομένων.",
    "about_stats_title": "Μετρήσεις Αξιοπιστίας Συστήματος",
    "about_stat_1": "Διενέξεις Δεδομένων",
    "about_stat_2": "Χρόνος Λειτουργίας (Uptime)",
    "about_stat_3": "Βαθμολογία Ελέγχου",
    "about_stat_4": "SLA Υποστήριξης",
    "about_team_title": "Η Ομάδα Μηχανικών",
    "about_team_intro": "Δεν είμαστε έμποροι. Είμαστε αρχιτέκτονες συστημάτων, ειδικοί ασφαλείας και μηχανικοί οικονομικού λογισμικού αφοσιωμένοι στην επιστήμη της διαχείρισης επιχειρήσεων.",
    "about_team_member_1_name": "Αρχιτεκτονική Συστημάτων",
    "about_team_member_1_role": "Κεντρική Υποδομή",
    "about_team_member_1_bio": "Σχεδιάζοντας το αμετάβλητο καθολικό και την τοπολογία βάσης δεδομένων offline-first που τροφοδοτεί κάθε εγκατάσταση Finio.",
    "about_team_member_2_name": "Ασφάλεια & Συμμόρφωση",
    "about_team_member_2_role": "Ακεραιότητα Δεδομένων",
    "about_team_member_2_bio": "Διασφαλίζοντας ότι κάθε συναλλαγή πληροί αυστηρά πρότυπα ελέγχου και προστατεύεται από παραποίηση.",
    "about_team_member_3_name": "Λειτουργίες Πελατών",
    "about_team_member_3_role": "Επιτυχία Ανάπτυξης",
    "about_team_member_3_bio": "Βοηθώντας τις επιχειρήσεις να μεταφέρουν τις πολύπλοκες δομές δεδομένων τους στο αυστηρό περιβάλλον του Finio.",
    "about_team_member_4_name": "Σχεδιασμός Προϊόντος",
    "about_team_member_4_role": "UX & Ροή Εργασίας",
    "about_team_member_4_bio": "Μεταφράζοντας την πολύπλοκη λογιστική λογική σε γρήγορες, διαισθητικές ροές εργασίας για το προσωπικό πρώτης γραμμής.",
    "about_core_values_title": "Βασικά Πρωτόκολλα",
    "about_value_1_title": "Πρώτα η Αλήθεια",
    "about_value_1_desc": "Ποτέ δεν αλλοιώνουμε τους αριθμούς. Αν τα μαθηματικά δεν ισορροπούν, το σύστημα σταματά. Η ασάφεια είναι σφάλμα (bug).",
    "about_value_2_title": "Πρώτα το Τοπικό",
    "about_value_2_desc": "Η συσκευή στο χέρι σας είναι η πηγή της αλήθειας. Το cloud είναι απλώς ένα αντίγραφο ασφαλείας.",
    "about_value_3_title": "Μηδενική Απώλεια Δεδομένων",
    "about_value_3_desc": "Είμαστε παρανοϊκοί με την ασφάλεια των δεδομένων. Υποθέτουμε ότι τα δίκτυα αποτυγχάνουν, το ρεύμα κόβεται και το υλικό χαλάει.",
    "about_value_4_title": "Διαφανής Λογική",
    "about_value_4_desc": "Δεν κρύβουμε τους υπολογισμούς. Κάθε σύνολο μπορεί να ανιχνευθεί πίσω στις ατομικές του συναλλαγές.",
    "about_value_5_title": "Συνεργασία",
    "about_value_5_desc": "Φτιάχνουμε εργαλεία για σοβαρούς επιχειρηματίες. Αν σας ενδιαφέρει η ακρίβεια, είμαστε ο συνεργάτης σας.",
    "about_growth_title": "Το Μέλλον της Ακεραιότητας",
    "about_growth_text_1": "Καθώς κλιμακωνόμαστε, η δέσμευσή μας στον πυρήνα της λογιστικής αυστηρότητας παραμένει αμετάβλητη. Επεκτεινόμαστε στην Τεχνητή Νοημοσύνη και τον αυτοματισμό, αλλά ποτέ εις βάρος της ακρίβειας.",
    "about_growth_text_2": "Ο οδικός μας χάρτης περιλαμβάνει ενότητες 'Smart Store' και 'Smart Property', αλλά σε αντίθεση με άλλους, οι δικές μας βασίζονται στο ίδιο αμετάβλητο θεμέλιο. Οι προτάσεις AI θα καταγράφονται για έλεγχο.",
    "about_growth_text_3": "Το Finio χτίζει το λειτουργικό σύστημα για την επόμενη γενιά διαφανών, αποδοτικών και ασφαλών για έλεγχο επιχειρήσεων στη Βόρεια Αφρική και πέρα από αυτήν.",
    "about_cta_title": "Έτοιμοι για Σοβαρές Δουλειές;",
    "about_cta_subtitle": "Γίνετε μέλος των επιχειρηματιών που σταμάτησαν να ανησυχούν για τις ασυμφωνίες δεδομένων και άρχισαν να εστιάζουν στην ανάπτυξη.",
    "explore_products": "Προβολή Τεχνικών Ενοτήτων",

    "// --- Checkout Page ---": "",
    "checkout_title": "Ολοκληρώστε τη Συνδρομή Σας",
    "checkout_intro": "Συμπληρώστε τα στοιχεία σας παρακάτω για να ολοκληρώσετε τη συνδρομή σας. Θα επικοινωνήσουμε μαζί σας εντός 24 ωρών για να επιβεβαιώσουμε το πρόγραμμά σας.",
    "complete_payment": "Ολοκλήρωση Πληρωμής",
    "subscription_success": "Επιτυχία! Η συνδρομή σας έχει ενεργοποιηθεί.",
    "fill_personal_info": "Παρακαλούμε συμπληρώστε πρώτα τα Προσωπικά Στοιχεία.",
    "email_required_card": "Η διεύθυνση email απαιτείται για πληρωμές με κάρτα.",
    "personal_info_title": "Προσωπικά Στοιχεία",
    "company_name_optional": "Όνομα Εταιρείας (Προαιρετικό)",
    "city": "Πόλη",
    "country": "Χώρα",
    "payment_method": "Τρόπος Πληρωμής",
    "select_payment": "Επιλέξτε τρόπο πληρωμής",
    "payment_card": "Με Κάρτα",
    "payment_cash": "Με Μετρητά",
    "payment_cheque": "Με Επιταγή",
    "payment_bank_cheque": "Με Τραπεζική Επιταγή",
    "subscription_details_title": "Λεπτομέρειες Συνδρομής",
    "selected_plan": "Επιλεγμένο Πρόγραμμα",
    "subscription_duration": "Διάρκεια Συνδρομής",
    "select_duration": "Επιλέξτε διάρκεια",
    "duration_3months": "3 Μήνες",
    "duration_6months": "6 Μήνες (1 Μήνας Δωρεάν)",
    "duration_12months": "12 Μήνες (2.5 Μήνες Δωρεάν)",
    "total_amount": "Συνολικό Ποσό",
    "complete_subscription": "Ολοκλήρωση Συνδρομής",
    "redirecting_to_payment": "Ανακατεύθυνση στην ασφαλή πληρωμή...",
    "payment_success_title": "Η πληρωμή ολοκληρώθηκε",
    "payment_success_desc": "Ευχαριστούμε! Λάβαμε την πληρωμή σας και η συνδρομή σας ενεργοποιείται.",
    "payment_failed_title": "Η πληρωμή απέτυχε",
    "payment_failed_desc": "Η πληρωμή σας δεν ολοκληρώθηκε και δεν χρεώθηκε κανένα ποσό. Δοκιμάστε ξανά ή επικοινωνήστε με την ομάδα μας.",
    "order_reference": "Αναφορά παραγγελίας",
    "amount_paid": "Ποσό που καταβλήθηκε",
    "try_again": "Δοκιμάστε ξανά",
    "sub_recurring_note": "Θα χρεωθείτε {amount} MAD για την επιλεγμένη περίοδο τώρα και στη συνέχεια θα ανανεώνεται αυτόματα στα {yearly} MAD/έτος μέχρι να ακυρώσετε.",

    // --- Σελίδα Hardware (μόνο Μαρόκο) ---
    "nav_hardware": "Εξοπλισμός",
    "hw_title": "Πακέτα Hardware POS",
    "hw_subtitle": "Πλήρεις διατάξεις σημείου πώλησης — συναρμολογημένες, δοκιμασμένες και παραδομένες σε όλο το Μαρόκο.",
    "hw_morocco_badge": "Διαθέσιμο μόνο στο Μαρόκο",
    "hw_delivery_badge": "Παράδοση από 50 MAD",
    "hw_packs_title": "Επιλέξτε το πακέτο σας",
    "hw_includes": "Τι περιλαμβάνει",
    "hw_hardware_price": "Τιμή hardware",
    "hw_with_scanner": "Με σαρωτή 2D",
    "hw_get_quote": "Επιλογή πακέτου",
    "hw_pricelist_title": "Εξαρτήματα που προσφέρουμε",
    "hw_pricelist_intro": "Όλα τα εξαρτήματα που μπορούμε να παρέχουμε για την εγκατάστασή σας.",
    "hw_item_col": "Είδος",
    "hw_price_col": "Τιμή",
    "hw_software_title": "Το λογισμικό Finio χρεώνεται ξεχωριστά",
    "hw_software_desc": "Οι τιμές hardware είναι εφάπαξ και το λογισμικό Finio χρεώνεται ξεχωριστά. Όταν προσθέτετε ετήσια συνδρομή Finio στην παραγγελία hardware, έχετε επιπλέον 30% έκπτωση μόνο τον πρώτο χρόνο — για παράδειγμα, το πακέτο Starter (κανονικά 1900 MAD/έτος) γίνεται 1330 MAD τον πρώτο χρόνο και μετά ανανεώνεται στα 1900 MAD/έτος.",
    "hw_cta_title": "Χρειάζεστε βοήθεια στην επιλογή;",
    "hw_cta_desc": "Πείτε μας για την επιχείρησή σας και θα σας προτείνουμε το κατάλληλο πακέτο.",
    "hw_pack_starter_name": "Πακέτο Starter",
    "hw_pack_starter_desc": "Η βασική διάταξη για μικρά καταστήματα και καφετέριες.",
    "hw_pack_android_name": "Πακέτο Android",
    "hw_pack_android_desc": "POS με οθόνη αφής σε Android — συμπαγές και ευέλικτο.",
    "hw_pack_pro_name": "Επαγγελματικό Πακέτο",
    "hw_pack_pro_desc": "Η διάταξη all-in-one για επιχειρήσεις υψηλού όγκου.",
    "hw_item_touch": "Οθόνη αφής 17\"",
    "hw_item_printer": "Θερμικός εκτυπωτής 80mm",
    "hw_item_scanner": "Σαρωτής 2D",
    "hw_item_drawer": "Συρτάρι μετρητών (Tiroir)",
    "hw_item_tablet": "Tablet G-Tab T12",
    "hw_item_support": "Βάση υψηλής ποιότητας",
    "hw_item_pc": "PC i5",
    "hw_item_aio": "Συσκευή all-in-one",
    "hw_item_tvbox": "AndroidBox",
    "hw_order_title": "Διαμορφώστε το πακέτο σας",
    "hw_order_subtitle": "Επιλέξτε τις επιλογές και τον τρόπο πληρωμής και υποβάλετε την παραγγελία.",
    "hw_order_back": "Επιστροφή στα πακέτα",
    "hw_order_selected_pack": "Επιλεγμένο πακέτο",
    "hw_scanner_title": "Σαρωτής 2D",
    "hw_scanner_without": "Χωρίς σαρωτή",
    "hw_scanner_with": "Με σαρωτή 2D",
    "hw_subscription_title": "Λογισμικό Finio",
    "hw_sub_hardware_only": "Μόνο hardware",
    "hw_sub_add": "Προσθήκη συνδρομής Finio",
    "hw_sub_plan": "Πρόγραμμα λογισμικού",
    "hw_sub_first_year": "Πρώτος χρόνος (-30%)",
    "hw_sub_renews": "Ανανεώνεται στα",
    "hw_sub_per_year": "/έτος",
    "hw_payment_title": "Τρόπος πληρωμής",
    "hw_pay_card": "Με κάρτα",
    "hw_pay_card_desc": "Ασφαλής online πληρωμή μέσω Chari Pay",
    "hw_pay_cod": "Αντικαταβολή",
    "hw_pay_transfer": "Τραπεζική μεταφορά",
    "hw_pay_transfer_note": "Θα σας στείλουμε τα τραπεζικά στοιχεία αμέσως μετά την παραγγελία.",
    "hw_details_title": "Τα στοιχεία σας",
    "hw_field_name": "Ονοματεπώνυμο",
    "hw_field_phone": "Τηλέφωνο",
    "hw_field_email": "Email",
    "hw_field_company": "Εταιρεία (προαιρετικό)",
    "hw_field_city": "Πόλη",
    "hw_field_address": "Διεύθυνση",
    "hw_summary_title": "Σύνοψη παραγγελίας",
    "hw_sum_pack": "Πακέτο hardware",
    "hw_sum_scanner": "Σαρωτής 2D",
    "hw_sum_subscription": "Λογισμικό Finio (1ος χρόνος)",
    "hw_sum_delivery": "Παράδοση",
    "hw_sum_total": "Σύνολο",
    "hw_place_order": "Υποβολή παραγγελίας",
    "hw_processing": "Επεξεργασία...",
    "hw_order_success_title": "Η παραγγελία ελήφθη!",
    "hw_order_success_desc": "Ευχαριστούμε. Η ομάδα μας θα επικοινωνήσει σύντομα μαζί σας για επιβεβαίωση παράδοσης και πληρωμής.",
    "hw_new_order": "Νέα παραγγελία",
    "hw_required_missing": "Συμπληρώστε τα υποχρεωτικά πεδία.",
    "months": "μήνες",
    "month_free": "μήνας δωρεάν",
    "months_free": "μήνες δωρεάν",
    "subscriptions_page_title": "Διαφανή Προγράμματα για την Επιχείρησή Σας",
    "subscriptions_intro": "Επιλέξτε το πρόγραμμα που ταιριάζει στις ανάγκες σας χωρίς κρυφές χρεώσεις.",
    "pricing_basic_title": "Αρχικό (Starter)",
    "pricing_basic_price": "200",
    "pricing_pro_title": "Επιχειρηματικό (Business)",
    "pricing_pro_price": "350",
    "pricing_custom_title": "Εξειδικευμένο (Custom)",
    "pricing_custom_contact": "Επικοινωνία",
    "pricing_currency": "MAD/μήνα",
    "pricing_basic_yearly": "Ετήσιο 1900 MAD/έτος",
    "pricing_basic_yearly_savings": "2.5 μήνες δωρεάν",
    "pricing_pro_yearly": "Ετήσιο 3325 MAD/έτος",
    "pricing_pro_yearly_savings": "2.5 μήνες δωρεάν",
    "pricing_most_popular": "Δημοφιλέστερο",
    "pricing_basic_feature_1": "Πλήρης εφαρμογή",
    "pricing_basic_feature_2": "Διαχείριση αποθεμάτων και πελατών",
    "pricing_basic_feature_3": "Έξυπνα αναλυτικά στοιχεία",
    "pricing_basic_feature_4": "24/7 τεχνική υποστήριξη",
    "pricing_pro_feature_1": "Όλα τα χαρακτηριστικά του Αρχικού",
    "pricing_pro_feature_2": "Web dashboard για λογιστή",
    "pricing_pro_feature_3": "Διαχείριση εγγράφων (Προσφορές, Τιμολόγια)",
    "pricing_pro_feature_4": "Προηγμένες αναφορές",
    "pricing_pro_feature_5": "Προτεραιότητα στην τεχνική υποστήριξη",
    "pricing_custom_feature_1": "Σύστημα Εστιατορίου (KDS)",
    "pricing_custom_feature_2": "Σύστημα Ξενοδοχείου (PMS)",
    "pricing_custom_feature_3": "Πλήρης παραμετροποίηση",
    "pricing_custom_feature_4": "Ενσωμάτωση με τα υπάρχοντα συστήματά σας",
    "pricing_custom_feature_5": "Τιμή προσαρμοσμένη στις ανάγκες σας",
    "pricing_get_started": "Ξεκινήστε",
    "pricing_choose_plan": "Επιλογή Επιχειρηματικού",
    "pricing_request_quote": "Ζητήστε Προσφορά",
    "contact_plans_title": "Για λεπτομέρειες προγραμμάτων και τιμολόγηση",
    "contact_plans_text": "Παρακαλούμε επικοινωνήστε απευθείας μαζί μας για να λάβετε μια εξατομικευμένη προσφορά που καλύπτει τις απαιτήσεις της επιχείρησής σας.",
    "contact_us_plans": "Επικοινωνία",
    "learn_more": "Μάθετε Περισσότερα",

    "// --- Custom Plan Content ---": "",
    "sub_custom_title": "Finio Custom",
    "sub_custom_tagline": "Προσαρμοσμένες εταιρικές λύσεις για σύνθετα περιβάλλοντα",
    "sub_custom_pricing_contact": "Επικοινωνήστε για προσφορά",
    "sub_custom_everything_pro": "ΤΑ ΠΑΝΤΑ ΣΤΟ ΕΠΙΧΕΙΡΗΜΑΤΙΚΟ, ΣΥΝ:",

    "sub_custom_f1_title": "Πλήρες Σύστημα Ξενοδοχείου (Enterprise PMS)",
    "sub_custom_f1_desc": "Δωμάτια, πληρότητα, υπηρεσίες, διαχείριση πολλαπλών τμημάτων και ιδιοκτησιών.",
    "sub_custom_f2_title": "Ενσωμάτωση Enterprise ERP",
    "sub_custom_f2_desc": "Συνδέστε το Finio με τα υπάρχοντα συστήματα ERP σας για λογιστική, αποθέματα, HR και αναφορές.",
    "sub_custom_f3_title": "Λύσεις Smart Store",
    "sub_custom_f3_desc": "Προηγμένος αυτοματισμός λιανικής, συνδεδεμένες συσκευές και προσαρμοσμένες ροές εργασίας.",
    "sub_custom_f4_title": "Ενσωμάτωση Smart House & Smart Property",
    "sub_custom_f4_desc": "Ενσωμάτωση με συστήματα IoT, έλεγχο πρόσβασης, αυτοματισμό και έξυπνες υποδομές.",
    "sub_custom_f5_title": "Πλήρης Προσαρμογή Συστήματος",
    "sub_custom_f5_desc": "Προσαρμοσμένα modules, ροές εργασίας και επιχειρηματική λογική προσαρμοσμένη ακριβώς στις ανάγκες σας.",
    "sub_custom_f6_title": "Προηγμένη Συνδεσιμότητα API & Συστήματος",
    "sub_custom_f6_desc": "Ασφαλείς ενσωματώσεις με πλατφόρμες τρίτων και εσωτερικά εταιρικά συστήματα.",
    "sub_custom_f7_title": "Επεκτασιμότητα Πολλαπλών Τοποθεσιών & Enterprise",
    "sub_custom_f7_desc": "Σχεδιασμένο για μεγάλες εταιρείες, αλυσίδες και σύνθετες οργανωτικές δομές.",
    "sub_custom_f8_title": "Αποκλειστική Ανάλυση Έργου & Υλοποίηση",
    "sub_custom_f8_desc": "Τεχνική μελέτη, διαμόρφωση και ανάπτυξη ευθυγραμμισμένη με τις επιχειρηματικές σας απαιτήσεις.",
    "sub_custom_f9_title": "Προσαρμοσμένη Τιμολόγηση & Μακροπρόθεσμη Υποστήριξη",
    "sub_custom_f9_desc": "Ένα μοντέλο τιμολόγησης προσαρμοσμένο στο εύρος, τις ενσωματώσεις και το επίπεδο υποστήριξης.",

    "sub_custom_contact_text": "Επικοινωνήστε για μια εξατομικευμένη πρόταση.",

    "// --- Subscription Page Enhanced Content ---": "",
    "sub_safety_header": "Η ασφάλεια και η λογιστική ακεραιότητα περιλαμβάνονται από προεπιλογή.",
    "sub_safety_desc": "Το Finio είναι κατασκευασμένο με ισχυρή λογιστική και προστασία δεδομένων στον πυρήνα του. Και τα δύο προγράμματα μοιράζονται την ίδια ασφαλή βάση — η διαφορά είναι πόσο έλεγχο, ορατότητα και επεκτασιμότητα χρειάζεστε.",
    "sub_safety_note": "Δεν πουλάμε την ασφάλεια ως αναβάθμιση.",

    "sub_basic_title": "Finio Starter",
    "sub_basic_tagline": "Όλα όσα χρειάζεστε για να πουλήσετε, να διαχειριστείτε και να κλείσετε τη μέρα σας με σιγουριά.",
    "sub_basic_desc": "Το Finio Starter περιλαμβάνει την πλήρη εμπειρία POS με ενσωματωμένες προστασίες που διασφαλίζουν ότι τα δεδομένα σας είναι πάντα σωστά και αξιόπιστα.",
    "sub_included_features": "Περιλαμβανόμενα Χαρακτηριστικά",
    "sub_basic_f1": "Πλήρες Σημείο Πώλησης (λιανική, fast food, εστιατόριο)",
    "sub_basic_f2": "Διαχείριση προϊόντων, πελατών και προμηθευτών",
    "sub_basic_f3": "Σάρωση barcode και πολλαπλά επίπεδα τιμών",
    "sub_basic_f4": "Εκπτώσεις, κουπόνια και αποθηκευμένα καλάθια",
    "sub_basic_f5": "Άνοιγμα ταμείου & κλείσιμο ημέρας (Z / clôture)",
    "sub_basic_f6": "Δημιουργία, εκτύπωση και παρακολούθηση τιμολογίων",
    "sub_basic_f7": "Ασφαλείς επιστροφές χρημάτων και χειρισμός υπολοίπου πελατών",
    "sub_basic_f8": "Λειτουργία Offline-first με αυτόματο συγχρονισμό",
    "sub_basic_f9": "Αμετάβλητα τιμολόγια και λογιστική ανθεκτική σε παραποιήσεις",
    "sub_basic_f10": "Ασφαλής συγχρονισμός πολλαπλών συσκευών",
    "sub_best_for": "Ιδανικό Για",
    "sub_basic_best_for": "Μικρές και μεσαίες επιχειρήσεις που διαχειρίζονται τις καθημερινές λειτουργίες απευθείας από την εφαρμογή και θέλουν ένα αξιόπιστο, συμβατό POS χωρίς πολυπλοκότητα.",

    "sub_pro_title": "Finio Business",
    "sub_pro_tagline": "Σχεδιασμένο για ανάπτυξη, δομή και επαγγελματικές λογιστικές ροές εργασίας.",
    "sub_pro_desc": "Το Finio Business βασίζεται σε όλα όσα παρέχει το Starter και προσθέτει προηγμένες ενότητες για διαχείριση, λογιστική και αυτοματισμό.",
    "sub_everything_basic_plus": "Όλα όσα παρέχει το Starter, συν:",
    "sub_pro_f1": "Web Panel Λογιστή & Διαχείρισης",
    "sub_pro_f1_detail": "Διαχειριστείτε μισθοδοσία υπαλλήλων, έξοδα, τιμολόγια (B2C & B2B) και λογιστικά στατιστικά από μια ειδική διεπαφή ιστού.",
    "sub_pro_f2": "Προηγμένες οικονομικές αναφορές και αναλυτικά στοιχεία",
    "sub_pro_f3": "Σύστημα Προβολής Κουζίνας (KDS) με σταθμούς και χρονόμετρα",
    "sub_pro_f4": "Modules Ξενοδοχείου / PMS (δωμάτια, πληρότητα, υπηρεσίες)",
    "sub_pro_f5": "Web παραγγελιοληψία (tablets σερβιτόρων & self-ordering πελατών)",
    "sub_pro_f6": "WhatsApp και αυτοματοποιημένες ειδοποιήσεις",
    "sub_pro_f7": "API προϊόντων & ενσωμάτωσης",
    "sub_pro_f8": "Συγχρονισμός WooCommerce",
    "sub_pro_f9": "Εργαλεία για κλιμάκωση και λειτουργίες πολλαπλών ροών εργασίας",
    "sub_pro_f10": "Προηγμένο Σύστημα Εκτύπωσης (Ετικέτες & Αποδείξεις)",
    "sub_pro_best_for": "Αναπτυσσόμενες επιχειρήσεις, εστιατόρια με κουζίνες, ξενοδοχεία, περιβάλλοντα πολλαπλών συσκευών και εταιρείες που συνεργάζονται με λογιστές ή εξωτερικά συστήματα.",

    "sub_safety_compliance_title": "Σημείωση για την ασφάλεια & τη συμμόρφωση",
    "sub_safety_compliance_intro": "Δεν πουλάμε την ασφάλεια ως αναβάθμιση. Τόσο το STARTER όσο και το BUSINESS περιλαμβάνουν:",
    "sub_compliance_1": "Αμετάβλητα τιμολόγια",
    "sub_compliance_2": "Καθολικό πληρωμών μόνο για προσθήκη (Append-only)",
    "sub_compliance_3": "Πληρωμές ασφαλείς έναντι αστοχιών (Crash-safe)",
    "sub_compliance_4": "Λογιστική κλειδωμένης περιόδου",
    "sub_compliance_5": "Ορθός χειρισμός μετρητών, τραπεζικών και καθολικού πελατών",
    "sub_compliance_6": "Θεμέλια έτοιμα για λογιστική πολλαπλών χωρών",
    "sub_safety_difference": "Η διαφορά μεταξύ των προγραμμάτων είναι η ικανότητα, όχι το ρίσκο.",

    "sub_compare_title": "Σύγκριση Χαρακτηριστικών",
    "sub_compare_feature": "Χαρακτηριστικό",
    "sub_cat_core_pos": "Βασικά Χαρακτηριστικά POS",
    "sub_cmp_pos": "Πλήρες Σύστημα Σημείου Πώλησης",
    "sub_cmp_products": "Διαχείριση προϊόντων, πελατών & προμηθευτών",
    "sub_cmp_barcode": "Σάρωση barcode & επίπεδα τιμών",
    "sub_cmp_discounts": "Εκπτώσεις, κουπόνια & αποθηκευμένα καλάθια",
    "sub_cmp_eod": "Κλείσιμο ημέρας (Z / clôture)",
    "sub_cmp_invoices": "Δημιουργία & παρακολούθηση τιμολογίων",
    "sub_cmp_refunds": "Ασφαλείς επιστροφές & υπόλοιπο πελατών",
    "sub_cmp_offline": "Offline-first με αυτόματο συγχρονισμό",
    "sub_cat_safety": "Ασφάλεια & Συμμόρφωση",
    "sub_cmp_immutable": "Αμετάβλητα τιμολόγια",
    "sub_cmp_ledger": "Καθολικό πληρωμών μόνο για προσθήκη",
    "sub_cmp_crash_safe": "Πληρωμές ασφαλείς έναντι αστοχιών",
    "sub_cmp_period_lock": "Λογιστική κλειδωμένης περιόδου",
    "sub_cat_advanced": "Προηγμένα Χαρακτηριστικά (Μόνο BUSINESS)",
    "sub_cmp_web_panel": "Web Panel Λογιστή & Διαχειριστή",
    "sub_cmp_advanced_reports": "Προηγμένες οικονομικές αναφορές",
    "sub_cmp_kds": "Σύστημα Προβολής Κουζίνας (KDS)",
    "sub_cmp_hotel": "Modules Ξενοδοχείου / PMS",
    "sub_cmp_web_ordering": "Web παραγγελιοληψία (tablets & self-order)",
    "sub_cmp_whatsapp": "Ειδοποιήσεις WhatsApp",
    "sub_cmp_api": "API Προϊόντων & Ενσωμάτωσης",
    "sub_cmp_woocommerce": "Συγχρονισμός WooCommerce",
    "sub_cmp_print": "Προηγμένο Σύστημα Εκτύπωσης",
    "sub_cmp_remote": "Απομακρυσμένη Πρόσβαση (iOS & Android)",
    "sub_cmp_auth": "Ταυτοποίηση Πολλαπλών Μισθωτών & Δικαιώματα",

    "sub_which_plan_title": "Ποιο πρόγραμμα να επιλέξω;",
    "sub_choose_basic": "Επιλέξτε Starter αν θέλετε ένα πλήρες, ασφαλές POS και διαχειρίζεστε τα πάντα από την εφαρμογή.",
    "sub_choose_pro": "Επιλέξτε Business αν χρειάζεστε λογιστική διαχείριση, προηγμένες ενότητες, αυτοματισμό ή ενσωματώσεις.",
    "sub_choose_custom": "Μια πλήρως προσαρμοσμένη λύση Finio σχεδιασμένη γύρω από τα υπάρχοντα συστήματά σας, τις λειτουργίες και τις εταιρικές ανάγκες σας. Το Finio Custom προσαρμόζεται στην επιχείρησή σας — όχι το αντίστροφο.",

    "// --- 404 Page ---": "",
    "page_not_found_title": "Η Σελίδα Δεν Βρέθηκε",
    "page_not_found_desc": "Η σελίδα που αναζητάτε ενδέχεται να έχει αφαιρεθεί, να έχει αλλάξει όνομα ή να μην είναι προσωρινά διαθέσιμη.",
    "back_to_home": "Επιστροφή στην Αρχική",

    "// --- Help Center ---": "",
    "help_welcome": "Καλώς ήρθατε στο Κέντρο Βοήθειας Finio",
    "help_intro": "Βρείτε απαντήσεις σε συχνές ερωτήσεις και λάβετε την υποστήριξη που χρειάζεστε για να αξιοποιήσετε στο έπακρο το Finio PRO.",
    "help_faq_title": "Συχνές Ερωτήσεις",
    "help_faq_1_q": "Τι είναι το Finio PRO;",
    "help_faq_1_a": "Το Finio PRO είναι μια ολοκληρωμένη πλατφόρμα διαχείρισης εστίασης και φιλοξενίας που περιλαμβάνει POS, KDS, διαχείριση αποθεμάτων, web server, PMS και προηγμένα συστήματα εκτύπωσης.",
    "help_faq_2_q": "Λειτουργεί το Finio PRO χωρίς διαδίκτυο;",
    "help_faq_2_a": "Ναι! Το Finio PRO διαθέτει αρχιτεκτονική offline-first που διατηρεί τις λειτουργίες σας ομαλές ακόμη και χωρίς internet. Τα δεδομένα συγχρονίζονται αυτόματα όταν συνδεθείτε ξανά.",
    "help_faq_3_q": "Τι εξοπλισμό χρειάζομαι;",
    "help_faq_3_a": "Το Finio PRO λειτουργεί με τυπικό υλικό POS, συμπεριλαμβανομένων εκτυπωτών αποδείξεων, σαρωτών barcode και συρταριών ταμείου. Παρέχουμε επίσης συμβουλευτική και υπηρεσίες εγκατάστασης εξοπλισμού.",
    "help_faq_4_q": "Πόσο ασφαλή είναι τα δεδομένα μου;",
    "help_faq_4_a": "Τα δεδομένα σας προστατεύονται με ασφάλεια εταιρικού επιπέδου, συμπεριλαμβανομένης κρυπτογράφησης, αυτόματων αντιγράφων ασφαλείας, δικαιωμάτων βάσει ρόλων και απομόνωσης δεδομένων πολλαπλών μισθωτών.",
    "help_faq_5_q": "Μπορώ να έχω πρόσβαση στο Web Panel Λογιστή εξ αποστάσεως;",
    "help_faq_5_a": "Απολύτως! Παρακολουθήστε και διαχειριστείτε την επιχείρησή σας από οπουδήποτε στον κόσμο χρησιμοποιώντας τις ειδικές εφαρμογές μας για iOS και Android.",
    "help_contact_support": "Χρειάζεστε Περισσότερη Βοήθεια;",
    "help_contact_text": "Η ομάδα υποστήριξής μας είναι εδώ για να σας βοηθήσει. Επικοινωνήστε μαζί μας μέσω:",
    "help_search_placeholder": "Αναζήτηση απαντήσεων...",
    "help_docs_desc": "Λεπτομερείς οδηγοί για το πώς να χρησιμοποιήσετε κάθε λειτουργία του Finio.",
    "help_faq_desc": "Γρήγορες απαντήσεις σε κοινές ερωτήσεις.",
    "help_security_desc": "Πώς διατηρούμε τα δεδομένα σας ασφαλή και συμμορφωμένα.",
    "help_changelog_desc": "Τελευταίες ενημερώσεις και βελτιώσεις.",
    "contact_email_plain": "",
    "contact_phone_plain": "+212808646672",

    "// --- Terms & Conditions ---": "",
    "terms_last_updated": "Τελευταία Ενημέρωση",
    "terms_date": "Νοέμβριος 2025",
    "terms_acceptance_title": "Αποδοχή των Όρων",
    "terms_acceptance_text": "Με την πρόσβαση και τη χρήση των υπηρεσιών Finio PRO, αποδέχεστε και συμφωνείτε να δεσμεύεστε από τους παρόντες Όρους και Προϋποθέσεις. Εάν δεν συμφωνείτε με αυτούς τους όρους, παρακαλούμε μην χρησιμοποιείτε τις υπηρεσίες μας.",
    "terms_services_title": "Περιγραφή Υπηρεσιών",
    "terms_services_text": "Η Finio Solutions παρέχει μια ολοκληρωμένη πλατφόρμα διαχείρισης επιχειρήσεων που περιλαμβάνει σημείο πώλησης, διαχείριση αποθεμάτων, διαχείριση πελατειακών σχέσεων και σχετικές υπηρεσίες. Διατηρούμε το δικαίωμα να τροποποιήσουμε, να αναστείλουμε ή να διακόψουμε οποιοδήποτε μέρος των υπηρεσιών μας ανά πάσα στιγμή.",
    "terms_account_title": "Λογαριασμοί Χρηστών και Ευθύνες",
    "terms_account_text": "Είστε υπεύθυνοι για τη διατήρηση της εμπιστευτικότητας των διαπιστευτηρίων του λογαριασμού σας και για όλες τις δραστηριότητες που συμβαίνουν στο λογαριασμό σας. Συμφωνείτε να μας ειδοποιήσετε αμέσως για οποιαδήποτε μη εξουσιοδοτημένη χρήση του λογαριασμού σας.",
    "terms_offline_title": "Λειτουργία Offline & Ευθύνη Δεδομένων",
    "terms_offline_text": "Η λειτουργία offline του Finio PRO αποθηκεύει δεδομένα τοπικά στη συσκευή σας. Είστε υπεύθυνοι για τη διασφάλιση ότι η συσκευή σας συγχρονίζεται τακτικά με το cloud. Η Finio Solutions δεν φέρει ευθύνη για απώλεια δεδομένων που προκαλείται από αστοχία ή απώλεια της συσκευής πριν από τον συγχρονισμό.",
    "terms_payment_title": "Πληρωμή και Συνδρομή",
    "terms_payment_text": "Τα τέλη συνδρομής χρεώνονται προκαταβολικά σε επαναλαμβανόμενη βάση. Όλα τα τέλη δεν επιστρέφονται, εκτός αν απαιτείται από το νόμο. Διατηρούμε το δικαίωμα να αλλάξουμε την τιμολόγησή μας με ειδοποίηση 30 ημερών στους ενεργούς συνδρομητές.",
    "terms_hardware_title": "Συμβατότητα Υλικού & Εκτυπωτών",
    "terms_hardware_text": "Το Finio λειτουργεί με τυπικούς εκτυπωτές ESC/POS και tablets. Παρέχουμε μια λίστα με προτεινόμενο υλικό, αλλά δεν εγγυόμαστε τη συμβατότητα με όλες τις συσκευές τρίτων, εκτός εάν έχουν πιστοποιηθεί ρητά από εμάς.",
    "terms_intellectual_title": "Πνευματική Ιδιοκτησία",
    "terms_intellectual_text": "Όλο το περιεχόμενο, τα χαρακτηριστικά και η λειτουργικότητα του Finio PRO ανήκουν στη Finio Solutions και προστατεύονται από διεθνείς νόμους περί πνευματικών δικαιωμάτων, εμπορικών σημάτων και άλλης πνευματικής ιδιοκτησίας.",
    "terms_limitation_title": "Περιορισμός Ευθύνης",
    "terms_limitation_text": "Η Finio Solutions δεν φέρει ευθύνη για τυχόν έμμεσες, θετικές, ειδικές, αποθετικές ή ποινικές ζημίες που προκύπτουν από τη χρήση ή την αδυναμία χρήσης της υπηρεσίας.",
    "terms_termination_title": "Τερματισμός",
    "terms_termination_text": "Διατηρούμε το δικαίωμα να τερματίσουμε ή να αναστείλουμε το λογαριασμό σας και την πρόσβαση στις υπηρεσίες μας κατά την αποκλειστική μας κρίση, χωρίς προειδοποίηση, για συμπεριφορά που πιστεύουμε ότι παραβιάζει τους παρόντες Όρους ή είναι επιβλαβής για άλλους χρήστες, εμάς ή τρίτα μέρη, ή για οποιονδήποτε άλλο λόγο.",
    "terms_governing_title": "Εφαρμοστέο Δίκαιο",
    "terms_governing_text": "Οι παρόντες Όροι διέπονται και ερμηνεύονται σύμφωνα με τους νόμους του Μαρόκου, χωρίς να λαμβάνονται υπόψη οι διατάξεις περί σύγκρουσης νόμων.",
    "terms_changes_title": "Αλλαγές στους Όρους",
    "terms_changes_text": "Διατηρούμε το δικαίωμα να τροποποιήσουμε αυτούς τους όρους ανά πάσα στιγμή. Θα ειδοποιήσουμε τους χρήστες για τυχόν ουσιώδεις αλλαγές μέσω email ή μέσω της υπηρεσίας μας. Η συνεχής χρήση της υπηρεσίας μετά από τέτοιες τροποποιήσεις συνιστά αποδοχή των ενημερωμένων όρων.",
    "terms_contact_title": "Πληροφορίες Επικοινωνίας",
    "terms_contact_text": "Για τυχόν ερωτήσεις σχετικά με αυτούς τους Όρους και Προϋποθέσεις, παρακαλούμε επικοινωνήστε μαζί μας στο:",

    "// --- Privacy Policy ---": "",
    "privacy_last_updated": "Τελευταία Ενημέρωση",
    "privacy_date": "Νοέμβριος 2025",
    "privacy_intro_title": "Εισαγωγή",
    "privacy_intro_text": "Η Finio Solutions ('εμείς', 'μας' ή 'εμάς') σέβεται το απόρρητό σας και δεσμεύεται να προστατεύει τα προσωπικά σας δεδομένα. Αυτή η πολιτική απορρήτου εξηγεί πώς συλλέγουμε, χρησιμοποιούμε και διασφαλίζουμε τις πληροφορίες σας όταν χρησιμοποιείτε τις υπηρεσίες Finio PRO.",
    "privacy_collection_title": "Πληροφορίες που Συλλέγουμε",
    "privacy_collection_text": "Συλλέγουμε διάφορους τύπους πληροφοριών για την παροχή και τη βελτίωση των υπηρεσιών μας:",
    "privacy_collection_item1": "Πληροφορίες προσωπικής ταυτοποίησης (όνομα, διεύθυνση email, αριθμός τηλεφώνου)",
    "privacy_collection_item2": "Επιχειρηματικές πληροφορίες (όνομα εταιρείας, διεύθυνση, φορολογικά στοιχεία)",
    "privacy_collection_item3": "Δεδομένα χρήσης (πώς αλληλεπιδράτε με τις υπηρεσίες μας, χαρακτηριστικά που χρησιμοποιούνται)",
    "privacy_collection_item4": "Τεχνικά δεδομένα (διεύθυνση IP, τύπος προγράμματος περιήγησης, πληροφορίες συσκευής)",
    "privacy_pos_data_item": "Συναλλακτικά δεδομένα (ιστορικό πωλήσεων, επίπεδα αποθεμάτων, αγορές πελατών) που παράγονται μέσω του συστήματος POS",
    "privacy_pos_title": "Δεδομένα POS & Συναλλαγών",
    "privacy_pos_text": "Συλλέγουμε και επεξεργαζόμαστε συναλλακτικά δεδομένα για να σας παρέχουμε αναφορές πωλήσεων, παρακολούθηση αποθεμάτων και επιχειρηματικές αναλύσεις. Αυτά τα δεδομένα ανήκουν σε εσάς. Δεν χρησιμοποιούμε τα δεδομένα πωλήσεών σας για σκοπούς μάρκετινγκ ούτε τα μοιραζόμαστε με τρίτους.",
    "privacy_offline_storage_title": "Offline Τοπική Αποθήκευση",
    "privacy_offline_storage_text": "Κατά τη χρήση του Finio PRO εκτός σύνδεσης, τα δεδομένα αποθηκεύονται με ασφάλεια στην τοπική σας συσκευή χρησιμοποιώντας μια κρυπτογραφημένη τοπική βάση δεδομένων. Αυτά τα δεδομένα παραμένουν ουσιαστικά υπό τον φυσικό σας έλεγχο έως ότου η συσκευή επανασυνδεθεί στο διαδίκτυο και συγχρονιστεί με τους ασφαλείς διακομιστές cloud μας.",
    "privacy_use_title": "Πώς Χρησιμοποιούμε τις Πληροφορίες Σας",
    "privacy_use_text": "Χρησιμοποιούμε τις πληροφορίες που συλλέγονται για τους ακόλουθους σκοπούς:",
    "privacy_use_item1": "Για την παροχή, συντήρηση και βελτίωση των υπηρεσιών μας",
    "privacy_use_item2": "Για την επεξεργασία συναλλαγών και την αποστολή σχετικών πληροφοριών",
    "privacy_use_item3": "Για την αποστολή διοικητικών πληροφοριών, ενημερώσεων και ειδοποιήσεων ασφαλείας",
    "privacy_use_item4": "Για να απαντήσουμε στα αιτήματα, τις ερωτήσεις σας και να παρέχουμε υποστήριξη πελατών",
    "privacy_sharing_title": "Κοινοποίηση και Γνωστοποίηση Πληροφοριών",
    "privacy_sharing_text": "Δεν πωλούμε, ανταλλάσσουμε ή ενοικιάζουμε τα προσωπικά σας στοιχεία σε τρίτους. Ενδέχεται να κοινοποιήσουμε τις πληροφορίες σας μόνο στις ακόλουθες περιπτώσεις: με τη συγκατάθεσή σας, για συμμόρφωση με νομικές υποχρεώσεις, για την προστασία των δικαιωμάτων και της ασφάλειάς μας ή με παρόχους υπηρεσιών που βοηθούν στις λειτουργίες μας υπό αυστηρές συμφωνίες εμπιστευτικότητας.",
    "privacy_security_title": "Ασφάλεια Δεδομένων",
    "privacy_security_text": "Εφαρμόζουμε μέτρα ασφαλείας βιομηχανικών προτύπων για την προστασία των προσωπικών σας πληροφοριών, συμπεριλαμβανομένης της κρυπτογράφησης, των ασφαλών διακομιστών, των τακτικών ελέγχων ασφαλείας και των ελέγχων πρόσβασης. Ωστόσο, καμία μέθοδος μετάδοσης μέσω διαδικτύου δεν είναι 100% ασφαλής.",
    "privacy_retention_title": "Διατήρηση Δεδομένων",
    "privacy_retention_text": "Διατηρούμε τα προσωπικά σας στοιχεία μόνο για όσο διάστημα είναι απαραίτητο για την εκπλήρωση των σκοπών που περιγράφονται στην παρούσα πολιτική απορρήτου, εκτός εάν απαιτείται μεγαλύτερη περίοδος διατήρησης από το νόμο.",
    "privacy_rights_title": "Τα Δικαιώματα Απορρήτου Σας",
    "privacy_rights_text": "Έχετε το δικαίωμα να:",
    "privacy_rights_item1": "Αποκτήσετε πρόσβαση και να λάβετε αντίγραφο των προσωπικών σας δεδομένων",
    "privacy_rights_item2": "Ζητήσετε διόρθωση ανακριβών ή ελλιπών δεδομένων",
    "privacy_rights_item3": "Ζητήσετε διαγραφή των προσωπικών σας δεδομένων",
    "privacy_rights_item4": "Αντιταχθείτε ή να περιορίσετε ορισμένη επεξεργασία των δεδομένων σας",
    "privacy_cookies_title": "Cookies και Τεχνολογίες Παρακολούθησης",
    "privacy_cookies_text": "Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες παρακολούθησης για να παρακολουθούμε τη δραστηριότητα στην υπηρεσία μας και να διατηρούμε ορισμένες πληροφορίες. Μπορείτε να δώσετε εντολή στο πρόγραμμα περιήγησής σας να αρνηθεί όλα τα cookies ή να υποδεικνύει πότε αποστέλλεται ένα cookie.",
    "privacy_children_title": "Απόρρητο Παιδιών",
    "privacy_children_text": "Οι υπηρεσίες μας δεν προορίζονται για άτομα ηλικίας κάτω των 18 ετών. Δεν συλλέγουμε εν γνώσει μας προσωπικές πληροφορίες από παιδιά. Εάν πιστεύετε ότι έχουμε συλλέξει πληροφορίες από παιδί, επικοινωνήστε μαζί μας αμέσως.",
    "privacy_changes_title": "Αλλαγές στην Παρούσα Πολιτική Απορρήτου",
    "privacy_changes_text": "Ενδέχεται να ενημερώνουμε την Πολιτική Απορρήτου μας κατά καιρούς. Θα σας ειδοποιήσουμε για τυχόν αλλαγές δημοσιεύοντας τη νέα Πολιτική Απορρήτου σε αυτή τη σελίδα και ενημερώνοντας την ημερομηνία 'Τελευταία Ενημέρωση'.",
    "privacy_contact_title": "Επικοινωνήστε Μαζί Μας",
    "privacy_contact_text": "Εάν έχετε οποιεσδήποτε ερωτήσεις σχετικά με αυτήν την Πολιτική Απορρήτου, παρακαλούμε επικοινωνήστε μαζί μας στο:",

    // --- Documentation ---
    // Docs Navigation
    "docs_nav_title": "Τεκμηρίωση",
    "docs_resources_title": "Πόροι",
    "docs_nav_getting_started": "Ξεκινώντας",
    "docs_nav_pos_usage": "Εγχειρίδιο Χρήσης POS",
    "docs_nav_kds_workflow": "Ροή Εργασίας KDS",
    "docs_nav_pms_overview": "PMS & Ξενοδοχείο",
    "docs_nav_offline_sync": "Εκτός Σύνδεσης & Συγχρονισμός",
    "docs_nav_roles_permissions": "Ρόλοι & Δικαιώματα",
    "docs_nav_finance_closures": "Τιμολόγια & Κλείσιμο",
    "docs_nav_hardware_printers": "Εκτυπωτές & Υλικό",
    "docs_nav_integrations": "Ενσωματώσεις",
    "docs_nav_compliance": "Συμμόρφωση & Φορολογικά",
    "docs_nav_troubleshooting": "Αντιμετώπιση προβλημάτων",

    // Getting Started
    "docs_gs_title": "Ξεκινώντας",
    "docs_gs_lead": "Καλώς ήλθατε στο Finio, ένα προηγμένο σύστημα POS & Διαχείρισης Επιχειρήσεων εκτός σύνδεσης, σχεδιασμένο για λιανική, εστιατόρια, καφέ και φιλοξενία.",
    "docs_gs_intro": "Το Finio συνδυάζει Σημείο Πώλησης (POS), Οθόνη Κουζίνας (KDS), Απογραφή, Λογιστική και PMS Ξενοδοχείου σε μία ενιαία πλατφόρμα, με ισχυρή ακεραιότητα δεδομένων, ελεξιμότητα και συγχρονισμό πολλαπλών συσκευών.",
    "docs_gs_access_title": "1. Πρόσβαση στο Finio",
    "docs_gs_step_1": "<strong>Εγκαταστήστε το Finio</strong> στη συσκευή σας (Android / Υπολογιστής / Tablet).",
    "docs_gs_step_2": "<strong>Ανοίξτε την εφαρμογή.</strong>",
    "docs_gs_step_3": "<strong>Συνδεθείτε</strong> χρησιμοποιώντας τον λογαριασμό ενοικιαστή σας.",
    "docs_gs_step_4": "<strong>Επιλέξτε το κατάστημα / υποκατάστημά σας</strong> αν υπάρχει.",
    "docs_gs_offline_note": "<em>Το Finio λειτουργεί εκτός σύνδεσης από προεπιλογή. Το Internet απαιτείται μόνο για συγχρονισμό στο cloud και κοινή χρήση πολλαπλών συσκευών.</em>",
    "docs_gs_config_title": "2. Διαμόρφωση καταστήματος",
    "docs_gs_config_intro": "Πριν ξεκινήσετε τις πωλήσεις, διαμορφώστε τις ρυθμίσεις του καταστήματός σας:",
    "docs_gs_company_title": "Στοιχεία Εταιρείας",
    "docs_gs_company_go_to": "Μεταβείτε: <strong>Ρυθμίσεις → Εταιρεία</strong>",
    "docs_gs_company_configure": "Διαμόρφωση:",
    "docs_gs_company_list_1": "Όνομα επιχείρησης",
    "docs_gs_company_list_2": "Λογότυπο",
    "docs_gs_company_list_3": "Διεύθυνση",
    "docs_gs_company_list_4": "ΑΦΜ (ICE / VAT)",
    "docs_gs_company_list_5": "Νόμισμα",
    "docs_gs_company_list_6": "Χώρα",
    "docs_gs_company_note": "Αυτά τα στοιχεία εμφανίζονται στα Τιμολόγια, τις Αποδείξεις και τις Λογιστικές εξαγωγές.",
    "docs_gs_taxes_title": "Φόροι",
    "docs_gs_taxes_go_to": "Μεταβείτε: <strong>Ρυθμίσεις → Φόροι</strong>",
    "docs_gs_taxes_list_1": "Δημιουργία ενός ή περισσότερων φορολογικών συντελεστών",
    "docs_gs_taxes_list_2": "Αντιστοίχιση φόρων σε προϊόντα",
    "docs_gs_taxes_list_3": "Υποστηρίζει: Τιμολόγηση με φόρο, Τιμολόγηση χωρίς φόρο, Πολλαπλές φορολογικές κατηγορίες",
    "docs_gs_taxes_note": "<strong>Σημείωση:</strong> Οι φόροι κλειδώνουν κατά τη δημιουργία του τιμολογίου για να διασφαλιστεί η λογιστική ακεραιότητα.",
    "docs_gs_hardware_title": "Εκτυπωτές & Υλικό",
    "docs_gs_hardware_go_to": "Μεταβείτε: <strong>Ρυθμίσεις → Υλικό</strong>",
    "docs_gs_hardware_list_1": "Ανακάλυψη εκτυπωτών δικτύου",
    "docs_gs_hardware_list_2": "Αντιστοίχιση εκτυπωτών για: Αποδείξεις, Δελτία κουζίνας, Μπαρ / σταθμούς",
    "docs_gs_hardware_list_3": "Διαμόρφωση προτύπων εκτύπωσης ανά σταθμό",
    "docs_gs_hardware_note": "<em>Τα πρότυπα εκτυπωτών έχουν εκδόσεις και είναι ασφαλώς ανακτήσιμα.</em>",
    "docs_gs_adding_title": "3. Προσθήκη Προϊόντων",
    "docs_gs_adding_go_to": "Μεταβείτε: <strong>Απογραφή → Προϊόντα</strong>",
    "docs_gs_adding_intro": "Μπορείτε να προσθέσετε προϊόντα με τρεις τρόπους:",
    "docs_gs_manual_title": "Χειροκίνητη Εισαγωγή",
    "docs_gs_manual_list_1": "Όνομα, Κατηγορία, Τιμή (υποστήριξη πολλαπλών τιμών)",
    "docs_gs_manual_list_2": "Φόρος, Παρακολούθηση αποθέματος",
    "docs_gs_manual_list_3": "Εικόνα, Τροποποιητές & παραλλαγές",
    "docs_gs_csv_title": "Εισαγωγή CSV (Pro Plan)",
    "docs_gs_csv_list_1": "Μαζική εισαγωγή προϊόντων",
    "docs_gs_csv_list_2": "Αυτόματη δημιουργία κατηγοριών",
    "docs_gs_csv_list_3": "Υποστηρίζει παραλλαγές & τιμές",
    "docs_gs_types_title": "Προηγμένοι Τύποι Προϊόντων",
    "docs_gs_types_list_1": "Απλά προϊόντα",
    "docs_gs_types_list_2": "Προϊόντα παραλλαγής (μέγεθος, χρώμα, κ.λπ.)",
    "docs_gs_types_list_3": "Σύνθετα προϊόντα (συνταγές / πακέτα)",
    "docs_gs_types_list_4": "Προϊόντα μόνο αποθέματος (μη πωλήσιμα)",
    "docs_pos_title": "Η Πρώτη σας Πώληση (POS)",
    "docs_pos_lead": "Η επεξεργασία πωλήσεων στο Finio είναι γρήγορη και ασφαλής. Ακολουθήστε αυτά τα βήματα για να ολοκληρώσετε την πρώτη σας συναλλαγή.",
    "docs_pos_steps_title": "Βήματα Πώλησης",
    "docs_pos_step_1": "<strong>Ανοίξτε το POS</strong> από το κύριο μενού.",
    "docs_pos_step_2": "<strong>Επιλέξτε προϊόντα</strong> από το πλέγμα ή σαρώστε ένα barcode.",
    "docs_pos_step_3": "<strong>Επιλέξτε πελάτη</strong> (προαιρετικό) από την επάνω μπάρα.",
    "docs_pos_step_4": "Πατήστε <strong>Πληρωμή</strong>.",
    "docs_pos_step_5": "<strong>Επιλέξτε μέθοδο πληρωμής:</strong>",
    "docs_pos_method_1": "Μετρητά",
    "docs_pos_method_2": "Κάρτα",
    "docs_pos_method_3": "Τράπεζα",
    "docs_pos_method_4": "Υπόλοιπο πελάτη",
    "docs_pos_method_5": "Επιταγή",
    "docs_pos_method_6": "Μικτές πληρωμές",
    "docs_pos_step_6": "<strong>Επιβεβαιώστε την πληρωμή</strong>.",
    "docs_pos_step_7": "<strong>Η απόδειξη εκτυπώνεται αυτόματα</strong> (αν έχει διαμορφωθεί).",
    "docs_pos_note": "<strong>Σημείωση:</strong> Κάθε πώληση δημιουργεί ένα αμετάβλητο τιμολόγιο για λογιστική ασφάλεια.",
    "docs_pos_features_title": "Βασικά Χαρακτηριστικά",
    "docs_pos_feat_multi": "<strong>Υποστήριξη πολλαπλών πληρωμών:</strong> Χωρίστε εύκολα λογαριασμούς μεταξύ μετρητών και κάρτας.",
    "docs_pos_feat_hold": "<strong>Αναμονή & Ανάκτηση:</strong> Αποθηκεύστε παραγγελίες για αργότερα αν ένας πελάτης δεν είναι έτοιμος.",
    "docs_pos_feat_offline": "<strong>Λειτουργία εκτός σύνδεσης:</strong> Συνεχίστε να πουλάτε ακόμη και χωρίς internet. Τα δεδομένα συγχρονίζονται αυτόματα μόλις συνδεθείτε.",

    "docs_hardware_title": "Εκτυπωτές & Εκτύπωση",
    "docs_hardware_lead": "Διαμορφώστε αποδείξεις, δελτία κουζίνας και δρομολόγηση εκτυπωτών για απρόσκοπτες λειτουργίες.",
    "docs_hardware_features_title": "Λειτουργίες Εκτύπωσης",
    "docs_hardware_feat_receipts": "<strong>Αποδείξεις:</strong> Αυτόματες αποδείξεις πελατών κατά την πληρωμή.",
    "docs_hardware_feat_kitchen": "<strong>Δελτία Κουζίνας:</strong> Αποστολή παραγγελιών απευθείας στην κουζίνα ή στο μπαρ.",
    "docs_hardware_feat_routing": "<strong>Δρομολόγηση Σταθμού:</strong> Δρομολόγηση συγκεκριμένων ειδών (π.χ. ποτά έναντι φαγητού) σε διαφορετικούς εκτυπωτές.",
    "docs_hardware_feat_templates": "<strong>Πρότυπα:</strong> Προσαρμόστε τις διατάξεις εκτύπωσης με τον επεξεργαστή προτύπων.",
    "docs_hardware_feat_queue": "<strong>Παρακολούθηση Ουράς:</strong> Παρακολουθήστε τις εργασίες εκτύπωσης και την κατάσταση.",
    "docs_hardware_supported_title": "Υποστηριζόμενο Υλικό",
    "docs_hardware_bt": "<strong>Εκτυπωτές Bluetooth:</strong> Για ρυθμίσεις κινητών και tablet.",
    "docs_hardware_network": "<strong>Εκτυπωτές Δικτύου (LAN):</strong> Τυπική σύνδεση ethernet για αξιοπιστία.",
    "docs_hardware_recovery_note": "<strong>Λειτουργία:</strong> Η αυτόματη ανάκτηση διασφαλίζει ότι οι εργασίες εκτύπωσης δεν χάνονται εάν ένας εκτυπωτής βγει προσωρινά εκτός σύνδεσης.",
    "docs_int_title": "Ενσωματώσεις",
    "docs_int_lead": "Συνδέστε το Finio με εργαλεία τρίτων για να επεκτείνετε το οικοσύστημά σας.",
    "docs_int_web_title": "Ενσωματώσεις Web",
    "docs_int_api": "<strong>API Προϊόντων:</strong> Συγχρονισμός προϊόντων και αποθέματος με εξωτερικά συστήματα.",
    "docs_int_kds": "<strong>KDS Web:</strong> Πρόσβαση στο σύστημα οθόνης κουζίνας μέσω προγράμματος περιήγησης.",
    "docs_int_pms": "<strong>PMS Web:</strong> Διεπαφή συστήματος διαχείρισης ακινήτων.",
    "docs_int_feedback": "<strong>Feedback Web:</strong> Συλλογή σχολίων πελατών.",
    "docs_int_waiter": "<strong>Σερβιτόρος / Αυτοεξυπηρέτηση:</strong> Παραγγελίες μέσω κινητού για προσωπικό ή πελάτες.",
    "docs_int_arch_title": "Αρχιτεκτονική Υπηρεσιών",
    "docs_int_arch_intro": "Όλες οι υπηρεσίες web είναι σχεδιασμένες για ασφάλεια και απόδοση:",
    "docs_int_local": "<strong>Τοπική Εκτέλεση:</strong> Εκτελέστε υπηρεσίες τοπικά για χαμηλή καθυστέρηση.",
    "docs_int_auth": "<strong>Πιστοποιημένο:</strong> Ασφαλής πρόσβαση μέσω διαπιστευτηρίων ενοικιαστή.",
    "docs_int_rate": "<strong>Περιορισμός Ρυθμού:</strong> Προστασία ID από κατάχρηση.",
    "docs_int_limited": "<strong>Απομονωμένο ανά Ενοικιαστή:</strong> Διαχωρισμός δεδομένων για περιβάλλοντα πολλαπλών ενοικιαστών.",
    "docs_comp_title": "Λογιστική & Συμμόρφωση",
    "docs_comp_lead": "Βασικό Λογιστικό Μοντέλο: Πώς το Finio προστατεύει τα οικονομικά σας δεδομένα.",
    "docs_comp_model_title": "Βασικό Λογιστικό Μοντέλο",
    "docs_comp_immutable_title": "Αμετάβλητα Τιμολόγια",
    "docs_comp_immutable_desc": "Μόλις οριστικοποιηθεί ένα τιμολόγιο, δεν μπορεί να επεξεργαστεί ή να διαγραφεί. Υπάρχει μόνιμα στο καθολικό.",
    "docs_comp_append_title": "Καθολικό Προσθήκης Μόνο",
    "docs_comp_append_desc": "Οι πληρωμές και οι ενημερώσεις υπολοίπου είναι αθροιστικές. Ποτέ δεν αντικαθιστούμε μια εγγραφή. προσθέτουμε μόνο νέες αντισταθμιστικές εγγραφές.",
    "docs_comp_safety_title": "Ασφάλεια Συναλλαγών",
    "docs_comp_safety_desc": "Όλες οι λειτουργίες είναι ατομικές. Εάν συμβεί σφάλμα, το σύστημα ανακτάται σε μια συνεπή κατάσταση χωρίς απώλεια δεδομένων.",
    "docs_sec_title": "Ασφάλεια & Προστασία Δεδομένων",
    "docs_sec_lead": "Το Finio χρησιμοποιεί ένα μοντέλο ασφαλείας \"Audit-First\" σχεδιασμένο για οικονομική συμμόρφωση και ακεραιότητα δεδομένων.",
    "docs_sec_enc_title": "Κρυπτογράφηση Δεδομένων",
    "docs_sec_enc_intro": "Αντιμετωπίζουμε τα δεδομένα της επιχείρησής σας ως ευαίσθητα οικονομικά περιουσιακά στοιχεία.",
    "docs_sec_table_state": "Κατάσταση",
    "docs_sec_table_method": "Μέθοδος",
    "docs_sec_table_detail": "Λεπτομέρεια",
    "docs_sec_rest_cloud": "<strong>Σε ηρεμία (Cloud)</strong>",
    "docs_sec_rest_cloud_desc": "Κρυπτογραφημένη αποθήκευση Google Cloud Firestore.",
    "docs_sec_rest_device": "<strong>Σε ηρεμία (Συσκευή)</strong>",
    "docs_sec_rest_device_desc": "Τα τοπικά αρχεία βάσης δεδομένων κρυπτογραφούνται για την αποτροπή εξαγωγής από κλεμμένα tablet.",
    "docs_sec_transit": "<strong>Σε μεταφορά</strong>",
    "docs_sec_transit_desc": "Όλη η κίνηση συγχρονισμού ρέει μέσω ασφαλών καναλιών HTTPS.",
    "docs_sec_rbac_title": "Έλεγχος Πρόσβασης Βάσει Ρόλων (RBAC)",
    "docs_sec_rbac_intro": "Τα δικαιώματα επιβάλλονται αυστηρά σε επίπεδο UI και API.",
    "docs_sec_role_owner": "<strong>Ιδιοκτήτης:</strong> Πλήρης πρόσβαση σε όλες τις ρυθμίσεις, τη λογιστική και τις διαγραφές.",
    "docs_sec_role_manager": "<strong>Διευθυντής:</strong> Μπορεί να ακυρώσει είδη και να εκτελέσει αναφορές τέλους ημέρας, αλλά δεν μπορεί να διαγράψει ιστορικό.",
    "docs_sec_role_cashier": "<strong>Ταμίας:</strong> Μπορεί να δημιουργήσει πωλήσεις και να δεχτεί πληρωμές. Δεν μπορεί να ακυρώσει απεσταλμένες παραγγελίες χωρίς έγκριση.",
    "docs_sec_role_waiter": "<strong>Σερβιτόρος:</strong> Περιορίζεται μόνο στη δημιουργία παραγγελιών. Καμία πρόσβαση σε πληρωμή ή συρτάρι.",
    "docs_sec_audit_title": "Αμετάβλητα Αρχεία Καταγραφής Ελέγχου",
    "docs_sec_audit_intro": "Κάθε κρίσιμη ενέργεια ενεργοποιεί μια μόνιμη εγγραφή στη συλλογή <code>audit_log</code>. Αυτό το αρχείο καταγραφής είναι ορατό στο Διαδικτυακό Πάνελ Λογιστή.",
    "docs_sec_audit_events": "<strong>Καταγεγραμμένα Συμβάντα:</strong> Ακυρώσεις, Επιστροφές, Εκπτώσεις (>10%), Ανοίγματα Συρταριού, Αλλαγές Τιμών.",
    "docs_sec_audit_meta": "<strong>Μεταδεδομένα:</strong> ID Χρήστη, Ακριβής Χρονική Σήμανση, ID Συσκευής, Τοποθεσία GPS (προαιρετικό).",
    "docs_sec_alert_box": "<strong>Σύστημα Ειδοποίησης:</strong> Ύποπτα μοτίβα (π.χ., 5 ακυρώσεις σε 1 λεπτό) ενεργοποιούν αυτόματες ειδοποιήσεις \"Κόκκινης Σημαίας\" στον ιδιοκτήτη της επιχείρησης μέσω WhatsApp.",
    "docs_ts_title": "Αντιμετώπιση προβλημάτων",
    "docs_ts_lead": "Βρείτε γρήγορες λύσεις σε κοινά λειτουργικά ζητήματα.",
    "docs_ts_issues_title": "Κοινά Ζητήματα",
    "docs_ts_printer_title": "Ο εκτυπωτής δεν εκτυπώνει",
    "docs_ts_printer_1": "<strong>Έλεγχος δικτύου:</strong> Βεβαιωθείτε ότι ο εκτυπωτής είναι συνδεδεμένος στο ίδιο Wi-Fi/LAN.",
    "docs_ts_printer_2": "<strong>Επανεκκίνηση υπηρεσίας:</strong> Επανεκκινήστε την Υπηρεσία Εκτυπωτή Finio ή τον ίδιο τον εκτυπωτή.",
    "docs_ts_printer_3": "<strong>Επαναχώρηση προτύπου:</strong> Μεταβείτε στις Ρυθμίσεις → Υλικό και επαληθεύστε την αντιστοίχιση του εκτυπωτή.",
    "docs_ts_sync_title": "Ο συγχρονισμός δεν λειτουργεί",
    "docs_ts_sync_1": "<strong>Έλεγχος internet:</strong> Βεβαιωθείτε ότι η συσκευή σας έχει ενεργή σύνδεση.",
    "docs_ts_sync_2": "<strong>Αυτόματος Συγχρονισμός:</strong> Ο συγχρονισμός συνεχίζεται αυτόματα μόλις συνδεθείτε. δεν απαιτείται χειροκίνητη ενέργεια.",
    "docs_ts_sync_3": "<strong>Ασφάλεια Δεδομένων:</strong> Τα δεδομένα σας είναι ασφαλή τοπικά και θα μεταφορτωθούν όταν είναι δυνατόν.",
    "docs_ts_balance_title": "Λάθος υπόλοιπο μετρητών",
    "docs_ts_balance_1": "<strong>Συνεδρίες:</strong> Ελέγξτε αν είναι ανοιχτή η σωστή συνεδρία ταμείου.",
    "docs_ts_balance_2": "<strong>Επιστροφές:</strong> Επαληθεύστε εάν υποβλήθηκαν σε επεξεργασία επιστροφές χρημάτων μετά την έναρξη της συνεδρίας.",
    "docs_ts_balance_3": "<strong>Τέλος Ημέρας:</strong> Ελέγξτε τις αναφορές κλεισίματος τέλους ημέρας για αποκλίσεις.",
    "docs_ts_help_box": "<strong>Χρειάζεστε περισσότερη βοήθεια;</strong> Επικοινωνήστε με την υποστήριξη στο <a href=\"mailto:support\u0040finio.pro\">support\u0040finio.pro</a>.",

    // Compliance Extended (Greek)
    "docs_comp_intro_title": "Πώς λειτουργεί η λογιστική στο Finio",
    "docs_comp_intro_p1": "Το Finio δεν είναι ένα \"απλό POS\". Είναι ένα σύστημα συναλλαγών ασφαλές για τη λογιστική, σχεδιασμένο για να προστατεύει τα οικονομικά δεδομένα από χειραγώγηση, λάθη, σφάλματα και διενέξεις πολλών συσκευών.",
    "docs_comp_intro_p2": "Αυτή η ενότητα εξηγεί πώς λειτουργεί η λογιστική Finio, γιατί περιορίζονται ορισμένες ενέργειες και πώς αυτό προστατεύει την επιχείρησή σας κατά τη διάρκεια των ελέγχων.",
    "docs_comp_invoice_title": "Τι είναι ένα τιμολόγιο;",
    "docs_comp_invoice_p1": "Ένα τιμολόγιο στο Finio είναι ένα νομικό λογιστικό έγγραφο, όχι ένα προσχέδιο.",
    "docs_comp_invoice_frozen": "Όταν δημιουργείται ένα τιμολόγιο, τα ακόλουθα \"παγώνουν\" μόνιμα:",
    "docs_comp_invoice_list_1": "Τιμές προϊόντων",
    "docs_comp_invoice_list_2": "Φόροι",
    "docs_comp_invoice_list_3": "Εκπτώσεις",
    "docs_comp_invoice_list_4": "Σύνολα",
    "docs_comp_immutability_title": "Αμετάβλητο τιμολογίου (Κρίσιμος κανόνας)",
    "docs_comp_immutability_intro": "Μόλις δημιουργηθεί:",
    "docs_comp_immutability_list_1": "Δεν μπορείτε να επεξεργαστείτε τα σύνολα",
    "docs_comp_immutability_list_2": "Δεν μπορείτε να αλλάξετε τον φόρο",
    "docs_comp_immutability_list_3": "Δεν μπορείτε να τροποποιήσετε αντικείμενα",
    "docs_comp_immutability_why": "<strong>Γιατί;</strong> Επειδή η επεξεργασία τιμολογίων μετά τη δημιουργία καταστρέφει τα ίχνη ελέγχου, ακυρώνει τις φορολογικές αναφορές και δημιουργεί κίνδυνο απάτης.",
    "docs_comp_immutability_compliance": "Αυτό είναι σκόπιμο και απαιτείται για τη συμμόρφωση.",
    "docs_comp_payments_title": "Πληρωμές: Καθολικό μόνο για προσθήκη",
    "docs_comp_payments_intro": "Το Finio δεν τροποποιεί τις πληρωμές. Αντίθετα, χρησιμοποιεί ένα καθολικό πληρωμών μόνο για προσθήκη.",
    "docs_comp_payments_how_title": "Πώς λειτουργούν οι πληρωμές",
    "docs_comp_payments_list_1": "Κάθε πληρωμή προστίθεται ως νέα εγγραφή",
    "docs_comp_payments_list_2": "Οι πληρωμές δεν αντικαθίστανται ποτέ",
    "docs_comp_payments_list_3": "Οι επιστροφές χρημάτων είναι νέες καταχωρίσεις καθολικού, όχι επεξεργασίες",
    "docs_comp_payments_guarantee": "Αυτό εγγυάται πλήρες ιστορικό πληρωμών, αντοχή σε παραβιάσεις και ασφαλή συγχρονισμό πολλών συσκευών.",
    "docs_comp_example_title": "Παράδειγμα",
    "docs_comp_table_action": "Ενέργεια",
    "docs_comp_table_result": "Τι κάνει το Finio",
    "docs_comp_table_row_1_a": "Ο πελάτης πληρώνει με μετρητά",
    "docs_comp_table_row_1_b": "Προσθέτει μια καταχώρηση πληρωμής",
    "docs_comp_table_row_2_a": "Επιταγή επικυρώθηκε αργότερα",
    "docs_comp_table_row_2_b": "Προσθέτει μια καταχώρηση αλλαγής κατάστασης",
    "docs_comp_table_row_3_a": "Εκδόθηκε επιστροφή χρημάτων",
    "docs_comp_table_row_3_b": "Προσθέτει μια καταχώρηση επιστροφής χρημάτων",
    "docs_comp_table_row_4_a": "Διένεξη συγχρονισμού",
    "docs_comp_table_row_4_b": "Συγχωνεύει καταχωρίσεις με ασφάλεια",
    "docs_comp_refunds_title": "Επιστροφές χρημάτων (Όχι επεξεργασία τιμολογίου)",
    "docs_comp_refunds_intro": "Επιστροφές χρημάτων στο Finio:",
    "docs_comp_refunds_list_1": "ΔΕΝ επεξεργάζονται το τιμολόγιο",
    "docs_comp_refunds_list_2": "ΔΕΝ αφαιρούν πληρωμές",
    "docs_comp_refunds_list_3": "Δημιουργούν μια αρνητική καταχώρηση καθολικού",
    "docs_comp_refunds_why": "<strong>Γιατί αυτό έχει σημασία:</strong> Η αρχική πώληση παραμένει ορατή, η επιστροφή χρημάτων παρακολουθείται σαφώς και οι ελεγκτές μπορούν να ανασυνθέσουν το πλήρες ιστορικό.",
    "docs_comp_refunds_box": "<strong>Δρομολόγηση:</strong> Οι επιστροφές μετρητών μειώνουν το ταμείο μετρητών. Οι επιστροφές καρτών μειώνουν το τραπεζικό υπόλοιπο. Η πίστωση καταστήματος αυξάνει το υπόλοιπο του πελάτη.",
    "docs_comp_eod_title": "Κλειστές περίοδοι και προστασία ελέγχου",
    "docs_comp_eod_intro": "Μόλις ολοκληρωθεί ένα Κλείσιμο Ημέρας (EOD):",
    "docs_comp_eod_list_1": "Η λογιστική περίοδος κλειδώνει",
    "docs_comp_eod_list_2": "Οι προηγούμενες συναλλαγές δεν μπορούν να τροποποιηθούν",
    "docs_comp_eod_list_3": "Οι επιστροφές χρημάτων μετά το EOD καταγράφονται ως νέα γεγονότα",
    "docs_comp_eod_prevent": "Αυτό αποτρέπει τη χειραγώγηση με προχρονολόγηση, τις κρυφές διορθώσεις και τις ασυνέπειες ελέγχου.",
    "docs_comp_trust_title": "Γιατί οι λογιστές εμπιστεύονται το Finio",
    "docs_comp_trust_intro": "Το Finio επιβάλλει επιχειρηματικούς λογιστικούς ελέγχους, όχι περιορισμούς POS:",
    "docs_comp_trust_list_1": "Αμετάβλητα τιμολόγια",
    "docs_comp_trust_list_2": "Πληρωμές μόνο για προσθήκη",
    "docs_comp_trust_list_3": "Κλείδωμα περιόδου",
    "docs_comp_trust_list_4": "Μετρητά συνδεδεμένα με το μητρώο",
    "docs_comp_trust_list_5": "Προστασία ώρας διακομιστή",
    "docs_comp_reg_title": "Ταμειακές μηχανές και συμφιλίωση",
    "docs_comp_reg_what_title": "Τι είναι μια ταμειακή μηχανή στο Finio;",
    "docs_comp_reg_what_desc": "Μια ταμειακή μηχανή αντιπροσωπεύει ένα φυσικό συρτάρι μετρητών. Κάθε κίνηση μετρητών πρέπει να συνδέεται με ένα μητρώο, να συνδέεται με μια ανοιχτή συνεδρία και να παρακολουθείται ανά περίοδο. Αυτό εξασφαλίζει τη λογοδοσία μετρητών.",
    "docs_comp_float_title": "Ταμείο έναρξης",
    "docs_comp_float_step_1": "Ανοίξτε το μητρώο",
    "docs_comp_float_step_2": "Εισάγετε το ποσό μετρητών έναρξης",
    "docs_comp_float_desc": "Αυτό γίνεται η βάση για τη συμφιλίωση.",
    "docs_comp_z_title": "Τέλος ημέρας (Κλείσιμο Z)",
    "docs_comp_z_intro": "Στο τέλος της ημέρας ή της βάρδιας:",
    "docs_comp_z_step_1": "Εκτελέστε κλείσιμο τέλους ημέρας",
    "docs_comp_z_step_2": "Το Finio υπολογίζει: Αναμενόμενα μετρητά, Πραγματικά μετρητά, Διαφορά, Περιθώριο",
    "docs_comp_z_step_3": "Η περίοδος κλείνει και κλειδώνει",
    "docs_comp_z_after": "Μετά το EOD:",
    "docs_comp_z_list_1": "Δεν μπορείτε να αλλάξετε προηγούμενες πωλήσεις μετρητών",
    "docs_comp_z_list_2": "Δεν μπορείτε να προχρονολογήσετε πληρωμές",
    "docs_comp_z_list_3": "Η νέα δραστηριότητα ξεκινά μια νέα περίοδο",
    "docs_comp_reg_box": "<strong>Γιατί τα μετρητά δεσμεύονται αυστηρά:</strong> Χωρίς δέσμευση μητρώου, τα μετρητά δεν παρακολουθούνται και η κλοπή δεν μπορεί να ανιχνευθεί. Το Finio αποκλείει τις πληρωμές με μετρητά χωρίς ανοιχτό μητρώο και πλαίσιο συνεδρίας.",
    "docs_comp_offline_title": "Συμπεριφορά εκτός σύνδεσης και πολλών συσκευών",
    "docs_comp_offline_first_title": "Εκτός σύνδεσης από σχεδιασμό",
    "docs_comp_offline_first_desc": "Το Finio λειτουργεί χωρίς internet. Όλες οι ενέργειες αποθηκεύονται πρώτα τοπικά, συγχρονίζονται με ασφάλεια αργότερα και δεν εμποδίζουν ποτέ τις πωλήσεις.",
    "docs_comp_drops_title": "Τι συμβαίνει εάν διακοπεί το Internet;",
    "docs_comp_drops_desc": "Τίποτα δεν χαλάει. Οι πωλήσεις συνεχίζονται, οι πληρωμές καταγράφονται και ο συγχρονισμός συνεχίζεται αυτόματα. Καμία απώλεια δεδομένων.",
    "docs_comp_multi_title": "Ασφάλεια πολλών συσκευών (Πολύ σημαντικό)",
    "docs_comp_multi_scenario": "<strong>Σενάριο:</strong> Δύο συσκευές, ίδιο τιμολόγιο.",
    "docs_comp_multi_list_1": "Η συσκευή Α προσθέτει πληρωμή",
    "docs_comp_multi_list_2": "Η συσκευή Β προσθέτει άλλη πληρωμή",
    "docs_comp_multi_list_3": "Και οι δύο συγχρονίζονται αργότερα",
    "docs_comp_multi_merge": "Το Finio συγχωνεύει τις πληρωμές, δεν αντικαθιστά. Αυτό είναι δυνατό επειδή οι πληρωμές είναι μόνο για προσθήκη και τα τιμολόγια έχουν εκδόσεις.",
    "docs_comp_time_title": "Προστασία ώρας συσκευής",
    "docs_comp_time_desc": "Το Finio χρησιμοποιεί ώρα ευθυγραμμισμένη με τον διακομιστή για να ανιχνεύσει τη χειραγώγηση του ρολογιού, να αποτρέψει την προχρονολόγηση και να προστατεύσει τις λογιστικές περιόδους.",
    "docs_comp_faq_title": "Συχνές ερωτήσεις (Λογιστική και Εμπιστοσύνη)",
    "docs_comp_faq_1_q": "Μπορώ να επεξεργαστώ ένα τιμολόγιο μετά την πληρωμή;",
    "docs_comp_faq_1_a": "Όχι. Τα τιμολόγια είναι αμετάβλητα για την προστασία της λογιστικής ακεραιότητας και των ελέγχων.",
    "docs_comp_faq_2_q": "Μπορώ να διαγράψω μια πληρωμή;",
    "docs_comp_faq_2_a": "Όχι. Οι πληρωμές είναι εγγραφές καθολικού και δεν μπορούν να αφαιρεθούν.",
    "docs_comp_faq_3_q": "Τι συμβαίνει εάν κάνω επιστροφή χρημάτων μετά το τέλος της ημέρας;",
    "docs_comp_faq_3_a": "Δημιουργείται μια νέα εγγραφή επιστροφής χρημάτων στην επόμενη περίοδο. Οι προηγούμενες περίοδοι παραμένουν κλειδωμένες.",
    "docs_comp_faq_4_q": "Μπορούν δύο ταμίες να χρησιμοποιούν το ίδιο μητρώο;",
    "docs_comp_faq_4_a": "Ναι, αλλά όχι ταυτόχρονα. Κάθε μητρώο έχει ελεγχόμενες συνεδρίες.",
    "docs_comp_faq_5_q": "Τι γίνεται αν η ώρα της συσκευής είναι λάθος;",
    "docs_comp_faq_5_a": "Το Finio ανιχνεύει και αποτρέπει τη χειραγώγηση του χρόνου.",
    "docs_comp_faq_6_q": "Είναι το Finio ασφαλές για φορολογικούς ελέγχους;",
    "docs_comp_faq_6_a": "Ναι. Επιβάλλει τους λογιστικούς κανόνες που απαιτούνται από τους ελεγκτές και τις φορολογικές αρχές.",
    "docs_comp_faq_7_q": "Μπορούν οι λογιστές να έχουν πρόσβαση στα δεδομένα εξ αποστάσεως;",
    "docs_comp_faq_7_a": "Ναι. Μέσω του Web Panel Λογιστή, όχι του POS.",
    "docs_comp_panel_title": "Web Panel Λογιστή",
    "docs_comp_panel_desc": "Μια ξεχωριστή διαδικτυακή πλατφόρμα για λογιστές και διαχειριστές. ΔΕΝ είναι POS.",
    "docs_comp_panel_what": "Τι μπορούν να κάνουν οι λογιστές",
    "docs_comp_panel_list_1": "Προβολή τιμολογίων",
    "docs_comp_panel_list_2": "Ελέγξτε τις πληρωμές και τις επιστροφές χρημάτων",
    "docs_comp_panel_list_3": "Παρακολούθηση εξόδων",
    "docs_comp_panel_list_4": "Διαχείριση μισθοδοσίας και μισθών",
    "docs_comp_panel_list_5": "Αναλύστε τα περιθώρια και τις ταμειακές ροές",
    "docs_comp_panel_list_6": "Εξαγωγή δεδομένων",
    "docs_comp_panel_list_7": "Παρακολούθηση πολλών υποκαταστημάτων",
    "docs_comp_panel_remote": "Όλα εξ αποστάσεως, χωρίς πρόσβαση σε συσκευές POS.",
    "docs_comp_panel_box": "<strong>Γιατί αυτό έχει σημασία:</strong> Διαχωρισμός καθηκόντων (καμία πρόσβαση ταμία σε λογιστικά εργαλεία) και επαγγελματικές ροές εργασίας ελέγχου. Αυτό είναι ένα σημαντικό χαρακτηριστικό της επιχείρησης.",
    "docs_comp_int_title": "Ενσωματώσεις και πόροι προγραμματιστών",
    "docs_comp_api_title": "API Προϊόντος",
    "docs_comp_api_desc": "Για συστήματα ERP, σαρωτές αποθέματος, εξωτερικές εφαρμογές και συστήματα αποθήκης.",
    "docs_comp_api_sec": "<strong>Μοντέλο ασφαλείας:</strong> Σύζευξη συσκευών, έλεγχος ταυτότητας βάσει διακριτικού, απομόνωση μισθωτή, περιορισμός ρυθμού. Δεν είναι δυνατή η πρόσβαση μεταξύ μισθωτών.",
    "docs_comp_woo_title": "Ενσωμάτωση WooCommerce",
    "docs_comp_woo_list_1": "Συγχρονισμός προϊόντων από Finio → WooCommerce",
    "docs_comp_woo_list_2": "Αποστολή παραγγελιών από WooCommerce → Finio",
    "docs_comp_woo_limits": "<strong>Σημαντικοί περιορισμοί:</strong> Μονόδρομος συγχρονισμός προϊόντων, Οι παραγγελίες απαιτούν έγκριση, Ο συγχρονισμός αποθεμάτων εξαρτάται από τη διαμόρφωση.",
    "docs_comp_local_title": "Τοπικοί διακομιστές Web (Για προχωρημένους)",
    "docs_comp_local_desc": "Το Finio περιλαμβάνει ενσωματωμένους διακομιστές ιστού για tablet σερβιτόρων, περίπτερα αυτόματης παραγγελίας, πρόσβαση στον ιστό PMS και φόρμες σχολίων. Όλα είναι πιστοποιημένα, βασισμένα σε LAN και περιορισμένης ταχύτητας.",
    "docs_comp_sec_title": "Μοντέλο ασφαλείας",
    "docs_comp_sec_list_1": "Απομόνωση ενοικιαστή",
    "docs_comp_sec_list_2": "Δικαιώματα βάσει ρόλου",
    "docs_comp_sec_list_3": "Επιβολή ώρας διακομιστή",
    "docs_comp_sec_list_4": "Κρυπτογράφηση εκτός σύνδεσης",
    "docs_comp_sec_list_5": "Δεν υπάρχουν κοινά δεδομένα μεταξύ επιχειρήσεων",

    // Offline Sync (Greek)
    "docs_offline_title": "Αρχιτεκτονική εκτός σύνδεσης και Συγχρονισμός",
    "docs_offline_lead": "Το κατανεμημένο σύστημα του Finio εξασφαλίζει 100% χρόνο λειτουργίας μέσω μιας αρχιτεκτονικής βάσης δεδομένων Hive 'local-first'.",
    "docs_offline_hive_title": "Βάση δεδομένων Local-First (Hive)",
    "docs_offline_hive_desc": "Το Finio δεν βασίζεται σε συνεχή σύνδεση στο cloud. Αντίθετα, εκτελεί μια πλήρη βάση δεδομένων NoSQL (Hive) απευθείας στο υλικό της συσκευής σας.",
    "docs_offline_hive_list_1": "<strong>Ταχύτητα ανάγνωσης/εγγραφής:</strong> Άμεσες λειτουργίες μηδενικής καθυστέρησης (< 16ms).",
    "docs_offline_hive_list_2": "<strong>Κυριαρχία δεδομένων:</strong> Ο πλήρης κατάλογος προϊόντων, η λίστα πελατών και το ιστορικό συναλλαγών σας βρίσκονται στη συσκευή σας.",
    "docs_offline_hive_list_3": "<strong>Κρυπτογράφηση:</strong> Τα τοπικά δεδομένα κρυπτογραφούνται σε κατάσταση ηρεμίας χρησιμοποιώντας πρότυπα AES-256.",
    "docs_offline_why_box": "<strong>Γιατί αυτό έχει σημασία:</strong> Τα περισσότερα συστήματα POS ιστού σταματούν να λειτουργούν όταν διακόπτεται το WiFi. Το Finio συνεχίζει να επεξεργάζεται τις πωλήσεις, να εκτυπώνει αποδείξεις και να ανοίγει συρτάρια μετρητών επειδή η λογική είναι τοπική.",
    "docs_offline_queue_title": "Η ουρά συγχρονισμού Delta",
    "docs_offline_queue_desc": "Όταν εκτελείτε μια ενέργεια (π.χ. Κλείσιμο λογαριασμού), το Finio την εγγράφει σε μια μόνιμη \"Ουρά συγχρονισμού\".",
    "docs_offline_queue_step_1": "<strong>Δέσμευση ενέργειας:</strong> Η συναλλαγή αποθηκεύεται στο τοπικό καθολικό άμεσα.",
    "docs_offline_queue_step_2": "<strong>Εισαγωγή ουράς:</strong> Ένα ωφέλιμο φορτίο JSON της μετάλλαξης προστίθεται στον πίνακα <code>sync_queue</code>.",
    "docs_offline_queue_step_3": "<strong>Εργαζόμενος στο παρασκήνιο:</strong> Μια διαδικασία στο παρασκήνιο προσπαθεί να αδειάσει την ουρά στο cloud.",
    "docs_offline_queue_step_4": "<strong>Λογική επανάληψης:</strong> Εάν το δίκτυο είναι εκτός λειτουργίας, ο εργαζόμενος κοιμάται και προσπαθεί ξανά με εκθετική οπισθοχώρηση.",
    "docs_offline_conflict_title": "Στρατηγική επίλυσης συγκρούσεων",
    "docs_offline_conflict_desc": "Σε περιβάλλον πολλών συσκευών, το Finio χρησιμοποιεί μια <strong>Ντετερμινιστική Στρατηγική Συγχώνευσης</strong> για τον χειρισμό συγκρούσεων.",
    "docs_offline_append_title": "Καθολικό μόνο για προσθήκη",
    "docs_offline_append_desc": "Δεν αντικαθιστούμε ποτέ τα αρχεία πληρωμών. Εάν η Συσκευή Α λάβει 50 $ και η Συσκευή Β λάβει 20 $ για το ίδιο τιμολόγιο:",
    "docs_offline_append_list_1": "Η Συσκευή Α δεσμεύει το αναγνωριστικό πληρωμής #101 (50 $).",
    "docs_offline_append_list_2": "Η Συσκευή Β δεσμεύει το αναγνωριστικό πληρωμής #102 (20 $).",
    "docs_offline_append_list_3": "Ο διακομιστής Cloud δέχεται και τα δύο.",
    "docs_offline_append_list_4": "Το υπόλοιπο τιμολογίου ενημερώνεται ώστε να αντικατοπτρίζει τα 70 $ που πληρώθηκαν.",
    "docs_offline_time_title": "Ώρα που καθορίζεται από τον διακομιστή",
    "docs_offline_time_desc": "Για την αποτροπή απάτης (π.χ. αλλαγή του ρολογιού της συσκευής για την προχρονολόγηση πωλήσεων), όλα τα συμβάντα συγχρονισμού επισημαίνονται με <code>server_timestamp</code> κατά την άφιξη. Οι οικονομικές αναφορές χρησιμοποιούν αυτήν την ώρα διακομιστή για να λένε την αλήθεια.",

    // KDS Workflow (Greek)
    "docs_kds_title": "Σύστημα Προβολής Κουζίνας (KDS)",
    "docs_kds_lead": "Βελτιστοποιήστε τις λειτουργίες της κουζίνας σας με παρακολούθηση παραγγελιών σε πραγματικό χρόνο.",
    "docs_kds_overview_title": "Επισκόπηση",
    "docs_kds_overview_desc": "Μεταβείτε στο: <strong>KDS</strong> από το κύριο μενού.",
    "docs_kds_list_1": "<strong>Παραγγελίες σε πραγματικό χρόνο:</strong> Οι παραγγελίες εμφανίζονται αμέσως μόλις τοποθετηθούν.",
    "docs_kds_list_2": "<strong>Ομαδοποίηση σταθμών:</strong> Οι παραγγελίες δρομολογούνται σε συγκεκριμένους σταθμούς (Κουζίνα, Μπαρ, Ψησταριά).",
    "docs_kds_list_3": "<strong>Χρονόμετρα:</strong> Παρακολουθήστε τον χρόνο προετοιμασίας για κάθε στοιχείο.",
    "docs_kds_list_4": "<strong>Πολλαπλές οθόνες:</strong> Υποστήριξη για πολλές οθόνες προβολής σε διαφορετικούς σταθμούς.",
    "docs_kds_flow_title": "Ροή κατάστασης",
    "docs_kds_flow_desc": "Οι παραγγελίες περνούν από τα ακόλουθα στάδια:",
    "docs_kds_flow_step_1": "<strong>Νέο:</strong> Παραγγελία ελήφθη.",
    "docs_kds_flow_step_2": "<strong>Σε προετοιμασία:</strong> Το προσωπικό επιβεβαιώνει την παραγγελία.",
    "docs_kds_flow_step_3": "<strong>Έτοιμο:</strong> Το στοιχείο έχει μαγειρευτεί και είναι έτοιμο για σερβίρισμα.",
    "docs_kds_flow_step_4": "<strong>Σερβιρίστηκε:</strong> Παραγγελία ολοκληρώθηκε.",
    "docs_kds_note_box": "<strong>Σημείωση:</strong> Το KDS λειτουργεί τοπικά μέσω LAN, διασφαλίζοντας τη λειτουργικότητα ακόμη και χωρίς internet.",

    // PMS Overview (Greek)
    "docs_pms_title": "PMS & Ξενοδοχειακή Μονάδα",
    "docs_pms_lead": "Διαχειριστείτε δωμάτια, κρατήσεις και επισκέπτες αποτελεσματικά με το ολοκληρωμένο Σύστημα Διαχείρισης Ακινήτων μας.",
    "docs_pms_key_title": "Βασικά Χαρακτηριστικά",
    "docs_pms_key_desc": "Μεταβείτε στο: <strong>PMS</strong> από το κύριο μενού.",
    "docs_pms_list_1": "<strong>Διαχείριση δωματίων:</strong> Οπτική επισκόπηση της κατάστασης και της διαθεσιμότητας των δωματίων.",
    "docs_pms_list_2": "<strong>Check-in / Check-out:</strong> Απλοποιημένες ροές άφιξης και αναχώρησης επισκεπτών.",
    "docs_pms_list_3": "<strong>Παρακολούθηση πληρότητας:</strong> Παρακολουθήστε τα ποσοστά πληρότητας σε πραγματικό χρόνο.",
    "docs_pms_list_4": "<strong>Ολοκληρωμένη χρέωση:</strong> Οι χρεώσεις δωματίου συνδέονται αυτόματα με τα τιμολόγια επισκεπτών.",
    "docs_pms_list_5": "<strong>Καθαριότητα:</strong> Διαχειριστείτε τα προγράμματα καθαρισμού και τη συντήρηση δωματίων.",
    "docs_pms_designed_title": "Σχεδιασμένο για",
    "docs_pms_designed_1": "Ξενοδοχεία",
    "docs_pms_designed_2": "Riads",
    "docs_pms_designed_3": "Ξενώνες",
    "docs_pms_designed_4": "Βραχυχρόνιες μισθώσεις",

    // Roles & Permissions (Greek)
    "docs_roles_title": "Ρόλοι και Δικαιώματα",
    "docs_roles_lead": "Ελέγξτε την πρόσβαση και διασφαλίστε την ασφάλεια με λεπτομερή διαχείριση ρόλων.",
    "docs_roles_manage_title": "Διαχείριση ρόλων",
    "docs_roles_manage_desc": "Μεταβείτε στο: <strong>Ρυθμίσεις → Ρόλοι και Δικαιώματα</strong>",
    "docs_roles_list_1": "<strong>Δημιουργία προσαρμοσμένων ρόλων:</strong> Καθορίστε μοναδικούς ρόλους προσαρμοσμένους στις ανάγκες της επιχείρησής σας (π.χ. Ταμίας, Διευθυντής, Λογιστής).",
    "docs_roles_list_2": "<strong>Λεπτομερή δικαιώματα:</strong> Εκχωρήστε συγκεκριμένα δικαιώματα σε κάθε ρόλο.",
    "docs_roles_scopes_title": "Εύρος δικαιωμάτων",
    "docs_roles_scopes_desc": "Τα δικαιώματα μπορούν να εφαρμοστούν σε διάφορες ενότητες:",
    "docs_roles_scopes_1": "<strong>POS:</strong> Πρόσβαση πώλησης, ακύρωση ειδών, εφαρμογή εκπτώσεων.",
    "docs_roles_scopes_2": "<strong>Απόθεμα:</strong> Προσθήκη προϊόντων, προσαρμογές αποθεμάτων.",
    "docs_roles_scopes_3": "<strong>Λογιστική:</strong> Προβολή αναφορών, κλείσιμο βάρδιας.",
    "docs_roles_scopes_4": "<strong>Ρυθμίσεις:</strong> Αλλαγή διαμορφώσεων συστήματος.",

    // Finance & Closures
    "docs_faq_title": "Συχνές Ερωτήσεις",
    "docs_faq_subtitle": "Απαντήσεις σε κοινές ερωτήσεις σχετικά με το Finio POS, KDS και την πλατφόρμα διαχείρισης.",
    "docs_faq_cat_pos": "POS & Πωλήσεις",
    "docs_faq_q_offline_sell": "Μπορώ να πουλήσω προϊόντα χωρίς σύνδεση στο internet;",
    "docs_faq_a_offline_sell": "Ναι. Το Finio είναι ένα σύστημα <strong>offline-first</strong>. Όλες οι πωλήσεις, τα κλεισίματα Z και οι κινήσεις τραπεζιών αποθηκεύονται τοπικά. Το σύστημα συγχρονίζει τα πάντα αυτόματα στο cloud μόλις αποκατασταθεί η σύνδεση.",
    "docs_faq_q_prices": "Υποστηρίζει το POS πολλαπλά επίπεδα τιμών;",
    "docs_faq_a_prices": "Ναι, μπορείτε να διαμορφώσετε πολλαπλά επίπεδα τιμών (π.χ. Happy Hour, VIP, Προσωπικό) και να κάνετε εναλλαγή άμεσα κατά την πώληση.",
    "docs_faq_q_split": "Μπορώ να χωρίσω λογαριασμούς ή να συγχωνεύσω τραπέζια;",
    "docs_faq_a_split": "Απολύτως. Μπορείτε να χωρίσετε πληρωμές κατά ποσό ή είδος, και να συγχωνεύσετε εύκολα τραπέζια ή να μεταφέρετε είδη.",
    "docs_faq_cat_sync": "Λειτουργία Offline & Συγχρονισμός",
    "docs_faq_q_break": "Τι συμβαίνει αν το tablet μου χαλάσει ενώ είμαι offline;",
    "docs_faq_a_break": "Το Finio έχει σχεδιαστεί για να προστατεύει τα δεδομένα σας ακόμη και σε περιβάλλοντα εκτός σύνδεσης. Όταν πολλαπλές συσκευές είναι συνδεδεμένες στο ίδιο τοπικό δίκτυο, μοιράζονται και συγχρονίζουν αυτόματα δεδομένα μεταξύ τους εκτός σύνδεσης. Αυτό σημαίνει ότι τα δεδομένα σας αποθηκεύονται τοπικά σε περισσότερες από μία συσκευές. Εάν μια συσκευή υποστεί ζημιά πριν από τον συγχρονισμό, τα δεδομένα παραμένουν διαθέσιμα στις άλλες συσκευές του δικτύου. Μόλις οποιαδήποτε συσκευή επανασυνδεθεί στο διαδίκτυο, όλα τα δεδομένα συγχρονίζονται αυτόματα στο cloud.",
    "docs_faq_q_status": "Πώς ξέρω αν είμαι online ή offline;",
    "docs_faq_a_status": "Ένας δείκτης κατάστασης στην επάνω δεξιά γωνία της οθόνης POS δείχνει την κατάστασή σας: Πράσινο για Online, και Γκρι/Κόκκινο για Offline.",
    "docs_faq_cat_billing": "Συνδρομές & Χρέωση",
    "docs_faq_q_plan": "Μπορώ να αλλάξω το πλάνο μου αργότερα;",
    "docs_faq_a_plan": "Ναι, μπορείτε να αναβαθμίσετε από Starter σε Business ανά πάσα στιγμή. Το υπόλοιπο της τρέχουσας συνδρομής σας θα συμψηφιστεί.",
    "docs_faq_q_cancel": "Υπάρχει τέλος ακύρωσης;",
    "docs_faq_a_cancel": "Όχι. Μπορείτε να ακυρώσετε τη συνδρομή σας οποιαδήποτε στιγμή. Η πρόσβασή σας θα παραμείνει ενεργή μέχρι το τέλος της τρέχουσας περιόδου χρέωσης.",
    "docs_faq_cat_hardware": "Υλικό",
    "docs_faq_q_printers": "Ποιοι εκτυπωτές είναι συμβατοί;",
    "docs_faq_a_printers": "Το Finio υποστηρίζει τους περισσότερους εκτυπωτές δικτύου ESC/POS (Ethernet/WiFi), συμπεριλαμβανομένων μαρκών όπως Epson, Star Micronics και Xprinter.",
    "docs_faq_q_tablet": "Μπορώ να χρησιμοποιήσω το υπάρχον tablet μου;",
    "docs_faq_a_tablet": "Ναι, το Finio λειτουργεί σε οποιοδήποτε σύγχρονο tablet iOS (iPad) ή Android. Συνιστούμε συσκευές με τουλάχιστον 3GB RAM.",
    "docs_faq_cat_security": "Ασφάλεια & Δεδομένα",
    "docs_faq_q_access": "Ποιος έχει πρόσβαση στα δεδομένα πωλήσεών μου;",
    "docs_faq_a_access": "Μόνο εσείς και οι εξουσιοδοτημένοι χρήστες. Το προσωπικό του Finio δεν μπορεί να έχει πρόσβαση στα δεδομένα σας χωρίς ρητή άδεια για υποστήριξη.",
    "docs_faq_q_legal": "Είναι τα τιμολόγιά μου νομικά συμβατά;",
    "docs_faq_a_legal": "Ναι. Το Finio δημιουργεί αμετάβλητα, διαδοχικά αριθμημένα τιμολόγια που συμμορφώνονται με τους φορολογικούς κανονισμούς. Μόλις οριστικοποιηθεί, ένα τιμολόγιο δεν μπορεί να τροποποιηθεί ή να διαγραφεί.",
    "docs_finance_title": "Τιμολόγια & Κλείσιμο",
    "docs_finance_lead": "Παρακολουθήστε τα οικονομικά σας, διαχειριστείτε τιμολόγια και εξασφαλίστε τη ροή μετρητών σας.",
    "docs_finance_invoices_title": "Τιμολόγια",
    "docs_finance_invoices_go_to": "Μεταβείτε: <strong>Τιμολόγια</strong>",
    "docs_finance_invoices_history": "<strong>Προβολή Ιστορικού:</strong> Πρόσβαση σε όλα τα προηγούμενα τιμολόγια.",
    "docs_finance_invoices_filtering": "<strong>Φιλτράρισμα:</strong> Φιλτράρισμα κατά ημερομηνία, κατάσταση, ταμείο ή χρήστη.",
    "docs_finance_invoices_actions": "<strong>Ενέργειες:</strong> Εκτύπωση ή εξαγωγή τιμολογίων PDF. Υποστηρίζεται επίσης η δημόσια κοινή χρήση τιμολογίων.",
    "docs_finance_important_rules": "Σημαντικοί Κανόνες:",
    "docs_finance_rule_1": "Τα σύνολα των τιμολογίων είναι αμετάβλητα μόλις δημιουργηθούν.",
    "docs_finance_rule_2": "Οι πληρωμές είναι μόνο για προσθήκη (δεν μπορούν να διαγραφούν, μόνο να αντισταθμιστούν).",
    "docs_finance_rule_3": "Οι επιστροφές χρημάτων δεν ξαναγράφουν ποτέ το ιστορικό. δημιουργούν νέες αντι-συναλλαγές.",
    "docs_finance_registers_title": "Ταμειακές Μηχανές",
    "docs_finance_registers_sessions": "<strong>Συνεδρίες:</strong> Κάθε ταμειακή μηχανή λειτουργεί σε συνεδρίες.",
    "docs_finance_registers_tracking": "<strong>Παρακολούθηση:</strong> Οι πληρωμές με μετρητά πρέπει να συνδέονται με μια ανοιχτή συνεδρία ταμείου.",
    "docs_finance_registers_security": "<strong>Ασφάλεια:</strong> Αποτρέπει μη ανιχνεύσιμες κινήσεις μετρητών και ενισχύει τη λογοδοσία.",
    "docs_finance_closures_title": "Άνοιγμα Ταμείου & Κλείσιμο Ημέρας (Z Closure)",
    "docs_finance_closures_start": "<strong>Έναρξη Βάρδιας:</strong> Ορισμός ποσού έναρξης ταμείου.",
    "docs_finance_closures_end": "<strong>Τέλος Βάρδιας:</strong> Εκτέλεση κλεισίματος ημέρας.",
    "docs_finance_closures_reconciliation": "<strong>Συμφιλίωση:</strong> Το σύστημα υπολογίζει τα Αναμενόμενα έναντι των Πραγματικών μετρητών και επισημαίνει τυχόν διαφορές.",
    "docs_finance_closures_locking": "<strong>Κλείδωμα:</strong> Οι κλειστές περίοδοι είναι κλειδωμένες και δεν μπορούν να τροποποιηθούν.",

    // --- Changelog ---
    "nav_changelog_title": "Αρχείο Αλλαγών",
    "changelog_version": "Έκδοση 1.0 → Έκδοση 2.0",
    "changelog_overview_title": "Επισκόπηση",
    "changelog_overview_p1": "Η έκδοση Finio 1.0 ήταν μια πιλοτική κυκλοφορία που παραδόθηκε σε περιορισμένο αριθμό επιλεγμένων πελατών σε διάφορους επιχειρηματικούς τομείς (εστιατόρια, λιανική, φιλοξενία και υπηρεσίες).",
    "changelog_overview_p2": "Ο στόχος της v1.0 δεν ήταν η κλίμακα, αλλά η μάθηση.",
    "changelog_overview_p3": "Μέσω άμεσης συνεργασίας με πραγματικές επιχειρήσεις, παρατηρήσαμε πώς συμπεριφέρονται τα συστήματα POS υπό πραγματική πίεση: χρήση εκτός σύνδεσης (offline), λάθη προσωπικού, επιστροφές, απώλεια αποθέματος, διαφορές στα μετρητά και χρήση πολλαπλών συσκευών.",
    "changelog_overview_p4": "Η Έκδοση 2.0 είναι το αποτέλεσμα αυτής της μάθησης.",
    "changelog_overview_p5": "Δεν είναι μια επανάληψη — είναι μια δομική αναβάθμιση που σχεδιάστηκε για να:",
    "changelog_overview_list_1": "Ελαχιστοποιήσει τον λειτουργικό κίνδυνο",
    "changelog_overview_list_2": "Αποτρέψει την αθόρυβη παραποίηση δεδομένων",
    "changelog_overview_list_3": "Μειώσει την κλοπή και τα λογιστικά σφάλματα",
    "changelog_overview_list_4": "Εντοπίσει αδύνατα σημεία στις επιχειρηματικές λειτουργίες",
    "changelog_overview_list_5": "Προετοιμάσει τις επιχειρήσεις να επεκταθούν σε στέρεα θεμέλια",

    "changelog_v1_title": "Έκδοση 1.0 — Πιλοτική Φάση (Θεμελιώδης Μάθηση)",
    "changelog_v1_purpose_title": "Σκοπός",
    "changelog_v1_purpose_1": "Επικύρωση χρήσης offline-first",
    "changelog_v1_purpose_2": "Παρατήρηση πραγματικής συμπεριφοράς προσωπικού",
    "changelog_v1_purpose_3": "Κατανόηση σημείων αποτυχίας σε υπάρχουσες ροές εργασίας POS",
    "changelog_v1_purpose_4": "Εντοπισμός πού χάνουν χρήματα οι επιχειρήσεις χωρίς να το συνειδητοποιούν",

    "changelog_v1_char_title": "Χαρακτηριστικά",
    "changelog_v1_char_1": "Βασική λειτουργικότητα POS",
    "changelog_v1_char_2": "Βασική υποστήριξη εκτός σύνδεσης",
    "changelog_v1_char_3": "Παρακολούθηση αποθέματος & πωλήσεων",
    "changelog_v1_char_4": "Ροές χειροκίνητης συμφιλίωσης",
    "changelog_v1_char_5": "Περιορισμένη προστασία κατά της αλλαγής δεδομένων",
    "changelog_v1_char_6": "Αντιδραστική αναφορά (τι συνέβη, όχι γιατί)",

    "changelog_findings_title": "Βασικά Ευρήματα από Πιλοτικούς Πελάτες",
    "changelog_findings_intro": "Κατά τη διάρκεια της πραγματικής χρήσης, εντοπίσαμε επαναλαμβανόμενα προβλήματα:",
    "changelog_findings_1": "Τιμολόγια που τροποποιούνται μετά την πληρωμή",
    "changelog_findings_2": "Πληρωμές που αντικαθίστανται ή χάνονται κατά τον συγχρονισμό",
    "changelog_findings_3": "Προϊόντα με πολλές επιστροφές που κρύβουν προβλήματα αποθέματος ή ποιότητας",
    "changelog_findings_4": "Διαφορές μετρητών χωρίς σαφή αιτία",
    "changelog_findings_5": "Διενέξεις πολλαπλών συσκευών που προκαλούν ασυνέπεια δεδομένων",
    "changelog_findings_6": "Επιχειρήσεις που επεκτείνονται πριν διορθώσουν εσωτερικές αδυναμίες",
    "changelog_findings_outro": "Αυτά τα συμπεράσματα διαμόρφωσαν άμεσα την Έκδοση 2.0.",

    "changelog_v2_title": "Έκδοση 2.0 — Παραγωγή & Έλεγχος Κινδύνου",
    "changelog_v2_desc": "Η Έκδοση 2.0 ξαναχτίστηκε με έλεγχο, ακεραιότητα και πρόληψη ως βασικές αρχές.",

    "changelog_acc_title": "Λογιστική & Ακεραιότητα Δεδομένων (Σημαντική Αναβάθμιση)",
    "changelog_acc_inv_title": "Αμετάβλητα Τιμολόγια",
    "changelog_acc_inv_1": "Μόλις εκδοθεί ένα τιμολόγιο, τα οικονομικά σύνολα δεν μπορούν να αλλάξουν.",
    "changelog_acc_inv_2": "Αποτρέπει αθόρυβες επεξεργασίες, παραποίηση ή αλλαγές μετά την πληρωμή.",
    "changelog_acc_inv_3": "Διασφαλίζει λογιστικά αρχεία ασφαλή για έλεγχο.",

    "changelog_acc_pay_title": "Καθολικό Πληρωμών Μόνο Προσθήκης",
    "changelog_acc_pay_1": "Οι πληρωμές δεν αντικαθίστανται ούτε διαγράφονται ποτέ.",
    "changelog_acc_pay_2": "Κάθε πληρωμή, επιστροφή ή αλλαγή κατάστασης καταγράφεται ως νέα εγγραφή.",
    "changelog_acc_pay_3": "Πλήρης ιχνηλασιμότητα όλων των οικονομικών ενεργειών.",

    "changelog_acc_atomic_title": "Ατομικές Συναλλαγές",
    "changelog_acc_atomic_1": "Οι πληρωμές επεξεργάζονται σε ατομικές λειτουργίες ασφαλείς από διακοπές.",
    "changelog_acc_atomic_2": "Χωρίς μερικές καταστάσεις εάν η συσκευή κλείσει κατά τη διάρκεια της συναλλαγής.",
    "changelog_acc_atomic_3": "Εξαλείφει πληρωμές 'φαντάσματα' και υπόλοιπα που δεν ταιριάζουν.",

    "changelog_acc_time_title": "Επιβολή Ώρας Διακομιστή",
    "changelog_acc_time_1": "Αποτρέπει την παραποίηση του ρολογιού της συσκευής.",
    "changelog_acc_time_2": "Όλα τα οικονομικά γεγονότα ακολουθούν ένα αξιόπιστο χρονοδιάγραμμα.",

    "changelog_off_title": "Αξιοπιστία Εκτός Σύνδεσης & Πολλαπλών Συσκευών",
    "changelog_off_design_title": "Offline-First εξ Σχεδιασμού",
    "changelog_off_design_1": "Πλήρης λειτουργία χωρίς internet.",
    "changelog_off_design_2": "Καμία υποβάθμιση λειτουργιών όταν είστε offline.",

    "changelog_off_local_title": "Συγχρονισμός Τοπικού Δικτύου",
    "changelog_off_local_1": "Οι συσκευές στο ίδιο τοπικό δίκτυο μοιράζονται δεδομένα offline.",
    "changelog_off_local_2": "Δημιουργεί τοπική εφεδρεία μεταξύ συσκευών.",
    "changelog_off_local_3": "Τα δεδομένα επιβιώνουν ακόμη και αν καταστραφεί μία συσκευή.",

    "changelog_off_sync_title": "Ασφαλής Συγχρονισμός Πολλαπλών Συσκευών",
    "changelog_off_sync_1": "Πληρωμές και τιμολόγια συγχωνεύονται με ασφάλεια.",
    "changelog_off_sync_2": "Χωρίς χαμένες συναλλαγές κατά τη χρήση πολλαπλών τερματικών.",
    "changelog_off_sync_3": "Η αρχή του διακομιστή επιλύει διενέξεις χωρίς να αντικαθιστά το ιστορικό.",

    "changelog_off_cloud_title": "Αυτόματη Ανάκτηση Cloud",
    "changelog_off_cloud_1": "Μόλις επανασυνδεθεί μια συσκευή, όλα τα δεδομένα συγχρονίζονται αυτόματα.",

    "changelog_inv_title": "Απόθεμα, Επιστροφές & Πρόληψη Απωλειών",
    "changelog_inv_refund_title": "Ευφυΐα Επιστροφών",
    "changelog_inv_refund_1": "Οι επιστροφές αντιμετωπίζονται ως οικονομικές αντιστροφές, όχι γενικά έξοδα.",
    "changelog_inv_refund_2": "Σαφής σύνδεση μεταξύ επιστροφών, προϊόντων και μεθόδων πληρωμής.",

    "changelog_inv_risk_title": "Ανίχνευση Κινδύνου Προϊόντος",
    "changelog_inv_risk_1": "Ταυτοποίηση προϊόντων με: Υψηλά ποσοστά επιστροφών, συχνά παράπονα ή διαφορές αποθέματος.",
    "changelog_inv_risk_2": "Βοηθά στον εντοπισμό: Κακών προμηθευτών, προβλημάτων ποιότητας ή απάτης.",

    "changelog_inv_stock_title": "Βελτιώσεις Ακεραιότητας Αποθέματος",
    "changelog_inv_stock_1": "Καλύτερη παρακολούθηση κατεστραμμένων, επιστρεφόμενων και μη πωλήσιμων ειδών.",
    "changelog_inv_stock_2": "Μειωμένη απόκλιση αποθέματος με την πάροδο του χρόνου.",

    "changelog_health_title": "Υγεία Επιχείρησης & Υποστήριξη Αποφάσεων",
    "changelog_health_ind_title": "Δείκτες Υγείας Επιχείρησης",
    "changelog_health_ind_1": "Σήματα έγκαιρης προειδοποίησης για: υπερβολικές επιστροφές, συρρίκνωση περιθωρίων, ανισορροπία μετρητών.",
    "changelog_health_ind_2": "Σχεδιασμένο για να διορθώνει προβλήματα πριν την επέκταση.",

    "changelog_health_ops_title": "Λειτουργική Ορατότητα",
    "changelog_health_ops_1": "Σαφής διαχωρισμός μεταξύ: απόδοσης πωλήσεων, λειτουργικών ζητημάτων και λογιστικών ασυνεπειών.",

    "changelog_cash_title": "Έλεγχος Μετρητών & Συμφιλίωση",
    "changelog_cash_bind_title": "Δέσμευση Ταμειακής Μηχανής",
    "changelog_cash_bind_1": "Κάθε κίνηση μετρητών πρέπει να συνδέεται με μια ενεργή συνεδρία.",
    "changelog_cash_bind_2": "Αποτρέπει μη ανιχνεύσιμες λειτουργίες μετρητών.",

    "changelog_cash_eod_title": "Κλειστές Λογιστικές Περίοδοι",
    "changelog_cash_eod_1": "Μόλις κλείσει η ημέρα, τα ιστορικά δεδομένα προστατεύονται.",
    "changelog_cash_eod_2": "Αποτρέπει αναδρομική παραποίηση.",
    "changelog_cash_eod_3": "Διασφαλίζει καθαρή ημερήσια συμφιλίωση.",

    "changelog_plat_title": "Βελτιώσεις Πλατφόρμας & Αρχιτεκτονικής",
    "changelog_plat_mod_title": "Ωριμότητα Αρθρωτού Συστήματος",
    "changelog_plat_mod_1": "POS, KDS, Απόθεμα, PMS, Delivery, Πάνελ Λογιστικής πλήρως ευθυγραμμισμένα.",
    "changelog_plat_mod_2": "Σαφής διαχωρισμός ρόλων (Ταμίας, Διευθυντής, Λογιστής, Διαχειριστής).",

    "changelog_plat_web_title": "Βελτιωμένα Πάνελ Web",
    "changelog_plat_web_1": "Πρόσβαση λογιστή & διαχείρισης για: Έξοδα, Μισθοδοσία, Τιμολόγια B2B, Οικονομικά στατιστικά.",

    "changelog_reps_title": "Τι Αντιπροσωπεύει η Έκδοση 2.0",
    "changelog_reps_intro": "Το Finio v2.0 κατασκευάστηκε για επιχειρήσεις που θέλουν να:",
    "changelog_reps_list_1": "Αναπτυχθούν χωρίς να χάσουν τον έλεγχο",
    "changelog_reps_list_2": "Αποτρέψουν κλοπές και αθόρυβα λάθη",
    "changelog_reps_list_3": "Εντοπίσουν έγκαιρα κακά προϊόντα",
    "changelog_reps_list_4": "Εμπιστεύονται τους αριθμούς τους",
    "changelog_reps_list_5": "Επεκταθούν σε υγιή λειτουργικά θεμέλια",
    "changelog_reps_outro": "Δεν είναι πλέον απλώς ένα POS — είναι μια πλατφόρμα επιχειρηματικής ακεραιότητας.",

    "changelog_future_title": "Κοιτάζοντας Μπροστά",
    "changelog_future_1": "Η Έκδοση 2.0 καθιερώνει τα αδιαπραγμάτευτα θεμέλια.",
    "changelog_future_2": "Οι μελλοντικές εκδόσεις θα χτιστούν πάνω σε αυτή την ακεραιότητα, όχι γύρω από αυτήν.",

  }
};

const i18n = createI18n({
  legacy: false, // Required for Composition API (useI18n)
  globalInjection: true, // Makes $t available in templates
  locale: localStorage.getItem('selectedLanguage') || 'en',
  fallbackLocale: 'en',
  messages,
});

export default i18n;
